import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { authTranslations } from '../i18n/authTranslations';
import { miscTranslations } from '../i18n/miscTranslations';
import { contractTranslations } from '../i18n/contractTranslations';
import { devTranslations } from '../i18n/devTranslations';

export type Language = 'pt' | 'en' | 'es';

const LANGUAGE_STORAGE_KEY = 'delivai_language';

function detectInitialLanguage(): Language {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved === 'pt' || saved === 'en' || saved === 'es') return saved;
  }
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'pt').toLowerCase();
  if (nav.startsWith('en')) return 'en';
  if (nav.startsWith('es')) return 'es';
  return 'pt';
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Traduções
const translations = {
  pt: {
    ...authTranslations.pt,
    ...miscTranslations.pt,
    ...devTranslations.pt,
    ...contractTranslations.pt,
    // Sidebar
    'sidebar.orders': 'PDV',
    'sidebar.conversations': 'Conversas',
    'sidebar.company': 'Empresa',
    'sidebar.employees': 'Colaboradores',
    'sidebar.delivery': 'Delivery',
    'sidebar.delivery_title': 'Delivery — despacho e Fast Order',
    'sidebar.motoboys': 'Motoboys',
    'sidebar.motoboys_title': 'Ranking de Motoboys',
    'sidebar.clients': 'Clientes',
    'sidebar.clients_title': 'Clientes (pedidos e agendamentos)',
    'sidebar.schedule': 'Agendamentos',
    'sidebar.manager': 'Vendas',
    'sidebar.managerTitle': 'Resumo de vendas e relatórios',
    'sidebar.license': 'Licença',
    'sidebar.logout': 'Sair',
    'sidebar.language': 'Idioma',
    'sidebar.section_main': 'Principal',
    'sidebar.section_orders': 'Pedidos',
    'sidebar.section_store': 'Loja',
    'pdv.title': 'PDV',
    'pdv.mode_mesa': 'Mesa',
    'pdv.mode_retirada': 'Retirada',
    'pdv.mode_toggle_aria': 'Alternar entre mesa e retirada',
    'pdv.catalog_all_categories': 'Todas',
    'pdv.catalog_categories': 'Categorias do cardápio',
    'pdv.catalog_other': 'Outros',
    'pdv.print_error': 'Pedido lançado, mas a impressão falhou. Verifique a impressora.',
    'pdv.sidebar_open_count': '{count} em aberto',
    'pdv.open_table': 'Abrir mesa',
    'pdv.new_pickup': 'Nova retirada',
    'pdv.no_open_pickups': 'Nenhuma retirada em aberto.',
    'pdv.open_table_title': 'Abrir mesa',
    'pdv.open_table_hint': 'Digite o número da mesa. Sem cadastro — use o que quiser.',
    'pdv.table_number': 'Número da mesa',
    'pdv.table_number_placeholder': 'Ex.: 5',
    'pdv.open_table_confirm': 'Abrir',
    'pdv.table_invalid': 'Informe o número da mesa.',
    'pdv.no_open_tables': 'Nenhuma mesa aberta.',
    'pdv.past_day_hint': 'Dia anterior: feche as comandas que ficaram abertas.',
    'pdv.waiting_digital': 'Digital',
    'pdv.waiting_digital_hint': 'Ainda não chegou',
    'pdv.waiting_pickup': 'Retiradas em aberto',
    'pdv.assign_table_title': 'Abrir mesa deste pedido',
    'pdv.assign_table_hint': 'Quando o cliente sentar, informe o número da mesa.',
    'pdv.back_to_pickup': 'Retiradas',
    'pdv.history_dock_aria': 'Histórico de comandas: toque para abrir ou fechar a lista',
    'pdv.open_tables': 'Mesas abertas',
    'pdv.table_n': 'Mesa {n}',
    'pdv.back_to_tables': 'Mesas',
    'pdv.close_table': 'Fechar mesa',
    'pdv.close_table_title': 'Fechar mesa',
    'pdv.close_table_msg': 'Finaliza a comanda desta mesa.',
    'pdv.close_table_hint': 'Toque na forma para lançar o restante, ou digite os valores. Pode dividir.',
    'pdv.charge_selected': 'Cobrar selecionado',
    'pdv.charge_selected_hint': 'Escolha como este valor entra. A mesa continua aberta.',
    'pdv.charge_qty': 'Cobrar {n} de {total}',
    'pdv.split_open': 'Dividir',
    'pdv.split_title': 'Dividir este item',
    'pdv.split_parts': 'Dividir o que falta em',
    'pdv.split_amount': 'Ou cobrar este valor',
    'pdv.split_hint': 'Se digitar um valor, é esse que entra nesta cobrança. Se deixar vazio, divide o que ainda falta.',
    'pdv.split_apply': 'Usar este valor',
    'pdv.split_now': 'Nesta cobrança {amount}',
    'pdv.charge_half': '1/2',
    'pdv.charge_third': '1/3',
    'pdv.charge_custom': 'Valor desta cobrança',
    'pdv.select_item': 'Selecionar item',
    'pdv.paid_open': '{paid} pago · {open} em aberto',
    'pdv.paid_amount_left': 'pago {paid} · falta {left}',
    'pdv.total_label': 'Total',
    'pdv.paid_label': 'Pago',
    'pdv.due_label': 'Falta',
    'pdv.charged': 'Cobrança lançada.',
    'pdv.paid_keep': 'Este item já tem valor pago.',
    'pdv.finalize_hint': 'Toque na forma para lançar o restante, ou digite os valores. Pode dividir.',
    'pdv.print_customer_title': 'Imprimir conta',
    'pdv.print_customer_hint': 'Quantas pessoas vão dividir? A conta sai com o valor por pessoa.',
    'pdv.split_people': 'Pessoas',
    'pdv.split_people_aria': 'Número de pessoas para dividir a conta',
    'pdv.increase_people': 'Aumentar pessoas',
    'pdv.decrease_people': 'Diminuir pessoas',
    'pdv.per_person': '{amount} por pessoa',
    'pdv.add_payment': 'Outra forma',
    'pdv.pay_click_method': 'Lançar o restante em {method}',
    'pdv.remove_payment': 'Remover pagamento',
    'pdv.pay_amount': 'Valor',
    'pdv.pay_remaining': 'Falta {amount}',
    'pdv.pay_over': 'Passou {amount}',
    'pdv.pay_ok': 'Pagamento fecha o total',
    'pdv.pay_need_cover': 'A soma das formas precisa fechar o total.',
    'pdv.pay_debit': 'Cartão débito',
    'pdv.pay_credit': 'Cartão crédito',
    'pdv.settle_total': 'Total',
    'pdv.catalog_empty': 'Nenhum item no cardápio.',
    'pdv.search_catalog': 'Buscar no cardápio',
    'pdv.no_photo': 'Sem foto',
    'pdv.unnamed_product': 'Item',
    'menu.channel_delivery': 'Delivery',
    'menu.channel_mesa': 'Mesa',
    'menu.channel_retirada': 'Retirada',
    'menu.channel_product': 'Cardápios do produto',
    'menu.channel_category': 'Cardápios da categoria',
    'pdv.cart_title': 'Comanda',
    'pdv.cart_empty': 'Toque um item para adicionar à comanda.',
    'pdv.client_name': 'Nome',
    'pdv.client_name_placeholder': 'Nome (opcional)',
    'pdv.payment': 'Pagamento',
    'pdv.pay_cash': 'Dinheiro',
    'pdv.pay_pix': 'PIX',
    'pdv.pay_card': 'Cartão',
    'pdv.launch': 'Lançar',
    'pdv.launching': 'Lançando…',
    'pdv.launched': 'Pedido lançado e impresso.',
    'pdv.launch_error': 'Não foi possível lançar. Tente de novo.',
    'pdv.item_added': 'Item adicionado.',
    'pdv.pick_size': 'Escolha o tamanho',
    'pdv.print_format': 'Tipo de cupom',
    'pdv.print_economic': 'Cupom 80mm (Econômico)',
    'pdv.print_large': 'Cupom 80mm (Grande)',
    'pdv.print_pro': 'Cupom 80mm (PRO)',
    'pdv.print_dispatch': 'Cupom 80mm (Despacho)',
    'pdv.finalize': 'Finalizar',
    'pdv.finalize_title': 'Finalizar pedido',
    'pdv.finalize_msg': 'Marca este pedido como finalizado.',
    'pdv.finalized': 'Pedido finalizado.',
    'pdv.finalize_error': 'Não foi possível finalizar.',
    'pdv.cancel': 'Cancelar pedido',
    'pdv.cancel_title': 'Cancelar este pedido?',
    'pdv.cancel_msg': 'O pedido some da produção. Os outros lançamentos continuam normais.',
    'pdv.cancelled': 'Pedido cancelado.',
    'pdv.cancel_error': 'Não foi possível cancelar.',
    'pdv.cancel_order_aria': 'Cancelar pedido de {name}',
    'pdv.other_tickets': 'Outras comandas desta mesa',
    'pdv.ticket_n': 'Comanda #{n}',
    'pdv.open_ticket_count': '{count} comandas',
    'pdv.walk_in': 'Balcão',
    'pdv.back_to_categories': 'Categorias',
    'pdv.category_item_count': '{count} itens',
    'pdv.add_to_comanda': 'Adicionar à comanda',
    'pdv.save_item': 'Salvar item',
    'pdv.edit_item': 'Editar',
    'pdv.remove_item': 'Remover',
    'pdv.increase_qty': 'Aumentar quantidade',
    'pdv.decrease_qty': 'Diminuir quantidade',
    'pdv.pending_badge': 'Novo',
    'pdv.pending_count': '{count} item(ns) para lançar',
    'pdv.not_launched': 'Ainda não lançado',
    'pdv.launch_print': 'Lançar e imprimir',
    'pdv.launch_charge': 'Lançar, cobrar e fechar',
    'pdv.confirm_launch_title': 'Confirmar lançamento',
    'pdv.confirm_launch_msg': 'Imprime na cozinha só o que ainda não foi lançado. Confere a comanda antes.',
    'pdv.confirm_launch_charge_title': 'Cobrar e fechar',
    'pdv.confirm_launch_charge_msg': 'Imprime cozinha (itens novos), a conta e finaliza com {method}.',
    'pdv.updated_no_print': 'Comanda atualizada.',
    'pdv.item_updated': 'Item atualizado.',
    'pdv.item_removed': 'Item removido.',
    'pdv.print_customer': 'Imprimir conta',
    'pdv.print_ask_hint': 'Nada imprime se você não marcar.',
    'pdv.print_ask_new': 'Imprimir os itens novos',
    'pdv.print_ask_bill': 'Imprimir o resumo',
    'pdv.print_customer_ok': 'Conta enviada à impressora.',
    'pdv.print_customer_need_order': 'Lance a comanda antes de imprimir a conta.',
    'pdv.leave_pending_title': 'Itens sem lançar',
    'pdv.leave_pending_msg': 'Há itens na comanda que ainda não foram lançados. Se sair, eles serão perdidos.',
    'pdv.leave_pending_confirm': 'Sair mesmo assim',
    'pdv.close_table_pending': 'Há itens sem lançar. Lance ou remova antes de fechar a mesa.',
    'pdv.edit_not_found': 'Não achei este item no cardápio para editar.',
    'sidebar.section_control': 'Controle',
    'sidebar.module_unavailable': 'Módulo não disponível',
    'sidebar.schedule_unavailable': 'Agendamento não disponível',
    'sidebar.open_menu': 'Abrir menu',
    'sidebar.company_logo': 'Logo da empresa',

    'common.open': 'Aberto',
    'common.closed': 'Fechado',
    'common.loading': 'Carregando…',
    'common.until': 'até',
    'common.cancel': 'Cancelar',
    'common.confirm': 'Confirmar',
    'delivery.loading': 'Carregando dados de delivery…',
    'delivery.error.title': 'Erro ao carregar',
    'delivery.error.retry': 'Tentar novamente',
    'delivery.error.no_cnpj': 'CNPJ da loja não disponível. Faça login novamente.',
    'delivery.error.customer_unavailable': 'Aguarde o carregamento dos dados da loja ou tente novamente.',
    'delivery.error.load_failed': 'Não foi possível carregar os dados de delivery.',
    'delivery.dispatch.module_name': 'Delivery',
    'delivery.dispatch.title': 'Delivery',
    'delivery.dispatch.subtitle': 'Análise, produção e despacho rápido — chips, motoboy e rota.',
    'delivery.dispatch.search_label': 'Buscar comanda',
    'delivery.dispatch.search_placeholder': 'Telefone ou nº da comanda',
    'delivery.dispatch.search_clear': 'Limpar busca',
    'delivery.dispatch.filter_store': 'Loja da comanda',
    'delivery.dispatch.filter_all_stores': 'Todas as lojas',
    'delivery.dispatch.filter_empty': 'Nenhuma comanda com esse filtro.',
    'delivery.dispatch.config_fees': 'Taxas e bairros',
    'delivery.dispatch.col_analysis': 'Em análise',
    'delivery.dispatch.empty_analysis': 'Nenhum delivery em análise.',
    'delivery.dispatch.accept': 'Aceitar',
    'delivery.dispatch.review': 'Revisar',
    'delivery.dispatch.locator': 'Localizador',
    'delivery.dispatch.demo_title': 'Pedido de demonstração iFood',
    'delivery.dispatch.demo_msg': 'Não é um pedido real. Revise taxa, itens e aprove para simular o fluxo.',
    'delivery.dispatch.details': 'Detalhes',
    'delivery.dispatch.edit': 'Alterar pedido',
    'delivery.dispatch.cancel_reason_other': 'Descreva o motivo',
    'delivery.dispatch.open_details': 'Ver detalhes do pedido #{n}',
    'delivery.dispatch.close_details': 'Fechar detalhes',
    'delivery.dispatch.print': 'Imprimir',
    'delivery.dispatch.print_ok': 'Comanda enviada para impressão.',
    'delivery.dispatch.print_error': 'Não foi possível imprimir o pedido.',
    'delivery.dispatch.items': 'Itens',
    'delivery.dispatch.no_items': 'Nenhum item listado.',
    'delivery.dispatch.col_production': 'Em produção',
    'delivery.dispatch.col_route': 'Em rota',
    'delivery.dispatch.empty_production': 'Nenhum delivery em produção.',
    'delivery.dispatch.empty_route': 'Nenhum delivery em rota.',
    'delivery.dispatch.order_n': 'Pedido #{n}',
    'delivery.dispatch.courier': 'Motoboy',
    'delivery.dispatch.pick_courier': 'Selecione o motoboy',
    'delivery.dispatch.no_couriers': 'Nenhum motoboy ativo',
    'delivery.dispatch.dispatch': 'Despachar',
    'delivery.dispatch.route': 'Montar rota',
    'delivery.dispatch.finish': 'Confirmar entrega',
    'delivery.dispatch.reject': 'Recusar',
    'delivery.dispatch.finish_confirm_title': 'Finalizar entrega?',
    'delivery.dispatch.finish_confirm_msg': 'A comanda será marcada como finalizada neste painel, mesmo se o motoboy ainda não tiver concluído no app.',
    'delivery.dispatch.select_to_dispatch': 'Marque a caixa para escolher o motoboy e despachar.',
    'delivery.dispatch.select_order': 'Selecionar pedido #{n}',
    'delivery.dispatch.select_all': 'Selecionar todos',
    'delivery.dispatch.unselect_all': 'Limpar seleção',
    'delivery.dispatch.selected_count': '{count} selecionado(s)',
    'delivery.dispatch.drag_handle': 'Arrastar pedido',
    'delivery.dispatch.select_or_drag_hint': 'Marque para agrupar ou arraste o card até um motoboy.',
    'delivery.dispatch.drag_selected_hint': 'Arraste o card — os selecionados vão juntos.',
    'delivery.dispatch.drag_panel_hint': 'Arraste os cards para um motoboy à direita.',
    'delivery.dispatch.couriers_panel': 'Motoboys',
    'delivery.dispatch.couriers_panel_hint': 'Solte em Despachar ou Montar rota.',
    'delivery.dispatch.drag_group_overlay': '{count} pedido(s)',
    'delivery.dispatch.drag_drop_on_courier': 'Solte em Despachar ou Montar rota',
    'delivery.dispatch.confirm_dispatch_title': 'Confirmar despacho',
    'delivery.dispatch.confirm_route_title': 'Montar rota',
    'delivery.dispatch.confirm_dispatch_msg': 'Despachar {count} pedido(s) com {courier}?',
    'delivery.dispatch.confirm_route_msg': 'Montar rota de {count} pedido(s) com {courier}?',
    'delivery.dispatch.load_error': 'Não foi possível carregar as entregas.',
    'delivery.detail.street': 'Rua',
    'delivery.detail.address': 'Endereço',
    'delivery.detail.wait_min': 'Prazo (min)',
    'delivery.detail.unassigned': 'Sem motoboy',
    'delivery.detail.assign_courier': 'Atribuir motoboy',
    'delivery.detail.no_address': 'Endereço não informado',
    'delivery.detail.minutes': '{n} min',
    'delivery.detail.discard_edit': 'Descartar alterações',
    'delivery.detail.unit_price': 'Preço unitário',
    'delivery.detail.eta_range': 'Prazo até {from}–{to}',
    'delivery.dispatch.prev_day': 'Dia anterior',
    'delivery.dispatch.next_day': 'Dia seguinte',
    'delivery.dispatch.pick_day': 'Escolher o dia',
    'delivery.dispatch.action_error': 'Não foi possível atualizar o pedido.',
    'delivery.dispatch.dispatched': 'Pedido despachado.',
    'delivery.dispatch.marked_route': 'Pedido marcado em rota.',
    'delivery.dispatch.finished': 'Entrega finalizada.',
    'delivery.dispatch.mark_paid': 'Marcar como pago',
    'delivery.dispatch.mark_paid_ok': 'Pedido marcado como pago. O motoboy já vê que não precisa cobrar.',
    'delivery.dispatch.pay_title': 'Registrar pagamento',
    'delivery.dispatch.pay_hint': 'O cliente pagou depois do checkout. Informe como entrou o valor — o motoboy vê na sacola se já está pago.',
    'delivery.dispatch.pay_confirm': 'Confirmar pagamento',
    'delivery.dispatch.wait_courier': 'Em rota — a loja também pode confirmar a entrega.',
    'delivery.dispatch.eta_label': 'Prazo até {time}',
    'delivery.dispatch.fee_label': 'Taxa {fee}',
    'delivery.dispatch.my_fees_title': 'Suas taxas do dia',
    'delivery.dispatch.my_deliveries_count': '{count} entrega(s) em andamento',
    'entregas.ifood_locator_label': 'Localizador iFood',
    'entregas.copy_locator': 'Copiar',
    'entregas.ifood_call_with_locator': 'com localizador',
    'entregas.toast.locator_copied': 'Localizador copiado',
    'entregas.toast.locator_copy_error': 'Não foi possível copiar o localizador',
    'orders.print_choice_title': 'O que deseja imprimir?',
    'orders.print_choice_comanda': 'Pedido (comanda)',
    'orders.print_choice_qr': 'QR Code (sacola)',
    'orders.print_choice_hint': 'Cada opção imprime numa via separada. Pode clicar nas duas.',
    'orders.print_choice_close': 'Fechar',
    'orders.toast.print_qr_ok': 'QR Code enviado para impressão',
    'orders.qr_offer_title': 'Imprimir QR Code na sacola?',
    'orders.qr_offer_body':
      'O cliente escaneia, confirma os dados e passa a pedir pelo DelivAI — fora do iFood.',
    'orders.qr_offer_yes': 'Sim, imprimir QR',
    'orders.qr_offer_no': 'Agora não',
    'order_claim.headline': 'Parabéns! Aqui tem um presente pra você',
    'order_claim.subtitle': 'Confirme seus dados para continuar. Nada sensível aparece antes disso.',
    'order_claim.loading': 'Carregando…',
    'order_claim.pick_name': 'Qual o seu nome?',
    'order_claim.pick_address': 'Qual o seu endereço?',
    'order_claim.continue': 'Continuar',
    'order_claim.phone_label': 'Seu telefone (WhatsApp)',
    'order_claim.phone_placeholder': '(11) 99999-9999',
    'order_claim.submit_phone': 'Salvar e resgatar',
    'order_claim.done_title': 'Tudo certo!',
    'order_claim.done_body': 'Seu cadastro foi vinculado. Nos próximos pedidos já usamos seus dados.',
    'order_claim.prize_title': 'Seu prêmio',
    'order_claim.prize_min': 'Válido em pedidos a partir de {min}',
    'order_claim.rule_first_order': 'Válido no primeiro pedido na loja',
    'order_claim.rule_subsequent': 'Válido a partir do segundo pedido',
    'order_claim.rule_any': 'Válido no próximo pedido elegível',
    'order_claim.coupon_code': 'Código do cupom',
    'order_claim.coupon_rules': 'Regras: {rules}',
    'order_claim.error_invalid_link': 'Link inválido',
    'order_claim.error_expired': 'Este link expirou',
    'order_claim.error_load': 'Não foi possível abrir este presente',
    'order_claim.error_select_both': 'Escolha o nome e o endereço',
    'order_claim.error_challenge': 'Dados não conferem — tente de novo',
    'order_claim.error_phone': 'Não foi possível salvar o telefone',
    'orders.channel_all': 'Todos',
    'orders.channel_store': 'Loja',
    'orders.channel_delivery': 'Delivery',
    'orders.channel_hint': 'Delivery operacional fica em Entregas.',
    'orders.channel_go_delivery': 'Abrir Entregas',
    'orders.type_mesa': 'Mesa',
    'orders.type_retirada': 'Retirada',
    'orders.type_delivery': 'Delivery',
    'orders.empty_mesa': 'Nenhum pedido de mesa em andamento.',
    'orders.empty_retirada': 'Nenhum pedido de retirada em andamento.',
    'orders.empty_delivery_type': 'Nenhum delivery em andamento.',
    'orders.status.ready_short': 'Pronto',
    'orders.status.completed': 'Finalizado',
    'orders.status.cancelled': 'Cancelado',
    'orders.select_to_show_courier': 'Marque a caixa para escolher o entregador.',
    'orders.select_order_dispatch': 'Selecionar pedido #{n}',
    'orders.pick_deliverer': 'Selecione o entregador',
    'orders.select_all': 'Selecionar todos',
    'orders.unselect_all': 'Limpar seleção',
    'orders.finish': 'Finalizar',
    'orders.print_ticket': 'Imprimir comanda',
    'orders.print_order_aria': 'Imprimir pedido #{n}',
    'orders.edit': 'Editar',
    'orders.phone_not_informed': 'Não informado',
    'common.got_it': 'Entendi',
    'common.delete': 'Deletar',
    'common.back': 'Voltar',

    'google.connect': 'Conectar com Google',
    'google.disconnect': 'Desconectar Google',
    'google.popup_blocked_title': 'Janela do Google bloqueada',
    'google.popup_blocked_message':
      'Não foi possível abrir a janela de autorização. Toque em Continuar para conectar o Google nesta mesma tela.',
    'google.popup_blocked_continue': 'Continuar',
    'google.config_error_title': 'Conexão indisponível',
    'google.config_error_message':
      'Não foi possível iniciar a conexão com o Google. Verifique a configuração ou contate o suporte DelivAI.',
    'common.characters': '{count} caracteres',
    'common.save': 'Salvar',
    'common.saving': 'Salvando…',
    'common.duration_min': '{count} min',
    'common.duration_days': '{count} dia(s)',
    'common.weekday.sunday': 'Domingo',
    'common.weekday.monday': 'Segunda-feira',
    'common.weekday.tuesday': 'Terça-feira',
    'common.weekday.wednesday': 'Quarta-feira',
    'common.weekday.thursday': 'Quinta-feira',
    'common.weekday.friday': 'Sexta-feira',
    'common.weekday.saturday': 'Sábado',

    'conversations.archived': 'Arquivadas',
    'conversations.whatsapp': 'WhatsApp',
    'conversations.search_short': 'Pesquisar…',
    'conversations.loading_list': 'Carregando conversas...',
    'conversations.loading_more_chats': 'Carregando conversas anteriores…',
    'conversations.scroll_for_more': 'Role para carregar mais conversas',
    'conversations.error.load_list': 'Erro ao carregar conversas: {error}',
    'conversations.error.load_list_generic': 'Erro ao carregar conversas',
    'conversations.reload': 'Recarregar conversas',
    'conversations.select_to_start': 'Selecione uma conversa para começar',
    'conversations.loading_messages': 'Carregando mensagens...',

    'motoboys.module_title': 'Módulo Motoboys',
    'motoboys.ranking': 'Ranking de Motoboys',
    'motoboys.podium': 'Pódio do dia',
    'motoboys.place_1': '1º lugar',
    'motoboys.place_2': '2º lugar',
    'motoboys.place_3': '3º lugar',
    'motoboys.deliveries_count': '{count} entrega(s)',
    'motoboys.fees_total_label': 'Total em taxas',
    'motoboys.daily_label': 'Diária:',
    'motoboys.daily_edit_hint': 'Clique duas vezes para editar',
    'motoboys.with_deliveries': 'Motoboys com Entregas',
    'motoboys.total_deliveries': 'Total de Entregas',
    'motoboys.total_fees': 'Total em Taxas',
    'motoboys.dailies': '+ Diárias',
    'motoboys.total': 'Total',
    'motoboys.kitchen_sum': 'Soma de todas as lojas da dark kitchen',
    'motoboys.empty': 'Nenhum motoboy com entregas no momento',
    'motoboys.empty_hint': 'Os motoboys aparecerão aqui quando houver pedidos com entregador atribuído',
    'motoboys.requirements_title': 'Para aparecer aqui, o pedido precisa:',
    'motoboys.req_assigned': 'Ter um entregador atribuído',
    'motoboys.req_not_cancelled': 'Não estar cancelado',
    'motoboys.req_ready_or_done': 'Estar em rota ou concluído',
    'motoboys.console_hint': 'Verifique o console do navegador (F12) para ver detalhes sobre a busca de pedidos',
    'motoboys.my_summary': 'Minhas entregas',
    'motoboys.my_name': 'Você',
    'motoboys.me': 'Eu',
    'motoboys.my_deliveries': 'Suas entregas',
    'motoboys.my_fees': 'Suas taxas',
    'motoboys.my_day': 'Seu dia',
    'motoboys.personal_empty': 'Nenhuma entrega sua neste dia',
    'motoboys.personal_empty_hint': 'Quando um pedido for despachado no seu nome, os números aparecem aqui.',

    'schedule.module_title': 'Módulo Agendamentos',
    'schedule.subtitle': 'Gerencie seus compromissos e agendamentos de forma organizada',
    'schedule.tab_agenda': 'Agenda',
    'schedule.tab_reports': 'Relatórios',
    'schedule.select_professional': 'Selecionar agenda do profissional',
    'schedule.store_calendar': 'Agenda da loja',
    'schedule.google_missing': 'Google não conectado',
    'schedule.staff_own_agenda': 'Você está na sua agenda. Conecte o Google nesta tela.',
    'schedule.pick_professional_first': 'Escolha um profissional para criar um horário.',
    'schedule.reports.from': 'De',
    'schedule.reports.to': 'Até',
    'schedule.reports.professional': 'Profissional',
    'schedule.reports.service': 'Serviço',
    'schedule.reports.area': 'Área',
    'schedule.reports.payment': 'Pagamento',
    'schedule.reports.filter_all_pros': 'Todos os profissionais',
    'schedule.reports.filter_all_services': 'Todos os serviços',
    'schedule.reports.filter_all_areas': 'Todas as áreas',
    'schedule.reports.filter_all_payment': 'Todos',
    'schedule.reports.store_calendar': 'Agenda da loja',
    'schedule.reports.me': 'Minha agenda',
    'schedule.reports.paid': 'Pago',
    'schedule.reports.pending_pay': 'Pendente',
    'schedule.reports.loading': 'Carregando…',
    'schedule.reports.refresh': 'Atualizar',
    'schedule.reports.load_error': 'Não foi possível carregar o relatório',
    'schedule.reports.total_count': 'Atendimentos',
    'schedule.reports.total_amount': 'Total',
    'schedule.reports.paid_count': 'Pagos',
    'schedule.reports.pending_pay_count': 'Pag. pendente',
    'schedule.reports.by_professional': 'Por profissional',
    'schedule.reports.by_service': 'Por serviço',
    'schedule.reports.empty': 'Sem dados no período',
    'schedule.public_link': 'Link público',
    'schedule.copy_public_link': 'Copiar link público de agendamentos',
    'schedule.public_link_copied': 'Link de agendamento copiado',
    'schedule.public_link_copy_error': 'Não foi possível copiar o link',
    'schedule.new_short': 'Novo',
    'schedule.define_schedule_hint': 'Toque no dia, arraste em sequência, Ctrl+clique para dias soltos, ou toque no nome da semana. Dá para fechar o dia inteiro, o mesmo horário em todos, ou um horário diferente em cada dia.',
    'schedule.day_action_new': 'Novo agendamento',
    'schedule.day_action_close': 'Fechar o dia',
    'schedule.day_action_reopen': 'Reabrir o dia',
    'schedule.day_action_lock_slot': 'Travar horário',
    'schedule.day_action_slots_title': 'Horários do dia',
    'schedule.day_action_no_slots': 'Sem horários neste dia.',
    'schedule.slot_locked': 'Horário travado',
    'schedule.slot_booked': 'Horário ocupado',
    'schedule.slot_lock_hint': 'Clique e arraste para travar ou liberar vários horários.',
    'schedule.slot_closed_by_store': 'Este horário foi travado pela loja.',
    'schedule.closed_slots_save_error': 'Não foi possível travar o horário.',
    'schedule.confirm_lock_day_title': 'Travar este dia?',
    'schedule.confirm_lock_days_title': 'Travar estes dias?',
    'schedule.confirm_lock_day_message': 'Ninguém poderá agendar em {date}.',
    'schedule.confirm_lock_days_message': 'Ninguém poderá agendar em {count} dias ({dates}).',
    'schedule.confirm_lock_slot_title': 'Travar este horário?',
    'schedule.confirm_lock_slots_title': 'Travar estes horários?',
    'schedule.confirm_lock_slot_message': 'Ninguém poderá agendar {times} em {date}.',
    'schedule.confirm_lock_slot_days_message': 'Ninguém poderá agendar {times} em {count} dias ({dates}).',
    'schedule.confirm_lock_slot_map_message': 'Ninguém poderá agendar: {details}.',
    'schedule.confirm_lock': 'Travar',
    'schedule.confirm_unlock': 'Destravar',
    'schedule.confirm_unlock_day_title': 'Destravar este dia?',
    'schedule.confirm_unlock_days_title': 'Destravar estes dias?',
    'schedule.confirm_unlock_day_message': 'Os horários de {date} voltam a ficar disponíveis para agendamento.',
    'schedule.confirm_unlock_days_message': 'Os horários de {count} dias ({dates}) voltam a ficar disponíveis para agendamento.',
    'schedule.confirm_unlock_day_reason': 'Motivo do bloqueio: {reason}',
    'schedule.confirm_unlock_slot_title': 'Destravar este horário?',
    'schedule.confirm_unlock_slot_message': '{times} em {date} volta a ficar disponível para agendamento.',
    'schedule.filter_locked': 'Travados',
    'schedule.lock_history_day': 'Dia fechado',
    'schedule.lock_history_slots': 'Horários travados',
    'schedule.lock_range_to': 'às',
    'schedule.lock_reason_mixed': 'Motivos diferentes',
    'schedule.confirm_unlock_slots_title': 'Destravar estes horários?',
    'schedule.lock_history_empty': 'Nenhum horário travado',
    'schedule.lock_history_empty_hint': 'Os horários e dias que você travar no calendário aparecem aqui.',
    'schedule.lock_history_by_store': 'Travado pela loja',
    'schedule.lock_reason_unknown': 'Sem motivo informado',
    'schedule.lock_reason_label': 'Motivo do bloqueio',
    'schedule.lock_reason_medical': 'Consulta médica',
    'schedule.lock_reason_personal': 'Compromisso pessoal',
    'schedule.lock_reason_other': 'Outra ocorrência',
    'schedule.lock_reason_other_placeholder': 'Descreva o motivo (até 200 caracteres)',
    'schedule.lock_reason_other_required': 'Descreva o motivo com pelo menos 3 caracteres.',
    'schedule.reason_locked_slots': 'Há horário(s) travado(s)',
    'schedule.day_click_hint': 'Clique, arraste, Ctrl+clique ou toque no nome da semana',
    'schedule.weekday_column_hint': 'Selecionar todas as {day} deste mês',
    'schedule.multi_selected': '{count} dias selecionados',
    'schedule.multi_clear': 'Limpar',
    'schedule.multi_close_days': 'Fechar estes dias',
    'schedule.multi_reopen_days': 'Reabrir estes dias',
    'schedule.multi_lock_slot': 'Travar um horário nestes dias',
    'schedule.multi_ctrl_holding': 'Solte Ctrl para escolher como bloquear estes dias',
    'schedule.batch_ask_title': 'Como fechar estes dias?',
    'schedule.batch_ask_hint': 'Dia inteiro, o mesmo horário em todos, ou um horário diferente em cada dia.',
    'schedule.batch_close_days': 'Fechar os dias inteiros',
    'schedule.batch_reopen_days': 'Reabrir estes dias',
    'schedule.batch_same_slot': 'Mesmo horário em todos',
    'schedule.batch_per_day_slot': 'Horário diferente por dia',
    'schedule.batch_back': 'Voltar',
    'schedule.batch_pick_times': 'Toque nos horários de cada dia',
    'schedule.batch_no_times': 'Nenhum horário',
    'schedule.batch_apply_slots': 'Travar horários escolhidos',
    'schedule.batch_apply_open_slots': 'Abrir horários escolhidos',
    'schedule.batch_day_times_confirmed': 'Confirmado: {times}',
    'schedule.day_appointments_count': '{count} agendamento(s)',
    'schedule.legend_free': 'Livre',
    'schedule.legend_partial': 'Com agendamentos',
    'schedule.legend_full': 'Lotado',
    'schedule.legend_outside_hours': 'Fora do horário cadastrado',
    'schedule.legend_closed': 'Fechado manualmente',
    'schedule.cannot_close_outside_hours': 'Estes dias já estão fora do horário cadastrado.',
    'schedule.skipped_outside_hours': '{count} dia(s) já estão fora do horário e não foram fechados.',
    'schedule.already_outside_hours_hint': 'Já estão fora do horário cadastrado. Não é preciso fechar de novo.',
    'schedule.cannot_mix_closed_open': 'Não dá para juntar dias fechados com dias abertos.',
    'schedule.batch_ask_reopen_title': 'Reabrir estes dias?',
    'schedule.batch_ask_reopen_hint': 'Todos os selecionados já estão fechados.',
    'schedule.batch_open_title': 'Abrir para atendimento?',
    'schedule.batch_open_hint': 'Dia inteiro usa o horário cadastrado. Ou escolha horários específicos.',
    'schedule.batch_open_full_day': 'Abrir o dia inteiro',
    'schedule.batch_revert_open': 'Voltar ao horário cadastrado',
    'schedule.batch_ask_revert_title': 'Voltar ao horário cadastrado?',
    'schedule.batch_ask_revert_hint': 'Estes dias voltam a ficar fora do expediente.',
    'schedule.day_action_open_full': 'Abrir para atendimento',
    'schedule.day_action_open_slots': 'Definir horários',
    'schedule.day_action_revert_open': 'Voltar ao horário cadastrado',
    'schedule.legend_open_extra': 'Aberto extra',
    'schedule.open_extra_day': 'Aberto extra',
    'schedule.open_extra_slots': 'Horários extras',
    'schedule.confirm_open_days_title': 'Abrir estes dias?',
    'schedule.confirm_open_day_title': 'Abrir este dia?',
    'schedule.confirm_open_days_message': 'Vão ficar disponíveis no horário cadastrado da loja ({count} dias: {dates}).',
    'schedule.confirm_open_day_message': '{date} fica disponível no horário cadastrado da loja.',
    'schedule.confirm_open_slots_title': 'Abrir estes horários?',
    'schedule.confirm_open_slots_message': 'Só estes horários ficam disponíveis para agendar.',
    'schedule.confirm_revert_open_title': 'Voltar ao cadastro?',
    'schedule.confirm_revert_open_message': 'Estes dias voltam a ficar fora do horário cadastrado ({dates}).',
    'schedule.confirm_open': 'Abrir',
    'schedule.confirm_revert_open': 'Voltar',
    'schedule.open_dates_save_error': 'Não foi possível salvar a abertura extra.',
    'schedule.reason_open_extra': 'Aberto extra — fora do expediente cadastrado',
    'schedule.day_closed_by_store': 'Este dia foi fechado pela loja.',
    'schedule.closed_dates_load_error': 'Não foi possível carregar os dias fechados.',
    'schedule.closed_dates_save_error': 'Não foi possível salvar o fechamento do dia.',
    'schedule.reason_outside_hours': 'Indisponível por conta do cadastro de horário',
    'schedule.reason_manual_closed': 'Dia fechado manualmente',
    'schedule.gcal_disconnected_banner': 'Google Agenda desconectado',
    'schedule.gcal_disconnected_banner_staff': 'Sua conta Google desconectada',
    'client_scheduling.email_hint':
      'Opcional. Informe o e-mail Google do cliente para o compromisso aparecer no Calendar dele.',
    'schedule.events_hint': 'Confirme para enviar ao Google Agenda e WhatsApp.',
    'schedule.no_events_hint': 'Os bookings do link público aparecem aqui.',
    'schedule.filter_all': 'Todos',
    'schedule.filter_weekday': 'Dia da semana',
    'schedule.filter_weekday_all': 'Todos os dias',
    'schedule.filter_time_from': 'De',
    'schedule.filter_time_to': 'Até',
    'schedule.filter_time_from_all': 'Qualquer',
    'schedule.filter_time_to_all': 'Qualquer',
    'schedule.filter_sort': 'Ordem',
    'schedule.filter_sort_asc': 'Crescente',
    'schedule.filter_sort_desc': 'Decrescente',
    'schedule.filter_empty': 'Nenhum item com esses filtros',
    'schedule.filter_empty_hint': 'Troque o dia da semana, o intervalo de horário ou a ordem.',
    'schedule.google_section': 'Google Calendar',
    'schedule.dismiss_alert': 'Fechar aviso',
    'schedule.calendar.prev_month': 'Mês anterior',
    'schedule.calendar.next_month': 'Próximo mês',
    'schedule.tap_to_available': 'Tocar para disponibilizar',
    'schedule.tap_to_unavailable': 'Tocar para fechar o dia',
    'client_scheduling.progress_label': 'Etapas do agendamento',
    'schedule.disconnected': 'Desconectado',
    'schedule.reconnect': 'Reconectar',
    'schedule.new': 'Novo Agendamento',
    'schedule.total': 'Total de Agendamentos',
    'schedule.confirmed': 'Confirmados',
    'schedule.pending': 'Pendentes',
    'schedule.cancelled': 'Cancelados',
    'schedule.connect_calendar': 'Conecte-se ao Google Calendar para ver seus agendamentos',
    'schedule.define_schedule': 'Definir Agenda',
    'schedule.available': 'Disponível',
    'schedule.unavailable': 'Indisponível',
    'schedule.past_date': 'Data passada',
    'schedule.connect_calendar_sync': 'Conecte-se ao Google Calendar e aguarde a sincronização dos agendamentos.',
    'schedule.events_title': 'Agendamentos',
    'schedule.last_update': 'Última atualização: {time}',
    'schedule.events_count': '{count} agendamentos',
    'schedule.load_error': 'Erro ao carregar agendamentos',
    'schedule.try_again': 'Tentar Novamente',
    'schedule.no_events': 'Nenhum agendamento encontrado',
    'schedule.notify.load_error': 'Erro ao carregar agendamentos',
    'schedule.notify.save_error': 'Erro ao salvar agendamento',
    'schedule.notify.created': 'Agendamento criado com sucesso!',
    'schedule.notify.updated': 'Agendamento atualizado com sucesso!',
    'schedule.notify.connect_to_confirm': 'Conecte-se ao Google Calendar para confirmar agendamentos',
    'schedule.notify.confirmed': 'Agendamento confirmado e evento criado no Google Calendar!',
    'schedule.notify.confirmed_no_google': 'Agendamento confirmado e WhatsApp enviado. Ative a API Google Calendar no Cloud Console para sincronizar a agenda.',
    'schedule.notify.confirmed_status_only': 'Agendamento confirmado. Conecte o Google Calendar para sincronizar eventos.',
    'schedule.notify.confirm_failed': 'Não foi possível confirmar o agendamento. Tente novamente.',
    'schedule.notify.confirm_error': 'Erro ao criar evento no Google Calendar: {error}',
    'schedule.notify.google_api_disabled': 'Ative a API Google Calendar no Google Cloud deste projeto e tente de novo.',
    'schedule.notify.cancelled': 'Agendamento cancelado com sucesso',
    'schedule.notify.cancel_error': 'Erro ao cancelar agendamento',
    'schedule.loading': 'Carregando agendamentos…',
    'schedule.status.confirmed': 'Confirmado',
    'schedule.status.pending': 'Pendente',
    'schedule.status.cancelled': 'Cancelado',
    'schedule.status.completed': 'Concluído',
    'schedule.status.unknown': 'Desconhecido',
    'schedule.synced_google': 'Sincronizado com Google Calendar',
    'schedule.google.processing_title': 'Processando autorização…',
    'schedule.google.processing_body': 'Aguarde enquanto conectamos sua conta ao Google Calendar',
    'schedule.google.success_title': 'Conectado com sucesso!',
    'schedule.google.error_title': 'Erro na conexão',
    'schedule.google.redirecting': 'Redirecionando para a página de agendamentos…',
    'schedule.google.connected': 'Conectado com sucesso ao Google Calendar!',
    'schedule.google.connect_failed': 'Não foi possível conectar ao Google Calendar. Tente novamente ou contate nosso suporte.',
    'schedule.google.redirect_mismatch': 'URI de redirecionamento não autorizada no Google Cloud. Inclua a URI do painel (.../google-callback) nas URIs autorizadas do cliente OAuth.',
    'schedule.google.server_config': 'Integração Google mal configurada no servidor. Confira Client ID e Secret no backend e tente de novo.',
    'schedule.google.invalid_grant': 'Código Google expirado ou já usado. Feche e clique em Conectar novamente.',
    'schedule.google.auth_denied': 'Autorização do Google cancelada ou negada',
    'schedule.google.missing_code': 'Código de autorização não encontrado',
    'schedule.google.need_login': 'Faça login na loja antes de conectar o Google Calendar',
    'schedule.google.exchange_failed': 'Não foi possível obter o token do Google',
    'schedule.edit_btn': 'Editar',
    'schedule.templates.title': 'Mensagens WhatsApp do agendamento',
    'schedule.templates.hint': 'Textos enviados na confirmação, lembrete, cancelamento e reagendamento. Use as variáveis entre chaves.',
    'schedule.templates.vars': 'Variáveis: {cliente}, {servico}, {data}, {hora}, {endereco}',
    'schedule.templates.pending': 'Pendente',
    'schedule.templates.confirmation': 'Confirmação',
    'schedule.templates.reminder': 'Lembrete',
    'schedule.templates.cancellation': 'Cancelamento',
    'schedule.templates.reschedule': 'Reagendamento',
    'schedule.templates.preview': 'Pré-visualização',
    'schedule.templates.restore_default': 'Restaurar padrão deste',
    'schedule.templates.restore_all_defaults': 'Restaurar todos os padrões',
    'schedule.templates.save': 'Salvar templates',
    'schedule.templates.saving': 'Salvando…',
    'schedule.templates.toast_saved': 'Templates salvos',
    'schedule.templates.toast_save_error': 'Não foi possível salvar os templates',
    'schedule.templates.sample_client': 'Maria',
    'schedule.templates.sample_service': 'Corte de cabelo',
    'schedule.templates.sample_address': 'Rua Exemplo, 100',
    'schedule.templates.send_reminder': 'Enviar lembrete',
    'schedule.templates.reminder_sent': 'Lembrete enviado pelo WhatsApp',
    'schedule.templates.reschedule_sent': 'Reagendamento enviado pelo WhatsApp',
    'schedule.templates.send_failed': 'Não foi possível enviar. Verifique a conexão do WhatsApp ou contate nosso suporte.',
    'client_scheduling.step_professional': 'Profissional',
    'client_scheduling.choose_professional': 'Escolha o profissional',
    'client_scheduling.back_to_professional': '← Voltar para profissional',
    'client_scheduling.no_services': 'Nenhum serviço cadastrado. Peça à empresa para configurar serviços na agenda.',
    'client_scheduling.no_professionals_for_service':
      'Nenhum profissional disponível para este serviço. Escolha outro serviço.',
    'client_scheduling.professional_day_closed': 'Indisponível neste dia',
    'client_scheduling.loading_slots': 'Carregando horários…',
    'client_scheduling.module_off': 'Agendamento indisponível para esta empresa.',
    'client_scheduling.module_off_title': 'Agendamento indisponível',
    'company.gift.title': 'Presente / cupom (QR na sacola)',
    'company.gift.hint': 'Configure o desconto que o cliente ganha ao resgatar o QR. O cupom aplica sozinho no próximo pedido elegível.',
    'company.gift.enabled': 'Programa ativo',
    'company.gift.print_qr': 'Imprimir QR na sacola',
    'company.gift.headline': 'Texto do QR / página',
    'company.gift.success_title': 'Título de sucesso',
    'company.gift.success_body': 'Mensagem de sucesso',
    'company.gift.programs': 'Programas de desconto',
    'company.gift.add_program': 'Adicionar programa',
    'company.gift.program_new_name': 'Novo programa',
    'company.gift.program_name': 'Nome do programa',
    'company.gift.program_active': 'Ativo',
    'company.gift.remove_program': 'Remover programa',
    'company.gift.type': 'Tipo',
    'company.gift.type_percent': 'Percentual (%)',
    'company.gift.type_fixed': 'Valor fixo (R$)',
    'company.gift.value': 'Valor',
    'company.gift.min_order': 'Pedido mínimo (R$)',
    'company.gift.eligibility': 'Elegibilidade',
    'company.gift.eligibility_any': 'Qualquer pedido',
    'company.gift.eligibility_first': 'Somente 1º pedido',
    'company.gift.eligibility_subsequent': 'A partir do 2º pedido',
    'company.gift.max_uses': 'Máx. cupons por cliente',
    'company.gift.expires_days': 'Validade (dias)',
    'company.gift.rule_percent': '{value}% de desconto · mínimo {min}',
    'company.gift.rule_fixed': '{value} de desconto · mínimo {min}',
    'company.gift.save': 'Salvar presente',
    'company.gift.saving': 'Salvando…',
    'company.gift.toast_saved': 'Regras do presente salvas',
    'company.gift.toast_save_error': 'Não foi possível salvar as regras do presente',
    'company.gift.issue_coupons': 'Emitir cupons',
    'company.gift.issue_title': 'Emitir cupons para clientes',
    'company.gift.issue_program': 'Programa',
    'company.gift.issue_search': 'Buscar por nome ou telefone',
    'company.gift.issue_loading': 'Carregando clientes…',
    'company.gift.issue_empty': 'Nenhum cliente encontrado',
    'company.gift.client_unnamed': 'Cliente sem nome',
    'company.gift.cancel': 'Cancelar',
    'company.gift.issuing': 'Emitindo…',
    'company.gift.issue_confirm': 'Emitir ({count})',
    'company.gift.toast_issued': '{count} cupom(ns) emitido(s)',
    'company.gift.toast_issue_error': 'Não foi possível emitir os cupons',
    'company.gift.toast_clients_error': 'Não foi possível carregar os clientes',
    'schedule.notify.deleted': 'Agendamento removido com sucesso',
    'schedule.notify.delete_error': 'Erro ao deletar agendamento',
    'schedule.notify.unknown_error': 'Erro desconhecido',
    'schedule.available_times_for': 'Horários disponíveis para {date}',
    'schedule.day_closed': 'Este dia está fechado para agendamentos',
    'schedule.time_selected': 'Horário selecionado: {time}',

    'password_req.title': 'Requisitos da senha:',
    'password_req.min_length': 'Mínimo 6 caracteres',
    'password_req.uppercase': 'Pelo menos 1 letra maiúscula',
    'password_req.number': 'Pelo menos 1 número',
    'password_req.special': 'Pelo menos 1 caractere especial',

    'image_crop.title': 'Ajustar imagem',
    'image_crop.use_image': 'Usar imagem',
    'image_crop.subtitle_zoom': 'Use a roda do mouse para zoom e arraste a imagem para posicionar. A área do quadrado será usada.',
    'image_crop.subtitle_crop': 'A imagem aparece inteira. Arraste o retângulo para posicionar; use o canto inferior direito para redimensionar. Fora da seleção a imagem fica mais suave.',
    'image_crop.preview_alt': 'Pré-visualização logomarca',
    'image_crop.resize_aria': 'Redimensionar seleção',
    'image_crop.zoom': 'Zoom',
    'image_crop.generate_failed': 'Falha ao gerar imagem',

    'gcal_events.invalid_date': 'Data inválida',
    'gcal_events.events_count_one': '{count} agendamento',
    'gcal_events.events_count_many': '{count} agendamentos',
    'gcal_events.current_month': 'Atual',
    'gcal_events.today_badge': 'Hoje',
    'gcal_events.loading': 'Carregando agendamentos...',

    'orders.start_sales_mid_body': 'Você entrou no painel dentro do horário de atendimento, mas a loja está fechada. Deseja abrir agora para receber pedidos?',
    'orders.start_sales_manual_body': 'A loja só abre quando você confirmar. Clique em Iniciar Vendas para abrir agora — o status não muda sozinho no horário.',
    'orders.start_sales_btn': 'Iniciar Vendas',
    'orders.opening_btn': 'Abrindo...',
    'orders.store_override_intro': 'Defina por quanto tempo este status manual deve valer para toda a loja.',
    'orders.store_override.auto': 'Automático',
    'orders.store_override.auto_close_hint': 'Fecha agora e reabre no próximo horário da agenda.',
    'orders.store_override.auto_close_at': 'A loja reabre em {when}.',
    'orders.store_override.auto_open_at': 'A loja fecha em {when}.',
    'orders.store_override.auto_no_schedule': 'Cadastre o horário de funcionamento para usar o Automático.',
    'orders.store_override.timed_reopen_at': 'Reabre em {when}.',
    'orders.store_override.timed_outside_hours': 'Indisponível: {when} está fora do expediente.',
    'orders.store_override.30min': '30 minutos',
    'orders.store_override.2h': '2 horas',
    'orders.store_override.4h': '4 horas',

    'public_orders.cep_error': 'Erro ao buscar CEP. Tente novamente.',
    'public_orders.no_products_search': 'Nenhum produto encontrado para "{term}"',
    'public_orders.edit': 'Editar',

    'client_scheduling.loading': 'Carregando disponibilidade...',
    'client_scheduling.loading_for': 'Buscando horários disponíveis para {name}...',
    'client_scheduling.module_name': 'Módulo de Agendamento',
    'client_scheduling.book_title': 'Agende seu horário',
    'client_scheduling.online': 'Online',
    'client_scheduling.step_datetime': 'Data e Hora',
    'client_scheduling.step_date': 'Data',
    'client_scheduling.step_time': 'Horário',
    'client_scheduling.step_service': 'Serviço',
    'client_scheduling.step_contact': 'Contato',
    'client_scheduling.step_name': 'Nome',
    'client_scheduling.step_address': 'Endereço',
    'client_scheduling.step_confirm': 'Confirmação',
    'client_scheduling.choose_time': 'Escolha o horário',
    'client_scheduling.contact_title': 'Seu telefone',
    'client_scheduling.contact_hint': 'Usamos o WhatsApp para confirmar o agendamento.',
    'client_scheduling.name_title': 'Seu nome',
    'client_scheduling.address_title': 'Endereço da visita',
    'client_scheduling.address_required_hint': 'Este serviço é no seu endereço.',
    'client_scheduling.continue': 'Continuar',
    'client_scheduling.badge_visit': 'Visita',
    'client_scheduling.badge_store': 'Na loja',
    'client_scheduling.place_store': 'Atendimento na loja',
    'client_scheduling.place_store_at': 'Atendimento na loja · {name}',
    'client_scheduling.sinal_required': 'Sinal de {amount}',
    'client_scheduling.price_service': 'Serviço',
    'client_scheduling.price_sinal': 'Sinal',
    'client_scheduling.price_total': 'Total',
    'client_scheduling.sinal_adds_hint':
      'O sinal não desconta do valor do serviço. Você paga sinal + serviço.',
    'client_scheduling.sinal_deducts_hint':
      'O sinal desconta do serviço. Restante no atendimento: {remaining}',
    'client_scheduling.back_to_time': '← Voltar para horários',
    'client_scheduling.back_to_contact': '← Voltar para contato',
    'client_scheduling.back_to_name': '← Voltar para o nome',
    'client_scheduling.error.address_required': 'Informe o endereço da visita',
    'client_scheduling.label_place': 'Local',
    'client_scheduling.choose_date': 'Escolha a Data',
    'client_scheduling.choose_date_hint': 'Selecione uma data disponível para o seu agendamento',
    'client_scheduling.weekday_short.sun': 'Dom',
    'client_scheduling.weekday_short.mon': 'Seg',
    'client_scheduling.weekday_short.tue': 'Ter',
    'client_scheduling.weekday_short.wed': 'Qua',
    'client_scheduling.weekday_short.thu': 'Qui',
    'client_scheduling.weekday_short.fri': 'Sex',
    'client_scheduling.weekday_short.sat': 'Sáb',
    'client_scheduling.closed_day': 'Fechado',
    'client_scheduling.legend_available': 'Disponível',
    'client_scheduling.legend_closed': 'Fechado',
    'client_scheduling.legend_past': 'Data passada',
    'client_scheduling.past_day': 'Data passada',
    'client_scheduling.legend_selected': 'Selecionado',
    'client_scheduling.today': 'Hoje',
    'client_scheduling.times_available': 'Horários disponíveis:',
    'client_scheduling.more_times': 'mais',
    'client_scheduling.times_for': 'Horários disponíveis para',
    'client_scheduling.day_closed_msg': 'Este dia está fechado para agendamentos',
    'client_scheduling.no_times': 'Nenhum horário disponível para este dia',
    'client_scheduling.choose_other_date': '← Escolher outra data',
    'client_scheduling.choose_service': 'Escolha o Serviço',
    'client_scheduling.date_label': 'Data:',
    'client_scheduling.at_time': 'às',
    'client_scheduling.duration_min': 'Duração: {count} min',
    'client_scheduling.back_to_date': '← Voltar para seleção de data',
    'client_scheduling.confirm_title': 'Confirme seu Agendamento',
    'client_scheduling.summary': 'Resumo do Agendamento',
    'client_scheduling.label_date': 'Data',
    'client_scheduling.label_time': 'Horário',
    'client_scheduling.label_service': 'Serviço',
    'client_scheduling.google_hint': 'O agendamento será adicionado automaticamente ao Google Calendar',
    'client_scheduling.full_name': 'Nome Completo *',
    'client_scheduling.name_placeholder': 'Seu nome completo',
    'client_scheduling.phone': 'Telefone *',
    'client_scheduling.phone_placeholder': '(11) 99999-9999',
    'client_scheduling.email': 'Email',
    'client_scheduling.email_placeholder': 'seu@email.com',
    'client_scheduling.location': 'Localização do Agendamento',
    'client_scheduling.company_address': 'Endereço da Empresa',
    'client_scheduling.automatic': 'Automático',
    'client_scheduling.your_address': 'Endereço da visita',
    'client_scheduling.address_placeholder': 'Rua, número, bairro',
    'client_scheduling.notes': 'Observações Adicionais',
    'client_scheduling.notes_placeholder': 'Informações adicionais sobre o agendamento...',
    'client_scheduling.confirm_btn': 'Confirmar Agendamento',
    'client_scheduling.creating_event': 'Criando evento no calendário...',
    'client_scheduling.confirming': 'Confirmando...',
    'client_scheduling.back_to_service': '← Voltar para seleção de serviço',
    'client_scheduling.error.name_required': 'Nome é obrigatório',
    'client_scheduling.error.name_min': 'Nome deve ter pelo menos 3 caracteres',
    'client_scheduling.error.phone_required': 'Telefone é obrigatório',
    'client_scheduling.error.phone_format': 'Telefone deve estar no formato (11) 99999-9999',
    'client_scheduling.error.email_invalid': 'Email deve estar em formato válido',
    'client_scheduling.lgpd_consent': 'Li e concordo com os',
    'client_scheduling.lgpd_terms_link': 'Termos de Uso de Agendamentos',
    'client_scheduling.lgpd_privacy_link': 'Política de Privacidade',
    'client_scheduling.error.lgpd_required': 'Para confirmar, aceite os termos de agendamento.',
    'client_scheduling.success_with_gcal': 'Agendamento realizado com sucesso! O evento foi adicionado ao seu Google Calendar.',
    'client_scheduling.success_no_gcal': 'Agendamento realizado com sucesso! Conecte-se ao Google para adicionar eventos ao seu calendário automaticamente.',
    'client_scheduling.success_booked': 'Agendamento realizado com sucesso! A empresa vai confirmar em breve.',
    'client_scheduling.error_confirm': 'Erro ao confirmar agendamento. Tente novamente.',
    'client_scheduling.return_included_legend': 'Retorno incluso neste serviço',
    'client_scheduling.return_needs_prior': 'O retorno só pode ser marcado depois de um atendimento anterior deste serviço.',
    'client_scheduling.kind_service': 'Atendimento',
    'client_scheduling.kind_return': 'Retorno (incluso)',

    'gcal.never': 'Nunca',
    'gcal.just_now': 'Agora mesmo',
    'gcal.mins_ago': '{count} min atrás',
    'gcal.hours_ago': '{count}h atrás',
    'gcal.checking': 'Verificando...',
    'gcal.connection_error': 'Erro na conexão',
    'gcal.connected': 'Conectado',
    'gcal.auto_refresh': '(Auto-refresh ativo)',
    'gcal.last_check': 'Última verificação:',
    'gcal.token': 'Token:',
    'gcal.error_label': 'Erro:',
    'gcal.verify_now': 'Verificar Agora',
    'gcal.disable_auto_refresh': 'Desabilitar Auto-refresh',
    'gcal.enable_auto_refresh': 'Habilitar Auto-refresh',

    'manager.revenue_total': 'Receita Total',
    'manager.orders': 'Pedidos',
    'manager.new_clients': 'Novos Clientes',
    'manager.avg_time': 'Tempo Médio',
    'manager.vs_previous_month': 'vs {count} mês anterior',
    'manager.tab_overview': 'Visão Geral',
    'manager.tab_reports': 'Relatórios',
    'manager.tab_analytics': 'Analytics',
    'manager.payment_summary': 'Resumo por Método de Pagamento',
    'manager.top_products': 'Top 5 Produtos',
    'manager.payment_methods': 'Formas de Pagamento',
    'manager.order_type': 'Tipo de Pedido',
    'manager.no_products': 'Nenhum produto encontrado',
    'manager.no_data': 'Nenhum dado disponível',
    'manager.reports_title': 'Relatórios Gerenciais',
    'manager.card': 'Cartão',
    'manager.cash': 'Dinheiro',
    'manager.analytics_title': 'Analytics Avançados',
    'manager.analytics_soon': 'Funcionalidade de analytics será implementada em breve',
    'manager.analytics_hint': 'Visualize gráficos detalhados de vendas, tendências e performance',
    'manager.reports_soon': 'Funcionalidade de relatórios será implementada em breve',
    'manager.reports_hint': 'Gere relatórios personalizados de vendas, clientes e performance',
    'manager.type_delivery': 'Entrega',
    'manager.type_dine_in': 'Consumo Local',
    'manager.orders_count_one': '{count} pedido',
    'manager.orders_count_many': '{count} pedidos',
    'manager.range_too_long': 'Escolha um período de no máximo 12 meses.',
    'manager.load_failed': 'Não foi possível carregar o resumo. Tente de novo.',

    'employees.edit_title': 'Editar Colaborador',
    'employees.add_title': 'Adicionar Colaborador',
    'employees.stores_label': 'Empresas',
    'employees.stores_all': 'Todas',
    'employees.stores_hint': 'Marque em quais empresas do grupo este colaborador atua.',
    'employees.stores_required': 'Escolha pelo menos uma empresa.',
    'employees.full_name_placeholder': 'Nome completo',
    'employees.new_role_placeholder': 'Nome do novo cargo',
    'employees.add_role': 'Adicionar cargo',
    'employees.daily_rate': 'Diária',
    'employees.observations': 'Observações',
    'employees.observations_placeholder': 'Observações sobre o colaborador...',
    'employees.permissions': 'Permissões',
    'employees.permissions_hint': 'Permissões deste colaborador (o mesmo cargo pode ter permissões diferentes).',
    'employees.permissions_btn': 'Permissões',
    'employees.permissions_denied': 'Você não pode alterar permissões de outros colaboradores.',
    'employees.cpf_optional': 'CPF (opcional)',
    'employees.cpf_hint': 'Deixe em branco se o colaborador informar no link de convite ao criar a senha.',
    'employees.cpf_placeholder': '000.000.000-00',
    'employees.status_label': 'Status',
    'employees.status.active': 'Ativo',
    'employees.status.inactive': 'Inativo',
    'employees.status.vacation': 'Férias',
    'employees.status.leave': 'Licença',
    'employees.role.waiter': 'Garçom',
    'employees.role.cook': 'Cozinheiro',
    'employees.role.manager': 'Gerente',
    'employees.role.attendant': 'Atendente',
    'employees.role.delivery': 'Entregador',
    'employees.role.admin': 'Admin',
    'employees.role.generic': 'Colaborador',
    'employees.delete_role_title': 'Excluir cargo "{name}"',
    'employees.close_new_role': 'Fechar novo cargo',
    'employees.open_new_role': 'Adicionar novo cargo',
    'employees.phone_placeholder': '(00) 00000-0000',
    'employees.edit_btn': 'Editar',

    'internal_order.title': 'Pedido Interno',
    'internal_order.back_orders': 'Voltar para Pedidos',
    'internal_order.back_delivery': 'Voltar para Delivery',
    'internal_order.view_cart': 'Ver carrinho',
    'internal_order.cart': 'Carrinho',
    'internal_order.cart_empty': 'Carrinho vazio',
    'internal_order.continue_shopping': 'Continuar comprando',
    'internal_order.click_see_products': 'Clique para ver produtos',
    'internal_order.click_collapse': 'Clique para recolher',
    'internal_order.item_one': 'item',
    'internal_order.item_many': 'itens',
    'internal_order.checkout_title': 'Finalizar Pedido',
    'internal_order.order_type': 'Tipo de Pedido',
    'internal_order.dine_in_short': 'Mesa',
    'internal_order.origin_internal': 'Pedido Interno',
    'internal_order.table_number': 'Número da Mesa',
    'internal_order.table_placeholder': 'Número da mesa (opcional)',
    'internal_order.delivery_address': 'Endereço de Entrega *',
    'internal_order.house_number': 'Número *',
    'internal_order.house_placeholder': 'Número da casa',
    'internal_order.already_paid': 'Pedido já foi pago',
    'internal_order.already_paid_hint': 'O pedido será marcado como pago',
    'internal_order.observations': 'Observações',
    'internal_order.observations_placeholder': 'Observações adicionais sobre o pedido',
    'internal_order.finish': 'Finalizar Pedido',
    'internal_order.modal.close_esc': 'Fechar (ESC)',
    'internal_order.modal.items_count': '{count} itens',
    'internal_order.modal.obs_short': 'Obs',
    'internal_order.modal.discount': 'Desconto',
    'internal_order.modal.type_delivery': 'Entrega',
    'internal_order.modal.type_pickup': 'Retirada',
    'internal_order.modal.phone_optional_placeholder': '(00) 00000-0000 (opcional)',
    'internal_order.modal.customer_name_placeholder': 'Nome do cliente',
    'internal_order.ifood_orders_count': '{count} pedidos pelo iFood',
    'internal_order.ifood_orders_count_one': 'primeiro pedido pelo iFood',
    'internal_order.store_orders_count': '{count} pedidos na loja',
    'internal_order.store_orders_count_one': 'primeiro pedido na loja',
    'internal_order.modal.optional': 'Opcional',
    'internal_order.modal.neighborhood_placeholder': 'Bairro',
    'internal_order.modal.change_placeholder': 'R$ 0,00',
    'internal_order.modal.order_summary': 'Resumo do Pedido',
    'internal_order.modal.ifood_discount': 'Desconto iFood',
    'internal_order.modal.total_order': 'Total do Pedido',
    'internal_order.modal.calculated_total': 'Total calculado',
    'internal_order.modal.creating_order': 'Criando pedido...',
    'internal_order.modal.create_internal_order': 'Criar Pedido Interno',
    'internal_order.modal.select_size_type': 'Selecione o tamanho/tipo:',
    'internal_order.modal.flavors_count': '{count} sabor(es)',
    'internal_order.modal.whole_pizza_one_flavor': 'Pizza inteira (1 sabor)',
    'internal_order.modal.select_up_to_flavors': 'Selecione até {max} sabor(es) ({total}/{max}):',
    'internal_order.modal.add_second_flavor': 'Adicionar segundo sabor',
    'internal_order.modal.add_more_flavors': 'Adicionar mais {count} sabor(es)',
    'internal_order.modal.select_second_flavor': 'Selecione o segundo sabor',
    'internal_order.modal.select_additional_flavors': 'Selecione {count} sabor(es) adicional(is)',
    'internal_order.modal.addons_optional': 'Adicionais (opcional):',
    'internal_order.modal.max_short': 'máx. {count}',
    'internal_order.modal.observations_optional': 'Observações (opcional):',
    'internal_order.modal.product_observations_placeholder': 'Ex: sem cebola, bem passado...',
    'internal_order.modal.product_fallback': 'Produto',
    'internal_order.modal.addon_fallback': 'Adicional',
    'internal_order.modal.obs_prefix': 'Obs: {obs}',
    'internal_order.modal.table_label_value': 'Mesa {number}',
    'internal_order.modal.obs_table': 'Mesa: {number}',
    'internal_order.modal.obs_ifood_discount': 'Desconto iFood: R$ {amount}',
    'internal_order.modal.obs_order_paid': 'Pedido já pago',
    'internal_order.modal.toast.product_added': 'Produto adicionado ao carrinho!',
    'internal_order.modal.toast.min_additional_required': 'É necessário escolher pelo menos {min} item(ns) de "{category}" para este produto',
    'internal_order.modal.toast.max_flavors': 'Máximo de {count} sabor(es) para este tipo',
    'internal_order.modal.toast.max_additional_limit': 'Limite máximo de {max} adicional(is) para {category}',
    'internal_order.modal.toast.enter_customer_name': 'Informe o nome do cliente',
    'internal_order.modal.toast.enter_valid_phone': 'Informe um telefone válido (mínimo 10 dígitos)',
    'internal_order.modal.toast.enter_house_number': 'Informe o número da casa',
    'internal_order.modal.toast.enter_neighborhood': 'Informe o bairro',
    'internal_order.modal.toast.min_additional_required_item': 'No item "{item}", é necessário escolher pelo menos {min} item(ns) de "{category}"',
    'internal_order.modal.toast.order_created': 'Pedido interno criado com sucesso!',
    'internal_order.modal.toast.create_order_error': 'Erro ao criar pedido interno',
    'internal_order.modal.toast.process_order_error': 'Erro ao processar pedido interno',

    'conversations.back': 'Voltar às conversas',
    'conversations.archived_hint': 'Conversas arquivadas permanecem aqui conforme o WhatsApp no celular.',
    'conversations.connected': 'Conectado ao WhatsApp',
    'conversations.disconnected': 'Desconectado do WhatsApp',
    'conversations.disconnect': 'Desconectar WhatsApp',
    'conversations.disconnecting': 'Desconectando…',
    'conversations.connect_qr': 'Conectar WhatsApp — gerar QR code',
    'conversations.generating_qr': 'Gerando QR code…',
    'conversations.generating_qr_ensure': 'Preparando sessão WhatsApp (criando instância se necessário)…',
    'conversations.view_archived': 'Ver conversas arquivadas',
    'conversations.bot_title': 'DelivAI — bot de atendimento',
    'conversations.bot_on': 'Bot ligado — clique para desligar',
    'conversations.bot_off': 'Bot desligado — clique para ligar',
    'conversations.bot_locked': 'Botão do Zap bloqueado pela DelivAI. Não é possível ligar ou desligar aqui.',
    'conversations.bot_locked_aria': 'Botão do Zap bloqueado pela DelivAI',
    'conversations.bot_group_save_failed': 'Não consegui gravar o bot desta loja. Tente de novo.',
    'conversations.bot_disable': 'Desligar bot de atendimento',
    'conversations.bot_enable': 'Ligar bot de atendimento',
    'conversations.bot_badge_lock': 'LOCK',
    'conversations.bot_badge_on': 'ON',
    'conversations.bot_badge_off': 'OFF',
    'conversations.wa_group': 'WhatsApps do grupo',
    'conversations.wa_group_open': 'Abrir conexões do grupo',
    'conversations.wa_group_close': 'Fechar conexões do grupo',
    'conversations.tone_ok': 'Atendimento automático ok',
    'conversations.tone_paused': 'Bot pausado nesta conversa',
    'conversations.tone_watch': 'IA com dificuldade nesta conversa',
    'conversations.tone_urgent': 'Precisa de atendente urgente',
    'conversations.load_image': 'Carregar imagem',
    'conversations.load_video': 'Carregar vídeo',
    'conversations.cancel_selection': 'Cancelar seleção de mensagens',

    'company.bairro_name_placeholder': 'Nome do bairro',
    'company.bairro_edit_title': 'Alterar nome do bairro',
    'company.bairro_delete_title': 'Excluir bairro',
    'company.bairro_add_title': 'Adicionar novo bairro',

    'common.hours_range': '{open} às {close}',

    'clients.title': 'Clientes',
    'clients.filter_all': 'Todas as empresas',
    'clients.filter_aria': 'Empresa',
    'clients.subtitle': 'Clientes salvos para pedidos e agendamentos (autocomplete por telefone)',
    'clients.search': 'Buscar por nome ou telefone...',
    'clients.sort_by': 'Ordenar por',
    'clients.name': 'Nome',
    'clients.ascending': 'Crescente (A → Z)',
    'clients.descending': 'Decrescente (Z → A)',
    'clients.new': 'Novo cliente',
    'clients.select_all': 'Selecionar todos ({count} visíveis)',
    'clients.bulk_edit': 'Alterar campo em massa',
    'clients.edit': 'Editar',
    'clients.order_one': '{count} pedido',
    'clients.orders_many': '{count} pedidos',
    'clients.badge_qr': 'Veio do QR',
    'clients.qr_stat_issued': 'QR gerados (links)',
    'clients.qr_stat_claimed': 'QR resgatados',
    'clients.qr_stat_clients': 'Clientes novos pelo QR',
    'clients.modal_edit': 'Editar cliente',
    'clients.modal_new': 'Novo cliente',
    'clients.field_name': 'Nome',
    'clients.field_phone': 'Telefone',
    'clients.field_zip': 'CEP',
    'clients.field_address': 'Endereço',
    'clients.field_number': 'Número',
    'clients.field_neighborhood': 'Bairro',
    'clients.field_complement': 'Complemento',
    'clients.field_reference': 'Referência',
    'clients.sort_orders': 'Pedidos na loja',
    'clients.phone_missing': 'Telefone não informado',
    'clients.no_name': 'Sem nome',
    'clients.empty_none': 'Nenhum cliente cadastrado. Use o botão acima ou cadastre ao fazer pedidos/agendamentos.',
    'clients.empty_search': 'Nenhum resultado para essa busca.',
    'clients.empty_error': 'Corrija o erro acima e use Atualizar ou altere a busca para tentar de novo.',
    'clients.searching_zip': 'Buscando CEP...',
    'clients.placeholder_name': 'Nome do cliente',
    'clients.placeholder_phone': '(00) 00000-0000',
    'clients.placeholder_zip': '00000-000',
    'clients.placeholder_address': 'Rua, avenida...',
    'clients.placeholder_number': 'Nº',
    'clients.placeholder_neighborhood': 'Bairro',
    'clients.placeholder_complement': 'Apto, bloco...',
    'clients.placeholder_reference': 'Ponto de referência',
    'clients.bulk_title': 'Atualizar em massa',
    'clients.bulk_field': 'Campo',
    'clients.bulk_new_value': 'Novo valor',
    'clients.bulk_placeholder': 'Valor para todos os selecionados',
    'clients.bulk_apply': 'Aplicar',
    'clients.bulk_applying': 'Aplicando...',
    'clients.bulk_hint': 'Aplicar o mesmo valor a {count} cliente(s) selecionado(s) (com ID válido).',

    'company.delivery_data_hint': 'Dados de Delivery (Duplo clique para editar)',
    'company.fee_label': 'Taxa',
    'company.neighborhoods_count': '{count} bairros',
    'company.order_types_public': 'Tipos de Pedido na Página Pública',
    'company.pix_data': 'Dados PIX',
    'company.menu_images': 'Imagens do Cardápio',
    'company.delivery_type_delivery': 'Entrega',
    'company.delivery_type_pickup': 'Retirada',
    'company.delivery_type_dine_in': 'Consumo no Local',
    'company.no_delivery_fees': 'Nenhuma taxa de delivery configurada',
    'company.configure_fees_hint': 'Configure taxas e bairros para começar',
    'company.add_first_fee': '+ Adicionar Primeira Taxa',
    'company.pix_disabled_hint': 'PIX desativado nas formas de pagamento acima. Os dados permanecem salvos; ative PIX para editar.',
    'company.pix_key_type': 'Tipo de chave *',
    'company.pix_key': 'Chave PIX *',
    'company.pix_legal_name': 'Razão social *',
    'company.pix_bank': 'Banco *',
    'company.pix_key_placeholder': 'Digite a chave PIX',
    'company.pix_key_type_first': 'Selecione o tipo primeiro',
    'company.pix_holder_placeholder': 'Nome do titular / empresa',
    'company.pix_bank_placeholder': 'Ex.: Nubank, Itaú...',
    'company.save_pix': 'Salvar dados PIX',
    'company.no_payment_methods': 'Nenhuma forma de pagamento ativada',
    'company.no_payment_methods_hint': 'Ative pelo menos uma forma de pagamento para que os clientes possam fazer pedidos.',
    'company.order_types_hint': 'Escolha quais tipos de pedido o cliente pode selecionar na página pública (entrega, retirada, consumo no local). No caixa interno, todos os tipos continuam disponíveis para lançamento.',
    'company.delivery_loading': 'Carregando dados de delivery...',
    'company.upload_menu_image': 'Enviar Nova Imagem',
    'company.menu_images_count': 'Imagens do Cardápio ({count} imagens)',

    'profile.email': 'E-mail',
    'profile.phone': 'Telefone',
    'profile.save': 'Salvar contato',
    'profile.saving': 'Salvando…',
    'profile.saved': 'Contato atualizado.',
    'profile.save_error': 'Erro ao salvar contato.',
    'profile.save_unavailable': 'Edição disponível apenas para colaboradores.',
    'profile.identity_locked': 'Nome e CPF/CNPJ não podem ser alterados aqui.',
    'profile.owner_hint': 'Dados da loja são editados em Empresa.',
    'profile.open_company': 'Abrir cadastro da empresa',
    'profile.panel_label': 'Dados do perfil',
    'profile.change_password': 'Alterar senha',
    'profile.password.title': 'Alterar senha',
    'profile.password.subtitle': 'Informe a senha atual e escolha uma nova senha segura.',
    'profile.password.current': 'Senha atual',
    'profile.password.new': 'Nova senha',
    'profile.password.confirm': 'Confirmar nova senha',
    'profile.password.submit': 'Salvar nova senha',
    'profile.password.saving': 'Salvando…',
    'profile.password.changed': 'Senha alterada com sucesso.',
    'profile.password.error': 'Não foi possível alterar a senha.',
    'profile.password.same_as_old': 'A nova senha deve ser diferente da atual.',
    'profile.password.mismatch': 'As senhas não coincidem.',
    'profile.password.forgot': 'Esqueci minha senha',
    'profile.password.close': 'Fechar',

    'dev.nav.home': 'Início',
    'dev.nav.licenses': 'Licenças',
    'dev.nav.contracts': 'Contratos',
    'dev.nav.contracts_title': 'Contratos e minutas jurídicas',
    'dev.nav.evolution': 'Evolution',
    'dev.nav.companies': 'Empresas',
    'dev.nav.prompts': 'Prompts',
    'dev.nav.consumers': 'Clientes',
    'dev.nav.database': 'Banco',
    'dev.nav.logs': 'Logs',
    'dev.nav.observability': 'Observabilidade',
    'dev.nav.ops_alerts': 'Alertas',
    'dev.nav.platform_ai': 'Chaves de API',
    'dev.nav.platform_ai_title': 'Chaves de API DelivAI',
    'dev.nav.agenda': 'Agenda',
    'dev.nav.agenda_title': 'Agenda — defaults e templates',
    'dev.card.platform_ai.title': 'Chaves de API',
    'dev.card.platform_ai.desc':
      'Chaves por serviço (Atendimento, Fast Order) para todos os clientes sem chave própria.',
    'dev.card.agenda.title': 'Agenda',
    'dev.card.agenda.desc':
      'Defaults/tetos da agenda e templates WhatsApp (plataforma e por loja).',
    'dev.agenda.title': 'Agenda — DelivAI',
    'dev.agenda.hint':
      'Parâmetros Dev: defaults, tetos (caps) e templates WhatsApp padrão. A loja ajusta o resto em Empresa → Agenda.',
    'dev.agenda.loading': 'Carregando…',
    'dev.agenda.load_error': 'Não foi possível carregar os defaults de agenda.',
    'dev.agenda.save_error': 'Não foi possível salvar.',
    'dev.agenda.saved': 'Defaults de agenda salvos.',
    'dev.agenda.saving': 'Salvando…',
    'dev.agenda.save': 'Salvar defaults',
    'dev.agenda.updated': 'Atualizado',
    'dev.agenda.defaults_title': 'Defaults e tetos (Dev)',
    'dev.agenda.defaults_hint':
      'Defaults = valor inicial/fallback. Caps = limite máximo que a loja pode configurar.',
    'dev.agenda.default_mode': 'Modo padrão',
    'dev.agenda.store_can_change_mode': 'Loja pode alterar o modo da agenda',
    'dev.agenda.default_slot': 'Slot padrão (min)',
    'dev.agenda.default_lead': 'Antecedência padrão (min)',
    'dev.agenda.default_max_days': 'Dias à frente (padrão)',
    'dev.agenda.default_buffer': 'Buffer padrão (min)',
    'dev.agenda.default_stations': 'Estações padrão',
    'dev.agenda.default_timezone': 'Fuso padrão',
    'dev.agenda.cap_max_days': 'Teto: dias à frente',
    'dev.agenda.cap_min_slot': 'Teto mínimo: slot (min)',
    'dev.agenda.cap_max_slot': 'Teto máximo: slot (min)',
    'dev.agenda.cap_stations': 'Teto: estações',
    'dev.agenda.cap_buffer': 'Teto: buffer (min)',
    'dev.agenda.templates_platform': 'Templates WhatsApp (padrão plataforma)',
    'dev.agenda.templates_platform_hint':
      'Usados quando a loja ainda não personalizou. Também editáveis por CNPJ abaixo.',
    'dev.agenda.templates_store': 'Templates da loja (por CNPJ)',
    'dev.agenda.templates_store_hint':
      'Carregue uma loja para editar os textos de confirmação/lembrete/cancelamento.',
    'dev.agenda.cnpj_invalid': 'Informe um CNPJ válido (14 dígitos).',
    'dev.agenda.store_load_error': 'Não foi possível carregar a loja.',
    'dev.agenda.load_store': 'Carregar loja',
    'dev.platform_ai.title': 'Chaves de API — DelivAI',
    'dev.platform_ai.models_button': 'Modelos',
    'dev.platform_ai.models_title': 'Modelos disponíveis',
    'dev.platform_ai.models_hint': 'Marque os que podem aparecer na lista. Pelo menos um fica ligado.',
    'dev.platform_ai.models_save': 'Salvar modelos',
    'dev.platform_ai.models_saved_local': 'A lista vale nesta tela. O servidor ainda não guardou.',
    'dev.platform_ai.model_hint.luna': 'Barato e rápido, para muito volume',
    'dev.platform_ai.model_hint.nano5': 'O mais barato da linha 5',
    'dev.platform_ai.model_hint.mini5': 'Rápido, bom para tarefa definida',
    'dev.platform_ai.model_hint.sol': 'Equilíbrio entre capacidade e custo',
    'dev.platform_ai.model_hint.nano41': 'Leve. É um dos que a tela já usava',
    'dev.platform_ai.model_hint.mini41': 'O padrão de hoje, custo baixo',
    'dev.platform_ai.model_hint.full41': 'Mais capaz que o mini, mais caro',
    'dev.platform_ai.model_hint.mini4o': 'Linha anterior, versão leve',
    'dev.platform_ai.model_hint.full4o': 'Linha anterior, mais capaz',
    'dev.platform_ai.model_hint.astra': 'O mais capaz. Bem mais caro',
    'dev.platform_ai.hint':
      'Chaves da DelivAI por serviço. Clientes sem chave própria na empresa usam estas. Empresa com chave própria usa a dela em todos os serviços.',
    'dev.platform_ai.loading': 'Carregando credenciais…',
    'dev.platform_ai.load_error': 'Não foi possível carregar as credenciais de IA.',
    'dev.platform_ai.save_error': 'Não foi possível salvar.',
    'dev.platform_ai.saved': 'Credenciais DelivAI salvas.',
    'dev.platform_ai.saving': 'Salvando…',
    'dev.platform_ai.save': 'Salvar chaves de API',
    'dev.platform_ai.privacy': 'Chaves nunca são reexibidas após salvar — só o status.',
    'dev.platform_ai.bot_title': 'Atendimento (bot WhatsApp)',
    'dev.platform_ai.bot_hint': 'Usada por todas as lojas sem chave própria no bot.',
    'dev.platform_ai.fast_title': 'Fast Order (iFood + pedido padrão)',
    'dev.platform_ai.fast_hint': 'Usada por todas as lojas sem chave própria no Fast Order.',
    'dev.platform_ai.general_title': 'Fallback geral',
    'dev.platform_ai.general_hint':
      'Se o serviço não tiver chave, usa esta antes da variável de ambiente do servidor.',
    'dev.platform_ai.new_key': 'Nova chave (vazio = manter)',
    'dev.platform_ai.model': 'Modelo',
    'dev.platform_ai.model_env': '(env do servidor)',
    'dev.platform_ai.clear_key': 'Remover esta chave',
    'dev.platform_ai.status_set': 'Cadastrada',
    'dev.platform_ai.status_fallback': 'Usa fallback / env',
    'dev.platform_ai.status_env': 'Usando env do servidor',
    'dev.card.ops_alerts.title': 'Alertas operacionais',
    'dev.card.ops_alerts.desc':
      'Rotas de alerta (Google Calendar, WhatsApp…) e quem recebe em queda em massa.',
    'dev.ops_alerts.title': 'Alertas operacionais',
    'dev.ops_alerts.hint':
      'Global (todas as lojas / Devs) e por loja na mesma tela. Sem módulo de agendamento o botão da loja fica travado em desligado.',
    'dev.ops_alerts.loading': 'Carregando alertas…',
    'dev.ops_alerts.load_error': 'Não foi possível carregar os alertas.',
    'dev.ops_alerts.save_error': 'Não foi possível salvar.',
    'dev.ops_alerts.routes': 'Rotas / serviços',
    'dev.ops_alerts.recipients': 'Quem é notificado (Dev)',
    'dev.ops_alerts.no_recipients': 'Nenhum destinatário ainda — adicione você abaixo.',
    'dev.ops_alerts.active': 'Ativo',
    'dev.ops_alerts.inactive': 'Inativo',
    'dev.ops_alerts.channel_stores': 'Lojas',
    'dev.ops_alerts.channel_devs': 'Devs',
    'dev.ops_alerts.global_stores': 'Todas as lojas',
    'dev.ops_alerts.gcal_store_rule':
      'Loja sem módulo de agendamento não recebe este WhatsApp — só o canal Dev, se estiver ligado.',
    'dev.ops_alerts.stores_title': 'Por loja',
    'dev.ops_alerts.stores_hint':
      'Escolha a rota e ligue/desligue cada loja. Sem módulo de agendamento o Google Calendar fica travado em desligado.',
    'dev.ops_alerts.stores_search': 'Buscar loja ou CNPJ',
    'dev.ops_alerts.stores_empty': 'Nenhuma loja nesta busca.',
    'dev.ops_alerts.store_locked_no_module':
      'Sem módulo de agendamento — alerta da loja permanece desligado.',
    'dev.ops_alerts.store_locked_global': 'Canal global de lojas está desligado.',
    'dev.ops_alerts.store_toggled': 'Alerta da loja atualizado.',
    'dev.ops_alerts.emitted_title': 'Notificações emitidas',
    'dev.ops_alerts.emitted_empty': 'Nenhum alerta emitido nas últimas 24 h.',
    'dev.ops_alerts.emitted_refresh': 'Atualizar',
    'dev.ops_alerts.route_toggled': 'Rota atualizada.',
    'dev.ops_alerts.mass_threshold': 'Limiar massa',
    'dev.ops_alerts.cooldown': 'Cooldown loja / Dev',
    'dev.ops_alerts.add_recipient': 'Novo destinatário',
    'dev.ops_alerts.name_placeholder': 'Nome',
    'dev.ops_alerts.phone_placeholder': 'WhatsApp (DDD + número)',
    'dev.ops_alerts.save_recipient': 'Salvar destinatário',
    'dev.ops_alerts.recipient_saved': 'Destinatário salvo.',
    'dev.ops_alerts.recipient_deleted': 'Destinatário removido.',
    'dev.ops_alerts.delete': 'Excluir',
    'dev.ops_alerts.delete_title': 'Excluir destinatário',
    'dev.ops_alerts.delete_confirm': 'Remover esta pessoa da lista de alertas Dev?',
    'dev.nav.permissions': 'Permissões',
    'dev.nav.bot': 'Bot',
    'dev.nav.bot_title': 'Controle do bot WhatsApp',
    'dev.nav.delidesk': 'DeliDesk',
    'dev.nav.delidesk_title': 'Funções globais do DeliDesk',
    'dev.bot.title': 'Controle do bot',
    'dev.bot.subtitle':
      'Global ON libera o Zap de todas as lojas — cada lojista liga ou desliga o próprio robô. Global OFF trava todos. Com o global ligado você pode travar uma loja; com o global desligado, pode liberar só uma.',
    'dev.bot.platform_title': 'Bot na plataforma',
    'dev.bot.platform_hint':
      'Incidente (spam/ban): desligue o global. Lojistas ficam com o Zap bloqueado até você ligar de novo ou liberar uma loja abaixo.',
    'dev.bot.platform_on': 'GLOBAL ON',
    'dev.bot.platform_off': 'GLOBAL OFF',
    'dev.bot.platform_unknown': '…',
    'dev.bot.platform_on_ok': 'Global ligado — Zap liberado em todas as lojas',
    'dev.bot.platform_off_ok': 'Global desligado — Zap travado em todas as lojas',
    'dev.bot.platform_locked_hint': 'Religue o kill-switch global primeiro',
    'dev.bot.companies_title': 'Botão do Zap por loja',
    'dev.bot.companies_empty': 'Nenhuma loja cadastrada',
    'dev.bot.company_saved': 'Botão da loja atualizado',
    'dev.bot.store_robot_on': 'Robô da loja: ligado (o lojista ligou)',
    'dev.bot.store_robot_off': 'Robô da loja: desligado (o lojista desligou)',
    'dev.bot.store_button_locked': 'Zap travado — o robô não atende',
    'dev.bot.store_inactive':
      'Loja inativa no cadastro (licença) — o Zap ainda pode estar no ar',
    'dev.bot.store_not_in_panel':
      'Instância na Evolution sem cadastro no painel — trava indisponível',
    'dev.bot.row_free': 'LIVRE',
    'dev.bot.row_locked': 'TRAVADO',
    'dev.bot.row_unavailable': 'SEM CADASTRO',
    'dev.bot.load_error': 'Não foi possível carregar o status do bot',
    'dev.bot.save_error': 'Não foi possível salvar',
    'dev.evolution.title': 'Instâncias Evolution',
    'dev.evolution.subtitle':
      'Visão por ambiente (Evolution test ou prod) e destino do webhook (backend test ou prod). Leitura/gravação via proxy do backend.',
    'dev.evolution.logs_title': 'Logs Evolution',
    'dev.evolution.logs_empty': 'Nenhum log Evolution nas últimas 6 h.',
    'dev.evolution.logs_refresh': 'Atualizar',
    'dev.evolution.logs_error': 'Não foi possível carregar os logs Evolution.',
    'dev.evolution.view_env_label': 'Ver Evolution',
    'dev.evolution.view_env_help':
      'Alterna qual Evolution API você está vendo. Cada ambiente tem suas próprias instâncias.',
    'dev.evolution.view_env_test_hint': 'Backend/Evolution de teste',
    'dev.evolution.view_env_prod_hint': 'Backend/Evolution de produção',
    'dev.evolution.env_test': 'Teste',
    'dev.evolution.env_prod': 'Produção',
    'dev.evolution.webhook_dest_label': 'Destino do webhook',
    'dev.evolution.webhook_dest_help':
      'Para onde esta Evolution envia eventos. Mesmo ambiente = URL Docker (rede do projeto). Outro ambiente = URL pública HTTPS (stacks EasyPanel isolados — hostname Docker do outro projeto não alcança).',
    'dev.evolution.webhook_dest_same': 'Mesmo ambiente',
    'dev.evolution.webhook_dest_cross': 'Outro ambiente (HTTPS)',
    'dev.evolution.view_prod_proxy_hint':
      'Visão Produção fala com o backend de produção. O proxy /api/evolution/webhook só está no test até o release para main — se aparecer “No static resource”, é isso.',
    'dev.evolution.webhook_dest_locked':
      'Instância desconectada neste ambiente — conecte (QR) para alterar o destino do webhook.',
    'dev.evolution.webhook_dest_lock_badge': 'LOCK',
    'dev.evolution.webhook_custom': 'URL customizada',
    'dev.evolution.list_error': 'Falha ao listar instâncias.',
    'dev.evolution.webhook_read_error': 'Falha ao ler webhook',
    'dev.evolution.webhook_save_error': 'Não foi possível salvar o webhook. Tente novamente.',
    'dev.evolution.settings_read_error': 'Falha ao ler settings',
    'dev.evolution.settings_save_error': 'Não foi possível salvar as configurações. Tente novamente.',
    'dev.evolution.reload_partial': 'Não foi possível recarregar da API',
    'dev.evolution.detail_error': 'Erro ao carregar detalhes.',
    'dev.evolution.connect_error': 'Erro ao conectar / QR',
    'dev.evolution.webhook_url_missing': 'URL de webhook do ambiente não disponível.',
    'dev.evolution.webhook_pointed_test': 'Webhook apontando para teste (sandbox).',
    'dev.evolution.webhook_pointed_prod': 'Webhook apontando para produção.',
    'dev.evolution.lease_error': 'Webhook gravado, mas lease de teste não iniciou no backend.',
    'dev.evolution.lease_hint':
      'Desvio temporário (visão Produção → destino Teste): sem atividade por 2h o webhook desta instância volta ao backend de produção. No ambiente de teste, o destino natural é o próprio teste — sem auto-revert.',
    'dev.evolution.connected': 'Conectado',
    'dev.evolution.disconnected': 'Desconectado',
    'dev.card.bot.title': 'Bot WhatsApp',
    'dev.card.bot.desc':
      'Kill-switch global e liga/desliga por loja. Com global OFF o botão da loja fica indisponível.',
    'dev.delidesk.title': 'Impressão — DeliDesk & PrintNode',
    'dev.delidesk.subtitle':
      'Kill-switches globais e por loja (igual ao bot): fila DeliDesk, captura de marketplace e PrintNode — independentes.',
    'dev.delidesk.warning':
      'Efetivo = global ON e loja ON. Desligar PrintNode de uma loja não corta a fila DeliDesk; desligar captura não corta comandas.',
    'dev.delidesk.platform_title': 'Globais (todas as lojas)',
    'dev.delidesk.companies_title': 'Por loja',
    'dev.delidesk.companies_hint':
      'Com global OFF o chip da loja fica bloqueado. Religue o global para alterar a loja.',
    'dev.delidesk.companies_empty': 'Nenhuma loja ativa encontrada',
    'dev.delidesk.company_saved': 'Feature da loja atualizada',
    'dev.delidesk.company_backend': 'Backend: {backend}',
    'dev.delidesk.platform_locked_hint': 'Religue o kill-switch global desta feature primeiro',
    'dev.delidesk.chip.printer': 'Fila',
    'dev.delidesk.chip.virtual_capture': 'Captura',
    'dev.delidesk.chip.printnode': 'PrintNode',
    'dev.delidesk.applies_label': 'Aplica a:',
    'dev.delidesk.empty': 'Nenhuma feature disponível',
    'dev.delidesk.load_error': 'Não foi possível carregar as funções do DeliDesk',
    'dev.delidesk.save_error': 'Não foi possível salvar',
    'dev.delidesk.feature_on': 'GLOBAL ON',
    'dev.delidesk.feature_off': 'GLOBAL OFF',
    'dev.delidesk.feature_on_ok': 'Função {feature} ligada',
    'dev.delidesk.feature_off_ok': 'Função {feature} desligada',
    'dev.delidesk.feature.generic_hint': 'Controle global desta função.',
    'dev.delidesk.feature.printer.title': 'Fila DeliDesk (comandas)',
    'dev.delidesk.feature.printer.hint':
      'Cupons do painel DelivAI enfileirados e impressos pelo DeliDesk (jobs/next). Não controla a captura de marketplace.',
    'dev.delidesk.feature.printer.applies':
      'enqueue de comanda · poll jobs/next · impressão térmica via agente',
    'dev.delidesk.feature.virtual_capture.title': 'Captura de marketplace (virtual)',
    'dev.delidesk.feature.virtual_capture.hint':
      'Quando um marketplace (iFood, etc.) imprime na impressora virtual do DeliDesk, o cupom sobe para a IA criar o pedido.',
    'dev.delidesk.feature.virtual_capture.applies':
      'API de captura virtual · listener Windows · criação de pedido a partir do cupom do marketplace',
    'dev.delidesk.feature.printnode.title': 'PrintNode',
    'dev.delidesk.feature.printnode.hint':
      'Envio de cupom via PrintNode (lojas com print_backend=printnode ou fallback se a fila DeliDesk estiver OFF).',
    'dev.delidesk.feature.printnode.applies':
      'OrderPrintService → PrintNode · independente da captura de marketplace e da fila do agente',
    'dev.card.delidesk.title': 'Impressão',
    'dev.card.delidesk.desc':
      'Kill-switch global + por loja: fila DeliDesk, captura de marketplace e PrintNode.',
    'dev.consumers.clear_response_id': 'Zerar response id',
    'dev.consumers.clear_response_id_hint':
      'Remove o openai_response_id deste cliente. A próxima mensagem inicia contexto novo na IA.',
    'dev.consumers.clear_response_id_ok': 'Response id zerado.',
    'dev.consumers.clear_response_id_none': 'Não havia response id para zerar.',
    'dev.consumers.clear_response_id_error': 'Não foi possível zerar o response id.',
    'dev.consumers.close_detail': 'Fechar',
    'dev.home.title': 'Painel de controle',
    'dev.home.subtitle':
      'Licenças, Evolution, empresas, clientes, prompts, banco, logs e permissões — console interno DelivAI.',
    'dev.home.overview': 'Visão geral',
    'dev.home.overview_hint': 'Escolha uma área. Cada card abre uma ferramenta do painel interno.',
    'dev.home.open': 'Abrir',
    'dev.home.continue': 'Continuar',
    'dev.access.denied_title': 'Sem permissão',
    'dev.access.denied_body':
      'Seu cargo não tem acesso a esta área. Peça ao administrador ou faça login novamente após alteração de permissões.',
    'dev.card.licenses.title': 'Licenças e planos',
    'dev.card.licenses.desc':
      'Consultar empresa por CNPJ, ajustar dias restantes e pay_types exibidos na tela de licença.',
    'dev.nav.reports': 'Reports',
    'dev.nav.reports_title': 'Reports das lojas',
    'dev.card.reports.title': 'Reports',
    'dev.card.reports.desc': 'Avisos enviados pela bandeira nas telas da loja, com o texto e as imagens.',
    'dev.reports.empty': 'Nenhum aviso.',
    'dev.reports.empty_phase': 'Nenhum aviso nesta fase.',
    'dev.reports.load_failed': 'Não foi possível carregar os avisos.',
    'dev.reports.save_failed': 'Não foi possível salvar a fase.',
    'dev.reports.image': 'Imagem {n}',
    'dev.reports.company_unknown': 'Empresa não identificada',
    'dev.reports.team': 'Equipe DelivAI',
    'dev.reports.filter_label': 'Fase',
    'dev.reports.filter_all': 'Todas',
    'dev.reports.note_label': 'Nota',
    'dev.reports.note_placeholder': 'O que vamos fazer com este aviso',
    'dev.reports.phase.open': 'Novo',
    'dev.reports.phase.reviewed': 'Revisado',
    'dev.reports.phase.doing': 'Em andamento',
    'dev.reports.phase.done': 'Feito',
    'dev.reports.phase.ignored': 'Ignorar',
    'dev.card.contracts.title': 'Contratos jurídicos',
    'dev.card.contracts.desc':
      'Revisar cláusulas B2B, termos do consumidor e preview com hash/versão.',
    'dev.contracts.title': 'Contratos por cláusulas',
    'dev.contracts.subtitle':
      'Edite por setor, escolha a versão e ative a oficial. Novos aceites usam o hash da versão oficial de cada aba.',
    'dev.contracts.version': 'Versão do documento',
    'dev.contracts.official': 'oficial',
    'dev.contracts.awaiting': 'aguardando revisão',
    'dev.contracts.notices': 'Para o advogado',
    'dev.contracts.subs_paying': 'Assinaturas ativas pagantes',
    'dev.contracts.subs_free': 'Assinaturas ativas em período grátis',
    'dev.contracts.subs_hint': 'Loja marcada como teste não entra nestes números.',
    'dev.company.page_title': 'Manutenção da empresa',
    'dev.company.page_hint': 'Cadastro da loja: licença, operação, cardápio e IA. Clientes finais ficam na aba Clientes.',
    'dev.company.jump': 'Seções do cadastro',
    'dev.company.jump_data': 'Dados',
    'dev.company.jump_ops': 'Operação',
    'dev.company.jump_license': 'Licença',
    'dev.company.jump_menu': 'Cardápio',
    'dev.company.test_store': 'Loja teste',
    'dev.company.test_store_hint': 'Não entra na conta de assinaturas do advogado.',
    'dev.company.fees_title': 'Bairros e taxas',
    'dev.company.fees_hint': 'Uma linha por bairro. O JSON fica recolhido.',
    'dev.company.fees_add': 'Adicionar bairro',
    'dev.company.fees_advanced': 'JSON avançado',
    'dev.contracts.notice_define': 'Definir',
    'dev.contracts.notice_comment': 'Comentário',
    'dev.contracts.notice_done': 'Concluída',
    'dev.contracts.notice_useless': 'Inútil',
    'dev.contracts.notice_save': 'Salvar',
    'dev.contracts.decision_pending': 'Ainda não definido',
    'dev.contracts.decision_yes': 'Precisa mudar o contrato',
    'dev.contracts.decision_no': 'Não precisa mudar',
    'dev.contracts.add_version': 'Nova versão (cópia da atual)',
    'dev.contracts.version_added': 'Nova versão criada. Ative como oficial quando estiver pronta.',
    'dev.contracts.activate_official': 'Ativar como oficial',
    'dev.contracts.official_on': 'Versão oficial',
    'dev.contracts.version_activated': 'Versão ativada como oficial neste setor.',
    'dev.contracts.view_clauses': 'Cláusula a cláusula',
    'dev.contracts.view_document': 'Documento inteiro',
    'dev.contracts.doc_title': 'Título do documento',
    'dev.contracts.save': 'Salvar minutas',
    'dev.contracts.save_ok': 'Minutas salvas. Ative a versão oficial ao publicar alteração relevante.',
    'dev.contracts.doc_view': 'Ver',
    'dev.contracts.doc_edit': 'Editar texto',
    'dev.contracts.doc_apply': 'Aplicar no documento',
    'dev.contracts.doc_apply_ok': 'Texto aplicado nesta versão. Salve as minutas e ative como oficial se for a publicação.',
    'dev.contracts.doc_edit_hint':
      'Cole o contrato inteiro aqui. Use os botões para inserir campos que o sistema preenche por loja (CNPJ, razão social…). Campos aparecem em amarelo na visualização. Ctrl+Z desfaz a última alteração no texto.',
    'dev.contracts.doc_cancel': 'Cancelar edição',
    'dev.contracts.doc_undo': 'Desfazer (Ctrl+Z)',
    'dev.contracts.insert_fields': 'Inserir campo dinâmico',
    'dev.contracts.suggest_fields': 'Detectar CNPJ no texto',
    'dev.contracts.suggest_ok': 'Campos dinâmicos sugeridos no texto. Revise e clique em Aplicar.',
    'dev.contracts.suggest_none': 'Nenhum CNPJ de loja encontrado para converter.',
    'dev.contracts.delete_version': 'Excluir versão',
    'dev.contracts.delete_version_title': 'Excluir esta versão?',
    'dev.contracts.delete_version_msg':
      'A versão vai para a lixeira (dá para restaurar). Não remove a última versão viva. Somente master pode excluir.',
    'dev.contracts.delete_version_ok': 'Versão movida para a lixeira.',
    'dev.contracts.delete_version_blocked': 'Não é possível excluir a única versão ativa.',
    'dev.contracts.master_only_publish': 'Somente master pode ativar a versão oficial.',
    'dev.contracts.master_only_delete': 'Somente master pode excluir versões.',
    'dev.contracts.trash': 'Lixeira de versões',
    'dev.contracts.trash_empty': 'Nenhuma versão na lixeira.',
    'dev.contracts.restore_version': 'Restaurar',
    'dev.contracts.restore_version_ok': 'Versão restaurada da lixeira.',
    'dev.contracts.history': 'Pontos de recuperação',
    'dev.contracts.history_empty': 'Nenhum ponto salvo ainda. Ao aplicar o texto integral, o conteúdo anterior fica aqui.',
    'dev.contracts.restore_revision': 'Restaurar este ponto',
    'dev.contracts.restore_revision_ok': 'Conteúdo anterior restaurado nesta versão.',
    'dev.contracts.field.razao': 'Razão social (loja)',
    'dev.contracts.field.cnpj': 'CNPJ (loja)',
    'dev.contracts.field.endereco': 'Endereço (loja)',
    'dev.contracts.field.representante': 'Representante',
    'dev.contracts.field.versao': 'Versão',
    'dev.contracts.field.data': 'Data atualização',
    'dev.contracts.field.email': 'E-mail contato',
    'dev.contracts.field.dpo': 'DPO',
    'dev.contracts.field.remuneracao': 'Remuneração',
    'dev.card.database.title': 'Banco de dados',
    'dev.card.database.desc': 'Studio interno para inspecionar tabelas e operações de suporte.',
    'dev.card.logs.title': 'Executions / logs',
    'dev.card.logs.desc': 'Execuções do backend e fluxos em tempo real para debug.',
    'dev.card.observability.title': 'Observabilidade',
    'dev.card.observability.desc': 'Métricas (Prometheus) e logs agregados (Loki).',
    'dev.home.team_badge': 'Equipe DelivAI',
    'dev.home.stack_badge': 'Backend · Postgres · MinIO',
    'dev.home.internal_badge': 'Interno',
    'dev.permissions.relogin_hint':
      'Permissões atualizadas. A pessoa afetada precisa sair e entrar de novo para aplicar.',
    'dev.observability.subtitle':
      'Métricas (Prometheus) e logs (Loki) do backend DelivAI em tempo quase real.',
    'dev.observability.refresh': 'Atualizar',
    'dev.observability.checking': 'checando stack…',
    'dev.observability.no_data': 'sem dados',
    'dev.observability.window': 'Janela:',
    'dev.observability.prometheus_warning':
      'Prometheus desabilitado ou inacessível. Configure PROMETHEUS_URL e LOKI_URL no backend e suba a stack para visualizar dados.',

    'collaborator.sidebar.title': 'Colaborador',
    'collaborator.sidebar.deliveries': 'Minhas entregas',
    'collaborator.sidebar.orders': 'PDV',
    'collaborator.sidebar.delivery_ops': 'Delivery',
    'collaborator.sidebar.agenda': 'Minha agenda',
    'collaborator.no_access.title': 'Nenhuma permissão atribuída',
    'collaborator.no_access.body':
      'Peça à loja para verificar as suas permissões nos módulos. Quando a loja liberar o acesso, as telas aparecerão aqui.',
    
    // License Screen
    'license.title': 'Gerenciamento de Licença',
    'license.status': 'Status da Licença',
    'license.active': 'Ativa',
    'license.inactive': 'Inativa',
    'license.expired': 'Expirada',
    'license.paused': 'Pausada',
    'license.paused_message': 'O acesso está pausado. Os dias abaixo ficam guardados e não são gastos até o fim da pausa.',
    'license.ended_message': 'Os dias acabaram e o acesso ao painel está desativado. Renove para voltar.',
    'license.days_remaining': 'dias restantes',
    'license.days_expired': 'dias expirados',
    'license.details': 'Detalhes da Licença',
    'license.days_remaining_label': 'Dias Restantes',
    'license.last_renewal': 'Última Renovação',
    'license.next_renewal': 'Próxima Renovação',
    'license.available_modules': 'Módulos Disponíveis',
    'license.attendance_module': 'Módulo Atendimento',
    'license.attendance_module_desc': 'Gestão de conversas e atendimento',
    'license.delivery_module': 'Módulo Motoboy',
    'license.delivery_module_desc': 'Gestão de entregadores',
    'license.manager_module': 'Módulo Resumo de Vendas',
    'license.manager_module_desc': 'Gestão administrativa e relatórios',
     'license.module_scheduling_title': 'Módulo de agendamentos',
     'license.module_scheduling_desc': 'Reservas e agenda para clientes',
     'license.snapshot_card_title': 'Plano contratado e renovações',
     'license.modules_summary_hint':
       'Módulos habilitados no seu contrato. Novos aparecem aqui quando forem ativados pela equipe DelivAI.',
     'license.checkout_notice_title': 'Pagamentos no painel',
     'manager.page_title': 'Resumo de vendas',
     'manager.filter_all': 'Todas',
     'manager.filter_sales': 'Vendas',
     'manager.filter_production': 'Em produção',
     'manager.ticket_avg': 'Ticket médio',
     'manager.now': 'Agora',
     'manager.before': 'Período anterior',
     'manager.by_store': 'Por loja',
     'manager.cmv_later': 'Custos e CMV entram num módulo seguinte. Este resumo fica só nas vendas.',
    'manager.page_subtitle': 'Painel financeiro com métricas e desempenho de produtos vendidos',
    'license.active_status': 'Ativo',
    'license.inactive_status': 'Inativo',
    'license.available_plans': 'Planos Disponíveis para Renovação',
    'license.monthly': 'Mensal',
    'license.quarterly': 'Trimestral',
         'license.annual': 'Anual',
     'license.annual_fire': 'ANUAL',
     'license.days_access': 'dias de acesso completo',
     'license.days_30': '30 dias de acesso completo',
     'license.days_90': '90 dias de acesso completo',
     'license.days_365': '365 dias de acesso completo',
    'license.per_month': 'por mês',
    'license.per_quarter': 'por trimestre',
    'license.per_year': 'por ano',
    'license.savings': 'Economia de',
    'license.most_popular': 'Mais Popular',
         'license.all_modules': 'Todos os Módulos',
     'license.suprema_title': 'SUPREMA - Todos os Módulos',
    
    // Company Screen
    'company.title': 'Configurações da Empresa',
    'kitchen.title': 'Dark kitchen',
    'kitchen.hint': 'Cadastro, taxas e cardápio seguem a loja escolhida. Os pedidos das duas ficam na mesma lista.',
    'kitchen.store_label': 'Loja em edição',
    'kitchen.catalog_label': 'Cardápio',
    'kitchen.mix_hint': 'Dá para misturar os cardápios no mesmo pedido.',
    'kitchen.other_cnpj': 'CNPJ da outra loja',
    'kitchen.email': 'E-mail da cozinha',
    'kitchen.password': 'Senha',
    'kitchen.password_confirm': 'Confirmar senha',
    'kitchen.password_rules': 'Mínimo 6 caracteres, com maiúscula, número e um símbolo.',
    'kitchen.email_default': 'E-mail da Dark Kitchen',
    'kitchen.add_store': 'Adicionar loja',
    'kitchen.configure': 'Configurar',
    'kitchen.configure_title': 'Configurar a Dark Kitchen',
    'kitchen.login_email_hint': 'Este e-mail entra na cozinha, junto com o CNPJ de qualquer loja.',
    'kitchen.save_email': 'Salvar e-mail',
    'kitchen.email_saved': 'E-mail da cozinha atualizado.',
    'kitchen.close': 'Fechar',
    'kitchen.extra_cost': 'A 1ª e a 2ª loja entram no plano de R$ 300. Da 3ª em diante, cada loja soma R$ 100.',
    'kitchen.need_plan': 'Comprar o plano Dark Kitchen',
    'kitchen.need_plan_hint': 'O convite só libera depois do pagamento do plano.',
    'kitchen.transform': 'Transformar em Dark Kitchen',
    'kitchen.invite': 'Convidar esta loja',
    'kitchen.cancel': 'Cancelar',
    'kitchen.suggestions': 'Lojas encontradas',
    'kitchen.other_store': 'Outra loja',
    'kitchen.invite_sent': 'A outra loja precisa aceitar no cadastro dela.',
    'kitchen.incoming': '{store} quer operar junto com esta loja.',
    'kitchen.accept': 'Aceitar',
    'kitchen.reject': 'Recusar',
    'kitchen.accepted': 'As lojas estão juntas.',
    'kitchen.configure_stores': 'Configurar lojas',
    'kitchen.back': 'Voltar',
    'kitchen.unlink_one': 'Separar',
    'kitchen.leave': 'Separar lojas',
    'kitchen.leave_title': 'Separar esta loja?',
    'kitchen.leave_body': 'Ela volta a entrar só com o CNPJ. Os pedidos de cada uma continuam no próprio cadastro.',
    'kitchen.err.company_not_found': 'Não achamos essa loja.',
    'kitchen.err.same_store': 'Informe o CNPJ da outra loja.',
    'kitchen.err.already_linked': 'Essa loja já está em outra cozinha.',
    'kitchen.err.invite_pending': 'Já existe um convite para essa loja.',
    'kitchen.err.kitchen_mismatch': 'O e-mail ou a senha não batem com a cozinha atual.',
    'kitchen.err.bad_password': 'Senha incorreta.',
    'kitchen.err.weak_password': 'A senha precisa de 6 caracteres, com maiúscula, número e um símbolo.',
    'kitchen.err.password_mismatch': 'As senhas não conferem.',
    'kitchen.err.email_taken': 'Esse e-mail já é de outra cozinha.',
    'kitchen.err.bad_email': 'Informe um e-mail válido.',
    'kitchen.err.no_invite': 'Não há convite para aceitar.',
    'kitchen.err.not_linked': 'Essa loja não está nesta cozinha.',
    'kitchen.err.kitchen_not_found': 'Cozinha não encontrada.',
    'kitchen.err.kitchen_failed': 'Não foi possível juntar as lojas. Tente de novo.',
    'kitchen.err.dark_kitchen_plan_required': 'Compre o plano Dark Kitchen antes de convidar outra loja.',
    'company.tabs.company': 'Empresa',
    'company.tabs.menu': 'Catálogo',
    'company.tabs.delivery': 'Delivery',
    'company.tabs.agenda': 'Agenda',
    'company.tabs.aria': 'Seções das configurações da empresa',
    'company.header_subtitle_empresa': 'Dados cadastrais, contato, endereço e logotipo.',
    'company.header_subtitle_menu': 'Cardápio e serviços oferecidos pela loja.',
    'company.header_subtitle_delivery': 'Taxas, bairros e opções de entrega.',
    'company.header_subtitle_agenda': 'Regras de agendamento da loja.',
    'company.empresa_toolbar_hint': 'Editar e atualizar valem só para os dados desta aba.',
    'company.agenda.title': 'Regras da agenda',
    'company.agenda.hint': 'Defina como a loja agenda (calendário único, multi-profissional ou espaço compartilhado). Ative cada profissional em Colaboradores.',
    'company.agenda.open_employees': 'Abrir tela de Colaboradores',
    'company.agenda.empty': 'Nenhum colaborador ativo encontrado.',
    'company.agenda.area_placeholder': 'Área / especialidade',
    'company.agenda.toggle_on': 'Agenda ligada',
    'company.agenda.toggle_off': 'Agenda desligada',
    'company.agenda.saved': 'Regras da agenda salvas',
    'company.agenda.save_error': 'Não foi possível salvar as regras da agenda',
    'company.agenda.load_error': 'Não foi possível carregar as regras da agenda',
    'company.agenda.save': 'Salvar regras',
    'company.agenda.saving': 'Salvando…',
    'company.agenda.mode': 'Modo da agenda',
    'company.agenda.mode_single': 'Agenda da loja (calendário único)',
    'company.agenda.mode_multi': 'Multi-profissional (podem coincidir)',
    'company.agenda.mode_shared': 'Espaço compartilhado (sem conflito)',
    'company.agenda.mode_single_hint': 'Usa o Google da loja. Sem seletor de profissional.',
    'company.agenda.mode_multi_hint': 'Cada profissional com calendário próprio; horários podem coincidir (salas separadas).',
    'company.agenda.mode_shared_hint': 'Mesmo espaço/sala: horários não podem coincidir além do número de estações.',
    'company.agenda.slot_minutes': 'Intervalo entre horários (min)',
    'company.agenda.min_lead_minutes': 'Antecedência mínima (min)',
    'company.agenda.max_days_ahead': 'Máximo de dias à frente',
    'company.agenda.buffer_minutes': 'Descanso entre atendimentos (min)',
    'company.agenda.shared_stations': 'Estações / salas compartilhadas',
    'company.agenda.timezone': 'Fuso horário',
    'company.agenda.country': 'País',
    'company.agenda.region': 'Região / cidade',
    'company.agenda.country_br': 'Brasil',
    'company.agenda.country_pt': 'Portugal',
    'company.agenda.country_ar': 'Argentina',
    'company.agenda.country_py': 'Paraguai',
    'company.agenda.country_uy': 'Uruguai',
    'company.agenda.country_us': 'Estados Unidos',
    'company.agenda.country_ao': 'Angola',
    'company.agenda.country_mz': 'Moçambique',
    'company.agenda.region_sao_paulo': 'São Paulo (Brasília)',
    'company.agenda.region_fortaleza': 'Fortaleza',
    'company.agenda.region_belem': 'Belém',
    'company.agenda.region_manaus': 'Manaus',
    'company.agenda.region_cuiaba': 'Cuiabá',
    'company.agenda.region_rio_branco': 'Rio Branco',
    'company.agenda.region_lisbon': 'Lisboa',
    'company.agenda.region_buenos_aires': 'Buenos Aires',
    'company.agenda.region_asuncion': 'Assunção',
    'company.agenda.region_montevideo': 'Montevidéu',
    'company.agenda.region_new_york': 'Nova York (Leste)',
    'company.agenda.region_chicago': 'Chicago (Central)',
    'company.agenda.region_denver': 'Denver (Montanha)',
    'company.agenda.region_los_angeles': 'Los Angeles (Pacífico)',
    'company.agenda.region_luanda': 'Luanda',
    'company.agenda.region_maputo': 'Maputo',
    'company.agenda.matrix_title': 'Quem faz o quê',
    'company.agenda.matrix_hint':
      'Marque os profissionais que realizam cada serviço e, se precisar, o tempo em minutos. Sem ninguém marcado no serviço, todos podem atendê-lo.',
    'company.agenda.matrix_service': 'Serviço',
    'company.agenda.matrix_empty_services':
      'Cadastre serviços no catálogo para definir quem realiza cada um.',
    'company.agenda.matrix_empty_pros':
      'Ative a agenda de pelo menos um colaborador para montar a matriz.',
    'company.agenda.matrix_default_duration': '{count} min no catálogo',
    'company.agenda.matrix_unrestricted': 'Todos atendem (sem restrição)',
    'company.agenda.matrix_minutes': 'Minutos deste profissional',
    'company.agenda.matrix_assign': 'Este profissional realiza o serviço',
    'company.agenda.open_catalog': 'Abrir catálogo de serviços',
    'company.services.return_included': 'Retorno incluso neste serviço',
    'company.services.return': 'Retorno',
    'company.services.return_on': 'ON retorno',
    'company.services.return_off': 'OFF retorno',
    'company.services.visit': 'Visita',
    'company.services.visit_on': 'Visita',
    'company.services.visit_off': 'Na loja',
    'company.services.sinal': 'Sinal',
    'company.services.sinal_amount': 'Valor do sinal',
    'company.services.professionals': 'Profissionais deste serviço',
    'company.services.professionals_empty': 'Nenhum colaborador com agenda ativa.',
    'company.services.professionals_hint':
      'Nenhum marcado = todos os profissionais podem atender.',
    'company.agenda.sinal_title': 'Regra do sinal',
    'company.agenda.sinal_hint':
      'Padrão: o sinal soma ao valor do serviço. O cliente vê isso no resumo do agendamento.',
    'company.agenda.sinal_adds': 'Sinal + serviço',
    'company.agenda.sinal_deducts': 'Sinal desconta do serviço',
    'company.basic_info': 'Informações Básicas',
    'company.store_switching': 'Carregando a loja…',
    'company.name': 'Nome da Empresa',
    'company.cnpj': 'CNPJ',
    'company.phone': 'Telefone da Empresa',
    'company.responsible_phone': 'Telefone do Responsável',
    'company.email': 'Email',
    'company.address': 'Endereço',
    'company.street': 'Logradouro',
    'company.number': 'Número',
    'company.neighborhood': 'Bairro',
    'company.city': 'Cidade',
    'company.state': 'Estado',
    'company.state_uf': 'UF',
    'company.state_placeholder': 'UF',
         'company.zip_code': 'CEP',
         'company.cep_looking_up': 'Buscando CEP...',
         'company.cep_fill_hint': 'Digite o CEP para preencher logradouro, bairro, cidade e UF automaticamente.',
         'company.toast.cep_not_found': 'CEP não encontrado.',
         'company.toast.cep_fetch_error': 'Erro ao buscar CEP. Tente novamente.',
         'company.toast.save_success_fields': 'Dados atualizados com sucesso! Campos modificados: {fields}',
     'company.description': 'Descrição',
     'company.operating_hours': 'Horários de Funcionamento',
     'company.hours.presets_label': 'Presets',
     'company.hours.preset_24h': 'Aberto 24h',
     'company.hours.preset_commercial': 'Comercial 09:00–17:00',
     'company.hours.preset_custom': 'Personalizado',
     'company.hours.open_time': 'Abertura',
     'company.hours.close_time': 'Fechamento',
     'company.hours.range_from': 'De',
     'company.hours.range_to': 'À',
     'company.hours.commercial_range_hint': 'Dias no intervalo ficam abertos; os demais (ex.: sábado e domingo) ficam fechados.',
     'company.hours.select_all': 'Selecionar todos',
     'company.hours.clear_selection': 'Limpar seleção',
     'company.hours.apply_preset': 'Aplicar aos dias selecionados',
     'company.hours.apply_commercial': 'Aplicar horário comercial',
     'company.hours.apply_24h': 'Aplicar 24h em todos os dias',
     'company.hours.selected_count': '{count} dia(s) selecionado(s)',
     'company.hours.preset_hint': 'Marque os dias e aplique um preset. Depois ajuste horários na tabela se precisar. Salvar grava os 7 dias.',
     'company.hours.col_select': 'Selecionar',
     'company.hours.col_day': 'Dia',
     'company.hours.col_open': 'Status',
     'company.hours.select_day': 'Selecionar {day}',
     'company.about_company': 'Sobre a Empresa',
     'company.company_description': 'Descrição da Empresa',
     'company.observations': 'Observações',
     'company.observations_placeholder': 'Ex: Promoções ativas, sugestões de combos, lançamentos de novos produtos...',
     'company.delivery_settings': 'Configurações de Delivery',
     'company.delivery_time': 'Tempo Médio de Entrega (minutos)',
     'company.delivery_fee': 'Taxa de Entrega (R$)',
     
     // Menu Screen
     'menu.title': 'Gerenciamento de Cardápio',
     'menu.new_category': 'Nova Categoria',
     'menu.update': 'Atualizar',
     
     // Services Screen
     'services.title': 'Serviços',
     'services.new_category': 'Nova Categoria',
     'services.update': 'Atualizar',
     'services.duration': 'Duração (minutos)',
     'services.duration_placeholder': 'Ex: 60',
     
     // License Screen - Additional
     'license.current_plan': 'Plano Atual',
     'license.annual_plan': 'Plano Anual',
     'license.days_complete_access': '365 dias de acesso completo',
     'license.monthly_plan': 'Plano Mensal',
     'license.semiannual_plan': 'Plano Semestral',
     'license.days_180': '180 dias de acesso',
     'license.trial_plan': 'Teste grátis',
     'license.trial_subtitle': '7 dias de acesso, sem contratação',
     'license.trial_price': 'Grátis',
     'license.trial_period': 'sem cobrança',
     'license.access_until': 'Acesso até',
     'license.working_normally': 'Sua licença está funcionando normalmente',
     'license.expired_message': 'Sua licença expirou. Entre em contato para renovar.',
     'license.loading': 'Carregando dados da licença...',
     'license.load_error': 'Não foi possível carregar a licença. Tente novamente.',
     'license.cnpj_unavailable': 'CNPJ da loja indisponível.',
     'license.try_again': 'Tentar Novamente',
     'license.edit': 'Editar',
     'license.view_license': 'Verificar licença',
     'license.banner_expiring': 'Restam apenas {{days}} dias na sua licença.',
     'license.screen_expiring_lead': 'Restam apenas {{days}} dias na sua licença.',
     'license.screen_expiring_body': 'Antecipe a renovação para evitar suspensão do painel e transtornos no atendimento. Fale com o nosso time: podemos apresentar condições exclusivas para adicionar mais dias de licença antes do vencimento.',
     'license.banner_inactive': 'Sua licença está sem dias ativos. Renove com o suporte para liberar o painel.',
     'license.expired_overlay_title': 'LICENÇA EXPIRADA!',
     'license.expired_overlay_body': 'O período da sua licença encerrou e o uso do painel fica suspenso até a renovação. Pedimos desculpas pelo transtorno e agradecemos a parceria. Fale com o nosso time: teremos prazer em orientar a renovação e restabelecer tudo com segurança e rapidez.',
     'license.sidebar_locked_hint': 'Renove a licença para acessar este módulo',
     'license.orders_inactive_banner': 'Para seguir gerenciando pedidos com tranquilidade, fale com nossa equipe para alinhar a renovação da licença — estamos à disposição para ajudar.',
     'license.toast_inactive_session': 'Sua licença está sem dias ativos. Renove com o suporte para manter o painel completo.',
     'license.pay_with_pix': 'Pagar com PIX',
     'license.pix_checkout_hint_off':
       'Pagamento PIX por aqui será ativado em breve.',
     'license.catalog_offers_title': 'Planos e valores',
     'license.catalog_offers_lead':
       'Contrate apenas o que precisa — por período mensal, trimestral ou anual — ou feche todos os módulos com uma oferta combinada.',
     'license.catalog_checkout_disabled_banner':
       'O pagamento pelo painel ainda está em configuração neste ambiente. Para renovar ou contratar um plano, fale com o suporte DelivAI.',
     'license.catalog_suprema_badge': 'Melhor custo-benefício',
     'license.catalog_pix_unavailable_tooltip':
       'Pagamento por PIX indisponível para este período. Use outro período ou fale com o suporte.',
     'license.catalog_pix_slot_off_hint':
       'Atalho PIX será exibido aqui quando liberarmos o pagamento pelo painel. Por ora, feche valores com o suporte.',
     'license.pack_title': 'Pacote completo',
     'license.pack_subtitle':
       'Acesso aos módulos essenciais do DelivAI com um só plano.',
     'license.pack_duration_note': 'Este pacote adiciona {{days}} dia(s) à licença após pagamento confirmado.',
     'license.pack_includes_title': 'Inclui',
     'license.pack_module_orders': 'Gerenciamento de pedidos',
     'license.pack_module_finance': 'Financeiro',
     'license.pack_module_staff': 'Gestão de colaboradores',
     'license.pack_module_motoboy': 'Módulo motoboys / entregas',
     'license.pack_module_company_clients': 'Cadastro da empresa e clientes',
     'license.pack_pay_pix': 'Pagar com PIX',
     'license.pack_pay_boleto': 'Pagar com boleto',
     'license.pack_pay_mercadopago': 'Pagar com Mercado Pago (PIX ou cartão)',
     'license.pack_mercadopago_hint':
       'Abre o checkout seguro do Mercado Pago; você pode pagar com PIX ou cartão. Depois, volte ao painel.',
     'license.pack_mercadopago_email_missing':
       'É necessário um email válido na sua conta para pagar com o Mercado Pago. Atualize os dados com o suporte.',
     'license.pack_mercadopago_error':
       'Não foi possível iniciar o pagamento. Tente novamente ou fale com o suporte.',
     'license.pack_mercadopago_loading': 'A abrir o checkout…',
     'license.pack_mercadopago_click_price_hint':
       'Toque ou clique no valor acima para pagar com Mercado Pago (PIX ou cartão).',
     'license.pack_segment_pix': 'Pagamento via PIX',
     'license.pack_segment_mp': 'Pagamento via Mercado Pago',
     'license.pack_mp_method_card': 'Cartão de crédito',
     'license.pack_mp_method_boleto': 'Boleto',
     'license.pack_segment_aria': 'Meio de pagamento do pacote',
     'license.pack_pix_click_price_hint':
       'Toque ou clique no valor acima para seguir com o pagamento PIX.',
     'license.pack_half_label_pix': 'PIX',
     'license.pack_half_label_mp': 'Mercado Pago',
     'license.pack_split_price_group_aria': 'Valores: PIX Santander e checkout Mercado Pago',
     'license.pack_split_payment_hint': '',
     'license.pack_footnote_monthly': '30 dias de acesso · renovação mensal',
     'license.pack_footnote_semiannual': '180 dias · economia em relação ao mensal',
     'license.pack_footnote_annual': '365 dias · melhor custo por dia',
    'license.pack_split_pix_nav_note':
      'O botão PIX fica clicável quando o checkout de licença pelo painel estiver liberado neste servidor.',
    'license.pack_split_pix_unavailable_title': 'Valor de referência — PIX não clicável aqui',
    'license.pack_split_pix_unavailable_toast':
      'O fluxo PIX por aqui ainda não está liberado neste ambiente. Use Mercado Pago à direita ou fale com o suporte.',
    'license.mp_feedback_page_title': 'Retorno do pagamento',
    'license.mp_feedback_page_subtitle':
      'Um lugar só seu depois do checkout Mercado Pago.',
    'license.mp_feedback_badge_success': 'Tudo certo',
    'license.mp_feedback_badge_pending': 'Pagamento em análise',
    'license.mp_feedback_badge_failure': 'Sem alteração na conta',
    'license.mp_feedback_success_title': 'Que alegria ter você aqui!',
    'license.mp_feedback_success_body':
      'Obrigado pela sua confiança na DelivAI. Nossa equipe tem orgulho real de fortalecer o seu dia a dia na operação — quando o Mercado Pago confirmar o pagamento, seus dias aparecem na próxima tela.',
    'license.mp_feedback_pending_title': 'Quase lá — só mais um instante',
    'license.mp_feedback_pending_body':
      'O pagamento está em processamento pelo Mercado Pago. Em poucos instantes atualize sua licença na próxima tela para ver se os dias já entraram.',
    'license.mp_feedback_failure_title': 'Ops, não concluímos o pagamento',
    'license.mp_feedback_failure_body':
      'Nada foi alterado na sua conta. Isso às vezes acontece — respira fundo e, quando quiser, tente de novo com calma.',
    'license.mp_feedback_cta': 'Acessar licença',
    'license.mp_feedback_subnote':
      'A contagem oficial de dias sempre fica na tela seguinte.',
    'license.mp_feedback_ref_prefix': 'Referência:',
     'license.pack_pix_hint':
       'Dias creditados após confirmação do pagamento.',
     'license.pack_boleto_compensation_notice':
       'Valor via boleto. Os dias da licença só serão acrescidos após a compensação bancária.',
     'license.pack_pay_disabled_hint':
       'Pagamento pelo painel será habilitado em breve. Para renovar, fale com o suporte.',
     'license.pack_delivery_title': 'Plano Operação Completa',
     'license.pack_delivery_subtitle':
       'Pedidos, financeiro, colaboradores, motoboys e cadastros — opção econômica para quem não usa o fluxo de atendimento no painel.',
     'license.pack_billing_heading': 'Forma de cobrança',
     'license.pack_period_tab_monthly': 'Mensal',
     'license.pack_period_tab_semiannual': 'Semestral',
     'license.pack_period_tab_annual': 'Anual',
     'license.pack_annual_badge_fallback': '⭐ Melhor custo-benefício',
     'license.pack_delivery_notice_label': 'Observação:',
     'license.pack_delivery_no_attendance_note':
       'este pacote não inclui o módulo de atendimento (conversas no painel).',
     'license.pack_pix_badge': 'Indicado',
     'public_orders.save_principal_title': 'Salvar endereço principal?',
     'public_orders.save_principal_message': 'Deseja salvar este novo endereço como principal no seu cadastro?',
     'public_orders.save_principal_yes': 'Sim, salvar',
     'public_orders.save_principal_no': 'Não',
     'public_orders.loading': 'Carregando...',
     'public_orders.cnpj_invalid': 'CNPJ Inválido',
     'public_orders.company_not_found': 'Empresa não encontrada',
     'public_orders.company_unavailable': 'A empresa solicitada não foi encontrada ou não está disponível.',
     'public_orders.order_online': 'Faça seu pedido online',
     'public_orders.open': 'Aberto',
     'public_orders.closed': 'Fechado',
     'public_orders.track_order': 'Rastrear Pedido',
     'public_orders.search_products': 'Pesquisar produtos...',
     'public_orders.add_to_cart': 'Adicionar ao carrinho',
     'public_orders.cart': 'Carrinho',
     'public_orders.checkout': 'Finalizar pedido',
     'public_orders.your_name': 'Seu nome completo',
     'public_orders.your_phone': 'Seu celular',
     'public_orders.delivery_address': 'Endereço de entrega',
     'public_orders.payment_method': 'Forma de pagamento',
     'public_orders.place_order': 'Fazer pedido',
     'public_orders.cancel': 'Cancelar',
     'public_orders.from_price': 'A partir de {price}',
     'public_orders.free_delivery': 'Grátis',
     'public_orders.select_neighborhood': 'Selecione um bairro da lista',
     'public_orders.view_cart': 'Ver Carrinho',
     'public_orders.empty_cart': 'Carrinho vazio',
     'public_orders.finish_order': 'Finalizar Pedido',
     'public_orders.add_to_cart_btn': 'Adicionar ao Carrinho',
     'public_orders.calc_delivery_btn': 'Calcular taxa e tempo de entrega',
     'public_orders.calc_delivery_hint': 'Informe seu CEP para verificarmos se entregamos na sua região',
     'public_orders.delivery_cep_hint': 'Informe seu CEP para verificarmos se entregamos em sua região e qual a taxa.',
     'public_orders.zip_label': 'CEP',
     'public_orders.zip_placeholder': '00000-000',
     'public_orders.neighborhood_identified': 'Bairro identificado: {name}',
     'public_orders.delivery_fee_label': 'Taxa de entrega:',
     'public_orders.ok_understood': 'Ok, entendi',
     'public_orders.dont_know_zip': 'Não sei meu CEP',
     'public_orders.close': 'Fechar',
     'public_orders.close_cart': 'Fechar carrinho',
     'public_orders.close_modal': 'Fechar modal',
     'public_orders.continue_shopping': 'Continuar comprando',
     'public_orders.subtotal': 'Subtotal:',
     'public_orders.total': 'Total:',
     'public_orders.order_type_label': 'Tipo de pedido',
     'public_orders.no_products_category': 'Nenhum produto disponível nesta categoria.',
     'public_orders.menu_coming_soon': 'Cardápio em breve',
     'public_orders.decrease_qty': 'Diminuir quantidade',
     'public_orders.increase_qty': 'Aumentar quantidade',
     'public_orders.delivery_eta_prefix': 'Entrega em',
     'public_orders.phone_label': 'Telefone',
     'public_orders.phone_searching': 'Buscando cliente...',
     'public_orders.phone_found': 'Seus dados foram preenchidos',
     'public_orders.phone_needs_verify': 'Cliente cadastrado — confirme o código',
     'public_orders.phone_not_found': 'Cliente não encontrado',
     'public_orders.phone_optional_hint': 'Opcional para consumo no local',
     'public_orders.name_label': 'Nome',
     'public_orders.saved_address': 'Endereço salvo',
     'public_orders.address_default': 'Endereço',
     'public_orders.new_address': '+ Novo endereço',
     'public_orders.where_delivery': 'Onde é a entrega?',
     'public_orders.where_delivery_hint': 'Arraste o pino até sua porta ou toque no mapa. Em esquina, ajuste para a rua certa.',
     'public_orders.search_address': 'Buscar endereço',
     'public_orders.address_placeholder': 'Ex.: Av Brasil, {city}',
     'public_orders.address_suggestions_hint': 'Sugestões priorizam {city}{state}. Ruas limítrofes na área de entrega também aparecem.',
     'public_orders.number_label': 'Número',
     'public_orders.number_required': 'Número *',
     'public_orders.number_placeholder': 'Número da casa',
     'public_orders.delivery_here': 'Entrega aqui',
     'public_orders.use_my_location': 'Usar minha localização',
     'public_orders.getting_location': 'Obtendo localização...',
     'public_orders.map_hint_no_pin': 'Toque no mapa, use o botão de localização ou informe o CEP abaixo.',
     'public_orders.searching_address_location': 'Buscando endereço da localização...',
     'public_orders.identifying_street': 'Identificando rua...',
     'public_orders.corner_pick_street': 'Sua casa é na esquina? Qual é a sua rua?',
     'public_orders.address_preview': 'Endereço:',
     'public_orders.confirm_pin_hint': 'A entrega será aqui? Ajuste o pino se precisar.',
     'public_orders.confirm_delivery_address': 'Confirmar endereço de entrega',
     'public_orders.prefer_manual_zip': 'Prefiro informar o CEP manualmente',
     'public_orders.searching_zip': 'Buscando CEP...',
     'public_orders.delivery_address_required': 'Endereço de Entrega *',
     'public_orders.street_placeholder': 'Rua, avenida — ex.: Av Brasil, {city}',
     'public_orders.street_type_hint': 'Digite o nome da rua — as sugestões aparecem conforme você digita (região de {city}{state}, incluindo bairros vizinhos na área de entrega).',
     'public_orders.number_map_hint': 'Com CEP e número, o pino vai mais perto da sua casa no mapa. CEP ajuda a achar bairro e taxa de entrega.',
     'public_orders.continue_map_confirm': 'Continuar para confirmar no mapa',
     'public_orders.use_map_location': 'Usar mapa e localização',
     'public_orders.adjust_on_map': 'Ajustar no mapa',
     'public_orders.edit_zip_street': 'Editar CEP e rua',
     'public_orders.street_placeholder_simple': 'Rua, avenida, travessa...',
     'public_orders.neighborhood_label': 'Bairro *',
     'public_orders.complement_label': 'Complemento',
     'public_orders.complement_placeholder': 'Apto, bloco, etc.',
     'public_orders.reference_label': 'Ponto de referência',
     'public_orders.reference_placeholder': 'Ponto de referência',
     'public_orders.select_payment': 'Selecione a forma de pagamento',
     'public_orders.no_payment_configured': 'Nenhuma forma de pagamento configurada. Entre em contato com o estabelecimento.',
     'public_orders.need_change': 'Precisa de troco?',
     'public_orders.change_for': 'Para quanto?',
     'public_orders.change_hint': 'Digite o valor total que você vai pagar (ex: digite 50 para R$ 0,50)',
     'public_orders.notes_label': 'Observações',
     'public_orders.notes_placeholder': 'Observações adicionais sobre o pedido',
     'public_orders.order_summary': 'Resumo do Pedido',
     'public_orders.delivery_fee_summary': 'Taxa de entrega',
     'public_orders.processing': 'Processando...',
     'public_orders.confirm_order': 'Confirmar Pedido',
     'public_orders.obs_prefix': 'Obs:',
     'public_orders.pay.cash': 'Dinheiro',
     'public_orders.pay.pix': 'PIX',
     'public_orders.pay.debit': 'Cartão de Débito',
     'public_orders.pay.credit': 'Cartão de Crédito',
     'public_orders.pay.meal_voucher': 'Vale Refeição',
     'public_orders.pay.other': 'Outro',
     'public_orders.toast.geolocation_unsupported': 'Seu navegador não suporta localização.',
     'public_orders.toast.geolocation_failed': 'Não foi possível obter sua localização. Informe o CEP ou use o mapa.',
     'public_orders.toast.corner_street': 'Sua casa parece ser na esquina. Selecione qual é a sua rua.',
     'public_orders.toast.address_confirmed': 'Endereço confirmado! Confira os dados e escolha a forma de pagamento.',
     'public_orders.toast.select_payment': 'Selecione a forma de pagamento',
     'public_orders.toast.select_neighborhood_fee': 'Selecione um bairro válido da lista para aplicar a taxa de entrega',
     'public_orders.toast.select_price_type': 'Selecione um tipo de preço',
     'public_orders.toast.order_type_unavailable': 'Este tipo de pedido não está disponível para esta loja.',
     'public_orders.toast.order_error': 'Erro ao criar pedido. Tente novamente.',
     'bairro_field.label': 'Bairro',
     'bairro_field.placeholder': 'Clique e selecione o bairro',
     'bairro_field.no_neighborhoods': 'Nenhum bairro cadastrado. Configure em Configurações → Delivery.',
     'bairro_field.no_match': 'Nenhum bairro corresponde — escolha um da lista.',
     'bairro_field.not_found': 'Bairro não encontrado — escolha um da lista para ver a taxa',
     'bairro_field.select_hint': 'Selecione o bairro na lista para ver a taxa de entrega',
     'bairro_field.delivery_fee': 'Taxa de entrega:',
     'bairro_field.ref_hint_whatsapp': ' (valor informado na mensagem — troque o bairro para usar o cadastro)',
     'bairro_field.ref_hint_ifood': ' (valor do iFood — troque o bairro para usar o cadastro)',
     'bairro_field.ref_label_whatsapp': 'Mensagem indicava',
     'bairro_field.ref_label_ifood': 'iFood indicava',
     'bairro_field.using_registered': '— usando taxa do bairro cadastrado.',
     'bairro_field.registered_hint': 'Bairro cadastrado:',
     'bairro_field.using_ifood_fee': 'usando a menor taxa. Alterne no resumo se quiser a outra.',
     'bairro_field.using_higher_ifood': 'usando a taxa do iFood. Alterne no resumo para a menor (cadastro).',
     'public_orders.product_details': 'Detalhes do produto',
     'public_orders.quantity': 'Quantidade',
     'public_orders.select_size_type': 'Selecione o tamanho/tipo:',
     'public_orders.flavors_count': '{count} sabor(es)',
     'public_orders.pizza_single_flavor': 'Pizza inteira (1 sabor)',
     'public_orders.pizza_select_flavors': 'Selecione até {max} sabor(es) ({current}/{max}):',
     'public_orders.add_second_flavor': 'Adicionar segundo sabor',
     'public_orders.add_more_flavors': 'Adicionar mais {count} sabor(es)',
     'public_orders.select_second_flavor': 'Selecione o segundo sabor',
     'public_orders.select_additional_flavors': 'Selecione {count} sabor(es) adicional(is)',
     'public_orders.addons_optional': 'Adicionais (opcional):',
     'public_orders.addons_required': 'Adicionais (obrigatório pelo menos um):',
     'public_orders.addons_min_required': 'É obrigatório escolher pelo menos {min} item(ns) de "{category}"',
     'public_orders.product_notes': 'Observações (opcional):',
     'public_orders.product_notes_placeholder': 'Ex: sem cebola, sem azeitona, bem passado...',
     'public_orders.toast.adjust_pin_gps': 'Ajuste o pino no mapa ou use o GPS antes de confirmar.',
     'public_orders.toast.address_confirmed_details': 'Endereço confirmado! Agora informe número e complemento.',
     'public_orders.toast.enter_zip_before_map': 'Informe o CEP ou o endereço antes de posicionar no mapa.',
     'public_orders.toast.pin_near_number': 'Pino posicionado perto do número informado. Ajuste no mapa se precisar.',
     'public_orders.toast.pin_at_address': 'Pino posicionado no endereço. Ajuste no mapa se precisar.',
     'public_orders.toast.pin_on_street': 'Pino posicionado na rua. Informe o número ou ajuste no mapa.',
     'public_orders.toast.cep_street_found': 'CEP localizado na rua. Informe o número para posicionar na casa.',
     'public_orders.toast.map_locate_failed': 'Não foi possível localizar no mapa. Use GPS ou toque no mapa para colocar o pino.',
     'public_orders.toast.map_unavailable': 'Mapa indisponível. Use GPS ou toque no mapa para colocar o pino.',
     'public_orders.toast.enter_zip_before_continue': 'Informe o CEP ou endereço antes de continuar.',
     'public_orders.toast.item_added': 'Item adicionado ao carrinho!',
     'public_orders.toast.item_updated': 'Item atualizado no carrinho!',
     'public_orders.toast.cart_empty': 'Adicione itens ao carrinho antes de finalizar o pedido',
     'public_orders.toast.enter_name': 'Informe seu nome',
     'public_orders.toast.invalid_phone': 'Informe um telefone válido (mínimo 10 dígitos)',
     'public_orders.toast.enter_delivery_address': 'Informe o endereço de entrega',
     'public_orders.toast.enter_house_number': 'Informe o número da casa',
     'public_orders.toast.accept_privacy': 'Aceite a Política de Privacidade para continuar',
     'public_orders.toast.enter_change_amount': 'Informe o valor para o troco',
     'public_orders.toast.delivery_fee_error': 'Erro no cálculo da taxa de entrega. Tente selecionar o bairro novamente.',
     'public_orders.toast.max_flavors': 'Máximo de {max} sabor(es) para este tipo de pizza',
     'appointment.modal.new_title': 'Novo Agendamento',
     'appointment.modal.edit_title': 'Editar Agendamento',
     'appointment.modal.create_hint': 'Preencha os dados abaixo para agendar',
     'appointment.modal.edit_hint': 'Edite as informações do agendamento',
     'appointment.modal.client_section': 'Informações do Cliente',
     'appointment.modal.full_name': 'Nome Completo',
     'appointment.modal.client_name_placeholder': 'Nome do cliente',
     'appointment.modal.phone': 'Telefone',
     'appointment.modal.email': 'Email',
     'appointment.modal.service_section': 'Serviço',
     'appointment.modal.service': 'Serviço',
     'appointment.modal.loading_services': 'Carregando serviços...',
     'appointment.modal.select_service': 'Selecione um serviço',
     'appointment.modal.no_services': 'Nenhum serviço encontrado. Configure os serviços na seção "Catálogo".',
     'appointment.modal.no_services_for_pro': 'Nenhum serviço habilitado para este profissional. Marque os serviços na ficha do colaborador.',
     'appointment.modal.duration': 'Duração',
     'appointment.modal.duration_placeholder': 'Selecione um serviço para ver a duração',
     'appointment.modal.duration_auto': 'Duração automática baseada no serviço selecionado',
     'appointment.modal.date_time_section': 'Data e Horário',
     'appointment.modal.notes_section': 'Observações',
     'appointment.modal.notes_placeholder': 'Observações adicionais sobre o agendamento...',
     'appointment.modal.address_unknown': 'Endereço não informado',
     'appointment.modal.company_default': 'Empresa',
     'appointment.modal.create_btn': 'Criar Agendamento',
     'appointment.modal.save_btn': 'Salvar Alterações',
     'appointment.modal.one_day': '1 dia',
     'appointment.modal.days': '{count} dias',
     'appointment.modal.minutes': '{count} min',
     'appointment.error.client_required': 'Nome do cliente é obrigatório',
     'appointment.error.phone_required': 'Telefone é obrigatório',
     'appointment.error.date_required': 'Data é obrigatória',
     'appointment.error.time_required': 'Horário é obrigatório',
     'appointment.error.service_required': 'Serviço é obrigatório',
     'appointment.error.address_required': 'Endereço é obrigatório',
     'appointment.error.email_invalid': 'Email inválido',
     'appointment.error.past_datetime': 'Data e hora não podem ser no passado',
     'appointment.error.day_unavailable': 'Este dia está marcado como indisponível',
     'appointment.error.time_taken': 'Este horário já está ocupado por outro agendamento',
     'appointment.error.time_locked': 'Este horário está travado',
     'appointment.error.save_failed': 'Erro ao salvar agendamento. Tente novamente.',
     'public_scheduling.loading': 'Carregando informações da empresa...',
     'public_scheduling.not_found_title': 'Empresa não encontrada',
     'public_scheduling.not_found_message': 'A empresa solicitada não foi encontrada ou não está disponível para agendamentos.',
     'public_scheduling.back_home': 'Voltar ao início',
     'public_scheduling.system_title': 'Sistema de Agendamentos',
     'public_scheduling.creating': 'Criando...',
     'public_scheduling.new_appointment': 'Novo Agendamento',
     'public_scheduling.company_info': 'Informações da Empresa',
     'public_scheduling.address': 'Endereço',
     'public_scheduling.phone': 'Telefone',
     'public_scheduling.business_hours': 'Horários de Funcionamento',
     'public_scheduling.closed': 'Fechado',
     'public_scheduling.available_services': 'Serviços Disponíveis',
     'public_scheduling.no_services': 'Nenhum serviço disponível no momento.',
     'public_scheduling.book_now': 'Agendar agora',
     'public_scheduling.duration_label': 'Duração:',
     'public_scheduling.success': 'Agendamento realizado com sucesso! Evento criado no Google Calendar.',
     'public_scheduling.error_unknown': 'Erro desconhecido ao salvar agendamento.',
     'public_scheduling.address_unknown': 'Endereço não informado',
     'public_scheduling.company_default': 'Empresa',
     'public_scheduling.unavailable_title': 'Sistema de Agendamentos Indisponível',
     'public_scheduling.unavailable_message': 'O sistema de agendamentos está temporariamente indisponível.',
     'public_scheduling.reconnect_hint': 'A empresa precisa reconectar a integração com Google Calendar.',
     'public_scheduling.error_prefix': 'Erro: {error}',
     'public_scheduling.reconnecting': 'Tentando reconectar...',
     'public_scheduling.reconnect_btn': 'Tentar reconectar',
     'public_scheduling.how_to_title': 'Como Agendar',
     'public_scheduling.how_to_step1': 'Conecte-se ao Google Calendar (se ainda não estiver conectado)',
     'public_scheduling.how_to_step2': 'Clique em "Novo Agendamento" para abrir o formulário',
     'public_scheduling.how_to_step3': 'Preencha seus dados pessoais e selecione o serviço desejado',
     'public_scheduling.how_to_step4': 'Escolha a data e horário disponível no calendário',
     'public_scheduling.how_to_step5': 'Confirme o agendamento — será criado automaticamente no Google Calendar',
     'fast_order.no_cnpj': 'CNPJ não encontrado',
     'fast_order.title': 'Fast Order',
     'fast_order.back_aria': 'Voltar',
     'fast_order.standard_title': 'Pedido padrão',
     'fast_order.ifood_title': 'Fast iFood',
     'fast_order.choose_intro': 'Crie pedidos rapidamente sem montar item a item no cardápio. Escolha o tipo:',
     'fast_order.standard_card_title': 'Pedido padrão',
     'fast_order.standard_card_desc': 'Cole mensagens do WhatsApp ou envie print — a IA monta o pedido no cardápio.',
     'fast_order.ifood_card_desc': 'Cole o pedido inteiro ou envie um print — a IA monta o pedido no sistema.',
     'fast_order.standard_banner': 'Pedido padrão — IA interpreta WhatsApp ou print e monta no cardápio',
     'fast_order.ifood_banner': 'Fast iFood — IA interpreta o pedido e preenche o sistema',
     'fast_order.paste_text': 'Colar texto',
     'fast_order.upload_print_btn': 'Enviar print',
     'fast_order.content_ifood_label': 'Conteúdo do pedido iFood *',
     'fast_order.content_whatsapp_label': 'Mensagens do pedido *',
     'fast_order.hint_ifood': 'Copie tudo da tela do pedido no portal iFood (número, cliente, endereço, itens e totais).',
     'fast_order.hint_whatsapp': 'Cole a conversa do WhatsApp com o cliente (nome, telefone, endereço, itens e forma de pagamento).',
     'fast_order.placeholder_ifood': 'Cole aqui o pedido completo do iFood...',
     'fast_order.placeholder_whatsapp': 'Cole aqui as mensagens do WhatsApp...',
     'fast_order.hint_print_ifood': 'Print da tela do pedido no app ou portal iFood.',
     'fast_order.hint_print_whatsapp': 'Print da conversa no WhatsApp ou anotação do pedido.',
     'fast_order.choose_image': 'Clique para escolher imagem',
     'fast_order.preview_alt': 'Preview do pedido',
     'fast_order.extra_text_label': 'Texto extra (opcional)',
     'fast_order.extra_text_placeholder': 'Cole detalhes adicionais se o print não tiver tudo...',
     'fast_order.parsing': 'Interpretando...',
     'fast_order.parse_btn': 'Interpretar com IA',
     'fast_order.apply_btn': 'Aplicar ao pedido',
     'fast_order.image_type_error': 'Envie uma imagem (PNG, JPG ou WebP)',
     'fast_order.image_size_error': 'Imagem muito grande (máx. 8 MB)',
     'fast_order.paste_ifood': 'Cole o conteúdo completo do pedido iFood',
     'fast_order.paste_whatsapp': 'Cole as mensagens do WhatsApp ou o texto do pedido',
     'fast_order.upload_print': 'Envie um print do pedido',
     'fast_order.read_image_failed': 'Falha ao ler imagem',
     'fast_order.parsed_success': 'Pedido interpretado — revise e aplique',
     'fast_order.parse_failed': 'Não foi possível interpretar o pedido com IA',
     'fast_order.image_too_large': 'Print muito grande. Envie uma foto menor ou cole o texto do pedido.',
     'fast_order.parse_unavailable': 'O servidor de teste não respondeu. Tente de novo em instantes.',
     'fast_order.parse_ai_unavailable': 'A interpretação com IA está temporariamente indisponível. Tente de novo mais tarde.',
     'fast_order.fee_registered': 'bairro cadastrado',
     'fast_order.fee_message': 'valor na mensagem',
     'fast_order.fee_ifood': 'valor iFood',
     'fast_order.fee_checkout_neighborhood': 'Taxa definida pelo bairro no checkout',
     'employees.invite_token_missing': 'Convite gerado, mas o token não veio na resposta. Use "Reenviar convite".',
     'employees.invite_whatsapp_failed': 'Convite não enviado por WhatsApp: {error}. Use "Reenviar convite" quando o WhatsApp estiver online.',
     'employees.saved_success': 'Colaborador "{name}" editado com sucesso!',
     'employees.added_success': 'Colaborador "{name}" adicionado com sucesso!',
     'employees.updated_whatsapp': 'Colaborador "{name}" atualizado. Convite enviado por WhatsApp (30 min).',
     'employees.added_whatsapp': 'Colaborador "{name}" adicionado. Convite enviado por WhatsApp (30 min).',
     'employees.removed_success': 'Colaborador removido com sucesso.',
     'employees.inactive_section': 'Inativos',
     'employees.inactive_hint': 'Estes logins não entram na operação. Excluir apaga o cadastro.',
     'employees.delete_login': 'Excluir login',
     'employees.deactivate_title': 'Inativar colaborador?',
     'employees.deactivate_message': '{name} sai da lista principal e o login deixa de valer. Depois você pode excluir o cadastro na seção de inativos.',
     'employees.delete_login_title': 'Excluir este login?',
     'employees.delete_login_message': 'O cadastro de {name} será apagado. Essa pessoa não entra mais com esse login.',
     'employees.delete_login_success': 'Login excluído.',
     'employees.role_added': 'Cargo "{name}" adicionado com sucesso!',
     'employees.role_deleted': 'Cargo "{name}" excluído com sucesso!',
     'employees.role_delete_blocked': 'Não é possível excluir o cargo "{name}" pois {count} colaborador(es) está(ão) usando este cargo.',
     'employees.error.invalid_cnpj': 'CNPJ da loja inválido.',
     'employees.error.invalid_cpf': 'CPF inválido. Informe 11 dígitos ou deixe em branco para o colaborador preencher no convite.',
     'employees.error.invalid_cpf_fix': 'CPF inválido. Corrija o número ou deixe em branco.',
     'employees.error.name_required': 'Informe o nome do colaborador.',
     'employees.error.no_permission_roles': 'Sem permissão para alterar cargos.',
     'employees.error.deactivate_failed': 'Não foi possível inativar este colaborador. Verifique CPF e CNPJ da loja.',
     'employees.error.delete_failed': 'Erro ao excluir colaborador. Tente novamente.',
     'orders.reject_pending_title': 'Rejeitar pedido pendente?',
     'orders.reject_pending_message': 'O pedido será removido da fila de pendentes e não será lançado na lista de pedidos da loja.',
     'orders.reject_reason_label': 'Motivo da rejeição',
     'orders.cancel_reason_label': 'Motivo do cancelamento',
     'orders.select_reason': 'Selecione um motivo',
     'orders.describe_reason': 'Descreva o motivo',
     'orders.reason_placeholder': 'Ex.: Hoje não teremos expediente por manutenção.',
     'orders.back': 'Voltar',
     'orders.rejecting': 'Rejeitando...',
     'orders.confirm_reject': 'Sim, rejeitar',
     'orders.reject': 'Rejeitar',
     'orders.approve': 'Aprovar',
     'orders.reject_pending': 'Rejeitar pedido pendente',
     'orders.cancel_order': 'Cancelar pedido',
     'orders.confirm_cancel': 'Confirmar Cancelamento',
     'orders.cancelling': 'Cancelando…',
     'orders.confirm': 'Confirmar',
     'orders.edit_item': 'Editar item',
     'orders.remove_item': 'Remover item',
     'orders.update_item': 'Atualizar item',
     'orders.add_to_order': 'Adicionar ao Pedido',
     'orders.add_product': 'Adicionar Produto',
     'orders.toast.product_not_in_menu': 'Produto não encontrado no cardápio atual. Remova e adicione de novo.',
     'orders.toast.menu_unavailable': 'Cardápio não carregado. Tente novamente.',
     'orders.confirm_admin': 'Confirmar com administrador',
     'orders.open_store_manual': 'Abrir loja manualmente',
     'orders.close_store_manual': 'Fechar loja manualmente',
     'orders.confirm_dispatch': 'Confirmar Despacho',
     'orders.cancel_order_title': 'Cancelar Pedido',
     'orders.cancel_confirm_message': 'Tem certeza que deseja cancelar este pedido? Esta ação não pode ser desfeita.',
     'orders.ready': 'Pronto',
     'orders.error.select_cancel_reason': 'Selecione um motivo para cancelamento. Em "Outros", digite a justificativa.',
     'orders.error.select_reject_reason': 'Selecione um motivo para rejeição. Em "Outros", digite a justificativa.',
     'orders.error.no_cnpj_accept': 'CNPJ não disponível para aceitar pedido.',
     'orders.error.admin_cpf': 'Selecione um administrador com CPF válido.',
     'orders.error.admin_password': 'Informe a senha do administrador.',
     'orders.error.wrong_password': 'Senha incorreta. Tente novamente.',
     'orders.error.validate_password': 'Não foi possível validar a senha.',
     'orders.error.store_status': 'Não foi possível atualizar o status manual da loja.',
     'orders.error.store_schedule_missing': 'Não há próximo horário na agenda. Cadastre o expediente da loja ou escolha 30 min / 2h / 4h.',
     'orders.error.store_reopen_outside_hours': 'Esse prazo reabriria fora do expediente. Use Automático ou outro tempo válido.',
     'orders.error.start_sales': 'Não foi possível iniciar as vendas agora.',
     'orders.detail.payment_method': 'Método de Pagamento',
     'orders.detail.created_at': 'Data de Criação',
     'orders.detail.order_type': 'Tipo de Pedido',
     'orders.detail.order_total': 'Total do Pedido:',
     'orders.detail.observations': 'Observações',
     'orders.detail.ifood_locator': 'Localizador iFood',
     'orders.delidesk_ifood.badge': 'Capturado do iFood via DeliDesk',
     'orders.delidesk_ifood.review_hint': 'Confira os itens e valores e aprove o pedido.',
     'orders.delidesk_ifood.new_toast': 'Novo pedido iFood #{number} de {client} — {total}. Confira e aprove.',
     'orders.delidesk_ifood.test_badge': 'Pedido de demonstração (impressão teste iFood)',
     'orders.delidesk_ifood.test_review_hint':
       'Não é um pedido real. Aprove, edite e simule o despacho com o motoboy para conhecer o fluxo.',
     'orders.delidesk_ifood.test_toast':
       'Impressão teste do iFood — pedido de demonstração #{number} ({total}).',
     'orders.delidesk_ifood.test_prompt_title': 'Impressão teste do iFood',
     'orders.delidesk_ifood.test_prompt_message':
       'Detectamos uma impressão de teste. Aprove para criar o fluxo (produção + cupom DelivAI na térmica) ou descarte o pedido demo.',
     'orders.delidesk_ifood.test_prompt_explore': 'Aprovar e imprimir',
     'orders.delidesk_ifood.test_prompt_discard': 'Descartar',
     'orders.delidesk_ifood.test_discarded': 'Pedido de demonstração descartado.',
     'orders.delidesk_ifood.test_approved': 'Demonstração aprovada e enviada para impressão.',
     'orders.print_slip': 'Imprimir comanda',
     'orders.print_slip_updated_title': 'Imprimir comanda atualizada?',
     'orders.print_slip_confirm': 'Imprimir comanda',
     'orders.not_now': 'Agora não',
     'orders.start_sales_now': 'Iniciar vendas agora?',
     'orders.start_sales_opening': 'Horário de abertura — iniciar vendas?',
     'orders.store_reopening_soon': 'Loja prestes a reabrir',
     'orders.store_opening_soon': 'Loja prestes a abrir',
     'orders.success_store_open': 'Loja aberta manualmente com sucesso.',
     'orders.success_store_open_auto': 'Loja aberta. Fecha automaticamente no próximo horário de fechamento da agenda.',
     'orders.success_store_auto': 'Loja no modo automático — segue o horário da agenda.',
     'orders.success_store_close': 'Loja fechada manualmente com sucesso.',
     'orders.success_store_close_auto': 'Loja fechada. Reabre automaticamente no próximo horário da agenda.',
     'orders.success_start_sales': 'Vendas iniciadas. Loja aberta.',
     'orders.success_approve_pending': 'Pedido pendente aprovado e criado com sucesso.',
     'orders.success_reject_pending': 'Pedido pendente rejeitado.',
     'orders.error_approve_pending': 'Erro ao aprovar pedido pendente.',
     'orders.error_reject_pending': 'Erro ao rejeitar pedido pendente.',
     'orders.error_cancel': 'Erro ao cancelar pedido. Tente novamente.',
     'orders.error_accept': 'Falha ao aceitar pedido',
     'orders.success_accept': 'Pedido aceito.',
     'orders.auto_accept_unavailable_closed': 'Aceite automático fica indisponível enquanto a loja estiver fechada.',
     'orders.badge_store_closed': 'Loja fechada',
     'orders.badge_waiting_accept': 'Aguardando aceite',
     'orders.error_dispatch': 'Não foi possível despachar o pedido. Tente novamente.',
     'orders.error_print_order': 'Não foi possível imprimir o pedido #{number}.',
     'orders.success_print_slip': 'Comanda enviada para impressão.',
     'orders.error_print_slip': 'Não foi possível imprimir a comanda. Tente o botão Imprimir no pedido.',
     'orders.warn_select_deliverer': 'Por favor, selecione um entregador',
     'orders.warn_select_orders_dispatch': 'Selecione pelo menos um pedido para despachar',
     'orders.warn_select_deliverer_dispatch': 'Selecione um entregador',
     'orders.success_dispatch_count': '{count} pedido(s) despachado(s) com sucesso!',
     'orders.warn_dispatch_failed_count': '{count} pedido(s) não puderam ser despachados',
     'orders.warn_dispatch_error': 'Erro ao despachar pedidos. Tente novamente.',
     'orders.success_products_updated': 'Produtos do pedido atualizados com sucesso!',
     'orders.error_products_update': 'Erro ao atualizar produtos do pedido',
     'orders.error_save_changes': 'Erro ao salvar alterações. Tente novamente.',
     'orders.error_no_cnpj_print': 'CNPJ não disponível para impressão',
     'orders.add_deadline': 'Adicionar Prazo',
     'orders.save_deadline_changes': 'Salvar Alterações',
     'orders.success_order_accepted_print': 'Pedido #{number} aceito. Imprimindo… 🖨️',
     'orders.success_order_accepted_printed': 'Pedido #{number} aceito e impresso! 🖨️',
     'orders.success_order_accept_auto_print': 'Pedido #{number} aceito; impressão automática em andamento 🖨️',
     'schedule.confirm_btn': 'Confirmar',
     'schedule.syncing': 'Sincronizando...',
     'schedule.delete_btn': 'Deletar',
     'schedule.notes_label': 'Observações:',
     'route_dispatch.title': 'Despachar rota',
     'route_dispatch.motoboy_label': 'Motoboy',
     'route_dispatch.select_motoboy': 'Selecione o motoboy...',
     'route_dispatch.stops_hint': '{count} parada(s) — arraste para definir a ordem de entrega',
     'route_dispatch.drag_aria': 'Arrastar',
     'route_dispatch.confirming': 'Confirmando...',
     'route_dispatch.confirm_route': 'Confirmar rota',
     'route_dispatch.error_select_motoboy': 'Selecione o motoboy',
     'route_dispatch.error_select_orders': 'Selecione pedidos',
     'route_dispatch.success': 'Rota criada e enviada ao motoboy!',
     'route_dispatch.error_create': 'Erro ao criar rota',
     'route_dispatch.client_fallback': 'Cliente',
     'orders.reject_reason.holiday': 'Feriado / horário de funcionamento',
     'orders.reject_reason.excess': 'Excesso de pedidos no momento',
     'orders.reject_reason.out_of_stock': 'Itens em falta',
     'orders.reject_reason.other': 'Outros (informar)',
     'employees.readonly_only': 'Somente leitura',
     'employees.copying_link': 'Copiando link…',
     'employees.generate_copy_link': 'Gerar e copiar link',
     'employees.copy_invite_link': 'Copiar link do convite',
     'employees.send_invite_whatsapp': 'Enviar convite por WhatsApp',
     'employees.sending_invite': 'Enviando convite…',
     'employees.resend_invite': 'Reenviar convite',
     'employees.perm_module_denied': 'Sem permissão para acessar: {name}',
     'employees.perm_editor_title': 'Permissões',
     'employees.perm_editor_subtitle': 'Por módulo — acesso e ações permitidas',
     'employees.perm_actions': 'Ações',
     'employees.perm_access_module': 'Acessar módulo: {name}',
     'employees.perm_collapse_actions': 'Recolher ações',
     'employees.perm_expand_actions': 'Expandir ações',
     'employees.perm_collapse': 'Recolher',
     'employees.perm_view_actions': 'Ver ações',
     'employees.perm_done': 'Concluir',
     'perm.mod.dashboard': 'Painel / dashboard',
     'perm.mod.pdv': 'PDV (mesa e retirada)',
     'perm.mod.delivery': 'Delivery (despacho)',
     'perm.mod.pedidos': 'Gerenciamento de pedidos (legado)',
     'perm.act.pdv.ver': 'Ver PDV',
     'perm.act.pdv.aceitar': 'Aceitar pedido',
     'perm.act.pdv.cancelar': 'Cancelar pedido',
     'perm.act.pdv.editar': 'Editar pedido',
     'perm.act.pdv.finalizar': 'Finalizar pedido',
     'perm.act.pdv.ver_historico': 'Ver histórico',
     'perm.act.pdv.pedido_interno': 'Colar / pedido interno',
     'perm.act.pdv.imprimir': 'Imprimir / comprovantes',
     'perm.act.delivery.ver': 'Ver Delivery',
     'perm.act.delivery.aceitar': 'Aceitar pedido',
     'perm.act.delivery.cancelar': 'Cancelar pedido',
     'perm.act.delivery.editar': 'Editar pedido',
     'perm.act.delivery.despachar': 'Despachar / atribuir entregador',
     'perm.act.delivery.finalizar': 'Finalizar pedido',
     'perm.act.delivery.ver_historico': 'Ver histórico',
     'perm.act.delivery.pedido_interno': 'Fast Order / pedido interno',
     'perm.act.delivery.imprimir': 'Imprimir / comprovantes',
     'perm.mod.conversas': 'Conversas (WhatsApp)',
     'perm.mod.company': 'Cardápio / empresa',
     'perm.mod.agendamentos': 'Agendamentos',
     'perm.mod.motoboys': 'Ranking Motoboys',
     'perm.mod.manager': 'Gerente / resumo',
     'perm.mod.colaboradores': 'Colaboradores',
     'perm.mod.clientes': 'Clientes',
     'perm.mod.financeiro': 'Financeiro',
     'perm.mod.configuracoes': 'Configurações',
     'perm.mod.license': 'Licença',
     'perm.mod.delivai_controle': 'Controle DelivAI (interno)',
     'perm.act.dashboard.ver': 'Acessar painel',
     'perm.act.pedidos.ver': 'Ver pedidos',
     'perm.act.pedidos.aceitar': 'Aceitar pedido',
     'perm.act.pedidos.cancelar': 'Cancelar pedido',
     'perm.act.pedidos.editar': 'Editar pedido',
     'perm.act.pedidos.despachar': 'Despachar / atribuir entregador',
     'perm.act.pedidos.finalizar': 'Finalizar pedido',
     'perm.act.pedidos.ver_historico': 'Ver histórico',
     'perm.act.pedidos.pedido_interno': 'Pedido interno',
     'perm.act.pedidos.imprimir': 'Imprimir / comprovantes',
     'perm.act.conversas.ver': 'Ver conversas',
     'perm.act.conversas.enviar_mensagens': 'Enviar mensagens',
     'perm.act.conversas.arquivar': 'Arquivar / gerenciar',
     'perm.act.company.ver': 'Ver',
     'perm.act.company.editar_cardapio': 'Editar cardápio',
     'perm.act.company.editar_delivery': 'Editar delivery / bairros',
     'perm.act.company.editar_dados_empresa': 'Editar dados da empresa',
     'perm.act.agendamentos.ver': 'Ver',
     'perm.act.agendamentos.criar': 'Criar',
     'perm.act.agendamentos.editar': 'Editar',
     'perm.act.agendamentos.cancelar': 'Cancelar',
     'perm.act.motoboys.ver': 'Ver ranking',
     'perm.act.motoboys.editar': 'Editar diárias / ranking',
     'perm.act.manager.ver': 'Ver',
     'perm.act.colaboradores.ver': 'Ver lista',
     'perm.act.colaboradores.criar_editar': 'Criar e editar colaboradores',
     'perm.act.colaboradores.editar_permissoes': 'Editar permissões de outros',
     'perm.act.clientes.ver': 'Ver',
     'perm.act.clientes.editar_clientes': 'Editar',
     'perm.act.financeiro.ver': 'Ver',
     'perm.act.financeiro.editar': 'Editar',
     'perm.act.configuracoes.ver': 'Ver',
     'perm.act.configuracoes.editar': 'Editar',
     'perm.act.license.ver': 'Ver licença',
     'perm.act.delivai_controle.ver': 'Acessar painel interno',
     'perm.act.delivai_controle.editar': 'Licenças — editar prazos, módulos e pay_types',
     'perm.act.delivai_controle.criar': 'Evolution — criar e gerir instâncias',
     'perm.act.delivai_controle.editar_cardapio': 'Empresas — editar registro companies',
     'perm.act.delivai_controle.editar_prompts': 'Prompts — editar templates do bot',
     'perm.act.delivai_controle.editar_consumers': 'Clientes — listar e mesclar consumers',
     'perm.act.delivai_controle.ver_banco': 'Banco — studio interno (tabelas e SQL)',
     'perm.act.delivai_controle.ver_logs': 'Logs — executions n8n em tempo real',
     'perm.act.delivai_controle.ver_observabilidade': 'Observabilidade — métricas (Prometheus) e Loki',
     'perm.act.delivai_controle.editar_permissoes': 'Permissões — gerir outros desenvolvedores',
     'perm.act.delivai_controle.editar_contratos': 'Contratos — revisar/editar minutas (B2B, consumidor, colaboradores, equipe interna, advogado)',
     'perm.act.delivai_controle.publicar_contratos': 'Contratos — ativar versão oficial (somente master)',
     'perm.act.delivai_controle.excluir_versoes_contratos': 'Contratos — excluir versões (somente master; vai para lixeira)',
     'identity_gate.welcome_prefix': 'Seja bem-vindo ao',
     'identity_gate.default_store': 'nossa loja',
     'identity_gate.verify': 'Verificar',
     'identity_gate.view_menu': 'Ver Cardápio',
     'consumer_verify.title_entry': 'Identifique-se com seu celular',
     'consumer_verify.title_checkout': 'Confirme para concluir o pedido',
     'consumer_verify.subtitle_whatsapp': 'Insira um WhatsApp válido',
     'consumer_verify.subtitle_checkout': 'Por segurança, confirme que é você antes de finalizar.',
     'consumer_verify.your_phone': 'WhatsApp',
     'consumer_verify.select_country': 'Selecionar país',
     'consumer_verify.continue': 'Continuar',
     'consumer_verify.close': 'Fechar',
     'consumer_verify.invalid_phone': 'Informe um WhatsApp válido com DDD',
     'consumer_verify.channel_prompt': 'Como deseja receber o código para {phone}?',
     'consumer_verify.send_whatsapp_code': 'Receber código no WhatsApp',
     'consumer_verify.retry_whatsapp_code': 'Tentar WhatsApp novamente',
     'consumer_verify.channel_or_email': 'ou por e-mail',
     'consumer_verify.sending_code': 'Enviando código...',
     'consumer_verify.verifying_code': 'Verificando...',
     'consumer_verify.code_sent_whatsapp': 'Código enviado no WhatsApp',
     'consumer_verify.code_sent_email': 'Código enviado no e-mail',
     'consumer_verify.code_prompt': 'Digite o código de 6 dígitos enviado por {channel}.',
     'consumer_verify.channel_whatsapp': 'WhatsApp',
     'consumer_verify.channel_email': 'e-mail',
     'consumer_verify.confirm_code': 'Confirmar código',
     'consumer_verify.resend_code': 'Reenviar código',
     'consumer_verify.change_phone': 'Alterar telefone',
     'consumer_verify.whatsapp_unavailable': 'WhatsApp da loja indisponível no momento. O responsável foi avisado. Use o e-mail.',
     'consumer_verify.send_email_code': 'Enviar código no e-mail',
     'consumer_verify.your_email': 'Seu e-mail',
     'consumer_verify.invalid_email': 'Informe um e-mail válido',
     'consumer_verify.invalid_code': 'Digite o código de 6 dígitos',
     'consumer_verify.verified': 'Identificação confirmada!',
     'consumer_verify.verified_as': '{name}',
     'consumer_verify.verified_phone': '{phone}',
     'consumer_verify.chip_details_title': 'Seus dados',
     'consumer_verify.chip_details_aria': 'Ver dados do cliente',
     'consumer_verify.chip_no_address': 'Nenhum endereço preenchido ainda',
     'consumer_verify.send_failed': 'Não foi possível enviar o código',
     'consumer_verify.send_failed_manual':
       'Erro na verificação de cadastro. Por favor, preencha seus dados manualmente.',
     'consumer_verify.fill_manually': 'Preencher dados manualmente',
     'consumer_verify.verify_failed': 'Verificação falhou — tente novamente',
     'consumer_verify.wrong_code': 'Código incorreto, tente novamente',
     'consumer_verify.code_expired': 'Código expirado — peça um novo',
     'consumer_verify.code_missing': 'Nenhum código pendente — peça um novo',
     'consumer_verify.rate_limited': 'Muitas tentativas — aguarde um pouco e tente de novo',
     'consumer_verify.confirm_checkout': 'Confirme seu celular para concluir o pedido',
     'consumer_verify.auth_methods': 'Formas de identificação',
     'consumer_verify.tab_whatsapp': 'WhatsApp',
     'consumer_verify.tab_password': 'Senha',
     'consumer_verify.your_name': 'Seu nome',
     'consumer_verify.name_placeholder': 'Como devemos te chamar',
     'consumer_verify.your_password': 'Senha',
     'consumer_verify.password_placeholder': 'Mínimo 6 caracteres',
     'consumer_verify.confirm_password': 'Confirmar senha',
     'consumer_verify.confirm_password_placeholder': 'Digite a senha novamente',
     'consumer_verify.confirm_password_required': 'Confirme a senha',
     'consumer_verify.password_mismatch': 'As senhas não coincidem',
     'consumer_verify.weak_password': 'A senha deve ter no mínimo 6 caracteres',
     'consumer_verify.login_password': 'Entrar com senha',
     'consumer_verify.create_password_account': 'Criar conta com senha',
     'consumer_verify.login_failed': 'Telefone ou senha incorretos',
     'consumer_verify.register_failed': 'Não foi possível criar a conta — tente novamente',
     
     // Orders Screen
    'orders.title': 'PDV',
    'orders.header_subtitle': 'Mesa e retirada — aceite, produza e acompanhe as comandas do dia.',
    'orders.paste_order': 'Colar',
    'orders.paste_order_title': 'Colar pedido (texto ou print)',
    'orders.print_format_aria': 'Tamanho do cupom térmico',
    'orders.new_order': 'Novo Pedido',
    'orders.pending': 'Pendentes',
    'orders.in_progress': 'Em Andamento',
    'orders.completed': 'Concluídos',
    'orders.cancelled': 'Cancelados',
    
    // Conversations Screen
    'conversations.title': 'Conversas',
    'conversations.new_conversation': 'Nova Conversa',
    'conversations.search': 'Buscar conversas...',
    
    // Employees Screen
    'employees.title': 'Colaboradores',
    'employees.add_employee': 'Adicionar Colaborador',
    'employees.name': 'Nome',
    'employees.role': 'Cargo',
    'employees.email': 'Email',
    'employees.phone': 'Telefone',
    'employees.status': 'Status',
    'employees.search_placeholder': 'Buscar colaborador...',
    'employees.no_employees': 'Nenhum colaborador encontrado',
    'employees.add_first': 'Adicione seu primeiro colaborador para começar',
    'employees.agenda_toggle': 'Agenda do profissional',
    'employees.agenda_hint': 'Liga a agenda Google individual deste colaborador.',
    'employees.agenda_area': 'Área / especialidade',
    'employees.agenda_area_placeholder': 'Ex.: manicure, cardiologia',
    'employees.agenda_saved': 'Agenda atualizada',
    'employees.agenda_save_error': 'Não foi possível atualizar a agenda',
    
    // Orders Screen - Additional
    'orders.update': 'Atualizar',
    'orders.search_placeholder': 'Buscar por número do pedido ou cliente...',
    'orders.status.in_analysis': 'Em Análise',
    'orders.status.in_production': 'Em Produção',
    'orders.status.ready_delivery': 'Prontos para Entrega',
    'orders.status.in_route': 'Em Rota',
    'orders.delivery_deadline': 'Prazo de Entrega',
    'orders.select_deadline': 'Selecione um prazo',
    'orders.manage_deadlines': 'Gerenciar prazos',
    'orders.internal_order': 'Pedido Interno',
    'orders.public_link': 'Link Público:',
    'orders.copy_link': 'Copiar link',
    'orders.store': 'Loja',
    'orders.store_open': 'Aberta',
    'orders.store_closed': 'Fechada',
    'orders.mode_auto': 'Auto',
    'orders.mode_manual': 'Manual',
    'orders.open_store_aria': 'Abrir loja manualmente',
    'orders.close_store_aria': 'Fechar loja manualmente',
    'orders.countdown.open_until': 'Aberta até',
    'orders.countdown.next_open': 'Próxima abertura',
    'orders.countdown.next_close': 'Próximo fechamento',
    'orders.auto_accept_title': 'Aceite / Impressão Automática',
    'orders.auto_accept_delivai_title': 'DelivAI',
    'orders.auto_accept_delidesk_title': 'DeliDesk',
    'orders.auto_accept_delivai_toggle': 'Aceite automático DelivAI (site, WhatsApp, interno)',
    'orders.auto_accept_delidesk_toggle': 'Aceite automático DeliDesk (captura marketplace)',
    'orders.fast_order_open': 'Fast Order — gerar pedido a partir de texto ou iFood',
    'orders.auto_accept_active': 'Ativa',
    'orders.auto_accept_disabled': 'Desabilitada',
    'orders.auto_accept_when_open': 'Disponível quando a loja estiver aberta',
    'orders.auto_accept_toggle': 'Ativar/desativar aceite automático',
    'orders.filters': 'Filtros',
    'orders.clear': 'Limpar',
    'orders.select_date': 'Selecionar data específica',
    'orders.quick_dispatch': 'Despacho rápido',
    'orders.no_ready_dispatch': 'Nenhum pedido pronto para despacho no momento',
    'orders.deliverer': 'Entregador',
    'orders.dispatch': 'Despachar',
    'orders.dispatch_route': 'Despachar rota',
    'orders.filter_status': 'Status',
    'orders.filter_period': 'Período',
    'orders.filter_all': 'Todos',
    'orders.filter_today': 'Hoje',
    'orders.filter_week': 'Esta Semana',
    'orders.filter_month': 'Este Mês',
    'orders.filter_selected_date': 'Data selecionada: {date}',
    'orders.sort_by': 'Ordenar por',
    'orders.sort_date': 'Data',
    'orders.sort_name': 'Nome',
    'orders.sort_total': 'Valor',
    'orders.sort_order': 'Ordem',
    'orders.empty_title': 'Nenhum pedido encontrado',
    'orders.empty_filters': 'Tente ajustar os filtros para encontrar pedidos.',
    'orders.empty_none': 'Não há pedidos cadastrados ainda.',
    'orders.orders_count_one': '{count} pedido',
    'orders.orders_count_many': '{count} pedidos',
    'orders.history_title': 'Histórico de Comandas',
    'orders.history_search': 'Buscar por número ou cliente...',
    'orders.history_search_aria': 'Buscar por número ou cliente no histórico',
    'orders.cash_summary': 'Resumo do Caixa',
    'orders.cash_entry_button': 'Lançamento',
    'orders.cash_entry_title': 'Lançamento de caixa',
    'orders.cash_entry_subtitle': 'Sangria, reforço de caixa, troco externo, despesa ou acerto de motoboy.',
    'orders.cash_entry_type': 'Tipo',
    'orders.cash_entry_amount': 'Valor',
    'orders.cash_entry_method': 'Forma de pagamento',
    'orders.cash_entry_note': 'Observação',
    'orders.cash_entry_save': 'Registrar',
    'orders.cash_entry_success': 'Lançamento registrado',
    'orders.cash_entry_error': 'Não foi possível registrar o lançamento',
    'orders.cash_entry_invalid_amount': 'Informe um valor válido maior que zero',
    'orders.cash_entry_no_session': 'Nenhum caixa aberto para lançar',
    'orders.cash_entry_insufficient_cash':
      'Saldo em dinheiro insuficiente. Faça um reforço de caixa antes da sangria ou retirada.',
    'orders.cash_entry_opening_session': 'Abrindo caixa…',
    'orders.cash_entry_open_failed': 'Não foi possível abrir o caixa para lançamento.',
    'orders.cash_entry_type_sangria': 'Sangria',
    'orders.cash_entry_type_retirada': 'Retirada',
    'orders.cash_entry_type_aporte': 'Reforço de caixa',
    'orders.cash_summary_reforco': 'Reforço',
    'orders.cash_summary_reforco_short': 'reforço',
    'orders.cash_summary_troco_ext_short': 'troco ext.',
    'orders.cash_summary_sangria_short': 'sangria',
    'orders.cash_summary_retirada_short': 'retirada',
    'orders.cash_summary_despesa_short': 'despesa',
    'orders.cash_summary_sales_short': 'vendas',
    'orders.cash_summary_hint': 'PIX, cartão, dinheiro e total são vendas. Reforço é troco/entrada de caixa (não é venda).',
    'orders.cash_entry_type_troco_externo': 'Troco externo',
    'orders.cash_entry_type_acerto_motoboy': 'Acerto de motoboy',
    'orders.cash_entry_type_despesa': 'Despesa',
    'orders.cash_entry_method_pix': 'PIX',
    'orders.cash_entry_method_cartao': 'Cartão',
    'orders.cash_entry_method_dinheiro': 'Dinheiro',
    'courier.settlement_title': 'Acerto com a loja',
    'courier.settlement_subtitle': 'Valores a acertar por forma de pagamento (pedidos do dia).',
    'orders.payment_card': 'Cartão',
    'orders.payment_cash': 'Dinheiro',
    'orders.payment_cancelled': 'Cancelados',
    'orders.payment_pix': 'PIX',
    'orders.col_number': 'Nº',
    'orders.col_datetime': 'Data/Hora',
    'orders.col_client': 'Cliente',
    'orders.col_phone': 'Telefone',
    'orders.col_address': 'Endereço',
    'orders.col_type': 'Tipo',
    'orders.col_status': 'Status',
    'orders.col_deliverer': 'Entregador',
    'orders.col_actions': 'Ações',
    'orders.history_empty': 'Nenhum pedido encontrado no histórico',
         'company.edit': 'Editar',
     'company.save': 'Salvar',
     'company.cancel': 'Cancelar',
     'company.update': 'Atualizar',
     
     // Orders Screen - Additional Details
     'orders.order_number': 'Pedido #',
     'orders.client_not_informed_short': 'Cliente não informado',
     'orders.client': 'Cliente:',
     'orders.phone': 'Telefone:',
     'orders.address': 'Endereço:',
     'orders.payment_method': 'Forma de Pagamento:',
     'orders.items': 'Itens:',
     'orders.no_items': 'Nenhum item disponível',
     'orders.total': 'Total:',
     'orders.payment': 'Pagamento:',
     'orders.details': 'Detalhes',
     'orders.print_agent.configure_title': 'Escolher impressora no computador',
     'orders.print_agent.configure_aria': 'Configurar impressora local',
     'orders.print_agent.modal_title': 'Programa de impressão (PC)',
     'orders.print_agent.modal_intro':
       'Defina a ordem de prioridade das impressoras do DeliDesk. A primeira marcada e online recebe os cupons.',
     'orders.print_agent.list_heading': 'Prioridade das impressoras',
     'orders.print_agent.priority_hint':
       'Arraste para ordenar. Marque as que devem receber cupom. Ao lado de cada uma aparece o PC do DeliDesk.',
     'orders.print_agent.drag_priority': 'Arrastar prioridade',
     'orders.print_agent.pcs_heading': 'PCs vinculados',
     'orders.print_agent.offline_banner':
       'A impressora padrão está offline. Cupons vão para a próxima online da sua lista, se houver.',
     'orders.print_agent.offline_title': 'Impressora padrão offline',
     'orders.print_agent.offline_body':
       'O PC da impressora padrão está desconectado. Definir {name} (online) como primeira prioridade?',
     'orders.print_agent.offline_confirm': 'Usar esta impressora',
     'orders.print_agent.offline_keep': 'Manter como está',
     'orders.print_agent.offline_use_other': 'Usar {name}',
    'orders.print_agent.vias': 'Vias',
    'orders.print_agent.vias_less': 'Menos vias',
    'orders.print_agent.vias_more': 'Mais vias',
    'orders.print_agent.formats_hint': 'Cada cupom marcado imprime a quantidade de vias ao lado.',
     'orders.print_agent.empty':
       'Nenhum PC com DeliDesk vinculado. Instale o app, abra e autorize este PC; as impressoras aparecerão aqui.',
     'orders.print_agent.saved': 'Impressora padrão salva. Pedidos serão enviados ao DeliDesk.',
     'orders.print_agent.save': 'Salvar',
     'orders.print_agent.saving': 'Salvando…',
     'orders.print_agent.cancel': 'Fechar',
     'orders.print_agent.close_aria': 'Fechar janela',
     'orders.print_agent.demo_note':
       'Lista de exemplo só para montar o layout; a lista real virá do aplicativo instalado no computador.',
     'orders.print_agent.download_windows': 'Baixar programa para Windows',
     'orders.print_agent.download_windows_aria': 'Baixar o instalador do programa de impressão para Windows',
     'orders.print_agent.download_unavailable_title':
       'O instalador para Windows será disponibilizado em breve.',
     'orders.print_agent.download_resolving': 'Buscando a versão mais recente…',
     'orders.print_agent.download_version_title': 'Versão mais recente: {version}',
     'orders.print_agent.refresh': 'Atualizar',
     'orders.print_agent.clear_queue': 'Limpar fila',
     'orders.print_agent.clear_queue_with_count': 'Limpar fila ({count})',
     'orders.print_agent.clear_queue_hint':
       'Cancela cupons aguardando no servidor — evita imprimir tudo ao abrir o DeliDesk no PC da impressora.',
     'orders.print_agent.clear_queue_confirm_title': 'Limpar fila de impressão?',
     'orders.print_agent.clear_queue_confirm_body':
       'Isso cancela {count} cupom(ns) pendente(s) no servidor. Não dá para desfazer.',
     'orders.print_agent.clear_queue_confirm': 'Limpar fila',
     'orders.print_agent.clear_queue_ok': '{count} cupom(ns) removido(s) da fila.',
     'orders.print_agent.clear_queue_error': 'Não foi possível limpar a fila de impressão.',
     'orders.print_agent.status_online': 'Online',
     'orders.print_agent.status_offline': 'Offline',
     'orders.print_agent.revoke': 'Desvincular',
     'orders.print_agent.revoked': 'PC desvinculado.',
     'orders.print_agent.revoke_error': 'Não foi possível desvincular o PC.',
     'orders.print_agent.load_error': 'Não foi possível carregar as impressoras.',
     'orders.print_agent.save_error': 'Não foi possível salvar a impressora padrão.',
     'orders.print_agent.no_printers_on_pc': 'Nenhuma impressora reportada neste PC ainda.',
     'orders.print_agent.current_default': 'Atual',
    'orders.print_agent.this_pc': 'Este PC',
    'support.report': 'Reportar problema nesta tela',
    'support.hint.company': 'Algum problema na configuração da empresa?',
    'support.hint.delivery': 'Algum problema no delivery ou na comanda?',
    'support.hint.pdv': 'Algum problema no PDV?',
    'support.hint.sales': 'Algum problema nas vendas?',
    'support.hint.chats': 'Algum problema no WhatsApp ou nas conversas?',
    'support.hint.license': 'Algum problema na licença?',
    'support.hint.staff': 'Algum problema nos colaboradores?',
    'support.hint.clients': 'Algum problema nos clientes?',
    'support.hint.couriers': 'Algum problema nos motoboys?',
    'support.hint.dev': 'Alguma melhoria da equipe DelivAI?',
    'support.hint.schedule': 'Algum problema nos agendamentos?',
    'support.hint.fast_order': 'O que você mandou no Fast Order e o que a comanda mostrou?',
    'support.hint.menu_read': 'O que a leitura errou? (ex.: item trocado, preço errado). O registro já vai junto.',
    'support.hint.screen': 'Algum problema nesta tela?',
    'support.report_send': 'Enviar',
    'support.report_close': 'Fechar',
    'support.report_sent': 'Recebido. Vamos olhar isso.',
    'support.report_image': 'Imagem {n}',
    'support.report_shot_fail': 'Não deu para ler esse print.',
    'support.report_failed': 'Não foi possível enviar. Tente de novo.',
    'support.report_short': 'Escreva um pouco mais sobre o problema.',
    'license.group_offer': 'Plano do grupo: R$ {price} por mês ({stores} lojas). Ao pagar, a licença de cada loja passa a ser a do grupo.',
    'license.group_pay': 'Contratar plano do grupo',
    'license.dk_title': 'Plano Dark Kitchen',
    'license.dk_subtitle': 'Junta as lojas no mesmo grupo. A 1ª e a 2ª entram por R$ 300. Da 3ª em diante, cada loja soma R$ 100. Os prazos seguem o Plano Operação Completa, com o valor menor em 180 e 365 dias.',
    'license.dk_days': '{days} dias',
    'license.dk_save': 'economia {amount}',
    'license.dk_extra': 'Cada loja a partir da 3ª soma {extra} neste prazo.',
    'license.dk_store_count': 'Quantas lojas',
    'license.dk_same_days': 'Todas as lojas desta licença ficam com os mesmos {days} dias.',
    'license.dk_after': 'Depois do pagamento, esta loja pode convidar outra empresa para o grupo. O contrato continua um por empresa.',
    'license.dk_paid': 'Plano pago. Você já pode convidar outra loja na tela da empresa.',
    'kitchen.group_accept_title': 'Licença do grupo',
    'kitchen.group_accept_body': 'Ao entrar no grupo, a próxima licença passa a ser R$ {price} por mês. As licenças atuais só mudam depois do pagamento. O contrato é um por empresa.',

     'delidesk.authorize.header': 'Autorizar',
     'delidesk.authorize.go_panel': 'Ir ao painel',
     'delidesk.authorize.title': 'Autorizar este PC?',
     'delidesk.authorize.subtitle_before': 'Vincular o DeliDesk à loja',
     'delidesk.authorize.subtitle_dev_before': 'Vincular o DeliDesk à conta',
     'delidesk.authorize.machine': 'máquina',
     'delidesk.authorize.warn_open_from_app':
       'Abra esta página pelo app DeliDesk (botão Entrar com DelivAI). Sem o parâmetro state a autorização não funciona.',
     'delidesk.authorize.approve': 'Autorizar este PC',
     'delidesk.authorize.authorizing': 'Autorizando…',
     'delidesk.authorize.open_app': 'Abrir DeliDesk',
     'delidesk.authorize.success_banner': 'PC autorizado com sucesso.',
     'delidesk.authorize.return_to_app':
       'Pode fechar esta aba e voltar ao DeliDesk — o aplicativo conclui o login sozinho, sem popup do navegador.',
     'delidesk.authorize.footer_hint':
       'Só confirme se você iniciou o login no DeliDesk neste computador. Depois o app recebe o vínculo e pode imprimir cupons desta loja.',
     'delidesk.authorize.footer_hint_collaborator':
       'O DeliDesk é para o dono da loja ou a equipe DelivAI. Peça ao responsável pela loja para autorizar este PC.',
     'delidesk.authorize.back_collaborator': 'Voltar ao painel do colaborador',
     'delidesk.authorize.dialog_title': 'Voltar ao DeliDesk',
     'delidesk.authorize.dialog_body':
       'O PC foi autorizado. Volte ao DeliDesk — o login termina automaticamente.',
     'delidesk.authorize.dialog_os_note':
       'Não é necessário abrir o app pelo navegador; o DeliDesk detecta a autorização sozinho.',
     'delidesk.authorize.error_missing_state':
       'Link inválido: falta o parâmetro state. Abra pelo botão Entrar no DeliDesk.',
     'delidesk.authorize.error_no_redirect': 'Backend não retornou redirect_uri',
     'delidesk.authorize.error_generic': 'Falha ao autorizar este PC',
     'delidesk.authorize.error_store_collaborator':
       'Colaboradores da loja não podem autorizar o DeliDesk. Entre com a conta do dono da loja ou com um usuário da equipe DelivAI.',
     'delidesk.embed.gate_title': 'Conecte pelo DeliDesk',
     'delidesk.embed.gate_body':
       'Esta tela do painel só abre depois do login Entrar com DelivAI no aplicativo. O formulário de CNPJ do site não é usado aqui.',
     'delidesk.embed.gate_hint':
       'Se o app já estiver vinculado, use Recarregar no DeliDesk ou saia e entre de novo com DelivAI.',

     // Employees Screen - Additional
     'employees.department': 'Departamento:',
     'employees.salary': 'Salário:',
     'employees.admission': 'Admissão:',
     'employees.edit': 'Editar',
     'employees.details': 'Detalhes',
     'menu_display.edit_category.title': 'Editar Categoria',
     'menu_display.edit_category.subtitle': 'Altere o nome e os tipos de preço da categoria',
     'menu_display.edit_category.service_title': 'Editar categoria de serviço',
     'menu_display.edit_category.service_subtitle':
       'Nome, duração (minutos ou dias) e preço desta categoria',
     'menu_display.edit_category.service_mode': 'Como a duração é medida',
     'menu_display.edit_category.mode_time': 'Por minutos',
     'menu_display.edit_category.mode_day': 'Por dias',
     'menu_display.edit_category.service_price': 'Preço do serviço',
     'menu_display.edit_category.close': 'Fechar',
     'menu_display.edit_category.type': 'Tipo de Categoria',
     'menu_display.edit_category.type_product': 'Produto',
     'menu_display.edit_category.type_pizza': 'Pizza',
     'menu_display.edit_category.type_additional': 'Adicional',
     'menu_display.edit_category.name': 'Nome da Categoria',
     'menu_display.edit_category.name_placeholder': 'Nome da categoria',
     'menu_display.edit_category.price_types': 'Tipos de Preço',
     'menu_display.edit_category.add': 'Adicionar',
     'menu_display.edit_category.price_type_placeholder': 'Tipo de preço {index}',
     'menu_display.edit_category.remove_price_type': 'Remover tipo de preço',
     'menu_display.edit_category.flavors': 'Sabores',
     'menu_display.edit_category.suggested_price': 'Preço Sugerido (R$)',
     'menu_display.edit_category.suggested_price_placeholder': 'R$ 0,00',
     'menu_display.edit_category.additional_scope_title': 'Em quais categorias de produtos estes adicionais podem ser aplicados',
     'menu_display.edit_category.additional_scope_hint': 'Marque a categoria inteira ou expanda e escolha apenas os produtos desejados.',
     'menu_display.edit_category.no_product_categories': 'Nenhuma categoria de produto.',
     'menu_display.edit_category.collapse': 'Recolher',
     'menu_display.edit_category.expand': 'Expandir',
     'menu_display.edit_category.additional_categories': 'Categorias de Adicionais',
     'menu_display.edit_category.no_additional_categories': 'Nenhuma categoria de adicional cadastrada.',
     'menu_display.edit_category.additional_limit': 'Limite de Adicionais por Produto',
     'menu_display.edit_category.additional_limit_hint': 'Número máximo de adicionais desta categoria que podem ser adicionados por produto. Deixe vazio para sem limite.',
     'menu_display.edit_category.type_value_placeholder': 'Ou digite um valor',
     'menu_display.edit_category.additional_minimum': 'Mínimo de Adicionais por Produto',
     'menu_display.edit_category.additional_minimum_hint': 'Número mínimo de adicionais desta categoria que o cliente deve escolher por produto. Deixe vazio ou 0 para sem mínimo.',
     'menu_display.edit_category.minimum_value_placeholder': 'Ou digite um valor (0 = sem mínimo)',
     'menu_display.edit_category.save_changes': 'Salvar Alterações',
     'menu_display.delete_image.title': 'Excluir imagem',
     'menu_display.delete_image.subtitle': 'Esta ação não pode ser desfeita',
     'menu_display.delete_image.confirm_text': 'Tem certeza que deseja excluir a imagem do produto "{product}"?',
     'menu_display.delete_image.no_name': 'sem nome',
     'conversations.group': 'Grupo',
     'conversations.hidden_number': 'Número oculto (privacidade WhatsApp)',
     'conversations.fallback_dash': '—',
     'conversations.tap_qr_to_connect': 'Toque no ícone do QR para conectar e escanear a sessão.',
     'conversations.instance_unavailable_title': 'Instância não disponível',
     'conversations.instance_unavailable_body': 'As conversas usam a instância do WhatsApp vinculada ao CNPJ da sua empresa. Faça login com um usuário que tenha CNPJ cadastrado ou crie uma instância abaixo.',
     'conversations.create_instance_title': 'Criar instância WhatsApp (Evolution API)',
     'conversations.create_instance_body': 'Será criada uma instância com o CNPJ da sua empresa. Informe o número do WhatsApp (com DDD) para vincular.',
     'conversations.phone_placeholder': '(11) 99999-9999',
     'conversations.creating_instance': 'Criando...',
     'conversations.create_instance': 'Criar instância',
     'conversations.instance_created_refreshing': 'Instância criada. Atualizando a página...',
     'conversations.empty_archived': 'Nenhuma conversa arquivada',
     'conversations.empty_found': 'Nenhuma conversa encontrada',
     'conversations.reopen_fast_order': 'Reabrir Fast Order',
     'conversations.fast_order_tooltip': 'Fast Order — selecionar mensagens para gerar pedido',
     'conversations.fast_order': 'Fast Order',
     'conversations.client_state_loading': 'A carregar estado do cliente…',
     'conversations.client_missing_hint': 'Cadastre o cliente em Clientes delivery (telefone igual ao WhatsApp). Se já cadastrou, confira o 9º dígito do celular — o sistema tenta combinar variações 10/11 dígitos.',
     'conversations.client_new_badge': 'Cliente novo',
     'conversations.client_orders_badge': '{count} pedidos',
     'conversations.client_orders_badge_one': '1 pedido',
     'conversations.auto_support_active_hint': 'Atendimento automático ativo para este contato. Clique para pausar.',
     'conversations.auto_support_paused_hint': 'Atendimento automático pausado para este contato. Clique para reativar.',
     'conversations.auto_support_individual': 'Atendimento Auto Individual',
     'conversations.protected_photo_hint': 'Foto protegida pelo WhatsApp — carregue pela API Evolution.',
     'conversations.open_fullscreen_zoom': 'Abrir em tela cheia com zoom (no próprio painel)',
     'conversations.encrypted_video_hint': 'Vídeo criptografado — carregar para reproduzir.',
     'conversations.audio_unrecognized': 'Áudio — formato não reconhecido nesta mensagem',
     'conversations.use_as_field': 'Usar como {field}',
     'conversations.message_placeholder': 'Digite uma mensagem',
     'conversations.send': 'Enviar',
     'conversations.confirm_pick_title': 'Usar mensagem do cliente',
     'conversations.message_label': 'Mensagem',
     'conversations.set_value_as': 'Definir',
     'conversations.set_value_as_suffix': 'como',
     'conversations.connect_whatsapp': 'Conectar WhatsApp',
     'conversations.scan_qr_hint': 'Escaneie com o WhatsApp → Dispositivos conectados',
     'conversations.qr_alt': 'QR Code WhatsApp',
     'conversations.waiting_scan': 'Aguardando leitura',
     'conversations.qr_modal_auto_close': 'O modal fecha automaticamente quando a conexão for estabelecida.',
     'conversations.unarchive_chat': 'Desarquivar conversa',
     'conversations.archive_chat': 'Arquivar conversa',
     'conversations.menu.mute_notifications': 'Silenciar notificações',
     'conversations.menu.pin_chat': 'Fixar conversa',
     'conversations.menu.mark_unread': 'Marcar como não lida',
     'conversations.menu.add_favorites': 'Adicionar aos Favoritos',
     'conversations.menu.add_list': 'Adicionar à lista',
     'conversations.menu.block_contact': 'Bloquear contato',
     'conversations.menu.block': 'Bloquear',
     'conversations.menu.clear_chat': 'Limpar conversa',
     'conversations.menu.delete_chat': 'Apagar conversa',
     'conversations.disconnect_confirm_title': 'Desconectar WhatsApp',
     'conversations.disconnect_confirm_message': 'Desconectar o WhatsApp desta instância? Será necessário escanear o QR code novamente para conectar.',
     'conversations.toast.instance_unavailable': 'Instância WhatsApp indisponível.',
     'conversations.toast.last_message_not_found': 'Não foi possível identificar a última mensagem. Abra o chat e tente de novo.',
     'conversations.toast.invalid_contact_action': 'Contato inválido para esta ação.',
     'conversations.toast.archive_failed': 'Falha ao atualizar arquivamento.',
     'conversations.toast.chat_archived': 'Conversa arquivada.',
     'conversations.toast.chat_unarchived': 'Conversa desarquivada.',
     'conversations.toast.mark_unread_failed': 'Falha ao marcar como não lida.',
     'conversations.toast.marked_unread': 'Marcado como não lida.',
     'conversations.toast.individual_support_update_failed': 'Não foi possível atualizar o atendimento individual.',
     'conversations.toast.number_unavailable_send': 'Número indisponível — não é possível enviar mensagem.',
     'conversations.toast.fast_order_individual_only': 'Fast Order disponível apenas em conversas individuais',
     'conversations.toast.contact_phone_unavailable_order': 'Telefone do contato indisponível para gerar pedido',
     'conversations.toast.select_messages_generate_order': 'Selecione as mensagens do pedido e toque em Gerar pedido',
     'conversations.toast.contact_phone_unavailable': 'Telefone do contato indisponível',
     'conversations.toast.select_at_least_one_message': 'Selecione ao menos uma mensagem',
     'conversations.toast.messages_without_text': 'As mensagens selecionadas não têm texto utilizável',
     'conversations.toast.order_interpreted_review': 'Pedido interpretado — revise e finalize',
     'conversations.toast.payment_not_identified': 'Não identifiquei a forma de pagamento nessa mensagem',
     'conversations.toast.field_updated': '{field} atualizado',
     'conversations.error.media_unavailable': 'Não é possível carregar esta mídia (instância WhatsApp ou ID da mensagem ausente).',
     'conversations.error.evolution_media_not_returned': 'A Evolution não retornou a mídia. Confira no servidor o endpoint getBase64FromMediaMessage.',
     'conversations.error.load_messages': 'Erro ao carregar mensagens',
     'conversations.error.cnpj_unavailable_send': 'CNPJ não disponível para enviar mensagem.',
     'conversations.error.privacy_number_unavailable': 'Número do contato indisponível (privacidade WhatsApp). Não é possível enviar até aparecer o telefone na lista.',
     'conversations.error.send_message': 'Erro ao enviar mensagem',
     'conversations.error.send_failed_support':
       'Verifique a conexão do WhatsApp ou contate nosso suporte.',
     'conversations.error.generate_qr': 'Não foi possível gerar o QR code',
     'conversations.error.get_qr': 'Erro ao obter QR code',
     'conversations.error.disconnect_failed':
       'Não foi possível desconectar. Verifique a conexão do WhatsApp ou contate nosso suporte.',
     'conversations.error.create_instance': 'Erro ao criar instância',
     'internal_order.toast.cep_not_found': 'CEP não encontrado',
     'internal_order.toast.paste_ifood_text': 'Cole o texto do pedido do iFood antes de interpretar.',
     'internal_order.toast.cnpj_not_found': 'CNPJ não encontrado para contextualizar o pedido.',
     'internal_order.toast.ifood_parse_error': 'Erro ao interpretar pedido do iFood via IA.',
     'internal_order.toast.ai_unexpected_format': 'Resposta da IA em formato inesperado.',
     'internal_order.toast.ifood_parsed_success': 'Pedido do iFood interpretado com sucesso. Revise antes de aplicar ao carrinho.',
     'internal_order.toast.ai_unexpected_error': 'Erro inesperado ao chamar IA.',
     'internal_order.toast.address_updated': 'Endereço atualizado no cadastro do cliente',
     'internal_order.toast.address_update_failed': 'Pedido criado, mas não foi possível atualizar o cadastro',
     'internal_order.phone_lookup.error': 'Falha ao buscar cadastro — tente de novo',
     'internal_order.toast.confirm_customer_name': 'Confirme o nome do cliente (parece inválido ou incompleto)',
     'internal_order.toast.missing_required_group': 'No item "{item}", escolha pelo menos {min} em "{group}" antes de finalizar',
     'internal_order.toast.open_required_addon': 'Escolha pelo menos {min} em "{group}" no item "{item}" — depois continue a finalização',
     'internal_order.toast.capture_loaded_review': 'Pedido capturado do iFood — revise taxa, itens e adicionais no checkout',
     'internal_order.toast.capture_order_not_found': 'Não encontramos o pedido capturado. Volte em Pedidos e abra de novo.',
     'internal_order.toast.capture_order_updated': 'Pedido iFood atualizado — aprove em Pedidos quando estiver ok',
     'internal_order.toast.double_click_message_name': 'Duplo clique numa mensagem do cliente para usar como nome',
     'internal_order.confirm_name_hint': 'Confirme o nome — parece inválido ou incompleto',
     'internal_order.online_ifood_payment': 'Pagamento Online/iFood',
     'internal_order.toggle_delivery_fee_source': 'Clique para alternar: Sistema ↔ iFood',
     'internal_order.fee_source_lowest': 'Menor',
     'internal_order.fee_source_system': 'Sistema',
     'internal_order.fee_source_ifood': 'iFood',
     'internal_order.charge_customer': 'Cobrar do Cliente',
     'internal_order.confirm_address_update_title': 'Atualizar endereço do cliente?',
     'internal_order.confirm_address_update_message': 'O endereço deste pedido é diferente do cadastro do cliente. Deseja salvar o novo endereço na base para os próximos pedidos?',
     'internal_order.current_registry_address': 'Cadastro atual',
     'internal_order.current_order_address': 'Endereço deste pedido',
     'internal_order.confirm_address_update_hint': 'Se for apenas uma entrega pontual em outro local, escolha "Não salvar".',
     'internal_order.save_to_registry': 'Salvar no cadastro',
     'internal_order.do_not_save': 'Não salvar',
     'company.toast.fill_pix_fields': 'Preencha todos os campos PIX antes de salvar.',
     'company.toast.pix_saved': 'Dados PIX salvos com sucesso',
     'company.toast.pix_save_error': 'Erro ao salvar dados PIX',
     'company.toast.refreshing_server_data': 'Atualizando dados do servidor...',
     'company.toast.refreshing_services_data': 'Atualizando dados de serviços do servidor...',
     'company.toast.service_category_created': 'Categoria "{name}" criada com sucesso!',
     'company.toast.cnpj_not_found': 'CNPJ não encontrado',
     'company.toast.save_data_error': 'Erro ao salvar dados: {error}',
     'company.toast.saving_services_changes': 'Salvando alterações de serviços...',
     'company.toast.services_changes_saved': 'Alterações de serviços salvas com sucesso!',
     'company.toast.save_services_error': 'Erro ao salvar serviços: {error}',
     'company.toast.unknown_error': 'Erro desconhecido',
     'company.toast.service_updated': 'Serviço atualizado com sucesso!',
     'company.toast.save_changes_error': 'Erro ao salvar alterações',
     'company.toast.service_not_found': 'Serviço não encontrado',
     'company.toast.invalid_categories_structure': 'Estrutura de categorias inválida',
     'company.toast.category_not_found_named': 'Categoria "{name}" não encontrada',
     'company.toast.invalid_service_identifier': 'Identificador do serviço inválido',
     'company.toast.service_deleted': 'Serviço "{name}" deletado com sucesso!',
     'company.toast.category_updated': 'Categoria atualizada com sucesso!',
     'company.toast.save_category_changes_error': 'Erro ao salvar alterações da categoria',
     'company.toast.no_category_found': 'Nenhuma categoria encontrada',
     'company.toast.category_not_found': 'Categoria não encontrada',
     'company.toast.category_access_error': 'Erro ao acessar categoria',
     'company.toast.category_deleted': 'Categoria "{name}" deletada com sucesso!',
     'company.toast.delete_category_error': 'Erro ao deletar categoria: {error}',
     'company.toast.operation_cancelled': 'Operação cancelada',
     'company.toast.save_categories_order_error': 'Erro ao salvar ordem das categorias',
     'company.toast.save_product_position_error': 'Erro ao salvar posição do produto',
     'company.toast.no_menu_data_delete_product': 'Nenhum dado do menu para excluir produto',
     'company.toast.save_deletion_error': 'Erro ao salvar exclusão no banco',
     'company.toast.product_not_found': 'Produto não encontrado',
     'company.toast.no_menu_data_delete_category': 'Nenhum dado do menu para excluir categoria',
     'company.toast.invalid_delivery_fee': 'Por favor, insira uma taxa válida maior que zero',
     'company.toast.delivery_fees_saved': 'Taxas de delivery salvas.',
     'company.toast.delivery_update_failed': 'Não deu para salvar as taxas. Tente de novo.',
     'company.toast.menu_images_saved': 'Imagens do cardápio atualizadas.',
     'company.confirm.delete_fee_title': 'Excluir esta taxa?',
     'company.confirm.delete_fee_message': 'Isso apaga a taxa de R$ {amount} e os {count} bairros dela.',
     'company.toast.add_at_least_one_neighborhood': 'Por favor, adicione pelo menos um bairro',
     'company.toast.delivery_data_not_found': 'Erro: dados de delivery não encontrados',
     'company.toast.delivery_fee_already_exists': 'Esta taxa já existe. Por favor, escolha outro valor.',
     'company.toast.category_name_required': 'Nome da categoria é obrigatório',
     'company.toast.at_least_one_price_type': 'Pelo menos um tipo de preço é obrigatório',
     'company.toast.pizza_flavors_range': 'Para categorias de Pizza, cada tipo de preço deve ter entre 1 e 4 sabores',
     'company.niche.label': 'Nicho da empresa',
     'company.niche.delivery': 'Delivery',
     'company.niche.clinic': 'Clínica',
     'company.niche.tattoo': 'Tattoo',
     'company.niche.workshop': 'Oficina',
     'company.niche.digital': 'Digital',
     'company.niche.aesthetics': 'Estética',
     'company.specialty.label': 'Especialidades',
     'company.specialty.hint': 'Pode marcar várias.',
     'company.specialty.pizza': 'Pizza',
     'company.specialty.sushi': 'Sushi',
     'company.specialty.hamburger': 'Hambúrguer',
     'company.specialty.pastel': 'Pastel',
     'company.specialty.acai': 'Açaí',
     'company.specialty.mexican': 'Mexicano',
     'company.specialty.snackbar': 'Lanchonete',
     'company.specialty.infoproducts': 'Infoprodutos',
     'company.specialty.marketplace': 'Marketplace',
     'company.specialty.marketplace_sales': 'Venda em marketplaces',
     'company.specialty.ecommerce': 'Loja virtual',
     'company.specialty.subscription': 'Assinatura',
     'company.specialty.affiliates': 'Afiliados',
     'company.specialty.digital_services': 'Serviços digitais',
     'company.specialty.digital_service_label': 'Qual serviço digital?',
     'company.specialty.digital_service_placeholder': 'Ex.: criação de sites, consultoria, design',
     'company.specialty.general_practice': 'Clínica geral',
     'company.specialty.cardiology': 'Cardiologia',
     'company.specialty.veterinary': 'Veterinária',
     'company.specialty.nutrition': 'Nutricionista',
     'company.specialty.dentistry': 'Odontologia',
     'company.specialty.dermatology': 'Dermatologia',
     'company.specialty.psychology': 'Psicologia',
     'company.specialty.physiotherapy': 'Fisioterapia',
     'company.specialty.ophthalmology': 'Oftalmologia',
     'company.specialty.gynecology': 'Ginecologia',
     'company.specialty.pediatrics': 'Pediatria',
     'company.specialty.orthopedics': 'Ortopedia',
     'company.specialty.aesthetics': 'Estética',
     'company.specialty.endocrinology': 'Endocrinologia',
     'company.specialty.neurology': 'Neurologia',
     'company.specialty.psychiatry': 'Psiquiatria',
     'company.specialty.speech_therapy': 'Fonoaudiologia',
     'company.specialty.urology': 'Urologia',
     'company.specialty.ent': 'Otorrino',
     'company.specialty.nails': 'Unhas',
     'company.specialty.hair': 'Cabelo',
     'company.specialty.brows': 'Sobrancelha',
     'company.specialty.lashes': 'Cílios',
     'company.specialty.makeup': 'Maquiagem',
     'company.specialty.hair_removal': 'Depilação',
     'company.specialty.facial': 'Estética facial',
     'company.specialty.body': 'Estética corporal',
     'company.specialty.massage': 'Massagem',
     'company.specialty.micropigmentation': 'Micropigmentação',
     'company.specialty.harmonization': 'Harmonização',
     'company.specialty.aesthetic_surgery': 'Cirurgia estética',
     'company.specialty.tanning': 'Bronzeamento',
     'dev.cardapio.save_failed': 'Não foi possível salvar. Tente de novo em instantes.',
     'dev.cardapio.load_failed': 'Não foi possível carregar a empresa. Tente de novo.',
     'company.toast.no_menu_data_add_category': 'Nenhum dado do menu para adicionar categoria',
     'company.toast.category_name_exists': 'Já existe uma categoria com este nome',
     'company.toast.additional_categories_missing': 'Algumas categorias de adicionais selecionadas não existem',
     'company.toast.no_menu_data_add_product': 'Nenhum dado do menu para adicionar produto',
     'company.toast.product_added_edit_name': 'Novo produto adicionado! Comece editando o nome.',
     'company.toast.fix_validation_errors': 'Por favor, corrija os erros de validação antes de salvar.',
     'company.toast.no_changes_detected': 'Nenhuma alteração foi detectada.',
     'company.toast.payment_methods_updated': 'Formas de pagamento atualizadas com sucesso',
     'company.toast.payment_methods_update_error': 'Erro ao atualizar formas de pagamento',
     'company.toast.payment_text_updated': 'Texto de pagamento atualizado com sucesso',
     'company.toast.payment_text_update_error': 'Erro ao atualizar texto de pagamento',
     'company.toast.public_order_types_updated': 'Tipos de pedido na página pública atualizados',
     'company.toast.public_order_types_update_error': 'Erro ao atualizar tipos de pedido: {error}',
     'company.toast.menu_image_replaced': 'Imagem substituída com sucesso.',
     'company.toast.process_image_error': 'Erro ao processar imagem.',
     'company.toast.cnpj_unavailable_delete_image': 'CNPJ não disponível para excluir imagem.',
     'company.toast.menu_image_deleted': 'Imagem excluída com sucesso.',
     'company.toast.delete_image_error': 'Erro ao excluir imagem.',
   },
  en: {
    ...authTranslations.en,
    ...miscTranslations.en,
    ...devTranslations.en,
    ...contractTranslations.en,
    // Sidebar
    'sidebar.orders': 'POS',
    'sidebar.conversations': 'Conversations',
    'sidebar.company': 'Company',
    'sidebar.employees': 'Employees',
    'sidebar.delivery': 'Delivery',
    'sidebar.delivery_title': 'Delivery — dispatch and Fast Order',
    'sidebar.motoboys': 'Motoboys',
    'sidebar.motoboys_title': 'Motoboy ranking',
    'sidebar.clients': 'Clients',
    'sidebar.clients_title': 'Clients (orders and appointments)',
    'sidebar.schedule': 'Schedule',
    'sidebar.manager': 'Sales',
    'sidebar.managerTitle': 'Sales summary and reports',
    'sidebar.license': 'License',
    'sidebar.logout': 'Logout',
    'sidebar.language': 'Language',
    'sidebar.section_main': 'Main',
    'sidebar.section_orders': 'Orders',
    'sidebar.section_store': 'Store',
    'pdv.title': 'POS',
    'pdv.mode_mesa': 'Table',
    'pdv.mode_retirada': 'Pickup',
    'pdv.mode_toggle_aria': 'Switch between table and pickup',
    'pdv.catalog_all_categories': 'All',
    'pdv.catalog_categories': 'Menu categories',
    'pdv.catalog_other': 'Other',
    'pdv.print_error': 'Order saved, but printing failed. Check the printer.',
    'pdv.sidebar_open_count': '{count} open',
    'pdv.open_table': 'Open table',
    'pdv.new_pickup': 'New pickup',
    'pdv.no_open_pickups': 'No open pickups.',
    'pdv.open_table_title': 'Open table',
    'pdv.open_table_hint': 'Enter any table number. No setup required.',
    'pdv.table_number': 'Table number',
    'pdv.table_number_placeholder': 'e.g. 5',
    'pdv.open_table_confirm': 'Open',
    'pdv.table_invalid': 'Enter the table number.',
    'pdv.no_open_tables': 'No open tables.',
    'pdv.past_day_hint': 'Earlier day: close the tickets that stayed open.',
    'pdv.waiting_digital': 'Digital',
    'pdv.waiting_digital_hint': 'Not here yet',
    'pdv.waiting_pickup': 'Open pickups',
    'pdv.assign_table_title': 'Seat this order',
    'pdv.assign_table_hint': 'When the guest sits, enter the table number.',
    'pdv.back_to_pickup': 'Pickups',
    'pdv.history_dock_aria': 'Order history: tap to open or close the list',
    'pdv.open_tables': 'Open tables',
    'pdv.table_n': 'Table {n}',
    'pdv.back_to_tables': 'Tables',
    'pdv.close_table': 'Close table',
    'pdv.close_table_title': 'Close table',
    'pdv.close_table_msg': 'This finishes the table check.',
    'pdv.close_table_hint': 'Tap a method to apply the remaining amount, or type to split.',
    'pdv.charge_selected': 'Charge selected',
    'pdv.charge_selected_hint': 'Choose how this amount is paid. The table stays open.',
    'pdv.charge_qty': 'Charge {n} of {total}',
    'pdv.split_open': 'Split',
    'pdv.split_title': 'Split this item',
    'pdv.split_parts': 'Split what is left into',
    'pdv.split_amount': 'Or charge this amount',
    'pdv.split_hint': 'If you type an amount, that is what this charge takes. If you leave it empty, it splits what is still left.',
    'pdv.split_apply': 'Use this amount',
    'pdv.split_now': 'This charge {amount}',
    'pdv.charge_half': '1/2',
    'pdv.charge_third': '1/3',
    'pdv.charge_custom': 'Amount for this charge',
    'pdv.select_item': 'Select item',
    'pdv.paid_open': '{paid} paid · {open} open',
    'pdv.paid_amount_left': 'paid {paid} · left {left}',
    'pdv.total_label': 'Total',
    'pdv.paid_label': 'Paid',
    'pdv.due_label': 'Left',
    'pdv.charged': 'Charge saved.',
    'pdv.paid_keep': 'This item already has a paid amount.',
    'pdv.finalize_hint': 'Tap a method to apply the remaining amount, or type to split.',
    'pdv.print_customer_title': 'Print bill',
    'pdv.print_customer_hint': 'How many people will split? The bill shows the amount per person.',
    'pdv.split_people': 'People',
    'pdv.split_people_aria': 'Number of people to split the bill',
    'pdv.increase_people': 'Increase people',
    'pdv.decrease_people': 'Decrease people',
    'pdv.per_person': '{amount} per person',
    'pdv.add_payment': 'Another method',
    'pdv.pay_click_method': 'Apply remaining to {method}',
    'pdv.remove_payment': 'Remove payment',
    'pdv.pay_amount': 'Amount',
    'pdv.pay_remaining': '{amount} left',
    'pdv.pay_over': '{amount} over',
    'pdv.pay_ok': 'Payment matches the total',
    'pdv.pay_need_cover': 'The payment amounts must add up to the total.',
    'pdv.pay_debit': 'Debit card',
    'pdv.pay_credit': 'Credit card',
    'pdv.settle_total': 'Total',
    'pdv.catalog_empty': 'No items in the menu.',
    'pdv.search_catalog': 'Search menu',
    'pdv.no_photo': 'No photo',
    'pdv.unnamed_product': 'Item',
    'menu.channel_delivery': 'Delivery',
    'menu.channel_mesa': 'Table',
    'menu.channel_retirada': 'Pickup',
    'menu.channel_product': 'Product menus',
    'menu.channel_category': 'Category menus',
    'pdv.cart_title': 'Check',
    'pdv.cart_empty': 'Tap an item to add it to the check.',
    'pdv.client_name': 'Name',
    'pdv.client_name_placeholder': 'Name (optional)',
    'pdv.payment': 'Payment',
    'pdv.pay_cash': 'Cash',
    'pdv.pay_pix': 'PIX',
    'pdv.pay_card': 'Card',
    'pdv.launch': 'Send',
    'pdv.launching': 'Sending…',
    'pdv.launched': 'Order sent and printed.',
    'pdv.launch_error': 'Could not send the order. Try again.',
    'pdv.item_added': 'Item added.',
    'pdv.pick_size': 'Choose size',
    'pdv.print_format': 'Receipt size',
    'pdv.print_economic': '80mm receipt (Compact)',
    'pdv.print_large': '80mm receipt (Large)',
    'pdv.print_pro': '80mm receipt (PRO)',
    'pdv.print_dispatch': '80mm receipt (Dispatch)',
    'pdv.finalize': 'Finish',
    'pdv.finalize_title': 'Finish order',
    'pdv.finalize_msg': 'Marks this order as finished.',
    'pdv.finalized': 'Order finished.',
    'pdv.finalize_error': 'Could not finish the order.',
    'pdv.cancel': 'Cancel order',
    'pdv.cancel_title': 'Cancel this order?',
    'pdv.cancel_msg': 'The order leaves production. Other tickets stay as they are.',
    'pdv.cancelled': 'Order cancelled.',
    'pdv.cancel_error': 'Could not cancel the order.',
    'pdv.cancel_order_aria': 'Cancel order for {name}',
    'pdv.other_tickets': 'Other tickets on this table',
    'pdv.ticket_n': 'Ticket #{n}',
    'pdv.open_ticket_count': '{count} tickets',
    'pdv.walk_in': 'Counter',
    'pdv.back_to_categories': 'Categories',
    'pdv.category_item_count': '{count} items',
    'pdv.add_to_comanda': 'Add to check',
    'pdv.save_item': 'Save item',
    'pdv.edit_item': 'Edit',
    'pdv.remove_item': 'Remove',
    'pdv.increase_qty': 'Increase quantity',
    'pdv.decrease_qty': 'Decrease quantity',
    'pdv.pending_badge': 'New',
    'pdv.pending_count': '{count} item(s) to send',
    'pdv.not_launched': 'Not sent yet',
    'pdv.launch_print': 'Send and print',
    'pdv.launch_charge': 'Send, charge and close',
    'pdv.confirm_launch_title': 'Confirm send',
    'pdv.confirm_launch_msg': 'Prints only what has not been sent yet. Check the items first.',
    'pdv.confirm_launch_charge_title': 'Charge and close',
    'pdv.confirm_launch_charge_msg': 'Prints kitchen (new items), the bill, and closes with {method}.',
    'pdv.updated_no_print': 'Check updated.',
    'pdv.item_updated': 'Item updated.',
    'pdv.item_removed': 'Item removed.',
    'pdv.print_customer': 'Print bill',
    'pdv.print_ask_hint': 'Nothing prints unless you check it.',
    'pdv.print_ask_new': 'Print the new items',
    'pdv.print_ask_bill': 'Print the summary',
    'pdv.print_customer_ok': 'Bill sent to the printer.',
    'pdv.print_customer_need_order': 'Send the check before printing the bill.',
    'pdv.leave_pending_title': 'Unsent items',
    'pdv.leave_pending_msg': 'There are items that were not sent yet. If you leave, they will be lost.',
    'pdv.leave_pending_confirm': 'Leave anyway',
    'pdv.close_table_pending': 'There are unsent items. Send or remove them before closing the table.',
    'pdv.edit_not_found': 'Could not find this item in the menu to edit.',
    'sidebar.section_control': 'Control',
    'sidebar.module_unavailable': 'Module unavailable',
    'sidebar.schedule_unavailable': 'Scheduling unavailable',
    'sidebar.open_menu': 'Open menu',
    'sidebar.company_logo': 'Company logo',

    'common.open': 'Open',
    'common.closed': 'Closed',
    'common.loading': 'Loading…',
    'common.until': 'to',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'delivery.loading': 'Loading delivery data…',
    'delivery.error.title': 'Failed to load',
    'delivery.error.retry': 'Try again',
    'delivery.error.no_cnpj': 'Store tax ID unavailable. Please sign in again.',
    'delivery.error.customer_unavailable': 'Wait for store data to load, or try again.',
    'delivery.error.load_failed': 'Could not load delivery data.',
    'delivery.dispatch.module_name': 'Delivery',
    'delivery.dispatch.title': 'Delivery',
    'delivery.dispatch.subtitle': 'Analysis, production and quick dispatch — chips, courier and route.',
    'delivery.dispatch.search_label': 'Search tickets',
    'delivery.dispatch.search_placeholder': 'Phone or ticket number',
    'delivery.dispatch.search_clear': 'Clear search',
    'delivery.dispatch.filter_store': 'Ticket store',
    'delivery.dispatch.filter_all_stores': 'All stores',
    'delivery.dispatch.filter_empty': 'No tickets match this filter.',
    'delivery.dispatch.config_fees': 'Fees and neighborhoods',
    'delivery.dispatch.col_analysis': 'In analysis',
    'delivery.dispatch.empty_analysis': 'No deliveries in analysis.',
    'delivery.dispatch.accept': 'Accept',
    'delivery.dispatch.review': 'Review',
    'delivery.dispatch.locator': 'Locator',
    'delivery.dispatch.demo_title': 'iFood demo order',
    'delivery.dispatch.demo_msg': 'This is not a real order. Review the fee and items, then approve to try the flow.',
    'delivery.dispatch.details': 'Details',
    'delivery.dispatch.edit': 'Change order',
    'delivery.dispatch.cancel_reason_other': 'Describe the reason',
    'delivery.dispatch.open_details': 'View details of order #{n}',
    'delivery.dispatch.close_details': 'Close details',
    'delivery.dispatch.print': 'Print',
    'delivery.dispatch.print_ok': 'Ticket sent to the printer.',
    'delivery.dispatch.print_error': 'Could not print the order.',
    'delivery.dispatch.items': 'Items',
    'delivery.dispatch.no_items': 'No items listed.',
    'delivery.dispatch.col_production': 'In production',
    'delivery.dispatch.col_route': 'On the road',
    'delivery.dispatch.empty_production': 'No deliveries in production.',
    'delivery.dispatch.empty_route': 'No deliveries on the road.',
    'delivery.dispatch.order_n': 'Order #{n}',
    'delivery.dispatch.courier': 'Courier',
    'delivery.dispatch.pick_courier': 'Select a courier',
    'delivery.dispatch.no_couriers': 'No active couriers',
    'delivery.dispatch.dispatch': 'Dispatch',
    'delivery.dispatch.route': 'Build route',
    'delivery.dispatch.finish': 'Confirm delivery',
    'delivery.dispatch.reject': 'Refuse',
    'delivery.dispatch.finish_confirm_title': 'Finish this delivery?',
    'delivery.dispatch.finish_confirm_msg': 'The order will be marked finished in this panel even if the courier has not completed it in the app.',
    'delivery.dispatch.select_to_dispatch': 'Check the box to pick a courier and dispatch.',
    'delivery.dispatch.select_order': 'Select order #{n}',
    'delivery.dispatch.select_all': 'Select all',
    'delivery.dispatch.unselect_all': 'Clear selection',
    'delivery.dispatch.selected_count': '{count} selected',
    'delivery.dispatch.drag_handle': 'Drag order',
    'delivery.dispatch.select_or_drag_hint': 'Check to group or drag the card onto a courier.',
    'delivery.dispatch.drag_selected_hint': 'Drag the card — selected orders move together.',
    'delivery.dispatch.drag_panel_hint': 'Drag cards onto a courier on the right.',
    'delivery.dispatch.couriers_panel': 'Couriers',
    'delivery.dispatch.couriers_panel_hint': 'Drop on Dispatch or Build route.',
    'delivery.dispatch.drag_group_overlay': '{count} order(s)',
    'delivery.dispatch.drag_drop_on_courier': 'Drop on Dispatch or Build route',
    'delivery.dispatch.confirm_dispatch_title': 'Confirm dispatch',
    'delivery.dispatch.confirm_route_title': 'Build route',
    'delivery.dispatch.confirm_dispatch_msg': 'Dispatch {count} order(s) with {courier}?',
    'delivery.dispatch.confirm_route_msg': 'Build a route of {count} order(s) with {courier}?',
    'delivery.dispatch.load_error': 'Could not load deliveries.',
    'delivery.detail.street': 'Street',
    'delivery.detail.address': 'Address',
    'delivery.detail.wait_min': 'ETA (min)',
    'delivery.detail.unassigned': 'No courier',
    'delivery.detail.assign_courier': 'Assign courier',
    'delivery.detail.no_address': 'No address on file',
    'delivery.detail.minutes': '{n} min',
    'delivery.detail.discard_edit': 'Discard changes',
    'delivery.detail.unit_price': 'Unit price',
    'delivery.detail.eta_range': 'ETA {from}–{to}',
    'delivery.dispatch.prev_day': 'Previous day',
    'delivery.dispatch.next_day': 'Next day',
    'delivery.dispatch.pick_day': 'Pick a day',
    'delivery.dispatch.action_error': 'Could not update the order.',
    'delivery.dispatch.dispatched': 'Order dispatched.',
    'delivery.dispatch.marked_route': 'Order marked on the road.',
    'delivery.dispatch.finished': 'Delivery finished.',
    'delivery.dispatch.mark_paid': 'Mark as paid',
    'delivery.dispatch.mark_paid_ok': 'Order marked as paid. The courier can already see there is nothing to collect.',
    'delivery.dispatch.pay_title': 'Record payment',
    'delivery.dispatch.pay_hint': 'The customer paid after checkout. Enter how the money came in — the courier sees on the bag if it is already paid.',
    'delivery.dispatch.pay_confirm': 'Confirm payment',
    'delivery.dispatch.wait_courier': 'In transit — the store can also confirm delivery.',
    'delivery.dispatch.eta_label': 'Due by {time}',
    'delivery.dispatch.fee_label': 'Fee {fee}',
    'delivery.dispatch.my_fees_title': 'Your fees today',
    'delivery.dispatch.my_deliveries_count': '{count} delivery(ies) in progress',
    'entregas.ifood_locator_label': 'iFood locator',
    'entregas.copy_locator': 'Copy',
    'entregas.ifood_call_with_locator': 'with locator',
    'entregas.toast.locator_copied': 'Locator copied',
    'entregas.toast.locator_copy_error': 'Could not copy the locator',
    'orders.print_choice_title': 'What do you want to print?',
    'orders.print_choice_comanda': 'Order (kitchen ticket)',
    'orders.print_choice_qr': 'QR Code (bag)',
    'orders.print_choice_hint': 'Each option prints on a separate slip. You can tap both.',
    'orders.print_choice_close': 'Close',
    'orders.toast.print_qr_ok': 'QR Code sent to printer',
    'orders.qr_offer_title': 'Print bag QR Code?',
    'orders.qr_offer_body':
      'The customer scans, confirms their details, and starts ordering via DelivAI — off iFood.',
    'orders.qr_offer_yes': 'Yes, print QR',
    'orders.qr_offer_no': 'Not now',
    'order_claim.headline': 'Congrats! There’s a gift for you',
    'order_claim.subtitle': 'Confirm your details to continue. Nothing sensitive shows before that.',
    'order_claim.loading': 'Loading…',
    'order_claim.pick_name': 'What’s your name?',
    'order_claim.pick_address': 'What’s your address?',
    'order_claim.continue': 'Continue',
    'order_claim.phone_label': 'Your phone (WhatsApp)',
    'order_claim.phone_placeholder': '+1 555 000 0000',
    'order_claim.submit_phone': 'Save and claim',
    'order_claim.done_title': 'All set!',
    'order_claim.done_body': 'Your profile is linked. We’ll use your details on the next orders.',
    'order_claim.prize_title': 'Your prize',
    'order_claim.prize_min': 'Valid on orders from {min}',
    'order_claim.rule_first_order': 'Valid on your first order at this store',
    'order_claim.rule_subsequent': 'Valid from your second order onward',
    'order_claim.rule_any': 'Valid on your next eligible order',
    'order_claim.coupon_code': 'Coupon code',
    'order_claim.coupon_rules': 'Rules: {rules}',
    'order_claim.error_invalid_link': 'Invalid link',
    'order_claim.error_expired': 'This link has expired',
    'order_claim.error_load': 'Could not open this gift',
    'order_claim.error_select_both': 'Pick your name and address',
    'order_claim.error_challenge': 'Details don’t match — try again',
    'order_claim.error_phone': 'Could not save the phone number',
    'orders.channel_all': 'All',
    'orders.channel_store': 'Store',
    'orders.channel_delivery': 'Delivery',
    'orders.channel_hint': 'Operational delivery is in Deliveries.',
    'orders.channel_go_delivery': 'Open Deliveries',
    'orders.type_mesa': 'Table',
    'orders.type_retirada': 'Pickup',
    'orders.type_delivery': 'Delivery',
    'orders.empty_mesa': 'No table orders in progress.',
    'orders.empty_retirada': 'No pickup orders in progress.',
    'orders.empty_delivery_type': 'No deliveries in progress.',
    'orders.status.ready_short': 'Ready',
    'orders.status.completed': 'Completed',
    'orders.status.cancelled': 'Cancelled',
    'orders.select_to_show_courier': 'Check the box to choose a courier.',
    'orders.select_order_dispatch': 'Select order #{n}',
    'orders.pick_deliverer': 'Select a courier',
    'orders.select_all': 'Select all',
    'orders.unselect_all': 'Clear selection',
    'orders.finish': 'Finish',
    'orders.print_ticket': 'Print ticket',
    'orders.print_order_aria': 'Print order #{n}',
    'orders.edit': 'Edit',
    'orders.phone_not_informed': 'Not provided',
    'common.got_it': 'Got it',

    'google.connect': 'Connect with Google',
    'google.disconnect': 'Disconnect Google',
    'google.popup_blocked_title': 'Google window blocked',
    'google.popup_blocked_message':
      'The authorization window could not be opened. Tap Continue to connect Google in this same screen.',
    'google.popup_blocked_continue': 'Continue',
    'google.config_error_title': 'Connection unavailable',
    'google.config_error_message':
      'Could not start the Google connection. Check the configuration or contact DelivAI support.',
    'common.delete': 'Delete',
    'common.back': 'Back',
    'common.characters': '{count} characters',
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.duration_min': '{count} min',
    'common.duration_days': '{count} day(s)',
    'common.weekday.sunday': 'Sunday',
    'common.weekday.monday': 'Monday',
    'common.weekday.tuesday': 'Tuesday',
    'common.weekday.wednesday': 'Wednesday',
    'common.weekday.thursday': 'Thursday',
    'common.weekday.friday': 'Friday',
    'common.weekday.saturday': 'Saturday',

    'conversations.archived': 'Archived',
    'conversations.whatsapp': 'WhatsApp',
    'conversations.search_short': 'Search…',
    'conversations.loading_list': 'Loading conversations...',
    'conversations.loading_more_chats': 'Loading older conversations…',
    'conversations.scroll_for_more': 'Scroll to load more conversations',
    'conversations.error.load_list': 'Failed to load conversations: {error}',
    'conversations.error.load_list_generic': 'Failed to load conversations',
    'conversations.reload': 'Reload conversations',
    'conversations.select_to_start': 'Select a conversation to start',
    'conversations.loading_messages': 'Loading messages...',

    'motoboys.module_title': 'Motoboys module',
    'motoboys.ranking': 'Motoboy ranking',
    'motoboys.podium': 'Podium of the day',
    'motoboys.place_1': '1st place',
    'motoboys.place_2': '2nd place',
    'motoboys.place_3': '3rd place',
    'motoboys.deliveries_count': '{count} delivery(ies)',
    'motoboys.fees_total_label': 'Total fees',
    'motoboys.daily_label': 'Daily:',
    'motoboys.daily_edit_hint': 'Double-click to edit',
    'motoboys.with_deliveries': 'Drivers with deliveries',
    'motoboys.total_deliveries': 'Total deliveries',
    'motoboys.total_fees': 'Total fees',
    'motoboys.dailies': '+ Daily rates',
    'motoboys.total': 'Total',
    'motoboys.kitchen_sum': 'Sum of every dark kitchen store',
    'motoboys.empty': 'No drivers with deliveries right now',
    'motoboys.empty_hint': 'Drivers will appear here when orders have an assigned deliverer',
    'motoboys.requirements_title': 'To appear here, the order must:',
    'motoboys.req_assigned': 'Have an assigned deliverer',
    'motoboys.req_not_cancelled': 'Not be cancelled',
    'motoboys.req_ready_or_done': 'Be en route or completed',
    'motoboys.console_hint': 'Check the browser console (F12) for order search details',
    'motoboys.my_summary': 'My deliveries',
    'motoboys.my_name': 'You',
    'motoboys.me': 'Me',
    'motoboys.my_deliveries': 'Your deliveries',
    'motoboys.my_fees': 'Your fees',
    'motoboys.my_day': 'Your day',
    'motoboys.personal_empty': 'No deliveries of yours on this day',
    'motoboys.personal_empty_hint': 'When an order is dispatched under your name, the numbers show up here.',

    'schedule.module_title': 'Scheduling module',
    'schedule.subtitle': 'Manage appointments and bookings in an organized way',
    'schedule.tab_agenda': 'Schedule',
    'schedule.tab_reports': 'Reports',
    'schedule.select_professional': 'Select professional schedule',
    'schedule.store_calendar': 'Store schedule',
    'schedule.google_missing': 'Google not connected',
    'schedule.staff_own_agenda': 'You are on your own schedule. Connect Google here.',
    'schedule.pick_professional_first': 'Choose a professional before creating a time slot.',
    'schedule.reports.from': 'From',
    'schedule.reports.to': 'To',
    'schedule.reports.professional': 'Professional',
    'schedule.reports.service': 'Service',
    'schedule.reports.area': 'Area',
    'schedule.reports.payment': 'Payment',
    'schedule.reports.filter_all_pros': 'All professionals',
    'schedule.reports.filter_all_services': 'All services',
    'schedule.reports.filter_all_areas': 'All areas',
    'schedule.reports.filter_all_payment': 'All',
    'schedule.reports.store_calendar': 'Store schedule',
    'schedule.reports.me': 'My schedule',
    'schedule.reports.paid': 'Paid',
    'schedule.reports.pending_pay': 'Pending',
    'schedule.reports.loading': 'Loading…',
    'schedule.reports.refresh': 'Refresh',
    'schedule.reports.load_error': 'Could not load the report',
    'schedule.reports.total_count': 'Appointments',
    'schedule.reports.total_amount': 'Total',
    'schedule.reports.paid_count': 'Paid',
    'schedule.reports.pending_pay_count': 'Pending pay',
    'schedule.reports.by_professional': 'By professional',
    'schedule.reports.by_service': 'By service',
    'schedule.reports.empty': 'No data in this period',
    'schedule.public_link': 'Public link',
    'schedule.copy_public_link': 'Copy public scheduling link',
    'schedule.public_link_copied': 'Scheduling link copied',
    'schedule.public_link_copy_error': 'Could not copy the link',
    'schedule.new_short': 'New',
    'schedule.define_schedule_hint': 'Tap a day, drag a range, Ctrl+click for non-consecutive days, or tap a weekday name. You can close full days, lock the same time on all, or a different time on each day.',
    'schedule.day_action_new': 'New appointment',
    'schedule.day_action_close': 'Close the day',
    'schedule.day_action_reopen': 'Reopen the day',
    'schedule.day_action_lock_slot': 'Lock time slot',
    'schedule.day_action_slots_title': 'Times of the day',
    'schedule.day_action_no_slots': 'No time slots on this day.',
    'schedule.slot_locked': 'Time slot locked',
    'schedule.slot_booked': 'Time slot booked',
    'schedule.slot_lock_hint': 'Click and drag to lock or unlock several time slots.',
    'schedule.slot_closed_by_store': 'This time slot was locked by the store.',
    'schedule.closed_slots_save_error': 'Could not lock the time slot.',
    'schedule.confirm_lock_day_title': 'Lock this day?',
    'schedule.confirm_lock_days_title': 'Lock these days?',
    'schedule.confirm_lock_day_message': 'Nobody will be able to book on {date}.',
    'schedule.confirm_lock_days_message': 'Nobody will be able to book on {count} days ({dates}).',
    'schedule.confirm_lock_slot_title': 'Lock this time slot?',
    'schedule.confirm_lock_slots_title': 'Lock these time slots?',
    'schedule.confirm_lock_slot_message': 'Nobody will be able to book {times} on {date}.',
    'schedule.confirm_lock_slot_days_message': 'Nobody will be able to book {times} on {count} days ({dates}).',
    'schedule.confirm_lock_slot_map_message': 'Nobody will be able to book: {details}.',
    'schedule.confirm_lock': 'Lock',
    'schedule.confirm_unlock': 'Unlock',
    'schedule.confirm_unlock_day_title': 'Unlock this day?',
    'schedule.confirm_unlock_days_title': 'Unlock these days?',
    'schedule.confirm_unlock_day_message': 'Time slots on {date} will be available for booking again.',
    'schedule.confirm_unlock_days_message': 'Time slots on {count} days ({dates}) will be available for booking again.',
    'schedule.confirm_unlock_day_reason': 'Lock reason: {reason}',
    'schedule.confirm_unlock_slot_title': 'Unlock this time slot?',
    'schedule.confirm_unlock_slot_message': '{times} on {date} will be available for booking again.',
    'schedule.filter_locked': 'Locked',
    'schedule.lock_history_day': 'Closed day',
    'schedule.lock_history_slots': 'Locked time slots',
    'schedule.lock_range_to': 'to',
    'schedule.lock_reason_mixed': 'Different reasons',
    'schedule.confirm_unlock_slots_title': 'Unlock these time slots?',
    'schedule.lock_history_empty': 'No locked time slots',
    'schedule.lock_history_empty_hint': 'Days and time slots you lock on the calendar show up here.',
    'schedule.lock_history_by_store': 'Locked by the store',
    'schedule.lock_reason_unknown': 'No reason given',
    'schedule.lock_reason_label': 'Reason for locking',
    'schedule.lock_reason_medical': 'Medical appointment',
    'schedule.lock_reason_personal': 'Personal commitment',
    'schedule.lock_reason_other': 'Other',
    'schedule.lock_reason_other_placeholder': 'Describe the reason (up to 200 characters)',
    'schedule.lock_reason_other_required': 'Describe the reason with at least 3 characters.',
    'schedule.reason_locked_slots': 'One or more time slots are locked',
    'schedule.day_click_hint': 'Click, drag, Ctrl+click, or tap a weekday name',
    'schedule.weekday_column_hint': 'Select every {day} in this month',
    'schedule.multi_selected': '{count} days selected',
    'schedule.multi_clear': 'Clear',
    'schedule.multi_close_days': 'Close these days',
    'schedule.multi_reopen_days': 'Reopen these days',
    'schedule.multi_lock_slot': 'Lock a time on these days',
    'schedule.multi_ctrl_holding': 'Release Ctrl to choose how to lock these days',
    'schedule.batch_ask_title': 'How should these days be closed?',
    'schedule.batch_ask_hint': 'Full days, the same time on all, or a different time on each day.',
    'schedule.batch_close_days': 'Close the full days',
    'schedule.batch_reopen_days': 'Reopen these days',
    'schedule.batch_same_slot': 'Same time on all',
    'schedule.batch_per_day_slot': 'Different time per day',
    'schedule.batch_back': 'Back',
    'schedule.batch_pick_times': 'Tap the times for each day',
    'schedule.batch_no_times': 'No times',
    'schedule.batch_apply_slots': 'Lock chosen times',
    'schedule.batch_apply_open_slots': 'Open chosen times',
    'schedule.batch_day_times_confirmed': 'Confirmed: {times}',
    'schedule.day_appointments_count': '{count} appointment(s)',
    'schedule.legend_free': 'Free',
    'schedule.legend_partial': 'Some bookings',
    'schedule.legend_full': 'Fully booked',
    'schedule.legend_outside_hours': 'Outside registered hours',
    'schedule.legend_closed': 'Manually closed',
    'schedule.cannot_close_outside_hours': 'These days are already outside registered hours.',
    'schedule.skipped_outside_hours': '{count} day(s) are already outside hours and were not closed.',
    'schedule.already_outside_hours_hint': 'Already outside registered hours. No need to close again.',
    'schedule.cannot_mix_closed_open': 'Closed days cannot be selected together with open days.',
    'schedule.batch_ask_reopen_title': 'Reopen these days?',
    'schedule.batch_ask_reopen_hint': 'Every selected day is already closed.',
    'schedule.batch_open_title': 'Open for appointments?',
    'schedule.batch_open_hint': 'A full day uses registered hours. Or pick specific times.',
    'schedule.batch_open_full_day': 'Open the full day',
    'schedule.batch_revert_open': 'Back to registered hours',
    'schedule.batch_ask_revert_title': 'Back to registered hours?',
    'schedule.batch_ask_revert_hint': 'These days go back to being outside operating hours.',
    'schedule.day_action_open_full': 'Open for appointments',
    'schedule.day_action_open_slots': 'Set times',
    'schedule.day_action_revert_open': 'Back to registered hours',
    'schedule.legend_open_extra': 'Extra open',
    'schedule.open_extra_day': 'Extra open',
    'schedule.open_extra_slots': 'Extra times',
    'schedule.confirm_open_days_title': 'Open these days?',
    'schedule.confirm_open_day_title': 'Open this day?',
    'schedule.confirm_open_days_message': 'They will use the store’s registered hours ({count} days: {dates}).',
    'schedule.confirm_open_day_message': '{date} will use the store’s registered hours.',
    'schedule.confirm_open_slots_title': 'Open these times?',
    'schedule.confirm_open_slots_message': 'Only these times will be available to book.',
    'schedule.confirm_revert_open_title': 'Back to registered hours?',
    'schedule.confirm_revert_open_message': 'These days go back to being outside hours ({dates}).',
    'schedule.confirm_open': 'Open',
    'schedule.confirm_revert_open': 'Revert',
    'schedule.open_dates_save_error': 'Could not save the extra opening.',
    'schedule.reason_open_extra': 'Extra open — outside registered hours',
    'schedule.day_closed_by_store': 'This day was closed by the store.',
    'schedule.closed_dates_load_error': 'Could not load closed days.',
    'schedule.closed_dates_save_error': 'Could not save the closed day.',
    'schedule.reason_outside_hours': 'Unavailable due to registered business hours',
    'schedule.reason_manual_closed': 'Day closed manually',
    'schedule.gcal_disconnected_banner': 'Google Calendar disconnected',
    'schedule.gcal_disconnected_banner_staff': 'Your Google account is disconnected',
    'client_scheduling.email_hint':
      'Optional. Enter the client’s Google email so the appointment appears on their Calendar.',
    'schedule.events_hint': 'Confirm to send to Google Calendar and WhatsApp.',
    'schedule.no_events_hint': 'Bookings from the public link show up here.',
    'schedule.filter_all': 'All',
    'schedule.filter_weekday': 'Weekday',
    'schedule.filter_weekday_all': 'Every day',
    'schedule.filter_time_from': 'From',
    'schedule.filter_time_to': 'To',
    'schedule.filter_time_from_all': 'Any',
    'schedule.filter_time_to_all': 'Any',
    'schedule.filter_sort': 'Order',
    'schedule.filter_sort_asc': 'Ascending',
    'schedule.filter_sort_desc': 'Descending',
    'schedule.filter_empty': 'Nothing matches these filters',
    'schedule.filter_empty_hint': 'Change the weekday, time range, or sort order.',
    'schedule.google_section': 'Google Calendar',
    'schedule.dismiss_alert': 'Dismiss alert',
    'schedule.calendar.prev_month': 'Previous month',
    'schedule.calendar.next_month': 'Next month',
    'schedule.tap_to_available': 'Tap to make available',
    'schedule.tap_to_unavailable': 'Tap to close the day',
    'client_scheduling.progress_label': 'Booking steps',
    'schedule.disconnected': 'Disconnected',
    'schedule.reconnect': 'Reconnect',
    'schedule.new': 'New appointment',
    'schedule.total': 'Total appointments',
    'schedule.confirmed': 'Confirmed',
    'schedule.pending': 'Pending',
    'schedule.cancelled': 'Cancelled',
    'schedule.connect_calendar': 'Connect to Google Calendar to see your appointments',
    'schedule.define_schedule': 'Set schedule',
    'schedule.available': 'Available',
    'schedule.unavailable': 'Unavailable',
    'schedule.past_date': 'Past date',
    'schedule.connect_calendar_sync': 'Connect to Google Calendar and wait for appointments to sync.',
    'schedule.events_title': 'Appointments',
    'schedule.last_update': 'Last update: {time}',
    'schedule.events_count': '{count} appointments',
    'schedule.load_error': 'Error loading appointments',
    'schedule.try_again': 'Try again',
    'schedule.no_events': 'No appointments found',
    'schedule.notify.load_error': 'Error loading appointments',
    'schedule.notify.save_error': 'Error saving appointment',
    'schedule.notify.created': 'Appointment created successfully!',
    'schedule.notify.updated': 'Appointment updated successfully!',
    'schedule.notify.connect_to_confirm': 'Connect to Google Calendar to confirm appointments',
    'schedule.notify.confirmed': 'Appointment confirmed and event created in Google Calendar!',
    'schedule.notify.confirmed_no_google': 'Appointment confirmed and WhatsApp sent. Enable the Google Calendar API in Cloud Console to sync the calendar.',
    'schedule.notify.confirmed_status_only': 'Appointment confirmed. Connect Google Calendar to sync events.',
    'schedule.notify.confirm_failed': 'Could not confirm the appointment. Try again.',
    'schedule.notify.confirm_error': 'Error creating Google Calendar event: {error}',
    'schedule.notify.google_api_disabled': 'Enable the Google Calendar API in Google Cloud for this project and try again.',
    'schedule.notify.cancelled': 'Appointment cancelled successfully',
    'schedule.notify.cancel_error': 'Error cancelling appointment',
    'schedule.loading': 'Loading appointments…',
    'schedule.status.confirmed': 'Confirmed',
    'schedule.status.pending': 'Pending',
    'schedule.status.cancelled': 'Cancelled',
    'schedule.status.completed': 'Completed',
    'schedule.status.unknown': 'Unknown',
    'schedule.synced_google': 'Synced with Google Calendar',
    'schedule.google.processing_title': 'Processing authorization…',
    'schedule.google.processing_body': 'Please wait while we connect your Google Calendar account',
    'schedule.google.success_title': 'Connected successfully!',
    'schedule.google.error_title': 'Connection error',
    'schedule.google.redirecting': 'Redirecting to the scheduling page…',
    'schedule.google.connected': 'Successfully connected to Google Calendar!',
    'schedule.google.connect_failed': 'Could not connect to Google Calendar. Try again or contact our support.',
    'schedule.google.redirect_mismatch': 'Redirect URI is not authorized in Google Cloud. Add the panel URI (.../google-callback) to the OAuth client authorized redirect URIs.',
    'schedule.google.server_config': 'Google integration is misconfigured on the server. Check Client ID and Secret on the backend and try again.',
    'schedule.google.invalid_grant': 'Google code expired or already used. Close and click Connect again.',
    'schedule.google.auth_denied': 'Google authorization was cancelled or denied',
    'schedule.google.missing_code': 'Authorization code not found',
    'schedule.google.need_login': 'Sign in to the store before connecting Google Calendar',
    'schedule.google.exchange_failed': 'Could not obtain the Google token',
    'schedule.edit_btn': 'Edit',
    'schedule.templates.title': 'Appointment WhatsApp messages',
    'schedule.templates.hint': 'Texts sent on confirmation, reminder, cancellation and reschedule. Use the variables in braces.',
    'schedule.templates.vars': 'Variables: {cliente}, {servico}, {data}, {hora}, {endereco}',
    'schedule.templates.pending': 'Pending',
    'schedule.templates.confirmation': 'Confirmation',
    'schedule.templates.reminder': 'Reminder',
    'schedule.templates.cancellation': 'Cancellation',
    'schedule.templates.reschedule': 'Reschedule',
    'schedule.templates.preview': 'Preview',
    'schedule.templates.restore_default': 'Restore this default',
    'schedule.templates.restore_all_defaults': 'Restore all defaults',
    'schedule.templates.save': 'Save templates',
    'schedule.templates.saving': 'Saving…',
    'schedule.templates.toast_saved': 'Templates saved',
    'schedule.templates.toast_save_error': 'Could not save templates',
    'schedule.templates.sample_client': 'Maria',
    'schedule.templates.sample_service': 'Haircut',
    'schedule.templates.sample_address': 'Example St, 100',
    'schedule.templates.send_reminder': 'Send reminder',
    'schedule.templates.reminder_sent': 'Reminder sent via WhatsApp',
    'schedule.templates.reschedule_sent': 'Reschedule sent via WhatsApp',
    'schedule.templates.send_failed': 'Could not send. Check your WhatsApp connection or contact our support.',
    'client_scheduling.step_professional': 'Professional',
    'client_scheduling.choose_professional': 'Choose a professional',
    'client_scheduling.back_to_professional': '← Back to professional',
    'client_scheduling.no_services': 'No services configured. Ask the business to set up scheduling services.',
    'client_scheduling.no_professionals_for_service':
      'No professional is available for this service. Choose another service.',
    'client_scheduling.professional_day_closed': 'Unavailable on this day',
    'client_scheduling.loading_slots': 'Loading times…',
    'client_scheduling.module_off': 'Scheduling is unavailable for this business.',
    'client_scheduling.module_off_title': 'Scheduling unavailable',
    'company.gift.title': 'Gift / coupon (bag QR)',
    'company.gift.hint': 'Configure the discount customers get when they claim the QR. The coupon applies automatically on the next eligible order.',
    'company.gift.enabled': 'Program enabled',
    'company.gift.print_qr': 'Print QR on bag',
    'company.gift.headline': 'QR / page headline',
    'company.gift.success_title': 'Success title',
    'company.gift.success_body': 'Success message',
    'company.gift.programs': 'Discount programs',
    'company.gift.add_program': 'Add program',
    'company.gift.program_new_name': 'New program',
    'company.gift.program_name': 'Program name',
    'company.gift.program_active': 'Active',
    'company.gift.remove_program': 'Remove program',
    'company.gift.type': 'Type',
    'company.gift.type_percent': 'Percent (%)',
    'company.gift.type_fixed': 'Fixed amount',
    'company.gift.value': 'Value',
    'company.gift.min_order': 'Minimum order',
    'company.gift.eligibility': 'Eligibility',
    'company.gift.eligibility_any': 'Any order',
    'company.gift.eligibility_first': 'First order only',
    'company.gift.eligibility_subsequent': 'From 2nd order on',
    'company.gift.max_uses': 'Max coupons per customer',
    'company.gift.expires_days': 'Validity (days)',
    'company.gift.rule_percent': '{value}% off · min {min}',
    'company.gift.rule_fixed': '{value} off · min {min}',
    'company.gift.save': 'Save gift settings',
    'company.gift.saving': 'Saving…',
    'company.gift.toast_saved': 'Gift rules saved',
    'company.gift.toast_save_error': 'Could not save gift rules',
    'company.gift.issue_coupons': 'Issue coupons',
    'company.gift.issue_title': 'Issue coupons to customers',
    'company.gift.issue_program': 'Program',
    'company.gift.issue_search': 'Search by name or phone',
    'company.gift.issue_loading': 'Loading customers…',
    'company.gift.issue_empty': 'No customers found',
    'company.gift.client_unnamed': 'Unnamed customer',
    'company.gift.cancel': 'Cancel',
    'company.gift.issuing': 'Issuing…',
    'company.gift.issue_confirm': 'Issue ({count})',
    'company.gift.toast_issued': '{count} coupon(s) issued',
    'company.gift.toast_issue_error': 'Could not issue coupons',
    'company.gift.toast_clients_error': 'Could not load customers',
    'schedule.notify.deleted': 'Appointment removed successfully',
    'schedule.notify.delete_error': 'Error deleting appointment',
    'schedule.notify.unknown_error': 'Unknown error',
    'schedule.available_times_for': 'Available times for {date}',
    'schedule.day_closed': 'This day is closed for appointments',
    'schedule.time_selected': 'Selected time: {time}',

    'password_req.title': 'Password requirements:',
    'password_req.min_length': 'At least 6 characters',
    'password_req.uppercase': 'At least 1 uppercase letter',
    'password_req.number': 'At least 1 number',
    'password_req.special': 'At least 1 special character',

    'image_crop.title': 'Adjust image',
    'image_crop.use_image': 'Use image',
    'image_crop.subtitle_zoom': 'Use the mouse wheel to zoom and drag the image to position. The square area will be used.',
    'image_crop.subtitle_crop': 'The full image is shown. Drag the rectangle to position; use the bottom-right corner to resize. Outside the selection the image is dimmed.',
    'image_crop.preview_alt': 'Logo preview',
    'image_crop.resize_aria': 'Resize selection',
    'image_crop.zoom': 'Zoom',
    'image_crop.generate_failed': 'Failed to generate image',

    'gcal_events.invalid_date': 'Invalid date',
    'gcal_events.events_count_one': '{count} appointment',
    'gcal_events.events_count_many': '{count} appointments',
    'gcal_events.current_month': 'Current',
    'gcal_events.today_badge': 'Today',
    'gcal_events.loading': 'Loading appointments...',

    'orders.start_sales_mid_body': 'You opened the dashboard during business hours, but the store is closed. Open now to receive orders?',
    'orders.start_sales_manual_body': 'The store only opens when you confirm. Click Start sales to open now — status does not change automatically at opening time.',
    'orders.start_sales_btn': 'Start sales',
    'orders.opening_btn': 'Opening...',
    'orders.store_override_intro': 'Set how long this manual status should apply to the entire store.',
    'orders.store_override.auto': 'Automatic',
    'orders.store_override.auto_close_hint': 'Closes now and reopens at the next scheduled opening time.',
    'orders.store_override.auto_close_at': 'The store reopens at {when}.',
    'orders.store_override.auto_open_at': 'The store closes at {when}.',
    'orders.store_override.auto_no_schedule': 'Set business hours to use Automatic.',
    'orders.store_override.timed_reopen_at': 'Reopens at {when}.',
    'orders.store_override.timed_outside_hours': 'Unavailable: {when} is outside business hours.',
    'orders.store_override.30min': '30 minutes',
    'orders.store_override.2h': '2 hours',
    'orders.store_override.4h': '4 hours',

    'public_orders.cep_error': 'Error fetching ZIP code. Please try again.',
    'public_orders.no_products_search': 'No products found for "{term}"',
    'public_orders.edit': 'Edit',

    'client_scheduling.loading': 'Loading availability...',
    'client_scheduling.loading_for': 'Fetching available times for {name}...',
    'client_scheduling.module_name': 'Scheduling module',
    'client_scheduling.book_title': 'Book your appointment',
    'client_scheduling.online': 'Online',
    'client_scheduling.step_datetime': 'Date & time',
    'client_scheduling.step_date': 'Date',
    'client_scheduling.step_time': 'Time',
    'client_scheduling.step_service': 'Service',
    'client_scheduling.step_contact': 'Contact',
    'client_scheduling.step_name': 'Name',
    'client_scheduling.step_address': 'Address',
    'client_scheduling.step_confirm': 'Confirmation',
    'client_scheduling.choose_time': 'Choose a time',
    'client_scheduling.contact_title': 'Your phone',
    'client_scheduling.contact_hint': 'We use WhatsApp to confirm the booking.',
    'client_scheduling.name_title': 'Your name',
    'client_scheduling.address_title': 'Visit address',
    'client_scheduling.address_required_hint': 'This service is at your address.',
    'client_scheduling.continue': 'Continue',
    'client_scheduling.badge_visit': 'Visit',
    'client_scheduling.badge_store': 'In store',
    'client_scheduling.place_store': 'In-store appointment',
    'client_scheduling.place_store_at': 'In-store appointment · {name}',
    'client_scheduling.sinal_required': '{amount} deposit',
    'client_scheduling.price_service': 'Service',
    'client_scheduling.price_sinal': 'Deposit',
    'client_scheduling.price_total': 'Total',
    'client_scheduling.sinal_adds_hint':
      'The deposit does not reduce the service price. You pay deposit + service.',
    'client_scheduling.sinal_deducts_hint':
      'The deposit is deducted from the service. Remaining at the appointment: {remaining}',
    'client_scheduling.back_to_time': '← Back to times',
    'client_scheduling.back_to_contact': '← Back to contact',
    'client_scheduling.back_to_name': '← Back to name',
    'client_scheduling.error.address_required': 'Enter the visit address',
    'client_scheduling.label_place': 'Place',
    'client_scheduling.choose_date': 'Choose a date',
    'client_scheduling.choose_date_hint': 'Select an available date for your appointment',
    'client_scheduling.weekday_short.sun': 'Sun',
    'client_scheduling.weekday_short.mon': 'Mon',
    'client_scheduling.weekday_short.tue': 'Tue',
    'client_scheduling.weekday_short.wed': 'Wed',
    'client_scheduling.weekday_short.thu': 'Thu',
    'client_scheduling.weekday_short.fri': 'Fri',
    'client_scheduling.weekday_short.sat': 'Sat',
    'client_scheduling.closed_day': 'Closed',
    'client_scheduling.legend_available': 'Available',
    'client_scheduling.legend_closed': 'Closed',
    'client_scheduling.legend_past': 'Past date',
    'client_scheduling.past_day': 'Past date',
    'client_scheduling.legend_selected': 'Selected',
    'client_scheduling.today': 'Today',
    'client_scheduling.times_available': 'Available times:',
    'client_scheduling.more_times': 'more',
    'client_scheduling.times_for': 'Available times for',
    'client_scheduling.day_closed_msg': 'This day is closed for appointments',
    'client_scheduling.no_times': 'No times available for this day',
    'client_scheduling.choose_other_date': '← Choose another date',
    'client_scheduling.choose_service': 'Choose a service',
    'client_scheduling.date_label': 'Date:',
    'client_scheduling.at_time': 'at',
    'client_scheduling.duration_min': 'Duration: {count} min',
    'client_scheduling.back_to_date': '← Back to date selection',
    'client_scheduling.confirm_title': 'Confirm your appointment',
    'client_scheduling.summary': 'Appointment summary',
    'client_scheduling.label_date': 'Date',
    'client_scheduling.label_time': 'Time',
    'client_scheduling.label_service': 'Service',
    'client_scheduling.google_hint': 'The appointment will be added automatically to Google Calendar',
    'client_scheduling.full_name': 'Full name *',
    'client_scheduling.name_placeholder': 'Your full name',
    'client_scheduling.phone': 'Phone *',
    'client_scheduling.phone_placeholder': '(11) 99999-9999',
    'client_scheduling.email': 'Email',
    'client_scheduling.email_placeholder': 'you@email.com',
    'client_scheduling.location': 'Appointment location',
    'client_scheduling.company_address': 'Business address',
    'client_scheduling.automatic': 'Automatic',
    'client_scheduling.your_address': 'Visit address',
    'client_scheduling.address_placeholder': 'Street, number, neighborhood',
    'client_scheduling.notes': 'Additional notes',
    'client_scheduling.notes_placeholder': 'Additional information about the appointment...',
    'client_scheduling.confirm_btn': 'Confirm appointment',
    'client_scheduling.creating_event': 'Creating calendar event...',
    'client_scheduling.confirming': 'Confirming...',
    'client_scheduling.back_to_service': '← Back to service selection',
    'client_scheduling.error.name_required': 'Name is required',
    'client_scheduling.error.name_min': 'Name must be at least 3 characters',
    'client_scheduling.error.phone_required': 'Phone is required',
    'client_scheduling.error.phone_format': 'Phone must be in format (11) 99999-9999',
    'client_scheduling.error.email_invalid': 'Email must be in a valid format',
    'client_scheduling.lgpd_consent': 'I have read and agree to the',
    'client_scheduling.lgpd_terms_link': 'Appointment Terms of Use',
    'client_scheduling.lgpd_privacy_link': 'Privacy Policy',
    'client_scheduling.error.lgpd_required': 'To confirm, accept the appointment terms.',
    'client_scheduling.success_with_gcal': 'Appointment booked successfully! The event was added to your Google Calendar.',
    'client_scheduling.success_no_gcal': 'Appointment booked successfully! Connect to Google to add events to your calendar automatically.',
    'client_scheduling.success_booked': 'Appointment booked successfully! The business will confirm soon.',
    'client_scheduling.error_confirm': 'Error confirming appointment. Please try again.',
    'client_scheduling.return_included_legend': 'Return included in this service',
    'client_scheduling.return_needs_prior': 'A return can only be booked after a prior appointment for this service.',
    'client_scheduling.kind_service': 'Service',
    'client_scheduling.kind_return': 'Return (included)',

    'gcal.never': 'Never',
    'gcal.just_now': 'Just now',
    'gcal.mins_ago': '{count} min ago',
    'gcal.hours_ago': '{count}h ago',
    'gcal.checking': 'Checking...',
    'gcal.connection_error': 'Connection error',
    'gcal.connected': 'Connected',
    'gcal.auto_refresh': '(Auto-refresh on)',
    'gcal.last_check': 'Last check:',
    'gcal.token': 'Token:',
    'gcal.error_label': 'Error:',
    'gcal.verify_now': 'Check now',
    'gcal.disable_auto_refresh': 'Disable auto-refresh',
    'gcal.enable_auto_refresh': 'Enable auto-refresh',

    'manager.revenue_total': 'Total revenue',
    'manager.orders': 'Orders',
    'manager.new_clients': 'New clients',
    'manager.avg_time': 'Average time',
    'manager.vs_previous_month': 'vs {count} previous month',
    'manager.tab_overview': 'Overview',
    'manager.tab_reports': 'Reports',
    'manager.tab_analytics': 'Analytics',
    'manager.payment_summary': 'Summary by payment method',
    'manager.top_products': 'Top 5 products',
    'manager.payment_methods': 'Payment methods',
    'manager.order_type': 'Order type',
    'manager.no_products': 'No products found',
    'manager.no_data': 'No data available',
    'manager.reports_title': 'Management reports',
    'manager.card': 'Card',
    'manager.cash': 'Cash',
    'manager.analytics_title': 'Advanced analytics',
    'manager.analytics_soon': 'Analytics functionality coming soon',
    'manager.analytics_hint': 'View detailed sales charts, trends and performance',
    'manager.reports_soon': 'Reports functionality coming soon',
    'manager.reports_hint': 'Generate custom sales, customer and performance reports',
    'manager.type_delivery': 'Delivery',
    'manager.type_dine_in': 'Dine in',
    'manager.orders_count_one': '{count} order',
    'manager.orders_count_many': '{count} orders',
    'manager.range_too_long': 'Choose a period of at most 12 months.',
    'manager.load_failed': 'Could not load the summary. Try again.',

    'employees.edit_title': 'Edit employee',
    'employees.add_title': 'Add employee',
    'employees.stores_label': 'Stores',
    'employees.stores_all': 'All',
    'employees.stores_hint': 'Choose which stores in the group this employee works at.',
    'employees.stores_required': 'Choose at least one store.',
    'employees.full_name_placeholder': 'Full name',
    'employees.new_role_placeholder': 'New role name',
    'employees.add_role': 'Add role',
    'employees.daily_rate': 'Daily rate',
    'employees.observations': 'Notes',
    'employees.observations_placeholder': 'Notes about the employee...',
    'employees.permissions': 'Permissions',
    'employees.permissions_hint': 'This employee\'s permissions (same role can have different permissions).',
    'employees.permissions_btn': 'Permissions',
    'employees.permissions_denied': 'You cannot change other employees\' permissions.',
    'employees.cpf_optional': 'Tax ID (optional)',
    'employees.cpf_hint': 'Leave blank if the employee will enter it via the invite link when creating their password.',
    'employees.cpf_placeholder': '000.000.000-00',
    'employees.status_label': 'Status',
    'employees.status.active': 'Active',
    'employees.status.inactive': 'Inactive',
    'employees.status.vacation': 'On leave (vacation)',
    'employees.status.leave': 'On leave',
    'employees.role.waiter': 'Waiter',
    'employees.role.cook': 'Cook',
    'employees.role.manager': 'Manager',
    'employees.role.attendant': 'Attendant',
    'employees.role.delivery': 'Delivery driver',
    'employees.role.admin': 'Admin',
    'employees.role.generic': 'Employee',
    'employees.delete_role_title': 'Delete role "{name}"',
    'employees.close_new_role': 'Close new role',
    'employees.open_new_role': 'Add new role',
    'employees.phone_placeholder': '(00) 00000-0000',
    'employees.edit_btn': 'Edit',

    'internal_order.title': 'Internal order',
    'internal_order.back_orders': 'Back to orders',
    'internal_order.back_delivery': 'Back to Delivery',
    'internal_order.view_cart': 'View cart',
    'internal_order.cart': 'Cart',
    'internal_order.cart_empty': 'Cart is empty',
    'internal_order.continue_shopping': 'Keep shopping',
    'internal_order.click_see_products': 'Click to see products',
    'internal_order.click_collapse': 'Click to collapse',
    'internal_order.item_one': 'item',
    'internal_order.item_many': 'items',
    'internal_order.checkout_title': 'Checkout',
    'internal_order.order_type': 'Order type',
    'internal_order.dine_in_short': 'Table',
    'internal_order.origin_internal': 'Internal order',
    'internal_order.table_number': 'Table number',
    'internal_order.table_placeholder': 'Table number (optional)',
    'internal_order.delivery_address': 'Delivery address *',
    'internal_order.house_number': 'Number *',
    'internal_order.house_placeholder': 'House number',
    'internal_order.already_paid': 'Order already paid',
    'internal_order.already_paid_hint': 'The order will be marked as paid',
    'internal_order.observations': 'Notes',
    'internal_order.observations_placeholder': 'Additional notes about the order',
    'internal_order.finish': 'Place order',
    'internal_order.modal.close_esc': 'Close (ESC)',
    'internal_order.modal.items_count': '{count} items',
    'internal_order.modal.obs_short': 'Note',
    'internal_order.modal.discount': 'Discount',
    'internal_order.modal.type_delivery': 'Delivery',
    'internal_order.modal.type_pickup': 'Pickup',
    'internal_order.modal.phone_optional_placeholder': '(00) 00000-0000 (optional)',
    'internal_order.modal.customer_name_placeholder': 'Customer name',
    'internal_order.ifood_orders_count': '{count} orders via iFood',
    'internal_order.ifood_orders_count_one': 'first order via iFood',
    'internal_order.store_orders_count': '{count} store orders',
    'internal_order.store_orders_count_one': 'first store order',
    'internal_order.modal.optional': 'Optional',
    'internal_order.modal.neighborhood_placeholder': 'Neighborhood',
    'internal_order.modal.change_placeholder': '$ 0.00',
    'internal_order.modal.order_summary': 'Order summary',
    'internal_order.modal.ifood_discount': 'iFood discount',
    'internal_order.modal.total_order': 'Order total',
    'internal_order.modal.calculated_total': 'Calculated total',
    'internal_order.modal.creating_order': 'Creating order...',
    'internal_order.modal.create_internal_order': 'Create Internal Order',
    'internal_order.modal.select_size_type': 'Select size/type:',
    'internal_order.modal.flavors_count': '{count} flavor(s)',
    'internal_order.modal.whole_pizza_one_flavor': 'Whole pizza (1 flavor)',
    'internal_order.modal.select_up_to_flavors': 'Select up to {max} flavor(s) ({total}/{max}):',
    'internal_order.modal.add_second_flavor': 'Add second flavor',
    'internal_order.modal.add_more_flavors': 'Add {count} more flavor(s)',
    'internal_order.modal.select_second_flavor': 'Select the second flavor',
    'internal_order.modal.select_additional_flavors': 'Select {count} additional flavor(s)',
    'internal_order.modal.addons_optional': 'Add-ons (optional):',
    'internal_order.modal.max_short': 'max. {count}',
    'internal_order.modal.observations_optional': 'Notes (optional):',
    'internal_order.modal.product_observations_placeholder': 'Ex.: no onion, well done...',
    'internal_order.modal.product_fallback': 'Product',
    'internal_order.modal.addon_fallback': 'Add-on',
    'internal_order.modal.obs_prefix': 'Note: {obs}',
    'internal_order.modal.table_label_value': 'Table {number}',
    'internal_order.modal.obs_table': 'Table: {number}',
    'internal_order.modal.obs_ifood_discount': 'iFood discount: R$ {amount}',
    'internal_order.modal.obs_order_paid': 'Order already paid',
    'internal_order.modal.toast.product_added': 'Product added to cart!',
    'internal_order.modal.toast.min_additional_required': 'You must choose at least {min} item(s) from "{category}" for this product',
    'internal_order.modal.toast.max_flavors': 'Maximum of {count} flavor(s) for this type',
    'internal_order.modal.toast.max_additional_limit': 'Maximum limit of {max} add-on(s) for {category}',
    'internal_order.modal.toast.enter_customer_name': 'Enter the customer name',
    'internal_order.modal.toast.enter_valid_phone': 'Enter a valid phone number (minimum 10 digits)',
    'internal_order.modal.toast.enter_house_number': 'Enter the house number',
    'internal_order.modal.toast.enter_neighborhood': 'Enter the neighborhood',
    'internal_order.modal.toast.min_additional_required_item': 'For item "{item}", you must choose at least {min} item(s) from "{category}"',
    'internal_order.modal.toast.order_created': 'Internal order created successfully!',
    'internal_order.modal.toast.create_order_error': 'Error creating internal order',
    'internal_order.modal.toast.process_order_error': 'Error processing internal order',

    'conversations.back': 'Back to conversations',
    'conversations.archived_hint': 'Archived conversations stay here as in WhatsApp on your phone.',
    'conversations.connected': 'Connected to WhatsApp',
    'conversations.disconnected': 'Disconnected from WhatsApp',
    'conversations.disconnect': 'Disconnect WhatsApp',
    'conversations.disconnecting': 'Disconnecting…',
    'conversations.connect_qr': 'Connect WhatsApp — generate QR code',
    'conversations.generating_qr': 'Generating QR code…',
    'conversations.generating_qr_ensure': 'Preparing WhatsApp session (creating instance if needed)…',
    'conversations.view_archived': 'View archived conversations',
    'conversations.bot_title': 'DelivAI — support bot',
    'conversations.bot_on': 'Bot on — click to turn off',
    'conversations.bot_off': 'Bot off — click to turn on',
    'conversations.bot_locked': 'WhatsApp bot button locked by DelivAI. It cannot be turned on or off here.',
    'conversations.bot_locked_aria': 'WhatsApp bot button locked by DelivAI',
    'conversations.bot_group_save_failed': 'Could not save this store bot. Try again.',
    'conversations.bot_disable': 'Turn off support bot',
    'conversations.bot_enable': 'Turn on support bot',
    'conversations.bot_badge_lock': 'LOCK',
    'conversations.bot_badge_on': 'ON',
    'conversations.bot_badge_off': 'OFF',
    'conversations.wa_group': 'Group WhatsApps',
    'conversations.wa_group_open': 'Open group connections',
    'conversations.wa_group_close': 'Close group connections',
    'conversations.tone_ok': 'Automatic support going well',
    'conversations.tone_paused': 'Bot paused on this chat',
    'conversations.tone_watch': 'AI is struggling with this chat',
    'conversations.tone_urgent': 'Needs an attendant urgently',
    'conversations.load_image': 'Load image',
    'conversations.load_video': 'Load video',
    'conversations.cancel_selection': 'Cancel message selection',

    'company.bairro_name_placeholder': 'Neighborhood name',
    'company.bairro_edit_title': 'Edit neighborhood name',
    'company.bairro_delete_title': 'Delete neighborhood',
    'company.bairro_add_title': 'Add neighborhood',

    'common.hours_range': '{open} to {close}',

    'clients.title': 'Clients',
    'clients.filter_all': 'All stores',
    'clients.filter_aria': 'Store',
    'clients.subtitle': 'Clients saved for orders and appointments (phone autocomplete)',
    'clients.search': 'Search by name or phone...',
    'clients.sort_by': 'Sort by',
    'clients.name': 'Name',
    'clients.ascending': 'Ascending (A → Z)',
    'clients.descending': 'Descending (Z → A)',
    'clients.new': 'New client',
    'clients.select_all': 'Select all ({count} visible)',
    'clients.bulk_edit': 'Bulk edit field',
    'clients.edit': 'Edit',
    'clients.order_one': '{count} order',
    'clients.orders_many': '{count} orders',
    'clients.badge_qr': 'From QR',
    'clients.qr_stat_issued': 'QR links generated',
    'clients.qr_stat_claimed': 'QR claimed',
    'clients.qr_stat_clients': 'New clients via QR',
    'clients.modal_edit': 'Edit client',
    'clients.modal_new': 'New client',
    'clients.field_name': 'Name',
    'clients.field_phone': 'Phone',
    'clients.field_zip': 'ZIP code',
    'clients.field_address': 'Address',
    'clients.field_number': 'Number',
    'clients.field_neighborhood': 'Neighborhood',
    'clients.field_complement': 'Complement',
    'clients.field_reference': 'Reference',
    'clients.sort_orders': 'Store orders',
    'clients.phone_missing': 'Phone not provided',
    'clients.no_name': 'No name',
    'clients.empty_none': 'No clients yet. Use the button above or register when placing orders/appointments.',
    'clients.empty_search': 'No results for this search.',
    'clients.empty_error': 'Fix the error above and refresh or change your search to try again.',
    'clients.searching_zip': 'Looking up ZIP code...',
    'clients.placeholder_name': 'Client name',
    'clients.placeholder_phone': '(00) 00000-0000',
    'clients.placeholder_zip': '00000-000',
    'clients.placeholder_address': 'Street, avenue...',
    'clients.placeholder_number': 'No.',
    'clients.placeholder_neighborhood': 'Neighborhood',
    'clients.placeholder_complement': 'Apt, block...',
    'clients.placeholder_reference': 'Reference point',
    'clients.bulk_title': 'Bulk update',
    'clients.bulk_field': 'Field',
    'clients.bulk_new_value': 'New value',
    'clients.bulk_placeholder': 'Value for all selected',
    'clients.bulk_apply': 'Apply',
    'clients.bulk_applying': 'Applying...',
    'clients.bulk_hint': 'Apply the same value to {count} selected client(s) (with valid ID).',

    'company.delivery_data_hint': 'Delivery data (double-click to edit)',
    'company.fee_label': 'Fee',
    'company.neighborhoods_count': '{count} neighborhoods',
    'company.order_types_public': 'Order types on public page',
    'company.pix_data': 'PIX data',
    'company.menu_images': 'Menu images',
    'company.delivery_type_delivery': 'Delivery',
    'company.delivery_type_pickup': 'Pickup',
    'company.delivery_type_dine_in': 'Dine in',
    'company.no_delivery_fees': 'No delivery fees configured',
    'company.configure_fees_hint': 'Set up fees and neighborhoods to get started',
    'company.add_first_fee': '+ Add first fee',
    'company.pix_disabled_hint': 'PIX is disabled in payment methods above. Data remains saved; enable PIX to edit.',
    'company.pix_key_type': 'Key type *',
    'company.pix_key': 'PIX key *',
    'company.pix_legal_name': 'Legal name *',
    'company.pix_bank': 'Bank *',
    'company.pix_key_placeholder': 'Enter PIX key',
    'company.pix_key_type_first': 'Select type first',
    'company.pix_holder_placeholder': 'Account holder / company name',
    'company.pix_bank_placeholder': 'E.g.: Nubank, Itaú...',
    'company.save_pix': 'Save PIX data',
    'company.no_payment_methods': 'No payment methods enabled',
    'company.no_payment_methods_hint': 'Enable at least one payment method so customers can place orders.',
    'company.order_types_hint': 'Choose which order types customers can select on the public page (delivery, pickup, dine in). All types remain available in the internal POS.',
    'company.delivery_loading': 'Loading delivery data...',
    'company.upload_menu_image': 'Upload new image',
    'company.menu_images_count': 'Menu images ({count} images)',

    'profile.unnamed': 'User',
    'profile.email': 'Email',
    'profile.phone': 'Phone',
    'profile.save': 'Save contact',
    'profile.saving': 'Saving…',
    'profile.saved': 'Contact updated.',
    'profile.save_error': 'Could not save contact.',
    'profile.save_unavailable': 'Editing is only available for staff accounts.',
    'profile.identity_locked': 'Name and tax ID cannot be changed here.',
    'profile.owner_hint': 'Store data is edited under Company.',
    'profile.open_company': 'Open company settings',
    'profile.panel_label': 'Profile details',
    'profile.change_password': 'Change password',
    'profile.password.title': 'Change password',
    'profile.password.subtitle': 'Enter your current password and choose a new secure password.',
    'profile.password.current': 'Current password',
    'profile.password.new': 'New password',
    'profile.password.confirm': 'Confirm new password',
    'profile.password.submit': 'Save new password',
    'profile.password.saving': 'Saving…',
    'profile.password.changed': 'Password changed successfully.',
    'profile.password.error': 'Could not change password.',
    'profile.password.same_as_old': 'The new password must differ from the current one.',
    'profile.password.mismatch': 'Passwords do not match.',
    'profile.password.forgot': 'Forgot my password',
    'profile.password.close': 'Close',
    'dev.nav.licenses': 'Licenses',
    'dev.nav.contracts': 'Contracts',
    'dev.nav.contracts_title': 'Contracts and legal drafts',
    'dev.nav.evolution': 'Evolution',
    'dev.nav.companies': 'Companies',
    'dev.nav.prompts': 'Prompts',
    'dev.nav.consumers': 'Customers',
    'dev.nav.database': 'Database',
    'dev.nav.logs': 'Logs',
    'dev.nav.observability': 'Observability',
    'dev.nav.ops_alerts': 'Alerts',
    'dev.nav.platform_ai': 'API keys',
    'dev.nav.platform_ai_title': 'DelivAI API keys',
    'dev.nav.agenda': 'Schedule',
    'dev.nav.agenda_title': 'Schedule — defaults and templates',
    'dev.card.platform_ai.title': 'API keys',
    'dev.card.platform_ai.desc':
      'Per-service keys (Support, Fast Order) for all clients without their own key.',
    'dev.card.agenda.title': 'Schedule',
    'dev.card.agenda.desc':
      'Schedule defaults/caps and WhatsApp templates (platform and per store).',
    'dev.agenda.title': 'Schedule — DelivAI',
    'dev.agenda.hint':
      'Dev params: defaults, caps, and default WhatsApp templates. Stores adjust the rest under Company → Schedule.',
    'dev.agenda.loading': 'Loading…',
    'dev.agenda.load_error': 'Could not load schedule defaults.',
    'dev.agenda.save_error': 'Could not save.',
    'dev.agenda.saved': 'Schedule defaults saved.',
    'dev.agenda.saving': 'Saving…',
    'dev.agenda.save': 'Save defaults',
    'dev.agenda.updated': 'Updated',
    'dev.agenda.defaults_title': 'Defaults and caps (Dev)',
    'dev.agenda.defaults_hint':
      'Defaults = initial/fallback. Caps = max the store can configure.',
    'dev.agenda.default_mode': 'Default mode',
    'dev.agenda.store_can_change_mode': 'Store can change schedule mode',
    'dev.agenda.default_slot': 'Default slot (min)',
    'dev.agenda.default_lead': 'Default lead time (min)',
    'dev.agenda.default_max_days': 'Default days ahead',
    'dev.agenda.default_buffer': 'Default buffer (min)',
    'dev.agenda.default_stations': 'Default stations',
    'dev.agenda.default_timezone': 'Default timezone',
    'dev.agenda.cap_max_days': 'Cap: days ahead',
    'dev.agenda.cap_min_slot': 'Cap min: slot (min)',
    'dev.agenda.cap_max_slot': 'Cap max: slot (min)',
    'dev.agenda.cap_stations': 'Cap: stations',
    'dev.agenda.cap_buffer': 'Cap: buffer (min)',
    'dev.agenda.templates_platform': 'WhatsApp templates (platform default)',
    'dev.agenda.templates_platform_hint':
      'Used when the store has not customized yet. Also editable per CNPJ below.',
    'dev.agenda.templates_store': 'Store templates (by CNPJ)',
    'dev.agenda.templates_store_hint':
      'Load a store to edit confirmation/reminder/cancellation texts.',
    'dev.agenda.cnpj_invalid': 'Enter a valid CNPJ (14 digits).',
    'dev.agenda.store_load_error': 'Could not load the store.',
    'dev.agenda.load_store': 'Load store',
    'dev.platform_ai.title': 'API keys — DelivAI',
    'dev.platform_ai.models_button': 'Models',
    'dev.platform_ai.models_title': 'Available models',
    'dev.platform_ai.models_hint': 'Check the ones that can appear in the list. At least one stays on.',
    'dev.platform_ai.models_save': 'Save models',
    'dev.platform_ai.models_saved_local': 'The list works on this screen. The server has not stored it yet.',
    'dev.platform_ai.model_hint.luna': 'Cheap and fast, for high volume',
    'dev.platform_ai.model_hint.nano5': 'Cheapest in the 5 line',
    'dev.platform_ai.model_hint.mini5': 'Fast, good for a defined task',
    'dev.platform_ai.model_hint.sol': 'Balance of skill and cost',
    'dev.platform_ai.model_hint.nano41': 'Light. Already on this screen',
    'dev.platform_ai.model_hint.mini41': 'Today’s default, low cost',
    'dev.platform_ai.model_hint.full41': 'More capable than mini, costs more',
    'dev.platform_ai.model_hint.mini4o': 'Previous line, light version',
    'dev.platform_ai.model_hint.full4o': 'Previous line, more capable',
    'dev.platform_ai.model_hint.astra': 'Most capable. Much more expensive',
    'dev.platform_ai.hint':
      'DelivAI keys per service. Clients without a company key use these. A company with its own key uses it for every AI service.',
    'dev.platform_ai.loading': 'Loading credentials…',
    'dev.platform_ai.load_error': 'Could not load AI credentials.',
    'dev.platform_ai.save_error': 'Could not save.',
    'dev.platform_ai.saved': 'DelivAI credentials saved.',
    'dev.platform_ai.saving': 'Saving…',
    'dev.platform_ai.save': 'Save API keys',
    'dev.platform_ai.privacy': 'Keys are never shown again after save — status only.',
    'dev.platform_ai.bot_title': 'Support (WhatsApp bot)',
    'dev.platform_ai.bot_hint': 'Used by all stores without their own bot key.',
    'dev.platform_ai.fast_title': 'Fast Order (iFood + standard order)',
    'dev.platform_ai.fast_hint': 'Used by all stores without their own Fast Order key.',
    'dev.platform_ai.general_title': 'General fallback',
    'dev.platform_ai.general_hint':
      'If a service has no key, this is used before the server env variable.',
    'dev.platform_ai.new_key': 'New key (empty = keep)',
    'dev.platform_ai.model': 'Model',
    'dev.platform_ai.model_env': '(server env)',
    'dev.platform_ai.clear_key': 'Remove this key',
    'dev.platform_ai.status_set': 'Set',
    'dev.platform_ai.status_fallback': 'Uses fallback / env',
    'dev.platform_ai.status_env': 'Using server env',
    'dev.card.ops_alerts.title': 'Operational alerts',
    'dev.card.ops_alerts.desc':
      'Alert routes (Google Calendar, WhatsApp…) and who gets mass-failure notices.',
    'dev.ops_alerts.title': 'Operational alerts',
    'dev.ops_alerts.hint':
      'Global (all stores / Devs) and per store on this page. Without the scheduling module the store button stays locked off.',
    'dev.ops_alerts.loading': 'Loading alerts…',
    'dev.ops_alerts.load_error': 'Could not load alerts.',
    'dev.ops_alerts.save_error': 'Could not save.',
    'dev.ops_alerts.routes': 'Routes / services',
    'dev.ops_alerts.recipients': 'Who is notified (Dev)',
    'dev.ops_alerts.no_recipients': 'No recipients yet — add yourself below.',
    'dev.ops_alerts.active': 'Active',
    'dev.ops_alerts.inactive': 'Inactive',
    'dev.ops_alerts.channel_stores': 'Stores',
    'dev.ops_alerts.channel_devs': 'Devs',
    'dev.ops_alerts.global_stores': 'All stores',
    'dev.ops_alerts.gcal_store_rule':
      'A store without the scheduling module does not get this WhatsApp — only the Dev channel, if it is on.',
    'dev.ops_alerts.stores_title': 'Per store',
    'dev.ops_alerts.stores_hint':
      'Pick a route and toggle each store. Without the scheduling module, Google Calendar stays locked off.',
    'dev.ops_alerts.stores_search': 'Search store or tax ID',
    'dev.ops_alerts.stores_empty': 'No store in this search.',
    'dev.ops_alerts.store_locked_no_module':
      'No scheduling module — store alert stays off.',
    'dev.ops_alerts.store_locked_global': 'The global store channel is off.',
    'dev.ops_alerts.store_toggled': 'Store alert updated.',
    'dev.ops_alerts.emitted_title': 'Emitted notifications',
    'dev.ops_alerts.emitted_empty': 'No alerts emitted in the last 24 h.',
    'dev.ops_alerts.emitted_refresh': 'Refresh',
    'dev.ops_alerts.route_toggled': 'Route updated.',
    'dev.ops_alerts.mass_threshold': 'Mass threshold',
    'dev.ops_alerts.cooldown': 'Store / Dev cooldown',
    'dev.ops_alerts.add_recipient': 'New recipient',
    'dev.ops_alerts.name_placeholder': 'Name',
    'dev.ops_alerts.phone_placeholder': 'WhatsApp (area code + number)',
    'dev.ops_alerts.save_recipient': 'Save recipient',
    'dev.ops_alerts.recipient_saved': 'Recipient saved.',
    'dev.ops_alerts.recipient_deleted': 'Recipient removed.',
    'dev.ops_alerts.delete': 'Delete',
    'dev.ops_alerts.delete_title': 'Delete recipient',
    'dev.ops_alerts.delete_confirm': 'Remove this person from Dev alerts?',
    'dev.consumers.clear_response_id': 'Clear response id',
    'dev.consumers.clear_response_id_hint':
      'Removes this customer openai_response_id. The next message starts a fresh AI context.',
    'dev.consumers.clear_response_id_ok': 'Response id cleared.',
    'dev.consumers.clear_response_id_none': 'There was no response id to clear.',
    'dev.consumers.clear_response_id_error': 'Could not clear the response id.',
    'dev.consumers.close_detail': 'Close',
    'dev.nav.permissions': 'Permissions',
    'dev.nav.bot': 'Bot',
    'dev.nav.bot_title': 'WhatsApp bot control',
    'dev.nav.delidesk': 'DeliDesk',
    'dev.nav.delidesk_title': 'Global DeliDesk features',
    'dev.bot.title': 'Bot control',
    'dev.bot.subtitle':
      'Global ON unlocks the Zap for every store — each owner turns their own robot on or off. Global OFF locks all. With global on you can lock one store; with global off you can unlock just one.',
    'dev.bot.platform_title': 'Platform bot',
    'dev.bot.platform_hint':
      'Incident (spam/ban): turn global off. Store owners get a locked Zap until you turn it back on or unlock one store below.',
    'dev.bot.platform_on': 'GLOBAL ON',
    'dev.bot.platform_off': 'GLOBAL OFF',
    'dev.bot.platform_unknown': '…',
    'dev.bot.platform_on_ok': 'Global on — Zap unlocked for every store',
    'dev.bot.platform_off_ok': 'Global off — Zap locked for every store',
    'dev.bot.platform_locked_hint': 'Re-enable the global kill-switch first',
    'dev.bot.companies_title': 'Zap button per store',
    'dev.bot.companies_empty': 'No stores found',
    'dev.bot.company_saved': 'Store button updated',
    'dev.bot.store_robot_on': 'Store robot: on (owner turned it on)',
    'dev.bot.store_robot_off': 'Store robot: off (owner turned it off)',
    'dev.bot.store_button_locked': 'Zap locked — the robot will not reply',
    'dev.bot.store_inactive':
      'Store inactive in the panel (license) — WhatsApp may still be connected',
    'dev.bot.store_not_in_panel':
      'Evolution instance with no store in the panel — lock unavailable',
    'dev.bot.row_free': 'FREE',
    'dev.bot.row_locked': 'LOCKED',
    'dev.bot.row_unavailable': 'NO STORE',
    'dev.bot.load_error': 'Could not load bot status',
    'dev.bot.save_error': 'Could not save',
    'dev.evolution.title': 'Evolution instances',
    'dev.evolution.subtitle':
      'View by environment (test or prod Evolution) and webhook destination (test or prod backend). Reads/writes go through the backend proxy.',
    'dev.evolution.logs_title': 'Evolution logs',
    'dev.evolution.logs_empty': 'No Evolution logs in the last 6 h.',
    'dev.evolution.logs_refresh': 'Refresh',
    'dev.evolution.logs_error': 'Could not load Evolution logs.',
    'dev.evolution.view_env_label': 'View Evolution',
    'dev.evolution.view_env_help':
      'Switches which Evolution API you are browsing. Each environment has its own instances.',
    'dev.evolution.view_env_test_hint': 'Test backend/Evolution',
    'dev.evolution.view_env_prod_hint': 'Production backend/Evolution',
    'dev.evolution.env_test': 'Test',
    'dev.evolution.env_prod': 'Production',
    'dev.evolution.webhook_dest_label': 'Webhook destination',
    'dev.evolution.webhook_dest_help':
      'Where this Evolution sends events. Same environment = Docker URL (project network). Other environment = public HTTPS (EasyPanel stacks are isolated — the other project Docker hostname is unreachable).',
    'dev.evolution.webhook_dest_same': 'Same environment',
    'dev.evolution.webhook_dest_cross': 'Other environment (HTTPS)',
    'dev.evolution.view_prod_proxy_hint':
      'Production view talks to the production backend. Proxy /api/evolution/webhook is only on test until release to main — “No static resource” means that.',
    'dev.evolution.webhook_dest_locked':
      'Instance disconnected in this environment — connect (QR) to change webhook destination.',
    'dev.evolution.webhook_dest_lock_badge': 'LOCK',
    'dev.evolution.webhook_custom': 'Custom URL',
    'dev.evolution.list_error': 'Failed to list instances.',
    'dev.evolution.webhook_read_error': 'Failed to read webhook',
    'dev.evolution.webhook_save_error': 'Could not save the webhook. Please try again.',
    'dev.evolution.settings_read_error': 'Failed to read settings',
    'dev.evolution.settings_save_error': 'Could not save settings. Please try again.',
    'dev.evolution.reload_partial': 'Could not reload from API',
    'dev.evolution.detail_error': 'Error loading details.',
    'dev.evolution.connect_error': 'Connect / QR error',
    'dev.evolution.webhook_url_missing': 'Webhook URL for this environment is unavailable.',
    'dev.evolution.webhook_pointed_test': 'Webhook pointing to test (sandbox).',
    'dev.evolution.webhook_pointed_prod': 'Webhook pointing to production.',
    'dev.evolution.lease_error': 'Webhook saved, but test lease did not start on the backend.',
    'dev.evolution.lease_hint':
      'Temporary divert (Production view → Test destination): after 2h idle this instance webhook returns to the production backend. On the test environment, the natural destination is test itself — no auto-revert.',
    'dev.evolution.connected': 'Connected',
    'dev.evolution.disconnected': 'Disconnected',
    'dev.card.bot.title': 'WhatsApp bot',
    'dev.card.bot.desc':
      'Global kill-switch and per-store toggle. When global is OFF the store button is unavailable.',
    'dev.delidesk.title': 'Printing — DeliDesk & PrintNode',
    'dev.delidesk.subtitle':
      'Global and per-store kill-switches (like the bot): DeliDesk queue, marketplace capture and PrintNode — independent.',
    'dev.delidesk.warning':
      'Effective = global ON and store ON. Turning off PrintNode for one store does not cut the DeliDesk queue; capture OFF does not cut tickets.',
    'dev.delidesk.platform_title': 'Global (all stores)',
    'dev.delidesk.companies_title': 'Per store',
    'dev.delidesk.companies_hint':
      'With global OFF the store chip is locked. Re-enable the global switch to change the store.',
    'dev.delidesk.companies_empty': 'No active stores found',
    'dev.delidesk.company_saved': 'Store feature updated',
    'dev.delidesk.company_backend': 'Backend: {backend}',
    'dev.delidesk.platform_locked_hint': 'Re-enable this feature’s global kill-switch first',
    'dev.delidesk.chip.printer': 'Queue',
    'dev.delidesk.chip.virtual_capture': 'Capture',
    'dev.delidesk.chip.printnode': 'PrintNode',
    'dev.delidesk.applies_label': 'Applies to:',
    'dev.delidesk.empty': 'No features available',
    'dev.delidesk.load_error': 'Could not load DeliDesk features',
    'dev.delidesk.save_error': 'Could not save',
    'dev.delidesk.feature_on': 'GLOBAL ON',
    'dev.delidesk.feature_off': 'GLOBAL OFF',
    'dev.delidesk.feature_on_ok': 'Feature {feature} enabled',
    'dev.delidesk.feature_off_ok': 'Feature {feature} disabled',
    'dev.delidesk.feature.generic_hint': 'Global control for this feature.',
    'dev.delidesk.feature.printer.title': 'DeliDesk queue (tickets)',
    'dev.delidesk.feature.printer.hint':
      'DelivAI panel tickets queued and printed by DeliDesk (jobs/next). Does not control marketplace capture.',
    'dev.delidesk.feature.printer.applies':
      'ticket enqueue · jobs/next poll · thermal print via agent',
    'dev.delidesk.feature.virtual_capture.title': 'Marketplace capture (virtual)',
    'dev.delidesk.feature.virtual_capture.hint':
      'When a marketplace (iFood, etc.) prints to the DeliDesk virtual printer, the ticket is sent so AI can create the order.',
    'dev.delidesk.feature.virtual_capture.applies':
      'virtual capture API · Windows listener · order creation from marketplace tickets',
    'dev.delidesk.feature.printnode.title': 'PrintNode',
    'dev.delidesk.feature.printnode.hint':
      'Ticket send via PrintNode (stores with print_backend=printnode, or fallback if DeliDesk queue is OFF).',
    'dev.delidesk.feature.printnode.applies':
      'OrderPrintService → PrintNode · independent from marketplace capture and agent queue',
    'dev.card.delidesk.title': 'Printing',
    'dev.card.delidesk.desc':
      'Global + per-store kill-switches: DeliDesk queue, marketplace capture and PrintNode.',
    'dev.home.title': 'Control panel',
    'dev.home.subtitle':
      'Licenses, Evolution, companies, customers, prompts, database, logs and permissions — DelivAI internal console.',
    'dev.home.overview': 'Overview',
    'dev.home.overview_hint': 'Pick an area. Each card opens an internal tool.',
    'dev.home.open': 'Open',
    'dev.home.continue': 'Continue',
    'dev.access.denied_title': 'No permission',
    'dev.access.denied_body':
      'Your role cannot access this area. Ask an admin or sign in again after permission changes.',
    'dev.card.licenses.title': 'Licenses and plans',
    'dev.card.licenses.desc': 'Look up a company by CNPJ and adjust license days and pay_types.',
    'dev.nav.reports': 'Reports',
    'dev.nav.reports_title': 'Store reports',
    'dev.card.reports.title': 'Reports',
    'dev.card.reports.desc': 'Notes sent from the flag on store screens, with text and images.',
    'dev.reports.empty': 'No reports.',
    'dev.reports.empty_phase': 'No reports in this phase.',
    'dev.reports.load_failed': 'Could not load the reports.',
    'dev.reports.save_failed': 'Could not save the phase.',
    'dev.reports.image': 'Image {n}',
    'dev.reports.company_unknown': 'Store not identified',
    'dev.reports.team': 'DelivAI team',
    'dev.reports.filter_label': 'Phase',
    'dev.reports.filter_all': 'All',
    'dev.reports.note_label': 'Note',
    'dev.reports.note_placeholder': 'What we will do with this report',
    'dev.reports.phase.open': 'New',
    'dev.reports.phase.reviewed': 'Reviewed',
    'dev.reports.phase.doing': 'In progress',
    'dev.reports.phase.done': 'Done',
    'dev.reports.phase.ignored': 'Ignore',
    'dev.card.contracts.title': 'Legal contracts',
    'dev.card.contracts.desc': 'Review B2B clauses, consumer terms and versioned previews.',
    'dev.contracts.title': 'Clause-based contracts',
    'dev.contracts.subtitle':
      'Edit by sector, pick a version and activate the official one. New acceptances use the hash of each tab’s official version.',
    'dev.contracts.version': 'Document version',
    'dev.contracts.official': 'official',
    'dev.contracts.awaiting': 'awaiting review',
    'dev.contracts.notices': 'For the lawyer',
    'dev.contracts.subs_paying': 'Active paying subscriptions',
    'dev.contracts.subs_free': 'Active free-period subscriptions',
    'dev.contracts.subs_hint': 'A store marked as test is left out of these numbers.',
    'dev.company.page_title': 'Company maintenance',
    'dev.company.page_hint': 'Store record: license, operations, menu and AI. End customers stay on the Clients tab.',
    'dev.company.jump': 'Registration sections',
    'dev.company.jump_data': 'Details',
    'dev.company.jump_ops': 'Operations',
    'dev.company.jump_license': 'License',
    'dev.company.jump_menu': 'Menu',
    'dev.company.test_store': 'Test store',
    'dev.company.test_store_hint': 'Does not count toward the lawyer subscription total.',
    'dev.company.fees_title': 'Neighborhoods and fees',
    'dev.company.fees_hint': 'One row per neighborhood. The JSON stays collapsed.',
    'dev.company.fees_add': 'Add neighborhood',
    'dev.company.fees_advanced': 'Advanced JSON',
    'dev.contracts.notice_define': 'Decide',
    'dev.contracts.notice_comment': 'Comment',
    'dev.contracts.notice_done': 'Done',
    'dev.contracts.notice_useless': 'Not needed',
    'dev.contracts.notice_save': 'Save',
    'dev.contracts.decision_pending': 'Not decided',
    'dev.contracts.decision_yes': 'Contract must change',
    'dev.contracts.decision_no': 'No contract change',
    'dev.contracts.add_version': 'New version (copy of current)',
    'dev.contracts.version_added': 'New version created. Activate it as official when ready.',
    'dev.contracts.activate_official': 'Activate as official',
    'dev.contracts.official_on': 'Official version',
    'dev.contracts.version_activated': 'Version activated as official for this sector.',
    'dev.contracts.view_clauses': 'Clause by clause',
    'dev.contracts.view_document': 'Full document',
    'dev.contracts.doc_title': 'Document title',
    'dev.contracts.save': 'Save drafts',
    'dev.contracts.save_ok': 'Drafts saved. Activate the official version when publishing a relevant change.',
    'dev.contracts.doc_view': 'View',
    'dev.contracts.doc_edit': 'Edit text',
    'dev.contracts.doc_apply': 'Apply to document',
    'dev.contracts.doc_apply_ok': 'Text applied to this version. Save drafts and activate as official to publish.',
    'dev.contracts.doc_edit_hint':
      'Paste the full contract here. Use the buttons to insert fields filled per store (CNPJ, legal name…). Fields show in yellow. Ctrl+Z undoes the last text change.',
    'dev.contracts.doc_cancel': 'Cancel edit',
    'dev.contracts.doc_undo': 'Undo (Ctrl+Z)',
    'dev.contracts.insert_fields': 'Insert dynamic field',
    'dev.contracts.suggest_fields': 'Detect CNPJ in text',
    'dev.contracts.suggest_ok': 'Dynamic fields suggested. Review and click Apply.',
    'dev.contracts.suggest_none': 'No store CNPJ found to convert.',
    'dev.contracts.delete_version': 'Delete version',
    'dev.contracts.delete_version_title': 'Delete this version?',
    'dev.contracts.delete_version_msg':
      'The version moves to trash (can be restored). Cannot remove the last live version. Master only.',
    'dev.contracts.delete_version_ok': 'Version moved to trash.',
    'dev.contracts.delete_version_blocked': 'Cannot delete the only live version.',
    'dev.contracts.master_only_publish': 'Only a master can activate the official version.',
    'dev.contracts.master_only_delete': 'Only a master can delete versions.',
    'dev.contracts.trash': 'Version trash',
    'dev.contracts.trash_empty': 'No versions in trash.',
    'dev.contracts.restore_version': 'Restore',
    'dev.contracts.restore_version_ok': 'Version restored from trash.',
    'dev.contracts.history': 'Recovery points',
    'dev.contracts.history_empty': 'No recovery points yet. Applying full text saves the previous content here.',
    'dev.contracts.restore_revision': 'Restore this point',
    'dev.contracts.restore_revision_ok': 'Previous content restored on this version.',
    'dev.contracts.field.razao': 'Legal name (store)',
    'dev.contracts.field.cnpj': 'CNPJ (store)',
    'dev.contracts.field.endereco': 'Address (store)',
    'dev.contracts.field.representante': 'Representative',
    'dev.contracts.field.versao': 'Version',
    'dev.contracts.field.data': 'Updated on',
    'dev.contracts.field.email': 'Contact email',
    'dev.contracts.field.dpo': 'DPO',
    'dev.contracts.field.remuneracao': 'Remuneration',
    'dev.card.database.title': 'Database',
    'dev.card.database.desc': 'Internal studio to inspect tables and support operations.',
    'dev.card.logs.title': 'Executions / logs',
    'dev.card.logs.desc': 'Backend executions and live flows for debugging.',
    'dev.card.observability.title': 'Observability',
    'dev.card.observability.desc': 'Prometheus metrics and Loki aggregated logs.',
    'dev.home.team_badge': 'DelivAI Team',
    'dev.home.stack_badge': 'Backend · Postgres · MinIO',
    'dev.home.internal_badge': 'Internal',
    'dev.permissions.relogin_hint':
      'Permissions updated. The affected user must sign out and back in to apply changes.',
    'dev.observability.subtitle':
      'Prometheus metrics and Loki logs from the DelivAI backend in near real time.',
    'dev.observability.refresh': 'Refresh',
    'dev.observability.checking': 'checking stack…',
    'dev.observability.no_data': 'no data',
    'dev.observability.window': 'Window:',
    'dev.observability.prometheus_warning':
      'Prometheus is disabled or unreachable. Configure PROMETHEUS_URL and LOKI_URL on the backend and start the stack to view data.',

    'collaborator.sidebar.title': 'Staff',
    'collaborator.sidebar.deliveries': 'My deliveries',
    'collaborator.sidebar.orders': 'POS',
    'collaborator.sidebar.delivery_ops': 'Delivery',
    'collaborator.sidebar.agenda': 'My schedule',
    'collaborator.no_access.title': 'No permissions assigned',
    'collaborator.no_access.body':
      'Ask the store to check your module permissions. Screens will appear here once access is granted.',
    
    // License Screen
    'license.title': 'License Management',
    'license.status': 'License Status',
    'license.active': 'Active',
    'license.inactive': 'Inactive',
    'license.expired': 'Expired',
    'license.paused': 'Paused',
    'license.paused_message': 'Access is paused. The days below stay saved and are not spent until the pause ends.',
    'license.ended_message': 'The days ran out and panel access is off. Renew to come back.',
    'license.days_remaining': 'days remaining',
    'license.days_expired': 'days expired',
    'license.details': 'License Details',
    'license.days_remaining_label': 'Days Remaining',
    'license.last_renewal': 'Last Renewal',
    'license.next_renewal': 'Next Renewal',
    'license.available_modules': 'Available Modules',
    'license.attendance_module': 'Attendance Module',
    'license.attendance_module_desc': 'Conversation and support management',
    'license.delivery_module': 'Delivery Module',
    'license.delivery_module_desc': 'Delivery rider management',
    'license.manager_module': 'Sales Summary Module',
    'license.manager_module_desc': 'Administration and reporting',
     'license.module_scheduling_title': 'Scheduling module',
     'license.module_scheduling_desc': 'Bookings and client scheduling',
     'license.snapshot_card_title': 'Current plan & renewals',
     'license.modules_summary_hint':
       "What's enabled under your agreement. New modules show up here once DelivAI turns them on.",
     'license.checkout_notice_title': 'In-dashboard billing',
     'manager.page_title': 'Sales summary',
    'manager.filter_all': 'All',
    'manager.filter_sales': 'Sales',
    'manager.filter_production': 'In production',
    'manager.ticket_avg': 'Average ticket',
    'manager.now': 'Now',
    'manager.before': 'Previous period',
    'manager.by_store': 'By store',
    'manager.cmv_later': 'Costs and COGS come in a later module. This summary stays on sales.',
    'manager.page_subtitle': 'Financial dashboard with metrics and sold-products performance',
    'license.active_status': 'Active',
    'license.inactive_status': 'Inactive',
    'license.available_plans': 'Available Plans for Renewal',
    'license.monthly': 'Monthly',
    'license.quarterly': 'Quarterly',
         'license.annual': 'Annual',
     'license.annual_fire': 'ANNUAL',
     'license.days_access': 'days of complete access',
     'license.days_30': '30 days of complete access',
     'license.days_90': '90 days of complete access',
     'license.days_365': '365 days of complete access',
    'license.per_month': 'per month',
    'license.per_quarter': 'per quarter',
    'license.per_year': 'per year',
    'license.savings': 'Savings of',
    'license.most_popular': 'Most Popular',
         'license.all_modules': 'All Modules',
     'license.suprema_title': 'SUPREMA - All Modules',
    
    // Company Screen
    'company.title': 'Company Settings',
    'kitchen.title': 'Dark kitchen',
    'kitchen.hint': 'Company profile, fees and menu follow the selected store. Orders from both stay in one list.',
    'kitchen.store_label': 'Store being edited',
    'kitchen.catalog_label': 'Menu',
    'kitchen.mix_hint': 'You can mix menus in the same order.',
    'kitchen.other_cnpj': 'Other store tax ID',
    'kitchen.email': 'Kitchen email',
    'kitchen.password': 'Password',
    'kitchen.password_confirm': 'Confirm password',
    'kitchen.password_rules': 'At least 6 characters, with an uppercase letter, a number and a symbol.',
    'kitchen.email_default': 'Dark Kitchen email',
    'kitchen.add_store': 'Add store',
    'kitchen.configure': 'Settings',
    'kitchen.configure_title': 'Dark Kitchen settings',
    'kitchen.login_email_hint': 'This email signs in to the kitchen, along with any store tax ID.',
    'kitchen.save_email': 'Save email',
    'kitchen.email_saved': 'Kitchen email updated.',
    'kitchen.close': 'Close',
    'kitchen.extra_cost': 'The 1st and 2nd stores are in the R$ 300 plan. From the 3rd on, each store adds R$ 100.',
    'kitchen.need_plan': 'Buy the Dark Kitchen plan',
    'kitchen.need_plan_hint': 'The invite unlocks only after the plan is paid.',
    'kitchen.transform': 'Turn into a Dark Kitchen',
    'kitchen.invite': 'Invite this store',
    'kitchen.cancel': 'Cancel',
    'kitchen.suggestions': 'Matching stores',
    'kitchen.other_store': 'Another store',
    'kitchen.invite_sent': 'The other store must accept this in its own settings.',
    'kitchen.incoming': '{store} wants to run together with this store.',
    'kitchen.accept': 'Accept',
    'kitchen.reject': 'Decline',
    'kitchen.accepted': 'The stores are linked.',
    'kitchen.configure_stores': 'Manage stores',
    'kitchen.back': 'Back',
    'kitchen.unlink_one': 'Unlink',
    'kitchen.leave': 'Unlink stores',
    'kitchen.leave_title': 'Unlink this store?',
    'kitchen.leave_body': 'It will sign in with its own tax ID again. Each store keeps its own orders.',
    'kitchen.err.company_not_found': 'We could not find that store.',
    'kitchen.err.same_store': 'Enter the other store tax ID.',
    'kitchen.err.already_linked': 'That store is already in another kitchen.',
    'kitchen.err.invite_pending': 'There is already an invite for that store.',
    'kitchen.err.kitchen_mismatch': 'Email or password does not match this kitchen.',
    'kitchen.err.bad_password': 'Wrong password.',
    'kitchen.err.weak_password': 'Password needs 6 characters, with an uppercase letter, a number and a symbol.',
    'kitchen.err.password_mismatch': 'Passwords do not match.',
    'kitchen.err.email_taken': 'That email already belongs to another kitchen.',
    'kitchen.err.bad_email': 'Enter a valid email.',
    'kitchen.err.no_invite': 'There is no invite to accept.',
    'kitchen.err.not_linked': 'That store is not in this kitchen.',
    'kitchen.err.kitchen_not_found': 'Kitchen not found.',
    'kitchen.err.kitchen_failed': 'Could not link the stores. Try again.',
    'kitchen.err.dark_kitchen_plan_required': 'Buy the Dark Kitchen plan before inviting another store.',
    'company.tabs.company': 'Company',
    'company.tabs.menu': 'Menu',
    'company.tabs.delivery': 'Delivery',
    'company.tabs.agenda': 'Schedule',
    'company.tabs.aria': 'Company settings sections',
    'company.header_subtitle_empresa': 'Registration data, contact, address and logo.',
    'company.header_subtitle_menu': 'Menu and services offered by the store.',
    'company.header_subtitle_delivery': 'Fees, neighborhoods and delivery options.',
    'company.header_subtitle_agenda': 'Store scheduling rules.',
    'company.empresa_toolbar_hint': 'Edit and refresh apply only to this tab.',
    'company.agenda.title': 'Schedule rules',
    'company.agenda.hint': 'Define how the store books (single calendar, multi-pro, or shared space). Enable each professional under Employees.',
    'company.agenda.open_employees': 'Open Employees screen',
    'company.agenda.empty': 'No active collaborators found.',
    'company.agenda.area_placeholder': 'Area / specialty',
    'company.agenda.toggle_on': 'Schedule on',
    'company.agenda.toggle_off': 'Schedule off',
    'company.agenda.saved': 'Schedule rules saved',
    'company.agenda.save_error': 'Could not save schedule rules',
    'company.agenda.load_error': 'Could not load schedule rules',
    'company.agenda.save': 'Save rules',
    'company.agenda.saving': 'Saving…',
    'company.agenda.mode': 'Schedule mode',
    'company.agenda.mode_single': 'Store calendar (single)',
    'company.agenda.mode_multi': 'Multi-professional (may overlap)',
    'company.agenda.mode_shared': 'Shared space (no conflict)',
    'company.agenda.mode_single_hint': 'Uses the store Google calendar. No professional picker.',
    'company.agenda.mode_multi_hint': 'Each professional has their own calendar; times may overlap (separate rooms).',
    'company.agenda.mode_shared_hint': 'Same room/space: times cannot overlap beyond the station count.',
    'company.agenda.slot_minutes': 'Slot interval (min)',
    'company.agenda.min_lead_minutes': 'Minimum lead time (min)',
    'company.agenda.max_days_ahead': 'Max days ahead',
    'company.agenda.buffer_minutes': 'Break between appointments (min)',
    'company.agenda.shared_stations': 'Shared stations / rooms',
    'company.agenda.timezone': 'Time zone',
    'company.agenda.country': 'Country',
    'company.agenda.region': 'Region / city',
    'company.agenda.country_br': 'Brazil',
    'company.agenda.country_pt': 'Portugal',
    'company.agenda.country_ar': 'Argentina',
    'company.agenda.country_py': 'Paraguay',
    'company.agenda.country_uy': 'Uruguay',
    'company.agenda.country_us': 'United States',
    'company.agenda.country_ao': 'Angola',
    'company.agenda.country_mz': 'Mozambique',
    'company.agenda.region_sao_paulo': 'São Paulo (Brasília)',
    'company.agenda.region_fortaleza': 'Fortaleza',
    'company.agenda.region_belem': 'Belém',
    'company.agenda.region_manaus': 'Manaus',
    'company.agenda.region_cuiaba': 'Cuiabá',
    'company.agenda.region_rio_branco': 'Rio Branco',
    'company.agenda.region_lisbon': 'Lisbon',
    'company.agenda.region_buenos_aires': 'Buenos Aires',
    'company.agenda.region_asuncion': 'Asunción',
    'company.agenda.region_montevideo': 'Montevideo',
    'company.agenda.region_new_york': 'New York (Eastern)',
    'company.agenda.region_chicago': 'Chicago (Central)',
    'company.agenda.region_denver': 'Denver (Mountain)',
    'company.agenda.region_los_angeles': 'Los Angeles (Pacific)',
    'company.agenda.region_luanda': 'Luanda',
    'company.agenda.region_maputo': 'Maputo',
    'company.agenda.matrix_title': 'Who does what',
    'company.agenda.matrix_hint':
      'Check the professionals who perform each service and, if needed, the minutes. If nobody is checked for a service, everyone can provide it.',
    'company.agenda.matrix_service': 'Service',
    'company.agenda.matrix_empty_services':
      'Add services in the catalog to define who performs each one.',
    'company.agenda.matrix_empty_pros':
      'Turn on the professional schedule for at least one employee to build the matrix.',
    'company.agenda.matrix_default_duration': '{count} min in the catalog',
    'company.agenda.matrix_unrestricted': 'Everyone can provide this (no restriction)',
    'company.agenda.matrix_minutes': 'Minutes for this professional',
    'company.agenda.matrix_assign': 'This professional performs the service',
    'company.agenda.open_catalog': 'Open services catalog',
    'company.services.return_included': 'Return included in this service',
    'company.services.return': 'Return',
    'company.services.return_on': 'ON return',
    'company.services.return_off': 'OFF return',
    'company.services.visit': 'Visit',
    'company.services.visit_on': 'Visit',
    'company.services.visit_off': 'In store',
    'company.services.sinal': 'Deposit',
    'company.services.sinal_amount': 'Deposit amount',
    'company.services.professionals': 'Professionals for this service',
    'company.services.professionals_empty': 'No staff with an active schedule.',
    'company.services.professionals_hint':
      'None selected = every professional can take the booking.',
    'company.agenda.sinal_title': 'Deposit rule',
    'company.agenda.sinal_hint':
      'Default: the deposit is added on top of the service price. Clients see this on the booking summary.',
    'company.agenda.sinal_adds': 'Deposit + service',
    'company.agenda.sinal_deducts': 'Deposit deducted from service',
    'company.basic_info': 'Basic Information',
    'company.store_switching': 'Loading store…',
    'company.name': 'Company Name',
    'company.cnpj': 'CNPJ',
    'company.phone': 'Company Phone',
    'company.responsible_phone': 'Responsible Person Phone',
    'company.email': 'Email',
    'company.address': 'Address',
    'company.street': 'Street',
    'company.number': 'Number',
    'company.neighborhood': 'Neighborhood',
    'company.city': 'City',
    'company.state': 'State',
    'company.state_uf': 'State',
    'company.state_placeholder': 'State',
         'company.zip_code': 'ZIP Code',
         'company.cep_looking_up': 'Looking up ZIP code...',
         'company.cep_fill_hint': 'Enter the ZIP code to auto-fill street, neighborhood, city and state.',
         'company.toast.cep_not_found': 'ZIP code not found.',
         'company.toast.cep_fetch_error': 'Error fetching ZIP code. Try again.',
         'company.toast.save_success_fields': 'Data updated successfully! Modified fields: {fields}',
     'company.description': 'Description',
     'company.operating_hours': 'Operating Hours',
     'company.hours.presets_label': 'Presets',
     'company.hours.preset_24h': 'Open 24h',
     'company.hours.preset_commercial': 'Business 09:00–17:00',
     'company.hours.preset_custom': 'Custom',
     'company.hours.open_time': 'Opens',
     'company.hours.close_time': 'Closes',
     'company.hours.range_from': 'From',
     'company.hours.range_to': 'To',
     'company.hours.commercial_range_hint': 'Days in the range stay open; the others (e.g. Saturday and Sunday) are closed.',
     'company.hours.select_all': 'Select all',
     'company.hours.clear_selection': 'Clear selection',
     'company.hours.apply_preset': 'Apply to selected days',
     'company.hours.apply_commercial': 'Apply business hours',
     'company.hours.apply_24h': 'Apply 24h to all days',
     'company.hours.selected_count': '{count} day(s) selected',
     'company.hours.preset_hint': 'Select days and apply a preset. Then fine-tune times in the table if needed. Save writes all 7 days.',
     'company.hours.col_select': 'Select',
     'company.hours.col_day': 'Day',
     'company.hours.col_open': 'Status',
     'company.hours.select_day': 'Select {day}',
     'company.about_company': 'About the Company',
     'company.company_description': 'Company Description',
     'company.observations': 'Observations',
     'company.observations_placeholder': 'Ex: Active promotions, combo suggestions, new product launches...',
     'company.delivery_settings': 'Delivery Settings',
     'company.delivery_time': 'Average Delivery Time (minutes)',
     'company.delivery_fee': 'Delivery Fee ($)',
     
     // Menu Screen
     'menu.title': 'Menu Management',
     'menu.new_category': 'New Category',
     'menu.update': 'Update',
     
     // License Screen - Additional
     'license.current_plan': 'Current Plan',
     'license.annual_plan': 'Annual Plan',
     'license.days_complete_access': '365 days of complete access',
     'license.monthly_plan': 'Monthly plan',
     'license.semiannual_plan': 'Semiannual plan',
     'license.days_180': '180 days of access',
     'license.trial_plan': 'Free trial',
     'license.trial_subtitle': '7 days of access, no subscription',
     'license.trial_price': 'Free',
     'license.trial_period': 'no charge',
     'license.access_until': 'Access until',
     'license.working_normally': 'Your license is working normally',
     'license.expired_message': 'Your license has expired. Contact us to renew.',
     'license.loading': 'Loading license data...',
     'license.load_error': 'Could not load the license. Try again.',
     'license.cnpj_unavailable': 'Store tax ID is unavailable.',
     'license.try_again': 'Try Again',
     'license.edit': 'Edit',
     'license.view_license': 'Check license',
     'license.banner_expiring': 'Only {{days}} days left on your license.',
     'license.screen_expiring_lead': 'Only {{days}} days left on your license.',
     'license.screen_expiring_body': 'Renew early to avoid suspending your dashboard and disrupting service. Talk to our team — we can share exclusive options to add more license days before expiry.',
     'license.banner_inactive': 'Your license has no active days. Contact support to restore full access.',
     'license.expired_overlay_title': 'LICENSE EXPIRED!',
     'license.expired_overlay_body': 'Your license period has ended and the dashboard stays paused until renewal. We apologize for the inconvenience and thank you for your partnership. Please contact our team — we will gladly guide you through renewal and restore full access safely and promptly.',
     'license.sidebar_locked_hint': 'Renew your license to open this module',
     'license.orders_inactive_banner': 'To keep managing orders smoothly, talk to our team about renewing your license — we are here to help.',
     'license.toast_inactive_session': 'Your license has no active days. Renew with support to keep full access to the dashboard.',
     'license.pay_with_pix': 'Pay with PIX',
     'license.pix_checkout_hint_off':
       'Online PIX checkout will be available here soon.',
     'license.catalog_offers_title': 'Plans and pricing',
     'license.catalog_offers_lead':
       'Pick what you need — monthly, quarterly, or yearly — or bundle every module at a discounted rate.',
     'license.catalog_checkout_disabled_banner':
       'Billing from the dashboard is still being finalized in this environment. To renew or purchase a plan, contact DelivAI support.',
     'license.catalog_suprema_badge': 'Best overall value',
     'license.catalog_pix_unavailable_tooltip':
       'PIX is not enabled for this term. Pick another period or contact support.',
     'license.catalog_pix_slot_off_hint':
       'PIX shortcuts show here once in-dashboard checkout is enabled. Until then, use support to purchase.',
     'license.pack_title': 'Full package',
     'license.pack_subtitle': 'Essential DelivAI modules in one bundle.',
     'license.pack_duration_note': 'Adds {{days}} day(s) to the license once payment is confirmed.',
     'license.pack_includes_title': 'Included',
     'license.pack_module_orders': 'Order management',
     'license.pack_module_finance': 'Finance module',
     'license.pack_module_staff': 'Team / collaborators management',
     'license.pack_module_motoboy': 'Delivery rider module',
     'license.pack_module_company_clients': 'Company and customer records',
     'license.pack_pay_pix': 'Pay with PIX',
     'license.pack_pay_boleto': 'Pay with slip (boleto)',
     'license.pack_pay_mercadopago': 'Pay with Mercado Pago (PIX or card)',
     'license.pack_mercadopago_hint':
       'Opens Mercado Pago secure checkout; you can pay with PIX or card. Then return to the dashboard.',
     'license.pack_mercadopago_email_missing':
       'A valid email on your account is required for Mercado Pago. Update your details with support.',
     'license.pack_mercadopago_error':
       'Could not start payment. Try again or contact support.',
     'license.pack_mercadopago_loading': 'Opening checkout…',
     'license.pack_mercadopago_click_price_hint':
       'Tap or click the price above to pay with Mercado Pago (PIX or card).',
     'license.pack_segment_pix': 'Pay via PIX',
     'license.pack_segment_mp': 'Pay via Mercado Pago',
     'license.pack_mp_method_card': 'Credit card',
     'license.pack_mp_method_boleto': 'Bank slip (boleto)',
     'license.pack_segment_aria': 'Pack payment method',
     'license.pack_pix_click_price_hint':
       'Tap or click the price above to continue with PIX payment.',
     'license.pack_half_label_pix': 'PIX',
     'license.pack_half_label_mp': 'Mercado Pago',
     'license.pack_split_price_group_aria': 'Prices: Santander PIX and Mercado Pago checkout',
     'license.pack_split_payment_hint': '',
     'license.pack_footnote_monthly': '30 days of access · monthly renewal',
     'license.pack_footnote_semiannual': '180 days · save vs. monthly',
     'license.pack_footnote_annual': '365 days · best daily rate',
    'license.pack_split_pix_nav_note':
      'The PIX button activates once dashboard license checkout is enabled on this deployment.',
    'license.pack_split_pix_unavailable_title': 'Reference amount — PIX not clickable here yet',
    'license.pack_split_pix_unavailable_toast':
      'PIX checkout is not enabled in this environment. Use Mercado Pago on the right or contact support.',
    'license.mp_feedback_page_title': 'Payment return',
    'license.mp_feedback_page_subtitle':
      'A calm stop right after Mercado Pago checkout.',
    'license.mp_feedback_badge_success': 'All good here',
    'license.mp_feedback_badge_pending': 'Payment in review',
    'license.mp_feedback_badge_failure': 'Nothing changed on file',
    'license.mp_feedback_success_title': 'So glad you are here!',
    'license.mp_feedback_success_body':
      'Thank you for trusting DelivAI. Our whole team feels proud powering your everyday operation — once Mercado Pago confirms the payment, your days roll into the next screen.',
    'license.mp_feedback_pending_title': 'Almost there — hang tight',
    'license.mp_feedback_pending_body':
      'Mercado Pago is still processing. Give it a few seconds, then open your license tab to refresh and see updated days.',
    'license.mp_feedback_failure_title': 'Checkout did not finish',
    'license.mp_feedback_failure_body':
      'Nothing changed on your account. It happens sometimes — take a breath and try again whenever you are ready.',
    'license.mp_feedback_cta': 'Open license',
    'license.mp_feedback_subnote': 'Official day counts live on the license screen.',
    'license.mp_feedback_ref_prefix': 'Reference:',
     'license.pack_pix_hint': 'Fast confirmation; license days apply once payment is acknowledged.',
     'license.pack_boleto_compensation_notice':
       'Paid via boleto. License days are added only after bank compensation.',
     'license.pack_pay_disabled_hint':
       'Payments from this screen will roll out shortly. Renew by contacting support.',
     'license.pack_delivery_title': 'Complete operations plan',
     'license.pack_delivery_subtitle':
       'Orders, finance, staff, riders, and company records — a lean option if you do not need the built-in attendance flow.',
     'license.pack_billing_heading': 'Billing cycle',
     'license.pack_period_tab_monthly': 'Monthly',
     'license.pack_period_tab_semiannual': 'Semi-annual',
     'license.pack_period_tab_annual': 'Annual',
     'license.pack_annual_badge_fallback': '⭐ Best value',
     'license.pack_delivery_notice_label': 'Note:',
     'license.pack_delivery_no_attendance_note':
       'this package does not include the attendance module (in-dashboard conversations).',
     'license.pack_pix_badge': 'Recommended',
     'public_orders.save_principal_title': 'Save as main address?',
     'public_orders.save_principal_message': 'Do you want to save this new address as the main one on your profile?',
     'public_orders.save_principal_yes': 'Yes, save',
     'public_orders.save_principal_no': 'No',
     'public_orders.loading': 'Loading...',
     'public_orders.cnpj_invalid': 'Invalid CNPJ',
     'public_orders.company_not_found': 'Company not found',
     'public_orders.company_unavailable': 'The requested company was not found or is unavailable.',
     'public_orders.order_online': 'Order online',
     'public_orders.open': 'Open',
     'public_orders.closed': 'Closed',
     'public_orders.track_order': 'Track Order',
     'public_orders.search_products': 'Search products...',
     'public_orders.add_to_cart': 'Add to cart',
     'public_orders.cart': 'Cart',
     'public_orders.checkout': 'Checkout',
     'public_orders.your_name': 'Your full name',
     'public_orders.your_phone': 'Your phone',
     'public_orders.delivery_address': 'Delivery address',
     'public_orders.payment_method': 'Payment method',
     'public_orders.place_order': 'Place order',
     'public_orders.cancel': 'Cancel',
     'public_orders.from_price': 'From {price}',
     'public_orders.free_delivery': 'Free',
     'public_orders.select_neighborhood': 'Select a neighborhood from the list',
     'public_orders.view_cart': 'View Cart',
     'public_orders.empty_cart': 'Cart is empty',
     'public_orders.finish_order': 'Checkout',
     'public_orders.add_to_cart_btn': 'Add to Cart',
     'public_orders.calc_delivery_btn': 'Calculate delivery fee and time',
     'public_orders.calc_delivery_hint': 'Enter your ZIP code to see if we deliver to your area',
     'public_orders.delivery_cep_hint': 'Enter your ZIP code so we can check delivery availability and the fee.',
     'public_orders.zip_label': 'ZIP code',
     'public_orders.zip_placeholder': '00000-000',
     'public_orders.neighborhood_identified': 'Neighborhood identified: {name}',
     'public_orders.delivery_fee_label': 'Delivery fee:',
     'public_orders.ok_understood': 'OK, got it',
     'public_orders.dont_know_zip': "I don't know my ZIP code",
     'public_orders.close': 'Close',
     'public_orders.close_cart': 'Close cart',
     'public_orders.close_modal': 'Close modal',
     'public_orders.continue_shopping': 'Continue shopping',
     'public_orders.subtotal': 'Subtotal:',
     'public_orders.total': 'Total:',
     'public_orders.order_type_label': 'Order type',
     'public_orders.no_products_category': 'No products available in this category.',
     'public_orders.menu_coming_soon': 'Menu coming soon',
     'public_orders.decrease_qty': 'Decrease quantity',
     'public_orders.increase_qty': 'Increase quantity',
     'public_orders.delivery_eta_prefix': 'Delivery in',
     'public_orders.phone_label': 'Phone',
     'public_orders.phone_searching': 'Looking up customer...',
     'public_orders.phone_found': 'Your details were filled in',
     'public_orders.phone_needs_verify': 'Registered customer — confirm the code',
     'public_orders.phone_not_found': 'Customer not found',
     'public_orders.phone_optional_hint': 'Optional for dine in',
     'public_orders.name_label': 'Name',
     'public_orders.saved_address': 'Saved address',
     'public_orders.address_default': 'Address',
     'public_orders.new_address': '+ New address',
     'public_orders.where_delivery': 'Where is the delivery?',
     'public_orders.where_delivery_hint': 'Drag the pin to your door or tap the map. On a corner, pick the correct street.',
     'public_orders.search_address': 'Search address',
     'public_orders.address_placeholder': 'E.g.: Main St, {city}',
     'public_orders.address_suggestions_hint': 'Suggestions prioritize {city}{state}. Nearby streets in the delivery area also appear.',
     'public_orders.number_label': 'Number',
     'public_orders.number_required': 'Number *',
     'public_orders.number_placeholder': 'House number',
     'public_orders.delivery_here': 'Deliver here',
     'public_orders.use_my_location': 'Use my location',
     'public_orders.getting_location': 'Getting location...',
     'public_orders.map_hint_no_pin': 'Tap the map, use the location button, or enter your ZIP below.',
     'public_orders.searching_address_location': 'Looking up address from location...',
     'public_orders.identifying_street': 'Identifying street...',
     'public_orders.corner_pick_street': 'On a corner? Which is your street?',
     'public_orders.address_preview': 'Address:',
     'public_orders.confirm_pin_hint': 'Deliver here? Adjust the pin if needed.',
     'public_orders.confirm_delivery_address': 'Confirm delivery address',
     'public_orders.prefer_manual_zip': 'I prefer to enter ZIP manually',
     'public_orders.searching_zip': 'Looking up ZIP...',
     'public_orders.delivery_address_required': 'Delivery address *',
     'public_orders.street_placeholder': 'Street — e.g.: Main St, {city}',
     'public_orders.street_type_hint': 'Type the street name — suggestions appear as you type ({city}{state} area, including nearby neighborhoods).',
     'public_orders.number_map_hint': 'ZIP and number place the pin closer to your home. ZIP helps find neighborhood and delivery fee.',
     'public_orders.continue_map_confirm': 'Continue to confirm on map',
     'public_orders.use_map_location': 'Use map and location',
     'public_orders.adjust_on_map': 'Adjust on map',
     'public_orders.edit_zip_street': 'Edit ZIP and street',
     'public_orders.street_placeholder_simple': 'Street, avenue, lane...',
     'public_orders.neighborhood_label': 'Neighborhood *',
     'public_orders.complement_label': 'Apt / suite',
     'public_orders.complement_placeholder': 'Apt, block, etc.',
     'public_orders.reference_label': 'Reference point',
     'public_orders.reference_placeholder': 'Reference point',
     'public_orders.select_payment': 'Select payment method',
     'public_orders.no_payment_configured': 'No payment method configured. Contact the store.',
     'public_orders.need_change': 'Need change?',
     'public_orders.change_for': 'Pay with how much?',
     'public_orders.change_hint': 'Enter the total amount you will pay (e.g. type 50 for $0.50)',
     'public_orders.notes_label': 'Notes',
     'public_orders.notes_placeholder': 'Additional notes about the order',
     'public_orders.order_summary': 'Order summary',
     'public_orders.delivery_fee_summary': 'Delivery fee',
     'public_orders.processing': 'Processing...',
     'public_orders.confirm_order': 'Confirm order',
     'public_orders.obs_prefix': 'Note:',
     'public_orders.pay.cash': 'Cash',
     'public_orders.pay.pix': 'PIX',
     'public_orders.pay.debit': 'Debit card',
     'public_orders.pay.credit': 'Credit card',
     'public_orders.pay.meal_voucher': 'Meal voucher',
     'public_orders.pay.other': 'Other',
     'public_orders.toast.geolocation_unsupported': 'Your browser does not support location.',
     'public_orders.toast.geolocation_failed': 'Could not get your location. Enter ZIP or use the map.',
     'public_orders.toast.corner_street': 'Your home seems to be on a corner. Select your street.',
     'public_orders.toast.address_confirmed': 'Address confirmed! Review details and choose payment.',
     'public_orders.toast.select_payment': 'Select a payment method',
     'public_orders.toast.select_neighborhood_fee': 'Select a valid neighborhood from the list to apply the delivery fee',
     'public_orders.toast.select_price_type': 'Select a price type',
     'public_orders.toast.order_type_unavailable': 'This order type is not available for this store.',
     'public_orders.toast.order_error': 'Error creating order. Please try again.',
     'bairro_field.label': 'Neighborhood',
     'bairro_field.placeholder': 'Click and select neighborhood',
     'bairro_field.no_neighborhoods': 'No neighborhoods registered. Configure in Settings → Delivery.',
     'bairro_field.no_match': 'No matching neighborhood — pick one from the list.',
     'bairro_field.not_found': 'Neighborhood not found — pick one from the list to see the fee',
     'bairro_field.select_hint': 'Select a neighborhood from the list to see the delivery fee',
     'bairro_field.delivery_fee': 'Delivery fee:',
     'bairro_field.ref_hint_whatsapp': ' (amount from message — change neighborhood to use registry)',
     'bairro_field.ref_hint_ifood': ' (iFood amount — change neighborhood to use registry)',
     'bairro_field.ref_label_whatsapp': 'Message indicated',
     'bairro_field.ref_label_ifood': 'iFood indicated',
     'bairro_field.using_registered': '— using registered neighborhood fee.',
     'bairro_field.registered_hint': 'Registered neighborhood:',
     'bairro_field.using_ifood_fee': 'using the lower fee. Toggle in the summary for the other.',
     'bairro_field.using_higher_ifood': 'using the iFood fee. Toggle in the summary for the lower (registered) fee.',
     'public_orders.product_details': 'Product details',
     'public_orders.quantity': 'Quantity',
     'public_orders.select_size_type': 'Select size/type:',
     'public_orders.flavors_count': '{count} flavor(s)',
     'public_orders.pizza_single_flavor': 'Whole pizza (1 flavor)',
     'public_orders.pizza_select_flavors': 'Select up to {max} flavor(s) ({current}/{max}):',
     'public_orders.add_second_flavor': 'Add second flavor',
     'public_orders.add_more_flavors': 'Add {count} more flavor(s)',
     'public_orders.select_second_flavor': 'Select the second flavor',
     'public_orders.select_additional_flavors': 'Select {count} additional flavor(s)',
     'public_orders.addons_optional': 'Add-ons (optional):',
     'public_orders.addons_required': 'Add-ons (at least one required):',
     'public_orders.addons_min_required': 'You must choose at least {min} item(s) from "{category}"',
     'public_orders.product_notes': 'Notes (optional):',
     'public_orders.product_notes_placeholder': 'E.g.: no onion, well done...',
     'public_orders.toast.adjust_pin_gps': 'Adjust the pin on the map or use GPS before confirming.',
     'public_orders.toast.address_confirmed_details': 'Address confirmed! Now enter number and apt/suite.',
     'public_orders.toast.enter_zip_before_map': 'Enter ZIP or address before placing on the map.',
     'public_orders.toast.pin_near_number': 'Pin placed near the number. Adjust on the map if needed.',
     'public_orders.toast.pin_at_address': 'Pin placed at the address. Adjust on the map if needed.',
     'public_orders.toast.pin_on_street': 'Pin placed on the street. Enter the number or adjust on the map.',
     'public_orders.toast.cep_street_found': 'ZIP found on the street. Enter the number to place at your home.',
     'public_orders.toast.map_locate_failed': 'Could not locate on the map. Use GPS or tap the map to place the pin.',
     'public_orders.toast.map_unavailable': 'Map unavailable. Use GPS or tap the map to place the pin.',
     'public_orders.toast.enter_zip_before_continue': 'Enter ZIP or address before continuing.',
     'public_orders.toast.item_added': 'Item added to cart!',
     'public_orders.toast.item_updated': 'Item updated in cart!',
     'public_orders.toast.cart_empty': 'Add items to the cart before checkout',
     'public_orders.toast.enter_name': 'Enter your name',
     'public_orders.toast.invalid_phone': 'Enter a valid phone number (at least 10 digits)',
     'public_orders.toast.enter_delivery_address': 'Enter the delivery address',
     'public_orders.toast.enter_house_number': 'Enter the house number',
     'public_orders.toast.accept_privacy': 'Accept the Privacy Policy to continue',
     'public_orders.toast.enter_change_amount': 'Enter the amount for change',
     'public_orders.toast.delivery_fee_error': 'Error calculating delivery fee. Try selecting the neighborhood again.',
     'public_orders.toast.max_flavors': 'Maximum of {max} flavor(s) for this pizza type',
     'appointment.modal.new_title': 'New appointment',
     'appointment.modal.edit_title': 'Edit appointment',
     'appointment.modal.create_hint': 'Fill in the details below to schedule',
     'appointment.modal.edit_hint': 'Edit the appointment details',
     'appointment.modal.client_section': 'Customer information',
     'appointment.modal.full_name': 'Full name',
     'appointment.modal.client_name_placeholder': 'Customer name',
     'appointment.modal.phone': 'Phone',
     'appointment.modal.email': 'Email',
     'appointment.modal.service_section': 'Service',
     'appointment.modal.service': 'Service',
     'appointment.modal.loading_services': 'Loading services...',
     'appointment.modal.select_service': 'Select a service',
     'appointment.modal.no_services': 'No services found. Configure services in the Catalog section.',
     'appointment.modal.no_services_for_pro': 'No services enabled for this professional. Assign services on the employee profile.',
     'appointment.modal.duration': 'Duration',
     'appointment.modal.duration_placeholder': 'Select a service to see duration',
     'appointment.modal.duration_auto': 'Automatic duration based on selected service',
     'appointment.modal.date_time_section': 'Date and time',
     'appointment.modal.notes_section': 'Notes',
     'appointment.modal.notes_placeholder': 'Additional notes about the appointment...',
     'appointment.modal.address_unknown': 'Address not provided',
     'appointment.modal.company_default': 'Company',
     'appointment.modal.create_btn': 'Create appointment',
     'appointment.modal.save_btn': 'Save changes',
     'appointment.modal.one_day': '1 day',
     'appointment.modal.days': '{count} days',
     'appointment.modal.minutes': '{count} min',
     'appointment.error.client_required': 'Customer name is required',
     'appointment.error.phone_required': 'Phone is required',
     'appointment.error.date_required': 'Date is required',
     'appointment.error.time_required': 'Time is required',
     'appointment.error.service_required': 'Service is required',
     'appointment.error.address_required': 'Address is required',
     'appointment.error.email_invalid': 'Invalid email',
     'appointment.error.past_datetime': 'Date and time cannot be in the past',
     'appointment.error.day_unavailable': 'This day is marked as unavailable',
     'appointment.error.time_taken': 'This time slot is already taken',
     'appointment.error.time_locked': 'This time slot is locked',
     'appointment.error.save_failed': 'Error saving appointment. Please try again.',
     'public_scheduling.loading': 'Loading company information...',
     'public_scheduling.not_found_title': 'Company not found',
     'public_scheduling.not_found_message': 'The requested company was not found or is not available for scheduling.',
     'public_scheduling.back_home': 'Back to home',
     'public_scheduling.system_title': 'Scheduling system',
     'public_scheduling.creating': 'Creating...',
     'public_scheduling.new_appointment': 'New appointment',
     'public_scheduling.company_info': 'Company information',
     'public_scheduling.address': 'Address',
     'public_scheduling.phone': 'Phone',
     'public_scheduling.business_hours': 'Business hours',
     'public_scheduling.closed': 'Closed',
     'public_scheduling.available_services': 'Available services',
     'public_scheduling.no_services': 'No services available at the moment.',
     'public_scheduling.book_now': 'Book now',
     'public_scheduling.duration_label': 'Duration:',
     'public_scheduling.success': 'Appointment booked successfully! Event created in Google Calendar.',
     'public_scheduling.error_unknown': 'Unknown error while saving appointment.',
     'public_scheduling.address_unknown': 'Address not provided',
     'public_scheduling.company_default': 'Company',
     'public_scheduling.unavailable_title': 'Scheduling system unavailable',
     'public_scheduling.unavailable_message': 'The scheduling system is temporarily unavailable.',
     'public_scheduling.reconnect_hint': 'The business needs to reconnect the Google Calendar integration.',
     'public_scheduling.error_prefix': 'Error: {error}',
     'public_scheduling.reconnecting': 'Trying to reconnect...',
     'public_scheduling.reconnect_btn': 'Try to reconnect',
     'public_scheduling.how_to_title': 'How to book',
     'public_scheduling.how_to_step1': 'Connect to Google Calendar (if not already connected)',
     'public_scheduling.how_to_step2': 'Click "New appointment" to open the form',
     'public_scheduling.how_to_step3': 'Fill in your personal details and select the desired service',
     'public_scheduling.how_to_step4': 'Choose an available date and time on the calendar',
     'public_scheduling.how_to_step5': 'Confirm the appointment — it will be created automatically in Google Calendar',
     'fast_order.no_cnpj': 'CNPJ not found',
     'fast_order.title': 'Fast Order',
     'fast_order.back_aria': 'Back',
     'fast_order.standard_title': 'Standard order',
     'fast_order.ifood_title': 'Fast iFood',
     'fast_order.choose_intro': 'Create orders quickly without building item by item from the menu. Choose the type:',
     'fast_order.standard_card_title': 'Standard order',
     'fast_order.standard_card_desc': 'Paste WhatsApp messages or upload a screenshot — AI builds the order in the menu.',
     'fast_order.ifood_card_desc': 'Paste the full order or upload a screenshot — AI fills the order in the system.',
     'fast_order.standard_banner': 'Standard order — AI interprets WhatsApp or screenshot and builds the menu order',
     'fast_order.ifood_banner': 'Fast iFood — AI interprets the order and fills the system',
     'fast_order.paste_text': 'Paste text',
     'fast_order.upload_print_btn': 'Upload screenshot',
     'fast_order.content_ifood_label': 'iFood order content *',
     'fast_order.content_whatsapp_label': 'Order messages *',
     'fast_order.hint_ifood': 'Copy everything from the iFood order screen (number, customer, address, items and totals).',
     'fast_order.hint_whatsapp': 'Paste the WhatsApp conversation with the customer (name, phone, address, items and payment method).',
     'fast_order.placeholder_ifood': 'Paste the full iFood order here...',
     'fast_order.placeholder_whatsapp': 'Paste WhatsApp messages here...',
     'fast_order.hint_print_ifood': 'Screenshot of the order in the iFood app or portal.',
     'fast_order.hint_print_whatsapp': 'Screenshot of the WhatsApp chat or order notes.',
     'fast_order.choose_image': 'Click to choose an image',
     'fast_order.preview_alt': 'Order preview',
     'fast_order.extra_text_label': 'Extra text (optional)',
     'fast_order.extra_text_placeholder': 'Paste additional details if the screenshot is incomplete...',
     'fast_order.parsing': 'Parsing...',
     'fast_order.parse_btn': 'Parse with AI',
     'fast_order.apply_btn': 'Apply to order',
     'fast_order.image_type_error': 'Upload an image (PNG, JPG or WebP)',
     'fast_order.image_size_error': 'Image too large (max 8 MB)',
     'fast_order.paste_ifood': 'Paste the full iFood order content',
     'fast_order.paste_whatsapp': 'Paste WhatsApp messages or order text',
     'fast_order.upload_print': 'Upload an order screenshot',
     'fast_order.read_image_failed': 'Failed to read image',
     'fast_order.parsed_success': 'Order parsed — review and apply',
     'fast_order.parse_failed': 'Could not parse the order with AI',
     'fast_order.image_too_large': 'Screenshot too large. Send a smaller photo or paste the order text.',
     'fast_order.parse_unavailable': 'The test server did not respond. Try again in a moment.',
     'fast_order.parse_ai_unavailable': 'AI order parsing is temporarily unavailable. Try again later.',
     'fast_order.fee_registered': 'registered neighborhood',
     'fast_order.fee_message': 'amount in message',
     'fast_order.fee_ifood': 'iFood amount',
     'fast_order.fee_checkout_neighborhood': 'Fee set by neighborhood at checkout',
     'employees.invite_token_missing': 'Invite generated, but token was missing from response. Use "Resend invite".',
     'employees.invite_whatsapp_failed': 'Invite not sent via WhatsApp: {error}. Use "Resend invite" when WhatsApp is online.',
     'employees.saved_success': 'Employee "{name}" updated successfully!',
     'employees.added_success': 'Employee "{name}" added successfully!',
     'employees.updated_whatsapp': 'Employee "{name}" updated. Invite sent via WhatsApp (30 min).',
     'employees.added_whatsapp': 'Employee "{name}" added. Invite sent via WhatsApp (30 min).',
     'employees.removed_success': 'Employee removed successfully.',
     'employees.inactive_section': 'Inactive',
     'employees.inactive_hint': 'These logins are out of daily work. Delete removes the account.',
     'employees.delete_login': 'Delete login',
     'employees.deactivate_title': 'Deactivate employee?',
     'employees.deactivate_message': '{name} leaves the main list and the login stops working. You can delete the account later in the inactive section.',
     'employees.delete_login_title': 'Delete this login?',
     'employees.delete_login_message': '{name} will be removed. They can no longer sign in with this login.',
     'employees.delete_login_success': 'Login deleted.',
     'employees.role_added': 'Role "{name}" added successfully!',
     'employees.role_deleted': 'Role "{name}" deleted successfully!',
     'employees.role_delete_blocked': 'Cannot delete role "{name}" because {count} employee(s) are using it.',
     'employees.error.invalid_cnpj': 'Invalid store CNPJ.',
     'employees.error.invalid_cpf': 'Invalid CPF. Enter 11 digits or leave blank for the employee to fill in the invite.',
     'employees.error.invalid_cpf_fix': 'Invalid CPF. Fix the number or leave blank.',
     'employees.error.name_required': 'Enter the employee name.',
     'employees.error.no_permission_roles': 'No permission to change roles.',
     'employees.error.deactivate_failed': 'Could not deactivate this employee. Check CPF and store CNPJ.',
     'employees.error.delete_failed': 'Error removing employee. Please try again.',
     'orders.reject_pending_title': 'Reject pending order?',
     'orders.reject_pending_message': 'The order will be removed from the pending queue and will not be added to the store order list.',
     'orders.reject_reason_label': 'Rejection reason',
     'orders.cancel_reason_label': 'Cancellation reason',
     'orders.select_reason': 'Select a reason',
     'orders.describe_reason': 'Describe the reason',
     'orders.reason_placeholder': 'E.g.: We will be closed today for maintenance.',
     'orders.back': 'Back',
     'orders.rejecting': 'Rejecting...',
     'orders.confirm_reject': 'Yes, reject',
     'orders.reject': 'Reject',
     'orders.approve': 'Approve',
     'orders.reject_pending': 'Reject pending order',
     'orders.cancel_order': 'Cancel order',
     'orders.confirm_cancel': 'Confirm cancellation',
     'orders.cancelling': 'Cancelling…',
     'orders.confirm': 'Confirm',
     'orders.edit_item': 'Edit item',
     'orders.remove_item': 'Remove item',
     'orders.update_item': 'Update item',
     'orders.add_to_order': 'Add to order',
     'orders.add_product': 'Add product',
     'orders.toast.product_not_in_menu': 'Product not found in the current menu. Remove it and add again.',
     'orders.toast.menu_unavailable': 'Menu not loaded. Please try again.',
     'orders.confirm_admin': 'Confirm with administrator',
     'orders.open_store_manual': 'Open store manually',
     'orders.close_store_manual': 'Close store manually',
     'orders.confirm_dispatch': 'Confirm dispatch',
     'orders.cancel_order_title': 'Cancel order',
     'orders.cancel_confirm_message': 'Are you sure you want to cancel this order? This action cannot be undone.',
     'orders.ready': 'Ready',
     'orders.error.select_cancel_reason': 'Select a cancellation reason. For "Other", enter details.',
     'orders.error.select_reject_reason': 'Select a rejection reason. For "Other", enter details.',
     'orders.error.no_cnpj_accept': 'CNPJ not available to accept order.',
     'orders.error.admin_cpf': 'Select an administrator with a valid tax ID.',
     'orders.error.admin_password': 'Enter the administrator password.',
     'orders.error.wrong_password': 'Incorrect password. Try again.',
     'orders.error.validate_password': 'Could not validate password.',
     'orders.error.store_status': 'Could not update manual store status.',
     'orders.error.store_schedule_missing': 'No next time on the schedule. Set store hours or choose 30 min / 2h / 4h.',
     'orders.error.store_reopen_outside_hours': 'That duration would reopen outside business hours. Use Automatic or another valid time.',
     'orders.error.start_sales': 'Could not start sales now.',
     'orders.detail.payment_method': 'Payment method',
     'orders.detail.created_at': 'Created at',
     'orders.detail.order_type': 'Order type',
     'orders.detail.order_total': 'Order total:',
     'orders.detail.observations': 'Notes',
     'orders.detail.ifood_locator': 'iFood locator',
     'orders.delidesk_ifood.badge': 'Captured from iFood via DeliDesk',
     'orders.delidesk_ifood.review_hint': 'Review items and amounts, then approve the order.',
     'orders.delidesk_ifood.new_toast': 'New iFood order #{number} from {client} — {total}. Review and approve.',
     'orders.delidesk_ifood.test_badge': 'Demo order (iFood test print)',
     'orders.delidesk_ifood.test_review_hint':
       'Not a real order. Approve, edit and try dispatch with a courier to learn the flow.',
     'orders.delidesk_ifood.test_toast':
       'iFood test print — demo order #{number} ({total}).',
     'orders.delidesk_ifood.test_prompt_title': 'iFood test print',
     'orders.delidesk_ifood.test_prompt_message':
       'We detected a test print. Approve to run the flow (production + DelivAI coupon on the thermal printer) or discard the demo order.',
     'orders.delidesk_ifood.test_prompt_explore': 'Approve and print',
     'orders.delidesk_ifood.test_prompt_discard': 'Discard',
     'orders.delidesk_ifood.test_discarded': 'Demo order discarded.',
     'orders.delidesk_ifood.test_approved': 'Demo approved and sent to print.',
     'orders.print_slip': 'Print ticket',
     'orders.print_slip_updated_title': 'Print updated ticket?',
     'orders.print_slip_confirm': 'Print ticket',
     'orders.not_now': 'Not now',
     'orders.start_sales_now': 'Start sales now?',
     'orders.start_sales_opening': 'Opening time — start sales?',
     'orders.store_reopening_soon': 'Store about to reopen',
     'orders.store_opening_soon': 'Store about to open',
     'orders.success_store_open': 'Store opened manually successfully.',
     'orders.success_store_open_auto': 'Store opened. It will close automatically at the next scheduled closing time.',
     'orders.success_store_auto': 'Store in automatic mode — follows the schedule.',
     'orders.success_store_close': 'Store closed manually successfully.',
     'orders.success_store_close_auto': 'Store closed. It will reopen automatically at the next scheduled opening.',
     'orders.success_start_sales': 'Sales started. Store is open.',
     'orders.success_approve_pending': 'Pending order approved and created successfully.',
     'orders.success_reject_pending': 'Pending order rejected.',
     'orders.error_approve_pending': 'Error approving pending order.',
     'orders.error_reject_pending': 'Error rejecting pending order.',
     'orders.error_cancel': 'Error cancelling order. Please try again.',
     'orders.error_accept': 'Failed to accept order',
     'orders.success_accept': 'Order accepted.',
     'orders.auto_accept_unavailable_closed': 'Auto accept is unavailable while the store is closed.',
     'orders.badge_store_closed': 'Store closed',
     'orders.badge_waiting_accept': 'Awaiting acceptance',
     'orders.error_dispatch': 'Could not dispatch the order. Please try again.',
     'orders.error_print_order': 'Could not print order #{number}.',
     'orders.success_print_slip': 'Ticket sent to printer.',
     'orders.error_print_slip': 'Could not print the ticket. Try the Print button on the order.',
     'orders.warn_select_deliverer': 'Please select a deliverer',
     'orders.warn_select_orders_dispatch': 'Select at least one order to dispatch',
     'orders.warn_select_deliverer_dispatch': 'Select a deliverer',
     'orders.success_dispatch_count': '{count} order(s) dispatched successfully!',
     'orders.warn_dispatch_failed_count': '{count} order(s) could not be dispatched',
     'orders.warn_dispatch_error': 'Error dispatching orders. Please try again.',
     'orders.success_products_updated': 'Order products updated successfully!',
     'orders.error_products_update': 'Error updating order products',
     'orders.error_save_changes': 'Error saving changes. Please try again.',
     'orders.error_no_cnpj_print': 'CNPJ not available for printing',
     'orders.add_deadline': 'Add deadline',
     'orders.save_deadline_changes': 'Save changes',
     'orders.success_order_accepted_print': 'Order #{number} accepted and printing! 🖨️',
     'orders.success_order_accepted_printed': 'Order #{number} accepted and printed! 🖨️',
     'orders.success_order_accept_auto_print': 'Order #{number} accepted; auto-print in progress 🖨️',
     'schedule.confirm_btn': 'Confirm',
     'schedule.syncing': 'Syncing...',
     'schedule.delete_btn': 'Delete',
     'schedule.notes_label': 'Notes:',
     'route_dispatch.title': 'Dispatch route',
     'route_dispatch.motoboy_label': 'Courier',
     'route_dispatch.select_motoboy': 'Select courier...',
     'route_dispatch.stops_hint': '{count} stop(s) — drag to set delivery order',
     'route_dispatch.drag_aria': 'Drag',
     'route_dispatch.confirming': 'Confirming...',
     'route_dispatch.confirm_route': 'Confirm route',
     'route_dispatch.error_select_motoboy': 'Select a courier',
     'route_dispatch.error_select_orders': 'Select orders',
     'route_dispatch.success': 'Route created and sent to courier!',
     'route_dispatch.error_create': 'Error creating route',
     'route_dispatch.client_fallback': 'Client',
     'orders.reject_reason.holiday': 'Holiday / business hours',
     'orders.reject_reason.excess': 'Too many orders at the moment',
     'orders.reject_reason.out_of_stock': 'Items out of stock',
     'orders.reject_reason.other': 'Other (specify)',
     'employees.readonly_only': 'Read only',
     'employees.copying_link': 'Copying link…',
     'employees.generate_copy_link': 'Generate and copy link',
     'employees.copy_invite_link': 'Copy invite link',
     'employees.send_invite_whatsapp': 'Send invite via WhatsApp',
     'employees.sending_invite': 'Sending invite…',
     'employees.resend_invite': 'Resend invite',
     'employees.perm_module_denied': 'No permission to access: {name}',
     'employees.perm_editor_title': 'Permissions',
     'employees.perm_editor_subtitle': 'Per module — access and allowed actions',
     'employees.perm_actions': 'Actions',
     'employees.perm_access_module': 'Access module: {name}',
     'employees.perm_collapse_actions': 'Collapse actions',
     'employees.perm_expand_actions': 'Expand actions',
     'employees.perm_collapse': 'Collapse',
     'employees.perm_view_actions': 'View actions',
     'employees.perm_done': 'Done',
     'perm.mod.dashboard': 'Dashboard',
     'perm.mod.pdv': 'POS (dine-in and pickup)',
     'perm.mod.delivery': 'Delivery (dispatch)',
     'perm.mod.pedidos': 'Order management (legacy)',
     'perm.act.pdv.ver': 'View POS',
     'perm.act.pdv.aceitar': 'Accept order',
     'perm.act.pdv.cancelar': 'Cancel order',
     'perm.act.pdv.editar': 'Edit order',
     'perm.act.pdv.finalizar': 'Complete order',
     'perm.act.pdv.ver_historico': 'View history',
     'perm.act.pdv.pedido_interno': 'Paste / internal order',
     'perm.act.pdv.imprimir': 'Print / receipts',
     'perm.act.delivery.ver': 'View Delivery',
     'perm.act.delivery.aceitar': 'Accept order',
     'perm.act.delivery.cancelar': 'Cancel order',
     'perm.act.delivery.editar': 'Edit order',
     'perm.act.delivery.despachar': 'Dispatch / assign driver',
     'perm.act.delivery.finalizar': 'Complete order',
     'perm.act.delivery.ver_historico': 'View history',
     'perm.act.delivery.pedido_interno': 'Fast Order / internal order',
     'perm.act.delivery.imprimir': 'Print / receipts',
     'perm.mod.conversas': 'Conversations (WhatsApp)',
     'perm.mod.company': 'Menu / company',
     'perm.mod.agendamentos': 'Appointments',
     'perm.mod.motoboys': 'Delivery riders ranking',
     'perm.mod.manager': 'Manager / overview',
     'perm.mod.colaboradores': 'Employees',
     'perm.mod.clientes': 'Customers',
     'perm.mod.financeiro': 'Finance',
     'perm.mod.configuracoes': 'Settings',
     'perm.mod.license': 'License',
     'perm.mod.delivai_controle': 'DelivAI control (internal)',
     'perm.act.dashboard.ver': 'Access dashboard',
     'perm.act.pedidos.ver': 'View orders',
     'perm.act.pedidos.aceitar': 'Accept order',
     'perm.act.pedidos.cancelar': 'Cancel order',
     'perm.act.pedidos.editar': 'Edit order',
     'perm.act.pedidos.despachar': 'Dispatch / assign driver',
     'perm.act.pedidos.finalizar': 'Complete order',
     'perm.act.pedidos.ver_historico': 'View history',
     'perm.act.pedidos.pedido_interno': 'Internal order',
     'perm.act.pedidos.imprimir': 'Print / receipts',
     'perm.act.conversas.ver': 'View conversations',
     'perm.act.conversas.enviar_mensagens': 'Send messages',
     'perm.act.conversas.arquivar': 'Archive / manage',
     'perm.act.company.ver': 'View',
     'perm.act.company.editar_cardapio': 'Edit menu',
     'perm.act.company.editar_delivery': 'Edit delivery / neighborhoods',
     'perm.act.company.editar_dados_empresa': 'Edit company details',
     'perm.act.agendamentos.ver': 'View',
     'perm.act.agendamentos.criar': 'Create',
     'perm.act.agendamentos.editar': 'Edit',
     'perm.act.agendamentos.cancelar': 'Cancel',
     'perm.act.motoboys.ver': 'View ranking',
     'perm.act.motoboys.editar': 'Edit daily rates / ranking',
     'perm.act.manager.ver': 'View',
     'perm.act.colaboradores.ver': 'View list',
     'perm.act.colaboradores.criar_editar': 'Create and edit employees',
     'perm.act.colaboradores.editar_permissoes': 'Edit others\' permissions',
     'perm.act.clientes.ver': 'View',
     'perm.act.clientes.editar_clientes': 'Edit',
     'perm.act.financeiro.ver': 'View',
     'perm.act.financeiro.editar': 'Edit',
     'perm.act.configuracoes.ver': 'View',
     'perm.act.configuracoes.editar': 'Edit',
     'perm.act.license.ver': 'View license',
     'perm.act.delivai_controle.ver': 'Access internal panel',
     'perm.act.delivai_controle.editar': 'Licenses — edit terms, modules and pay_types',
     'perm.act.delivai_controle.criar': 'Evolution — create and manage instances',
     'perm.act.delivai_controle.editar_cardapio': 'Companies — edit companies registry',
     'perm.act.delivai_controle.editar_prompts': 'Prompts — edit bot templates',
     'perm.act.delivai_controle.editar_consumers': 'Customers — list and merge consumers',
     'perm.act.delivai_controle.ver_banco': 'Database — internal studio (tables and SQL)',
     'perm.act.delivai_controle.ver_logs': 'Logs — n8n executions in real time',
     'perm.act.delivai_controle.ver_observabilidade': 'Observability — metrics (Prometheus) and Loki',
     'perm.act.delivai_controle.editar_permissoes': 'Permissions — manage other developers',
     'perm.act.delivai_controle.editar_contratos': 'Contracts — review/edit drafts (B2B, consumer, employees, internal team, legal)',
     'perm.act.delivai_controle.publicar_contratos': 'Contracts — activate official version (master only)',
     'perm.act.delivai_controle.excluir_versoes_contratos': 'Contracts — delete versions (master only; moves to trash)',
     'identity_gate.welcome_prefix': 'Welcome to',
     'identity_gate.default_store': 'our store',
     'identity_gate.verify': 'Verify',
     'identity_gate.view_menu': 'View Menu',
     'consumer_verify.title_entry': 'Identify with your phone',
     'consumer_verify.title_checkout': 'Confirm to complete your order',
     'consumer_verify.subtitle_whatsapp': 'Enter a valid WhatsApp number',
     'consumer_verify.subtitle_checkout': 'For your security, confirm it is you before finishing.',
     'consumer_verify.your_phone': 'WhatsApp',
     'consumer_verify.select_country': 'Select country',
     'consumer_verify.continue': 'Continue',
     'consumer_verify.close': 'Close',
     'consumer_verify.invalid_phone': 'Enter a valid phone number with area code',
     'consumer_verify.channel_prompt': 'How should we send the code to {phone}?',
     'consumer_verify.send_whatsapp_code': 'Receive code on WhatsApp',
     'consumer_verify.retry_whatsapp_code': 'Try WhatsApp again',
     'consumer_verify.channel_or_email': 'or by email',
     'consumer_verify.sending_code': 'Sending code...',
     'consumer_verify.verifying_code': 'Verifying...',
     'consumer_verify.code_sent_whatsapp': 'Code sent on WhatsApp',
     'consumer_verify.code_sent_email': 'Code sent by email',
     'consumer_verify.code_prompt': 'Enter the 6-digit code sent via {channel}.',
     'consumer_verify.channel_whatsapp': 'WhatsApp',
     'consumer_verify.channel_email': 'email',
     'consumer_verify.confirm_code': 'Confirm code',
     'consumer_verify.resend_code': 'Resend code',
     'consumer_verify.change_phone': 'Change phone',
     'consumer_verify.whatsapp_unavailable': 'Store WhatsApp is unavailable. The manager was notified. Use email.',
     'consumer_verify.send_email_code': 'Send code by email',
     'consumer_verify.your_email': 'Your email',
     'consumer_verify.invalid_email': 'Enter a valid email',
     'consumer_verify.invalid_code': 'Enter the 6-digit code',
     'consumer_verify.verified': 'Identity confirmed!',
     'consumer_verify.verified_as': '{name}',
     'consumer_verify.verified_phone': '{phone}',
     'consumer_verify.chip_details_title': 'Your details',
     'consumer_verify.chip_details_aria': 'View customer details',
     'consumer_verify.chip_no_address': 'No address filled in yet',
     'consumer_verify.send_failed': 'Could not send the code',
     'consumer_verify.send_failed_manual':
       'Registration verification error. Please fill in your details manually.',
     'consumer_verify.fill_manually': 'Fill in details manually',
     'consumer_verify.verify_failed': 'Verification failed — try again',
     'consumer_verify.wrong_code': 'Incorrect code, please try again',
     'consumer_verify.code_expired': 'Code expired — request a new one',
     'consumer_verify.code_missing': 'No pending code — request a new one',
     'consumer_verify.rate_limited': 'Too many attempts — wait a bit and try again',
     'consumer_verify.confirm_checkout': 'Confirm your phone to complete the order',
     'consumer_verify.auth_methods': 'Identification methods',
     'consumer_verify.tab_whatsapp': 'WhatsApp',
     'consumer_verify.tab_password': 'Password',
     'consumer_verify.your_name': 'Your name',
     'consumer_verify.name_placeholder': 'How should we call you',
     'consumer_verify.your_password': 'Password',
     'consumer_verify.password_placeholder': 'At least 6 characters',
     'consumer_verify.confirm_password': 'Confirm password',
     'consumer_verify.confirm_password_placeholder': 'Type the password again',
     'consumer_verify.confirm_password_required': 'Confirm your password',
     'consumer_verify.password_mismatch': 'Passwords do not match',
     'consumer_verify.weak_password': 'Password must be at least 6 characters',
     'consumer_verify.login_password': 'Sign in with password',
     'consumer_verify.create_password_account': 'Create account with password',
     'consumer_verify.login_failed': 'Incorrect phone or password',
     'consumer_verify.register_failed': 'Could not create the account — try again',
     
     // Orders Screen
    'orders.title': 'POS',
    'orders.header_subtitle': "Dine-in and pickup — accept, prepare and track today's tickets.",
    'orders.paste_order': 'Paste',
    'orders.paste_order_title': 'Paste order (text or screenshot)',
    'orders.print_format_aria': 'Thermal receipt size',
    'orders.new_order': 'New Order',
    'orders.pending': 'Pending',
    'orders.in_progress': 'In Progress',
    'orders.completed': 'Completed',
    'orders.cancelled': 'Cancelled',
    
    // Conversations Screen
    'conversations.title': 'Conversations',
    'conversations.new_conversation': 'New Conversation',
    'conversations.search': 'Search conversations...',
    
    // Employees Screen
    'employees.title': 'Employees',
    'employees.add_employee': 'Add Employee',
    'employees.name': 'Name',
    'employees.role': 'Role',
    'employees.email': 'Email',
    'employees.phone': 'Phone',
    'employees.status': 'Status',
    'employees.search_placeholder': 'Search employee...',
    'employees.no_employees': 'No employees found',
    'employees.add_first': 'Add your first employee to get started',
    'employees.agenda_toggle': 'Professional schedule',
    'employees.agenda_hint': 'Enables this collaborator’s individual Google calendar.',
    'employees.agenda_area': 'Area / specialty',
    'employees.agenda_area_placeholder': 'E.g. manicure, cardiology',
    'employees.agenda_saved': 'Schedule updated',
    'employees.agenda_save_error': 'Could not update the schedule',
    
    // Orders Screen - Additional
    'orders.update': 'Update',
    'orders.search_placeholder': 'Search by order number or client...',
    'orders.status.in_analysis': 'In Analysis',
    'orders.status.in_production': 'In Production',
    'orders.status.ready_delivery': 'Ready for Delivery',
    'orders.status.in_route': 'En Route',
    'orders.delivery_deadline': 'Delivery time',
    'orders.select_deadline': 'Select a deadline',
    'orders.manage_deadlines': 'Manage deadlines',
    'orders.internal_order': 'Internal order',
    'orders.public_link': 'Public link:',
    'orders.copy_link': 'Copy link',
    'orders.store': 'Store',
    'orders.store_open': 'Open',
    'orders.store_closed': 'Closed',
    'orders.mode_auto': 'Auto',
    'orders.mode_manual': 'Manual',
    'orders.open_store_aria': 'Open store manually',
    'orders.close_store_aria': 'Close store manually',
    'orders.countdown.open_until': 'Open until',
    'orders.countdown.next_open': 'Next opening',
    'orders.countdown.next_close': 'Next closing',
    'orders.auto_accept_title': 'Auto accept / print',
    'orders.auto_accept_delivai_title': 'DelivAI',
    'orders.auto_accept_delidesk_title': 'DeliDesk',
    'orders.auto_accept_delivai_toggle': 'DelivAI auto-accept (site, WhatsApp, internal)',
    'orders.auto_accept_delidesk_toggle': 'DeliDesk auto-accept (marketplace capture)',
    'orders.fast_order_open': 'Fast Order — create order from text or iFood',
    'orders.auto_accept_active': 'Active',
    'orders.auto_accept_disabled': 'Disabled',
    'orders.auto_accept_when_open': 'Available when the store is open',
    'orders.auto_accept_toggle': 'Toggle automatic order acceptance',
    'orders.filters': 'Filters',
    'orders.clear': 'Clear',
    'orders.select_date': 'Select specific date',
    'orders.quick_dispatch': 'Quick dispatch',
    'orders.no_ready_dispatch': 'No orders ready for dispatch right now',
    'orders.deliverer': 'Deliverer',
    'orders.dispatch': 'Dispatch',
    'orders.dispatch_route': 'Dispatch route',
    'orders.filter_status': 'Status',
    'orders.filter_period': 'Period',
    'orders.filter_all': 'All',
    'orders.filter_today': 'Today',
    'orders.filter_week': 'This week',
    'orders.filter_month': 'This month',
    'orders.filter_selected_date': 'Selected date: {date}',
    'orders.sort_by': 'Sort by',
    'orders.sort_date': 'Date',
    'orders.sort_name': 'Name',
    'orders.sort_total': 'Amount',
    'orders.sort_order': 'Order',
    'orders.empty_title': 'No orders found',
    'orders.empty_filters': 'Try adjusting the filters to find orders.',
    'orders.empty_none': 'There are no orders yet.',
    'orders.orders_count_one': '{count} order',
    'orders.orders_count_many': '{count} orders',
    'orders.history_title': 'Order history',
    'orders.history_search': 'Search by number or client...',
    'orders.history_search_aria': 'Search order history by number or client',
    'orders.cash_summary': 'Cash summary',
    'orders.cash_entry_button': 'Entry',
    'orders.cash_entry_title': 'Cash entry',
    'orders.cash_entry_subtitle': 'Withdrawal, cash top-up, external change, expense or courier settlement.',
    'orders.cash_entry_type': 'Type',
    'orders.cash_entry_amount': 'Amount',
    'orders.cash_entry_method': 'Payment method',
    'orders.cash_entry_note': 'Note',
    'orders.cash_entry_save': 'Save',
    'orders.cash_entry_success': 'Entry saved',
    'orders.cash_entry_error': 'Could not save the entry',
    'orders.cash_entry_invalid_amount': 'Enter a valid amount greater than zero',
    'orders.cash_entry_no_session': 'No open cash session',
    'orders.cash_entry_insufficient_cash':
      'Insufficient cash balance. Add a cash top-up before a withdrawal.',
    'orders.cash_entry_opening_session': 'Opening cash register…',
    'orders.cash_entry_open_failed': 'Could not open the cash register for an entry.',
    'orders.cash_entry_type_sangria': 'Cash withdrawal',
    'orders.cash_entry_type_retirada': 'Withdrawal',
    'orders.cash_entry_type_aporte': 'Cash top-up',
    'orders.cash_summary_reforco': 'Top-up',
    'orders.cash_summary_reforco_short': 'top-up',
    'orders.cash_summary_troco_ext_short': 'ext. change',
    'orders.cash_summary_sangria_short': 'skim',
    'orders.cash_summary_retirada_short': 'withdrawal',
    'orders.cash_summary_despesa_short': 'expense',
    'orders.cash_summary_sales_short': 'sales',
    'orders.cash_summary_hint': 'PIX, card, cash and total are sales. Top-up is float/cash in (not a sale).',
    'orders.cash_entry_type_troco_externo': 'External change',
    'orders.cash_entry_type_acerto_motoboy': 'Courier settlement',
    'orders.cash_entry_type_despesa': 'Expense',
    'orders.cash_entry_method_pix': 'PIX',
    'orders.cash_entry_method_cartao': 'Card',
    'orders.cash_entry_method_dinheiro': 'Cash',
    'courier.settlement_title': 'Settlement with store',
    'courier.settlement_subtitle': 'Amounts to settle by payment method (today’s orders).',
    'orders.payment_card': 'Card',
    'orders.payment_cash': 'Cash',
    'orders.payment_cancelled': 'Cancelled',
    'orders.payment_pix': 'PIX',
    'orders.col_number': 'No.',
    'orders.col_datetime': 'Date/Time',
    'orders.col_client': 'Client',
    'orders.col_phone': 'Phone',
    'orders.col_address': 'Address',
    'orders.col_type': 'Type',
    'orders.col_status': 'Status',
    'orders.col_deliverer': 'Deliverer',
    'orders.col_actions': 'Actions',
    'orders.history_empty': 'No orders found in history',
         'company.edit': 'Edit',
     'company.save': 'Save',
     'company.cancel': 'Cancel',
     'company.update': 'Update',
     
     // Orders Screen - Additional Details
     'orders.order_number': 'Order #',
     'orders.client_not_informed_short': 'Client not informed',
     'orders.client': 'Client:',
     'orders.phone': 'Phone:',
     'orders.address': 'Address:',
     'orders.payment_method': 'Payment Method:',
     'orders.items': 'Items:',
     'orders.no_items': 'No items available',
     'orders.total': 'Total:',
     'orders.payment': 'Payment:',
     'orders.details': 'Details',
     'orders.print_agent.configure_title': 'Choose computer printer',
     'orders.print_agent.configure_aria': 'Configure local printer',
     'orders.print_agent.modal_title': 'Desktop print agent',
     'orders.print_agent.modal_intro':
       'Set the priority order of DeliDesk printers. The first checked online printer receives the slips.',
     'orders.print_agent.list_heading': 'Printer priority',
     'orders.print_agent.priority_hint':
       'Drag to reorder. Check the printers that should receive slips. Each row shows its DeliDesk PC.',
     'orders.print_agent.drag_priority': 'Drag priority',
     'orders.print_agent.pcs_heading': 'Linked PCs',
     'orders.print_agent.offline_banner':
       'The default printer is offline. Slips go to the next online printer in your list, if any.',
     'orders.print_agent.offline_title': 'Default printer offline',
     'orders.print_agent.offline_body':
       'The PC for the default printer is disconnected. Set {name} (online) as first priority?',
     'orders.print_agent.offline_confirm': 'Use this printer',
     'orders.print_agent.offline_keep': 'Keep as is',
     'orders.print_agent.offline_use_other': 'Use {name}',
    'orders.print_agent.vias': 'Copies',
    'orders.print_agent.vias_less': 'Fewer copies',
    'orders.print_agent.vias_more': 'More copies',
    'orders.print_agent.formats_hint': 'Each checked receipt prints its own number of copies.',
     'orders.print_agent.empty':
       'No DeliDesk PC linked yet. Install the app, open it and authorize this store; printers will show up here.',
     'orders.print_agent.saved': 'Default printer saved. Orders will be sent to DeliDesk.',
     'orders.print_agent.save': 'Save',
     'orders.print_agent.saving': 'Saving…',
     'orders.print_agent.cancel': 'Close',
     'orders.print_agent.close_aria': 'Close dialog',
     'orders.print_agent.demo_note':
       'Sample list for layout only; the real list will come from the installed desktop agent.',
     'orders.print_agent.download_windows': 'Download Windows app',
     'orders.print_agent.download_windows_aria': 'Download the Windows print agent installer',
     'orders.print_agent.download_unavailable_title':
       'The Windows installer will be available soon.',
     'orders.print_agent.download_resolving': 'Fetching the latest version…',
     'orders.print_agent.download_version_title': 'Latest version: {version}',
     'orders.print_agent.refresh': 'Refresh',
     'orders.print_agent.clear_queue': 'Clear queue',
     'orders.print_agent.clear_queue_with_count': 'Clear queue ({count})',
     'orders.print_agent.clear_queue_hint':
       'Cancels tickets waiting on the server — avoids printing everything when you open DeliDesk on the printer PC.',
     'orders.print_agent.clear_queue_confirm_title': 'Clear print queue?',
     'orders.print_agent.clear_queue_confirm_body':
       'This cancels {count} pending ticket(s) on the server. This cannot be undone.',
     'orders.print_agent.clear_queue_confirm': 'Clear queue',
     'orders.print_agent.clear_queue_ok': '{count} ticket(s) removed from the queue.',
     'orders.print_agent.clear_queue_error': 'Could not clear the print queue.',
     'orders.print_agent.status_online': 'Online',
     'orders.print_agent.status_offline': 'Offline',
     'orders.print_agent.revoke': 'Unlink',
     'orders.print_agent.revoked': 'PC unlinked.',
     'orders.print_agent.revoke_error': 'Could not unlink this PC.',
     'orders.print_agent.load_error': 'Could not load printers.',
     'orders.print_agent.save_error': 'Could not save the default printer.',
     'orders.print_agent.no_printers_on_pc': 'No printers reported on this PC yet.',
     'orders.print_agent.current_default': 'Current',
    'orders.print_agent.this_pc': 'This PC',
    'support.report': 'Report a problem on this screen',
    'support.hint.company': 'A problem with the company settings?',
    'support.hint.delivery': 'A problem with delivery or the order slip?',
    'support.hint.pdv': 'A problem at the POS?',
    'support.hint.sales': 'A problem in sales?',
    'support.hint.chats': 'A problem with WhatsApp or chats?',
    'support.hint.license': 'A problem with the license?',
    'support.hint.staff': 'A problem with staff?',
    'support.hint.clients': 'A problem with clients?',
    'support.hint.couriers': 'A problem with couriers?',
    'support.hint.dev': 'An improvement from the DelivAI team?',
    'support.hint.schedule': 'A problem with appointments?',
    'support.hint.fast_order': 'What did you send in Fast Order, and what did the slip show?',
    'support.hint.menu_read': 'What did the reading get wrong? (e.g. wrong item or price). The record id is included.',
    'support.hint.screen': 'A problem on this screen?',
    'support.report_send': 'Send',
    'support.report_close': 'Close',
    'support.report_sent': 'Received. We will look at it.',
    'support.report_image': 'Image {n}',
    'support.report_shot_fail': 'Could not read that screenshot.',
    'support.report_failed': 'Could not send. Try again.',
    'support.report_short': 'Write a bit more about the problem.',
    'license.group_offer': 'Group plan: R$ {price} per month ({stores} stores). After payment, each store uses the group license.',
    'license.group_pay': 'Buy the group plan',
    'license.dk_title': 'Dark Kitchen plan',
    'license.dk_subtitle': 'Joins stores in one group. The 1st and 2nd are included for R$ 300. From the 3rd on, each store adds R$ 100. Periods follow the Full Operation plan, with a lower price at 180 and 365 days.',
    'license.dk_days': '{days} days',
    'license.dk_save': 'save {amount}',
    'license.dk_extra': 'Each store from the 3rd adds {extra} in this period.',
    'license.dk_store_count': 'How many stores',
    'license.dk_same_days': 'Every store on this license gets the same {days} days.',
    'license.dk_after': 'After payment, this store can invite another company into the group. The contract stays one per company.',
    'license.dk_paid': 'Plan paid. You can invite another store from the company screen.',
    'kitchen.group_accept_title': 'Group license',
    'kitchen.group_accept_body': 'Joining the group makes the next license R$ {price} per month. Current licenses change only after payment. The contract is one per company.',

     'delidesk.authorize.header': 'Authorize',
     'delidesk.authorize.go_panel': 'Go to panel',
     'delidesk.authorize.title': 'Authorize this PC?',
     'delidesk.authorize.subtitle_before': 'Link DeliDesk to store',
     'delidesk.authorize.subtitle_dev_before': 'Link DeliDesk to account',
     'delidesk.authorize.machine': 'machine',
     'delidesk.authorize.warn_open_from_app':
       'Open this page from the DeliDesk app (Sign in with DelivAI). Without the state parameter authorization will not work.',
     'delidesk.authorize.approve': 'Authorize this PC',
     'delidesk.authorize.authorizing': 'Authorizing…',
     'delidesk.authorize.open_app': 'Open DeliDesk',
     'delidesk.authorize.success_banner': 'PC authorized successfully.',
     'delidesk.authorize.return_to_app':
       'You can close this tab and return to DeliDesk — the app finishes login by itself, with no browser popup.',
     'delidesk.authorize.footer_hint':
       'Only confirm if you started login in DeliDesk on this computer. The app then receives the link and can print receipts for this store.',
     'delidesk.authorize.footer_hint_collaborator':
       'DeliDesk is for the store owner or the DelivAI team. Ask the store owner to authorize this PC.',
     'delidesk.authorize.back_collaborator': 'Back to collaborator panel',
     'delidesk.authorize.dialog_title': 'Return to DeliDesk',
     'delidesk.authorize.dialog_body':
       'This PC was authorized. Return to DeliDesk — login finishes automatically.',
     'delidesk.authorize.dialog_os_note':
       'You do not need to open the app from the browser; DeliDesk detects authorization on its own.',
     'delidesk.authorize.error_missing_state':
       'Invalid link: missing state. Open from the Sign in button in DeliDesk.',
     'delidesk.authorize.error_no_redirect': 'Backend did not return redirect_uri',
     'delidesk.authorize.error_generic': 'Failed to authorize this PC',
     'delidesk.authorize.error_store_collaborator':
       'Store staff cannot authorize DeliDesk. Sign in with the store owner account or a DelivAI team user.',
     'delidesk.embed.gate_title': 'Connect via DeliDesk',
     'delidesk.embed.gate_body':
       'This panel screen only opens after Sign in with DelivAI in the desktop app. The website CNPJ form is not used here.',
     'delidesk.embed.gate_hint':
       'If the app is already linked, use Reload in DeliDesk or sign out and sign in with DelivAI again.',

     // Employees Screen - Additional
     'employees.department': 'Department:',
     'employees.salary': 'Salary:',
     'employees.admission': 'Admission:',
     'employees.edit': 'Edit',
     'employees.details': 'Details',
     'menu_display.edit_category.title': 'Edit Category',
     'menu_display.edit_category.subtitle': 'Change the category name and price types',
     'menu_display.edit_category.service_title': 'Edit service category',
     'menu_display.edit_category.service_subtitle':
       'Name, duration (minutes or days) and price for this category',
     'menu_display.edit_category.service_mode': 'How duration is measured',
     'menu_display.edit_category.mode_time': 'By minutes',
     'menu_display.edit_category.mode_day': 'By days',
     'menu_display.edit_category.service_price': 'Service price',
     'menu_display.edit_category.close': 'Close',
     'menu_display.edit_category.type': 'Category Type',
     'menu_display.edit_category.type_product': 'Product',
     'menu_display.edit_category.type_pizza': 'Pizza',
     'menu_display.edit_category.type_additional': 'Add-on',
     'menu_display.edit_category.name': 'Category Name',
     'menu_display.edit_category.name_placeholder': 'Category name',
     'menu_display.edit_category.price_types': 'Price Types',
     'menu_display.edit_category.add': 'Add',
     'menu_display.edit_category.price_type_placeholder': 'Price type {index}',
     'menu_display.edit_category.remove_price_type': 'Remove price type',
     'menu_display.edit_category.flavors': 'Flavors',
     'menu_display.edit_category.suggested_price': 'Suggested Price ($)',
     'menu_display.edit_category.suggested_price_placeholder': '$ 0.00',
     'menu_display.edit_category.additional_scope_title': 'Which product categories can use these add-ons',
     'menu_display.edit_category.additional_scope_hint': 'Mark the whole category or expand and choose specific products.',
     'menu_display.edit_category.no_product_categories': 'No product category.',
     'menu_display.edit_category.collapse': 'Collapse',
     'menu_display.edit_category.expand': 'Expand',
     'menu_display.edit_category.additional_categories': 'Add-on Categories',
     'menu_display.edit_category.no_additional_categories': 'No add-on category registered.',
     'menu_display.edit_category.additional_limit': 'Add-on Limit per Product',
     'menu_display.edit_category.additional_limit_hint': 'Maximum number of add-ons from this category per product. Leave empty for no limit.',
     'menu_display.edit_category.type_value_placeholder': 'Or type a value',
     'menu_display.edit_category.additional_minimum': 'Minimum Add-ons per Product',
     'menu_display.edit_category.additional_minimum_hint': 'Minimum number of add-ons from this category per product. Leave empty or 0 for no minimum.',
     'menu_display.edit_category.minimum_value_placeholder': 'Or type a value (0 = no minimum)',
     'menu_display.edit_category.save_changes': 'Save Changes',
     'menu_display.delete_image.title': 'Delete image',
     'menu_display.delete_image.subtitle': 'This action cannot be undone',
     'menu_display.delete_image.confirm_text': 'Are you sure you want to delete the image for product "{product}"?',
     'menu_display.delete_image.no_name': 'unnamed',
     'conversations.group': 'Group',
     'conversations.hidden_number': 'Hidden number (WhatsApp privacy)',
     'conversations.fallback_dash': '—',
     'conversations.tap_qr_to_connect': 'Tap the QR icon to connect and scan the session.',
     'conversations.instance_unavailable_title': 'Instance unavailable',
     'conversations.instance_unavailable_body': 'Conversations use the WhatsApp instance linked to your company CNPJ. Sign in with a user that has CNPJ set, or create an instance below.',
     'conversations.create_instance_title': 'Create WhatsApp instance (Evolution API)',
     'conversations.create_instance_body': 'A new instance will be created with your company CNPJ. Enter the WhatsApp number (with area code) to link it.',
     'conversations.phone_placeholder': '(11) 99999-9999',
     'conversations.creating_instance': 'Creating...',
     'conversations.create_instance': 'Create instance',
     'conversations.instance_created_refreshing': 'Instance created. Refreshing page...',
     'conversations.empty_archived': 'No archived conversations',
     'conversations.empty_found': 'No conversations found',
     'conversations.reopen_fast_order': 'Reopen Fast Order',
     'conversations.fast_order_tooltip': 'Fast Order — select messages to generate order',
     'conversations.fast_order': 'Fast Order',
     'conversations.client_state_loading': 'Loading client state…',
     'conversations.client_missing_hint': 'Register this customer in Delivery Clients (same WhatsApp phone). If already registered, check the 9th mobile digit — the system tries 10/11-digit variants.',
     'conversations.client_new_badge': 'New customer',
     'conversations.client_orders_badge': '{count} orders',
     'conversations.client_orders_badge_one': '1 order',
     'conversations.auto_support_active_hint': 'Auto support active for this contact. Click to pause.',
     'conversations.auto_support_paused_hint': 'Auto support paused for this contact. Click to reactivate.',
     'conversations.auto_support_individual': 'Individual Auto Support',
     'conversations.protected_photo_hint': 'WhatsApp-protected photo — load via Evolution API.',
     'conversations.open_fullscreen_zoom': 'Open fullscreen with zoom (inside panel)',
     'conversations.encrypted_video_hint': 'Encrypted video — load to play.',
     'conversations.audio_unrecognized': 'Audio — unrecognized format in this message',
     'conversations.use_as_field': 'Use as {field}',
     'conversations.message_placeholder': 'Type a message',
     'conversations.send': 'Send',
     'conversations.confirm_pick_title': 'Use customer message',
     'conversations.message_label': 'Message',
     'conversations.set_value_as': 'Set',
     'conversations.set_value_as_suffix': 'as',
     'conversations.connect_whatsapp': 'Connect WhatsApp',
     'conversations.scan_qr_hint': 'Scan with WhatsApp → Linked devices',
     'conversations.qr_alt': 'WhatsApp QR Code',
     'conversations.waiting_scan': 'Waiting scan',
     'conversations.qr_modal_auto_close': 'The modal closes automatically when connection is established.',
     'conversations.unarchive_chat': 'Unarchive chat',
     'conversations.archive_chat': 'Archive chat',
     'conversations.menu.mute_notifications': 'Mute notifications',
     'conversations.menu.pin_chat': 'Pin chat',
     'conversations.menu.mark_unread': 'Mark as unread',
     'conversations.menu.add_favorites': 'Add to favorites',
     'conversations.menu.add_list': 'Add to list',
     'conversations.menu.block_contact': 'Block contact',
     'conversations.menu.block': 'Block',
     'conversations.menu.clear_chat': 'Clear chat',
     'conversations.menu.delete_chat': 'Delete chat',
     'conversations.disconnect_confirm_title': 'Disconnect WhatsApp',
     'conversations.disconnect_confirm_message': 'Disconnect WhatsApp from this instance? You will need to scan the QR code again to reconnect.',
     'conversations.toast.instance_unavailable': 'WhatsApp instance unavailable.',
     'conversations.toast.last_message_not_found': 'Could not identify the latest message. Open the chat and try again.',
     'conversations.toast.invalid_contact_action': 'Invalid contact for this action.',
     'conversations.toast.archive_failed': 'Failed to update archive status.',
     'conversations.toast.chat_archived': 'Conversation archived.',
     'conversations.toast.chat_unarchived': 'Conversation unarchived.',
     'conversations.toast.mark_unread_failed': 'Failed to mark as unread.',
     'conversations.toast.marked_unread': 'Marked as unread.',
     'conversations.toast.individual_support_update_failed': 'Could not update individual support.',
     'conversations.toast.number_unavailable_send': 'Number unavailable — cannot send message.',
     'conversations.toast.fast_order_individual_only': 'Fast Order is available only for individual chats',
     'conversations.toast.contact_phone_unavailable_order': 'Contact phone unavailable to generate order',
     'conversations.toast.select_messages_generate_order': 'Select order messages and tap Generate order',
     'conversations.toast.contact_phone_unavailable': 'Contact phone unavailable',
     'conversations.toast.select_at_least_one_message': 'Select at least one message',
     'conversations.toast.messages_without_text': 'Selected messages have no usable text',
     'conversations.toast.order_interpreted_review': 'Order interpreted — review and finish',
     'conversations.toast.payment_not_identified': 'Could not identify payment method in this message',
     'conversations.toast.field_updated': '{field} updated',
     'conversations.error.media_unavailable': 'Cannot load this media (WhatsApp instance or message ID missing).',
     'conversations.error.evolution_media_not_returned': 'Evolution did not return media. Check getBase64FromMediaMessage endpoint on server.',
     'conversations.error.load_messages': 'Error loading messages',
     'conversations.error.cnpj_unavailable_send': 'CNPJ unavailable to send message.',
     'conversations.error.privacy_number_unavailable': 'Contact number unavailable (WhatsApp privacy). Cannot send until phone appears in list.',
     'conversations.error.send_message': 'Error sending message',
     'conversations.error.send_failed_support':
       'Check your WhatsApp connection or contact our support.',
     'conversations.error.generate_qr': 'Could not generate QR code',
     'conversations.error.get_qr': 'Error getting QR code',
     'conversations.error.disconnect_failed':
       'Could not disconnect. Check your WhatsApp connection or contact our support.',
     'conversations.error.create_instance': 'Error creating instance',
     'internal_order.toast.cep_not_found': 'ZIP code not found',
     'internal_order.toast.paste_ifood_text': 'Paste the iFood order text before interpreting.',
     'internal_order.toast.cnpj_not_found': 'CNPJ not found to contextualize order.',
     'internal_order.toast.ifood_parse_error': 'Error interpreting iFood order with AI.',
     'internal_order.toast.ai_unexpected_format': 'AI response in unexpected format.',
     'internal_order.toast.ifood_parsed_success': 'iFood order interpreted successfully. Review before applying to cart.',
     'internal_order.toast.ai_unexpected_error': 'Unexpected error calling AI.',
     'internal_order.toast.address_updated': 'Customer address updated in registry',
     'internal_order.toast.address_update_failed': 'Order created, but could not update registry',
     'internal_order.phone_lookup.error': 'Could not look up customer — try again',
     'internal_order.toast.confirm_customer_name': 'Confirm customer name (seems invalid or incomplete)',
     'internal_order.toast.missing_required_group': 'In item "{item}", choose at least {min} in "{group}" before checkout',
     'internal_order.toast.open_required_addon': 'Choose at least {min} in "{group}" for "{item}" — then continue checkout',
     'internal_order.toast.capture_loaded_review': 'iFood capture loaded — review fee, items and add-ons in checkout',
     'internal_order.toast.capture_order_not_found': 'Captured order not found. Go back to Orders and open it again.',
     'internal_order.toast.capture_order_updated': 'iFood order updated — approve it in Orders when ready',
     'internal_order.toast.double_click_message_name': 'Double-click a customer message to use it as name',
     'internal_order.confirm_name_hint': 'Confirm name — it seems invalid or incomplete',
     'internal_order.online_ifood_payment': 'Online/iFood Payment',
     'internal_order.toggle_delivery_fee_source': 'Click to switch: System ↔ iFood',
     'internal_order.fee_source_lowest': 'Lowest',
     'internal_order.fee_source_system': 'System',
     'internal_order.fee_source_ifood': 'iFood',
     'internal_order.charge_customer': 'Charge Customer',
     'internal_order.confirm_address_update_title': 'Update customer address?',
     'internal_order.confirm_address_update_message': 'This order address differs from the customer record. Save this new address for future orders?',
     'internal_order.current_registry_address': 'Current registry',
     'internal_order.current_order_address': 'This order address',
     'internal_order.confirm_address_update_hint': 'If this is just a one-time delivery somewhere else, choose "Do not save".',
     'internal_order.save_to_registry': 'Save to registry',
     'internal_order.do_not_save': 'Do not save',
     'company.toast.fill_pix_fields': 'Fill all PIX fields before saving.',
     'company.toast.pix_saved': 'PIX data saved successfully',
     'company.toast.pix_save_error': 'Error saving PIX data',
     'company.toast.refreshing_server_data': 'Refreshing server data...',
     'company.toast.refreshing_services_data': 'Refreshing services data from server...',
     'company.toast.service_category_created': 'Category "{name}" created successfully!',
     'company.toast.cnpj_not_found': 'CNPJ not found',
     'company.toast.save_data_error': 'Error saving data: {error}',
     'company.toast.saving_services_changes': 'Saving service changes...',
     'company.toast.services_changes_saved': 'Service changes saved successfully!',
     'company.toast.save_services_error': 'Error saving services: {error}',
     'company.toast.unknown_error': 'Unknown error',
     'company.toast.service_updated': 'Service updated successfully!',
     'company.toast.save_changes_error': 'Error saving changes',
     'company.toast.service_not_found': 'Service not found',
     'company.toast.invalid_categories_structure': 'Invalid categories structure',
     'company.toast.category_not_found_named': 'Category "{name}" not found',
     'company.toast.invalid_service_identifier': 'Invalid service identifier',
     'company.toast.service_deleted': 'Service "{name}" deleted successfully!',
     'company.toast.category_updated': 'Category updated successfully!',
     'company.toast.save_category_changes_error': 'Error saving category changes',
     'company.toast.no_category_found': 'No category found',
     'company.toast.category_not_found': 'Category not found',
     'company.toast.category_access_error': 'Error accessing category',
     'company.toast.category_deleted': 'Category "{name}" deleted successfully!',
     'company.toast.delete_category_error': 'Error deleting category: {error}',
     'company.toast.operation_cancelled': 'Operation cancelled',
     'company.toast.save_categories_order_error': 'Error saving category order',
     'company.toast.save_product_position_error': 'Error saving product position',
     'company.toast.no_menu_data_delete_product': 'No menu data to delete product',
     'company.toast.save_deletion_error': 'Error saving deletion to database',
     'company.toast.product_not_found': 'Product not found',
     'company.toast.no_menu_data_delete_category': 'No menu data to delete category',
     'company.toast.invalid_delivery_fee': 'Please enter a valid fee greater than zero',
     'company.toast.delivery_fees_saved': 'Delivery fees saved.',
     'company.toast.delivery_update_failed': 'Could not save delivery fees. Try again.',
     'company.toast.menu_images_saved': 'Menu images updated.',
     'company.confirm.delete_fee_title': 'Delete this fee?',
     'company.confirm.delete_fee_message': 'This removes the R$ {amount} fee and its {count} neighborhoods.',
     'company.toast.add_at_least_one_neighborhood': 'Please add at least one neighborhood',
     'company.toast.delivery_data_not_found': 'Error: delivery data not found',
     'company.toast.delivery_fee_already_exists': 'This fee already exists. Please choose another value.',
     'company.toast.category_name_required': 'Category name is required',
     'company.toast.at_least_one_price_type': 'At least one price type is required',
     'company.toast.pizza_flavors_range': 'For Pizza categories, each price type must have between 1 and 4 flavors',
     'company.niche.label': 'Business niche',
     'company.niche.delivery': 'Delivery',
     'company.niche.clinic': 'Clinic',
     'company.niche.tattoo': 'Tattoo',
     'company.niche.workshop': 'Workshop',
     'company.niche.digital': 'Digital',
     'company.niche.aesthetics': 'Aesthetics',
     'company.specialty.label': 'Specialties',
     'company.specialty.hint': 'You can pick several.',
     'company.specialty.pizza': 'Pizza',
     'company.specialty.sushi': 'Sushi',
     'company.specialty.hamburger': 'Burger',
     'company.specialty.pastel': 'Pastry',
     'company.specialty.acai': 'Açaí',
     'company.specialty.mexican': 'Mexican',
     'company.specialty.snackbar': 'Snack bar',
     'company.specialty.infoproducts': 'Info products',
     'company.specialty.marketplace': 'Marketplace',
     'company.specialty.marketplace_sales': 'Selling on marketplaces',
     'company.specialty.ecommerce': 'Online store',
     'company.specialty.subscription': 'Subscription',
     'company.specialty.affiliates': 'Affiliates',
     'company.specialty.digital_services': 'Digital services',
     'company.specialty.digital_service_label': 'What digital service?',
     'company.specialty.digital_service_placeholder': 'E.g. websites, consulting, design',
     'company.specialty.general_practice': 'General practice',
     'company.specialty.cardiology': 'Cardiology',
     'company.specialty.veterinary': 'Veterinary',
     'company.specialty.nutrition': 'Nutritionist',
     'company.specialty.dentistry': 'Dentistry',
     'company.specialty.dermatology': 'Dermatology',
     'company.specialty.psychology': 'Psychology',
     'company.specialty.physiotherapy': 'Physiotherapy',
     'company.specialty.ophthalmology': 'Ophthalmology',
     'company.specialty.gynecology': 'Gynecology',
     'company.specialty.pediatrics': 'Pediatrics',
     'company.specialty.orthopedics': 'Orthopedics',
     'company.specialty.aesthetics': 'Aesthetics',
     'company.specialty.endocrinology': 'Endocrinology',
     'company.specialty.neurology': 'Neurology',
     'company.specialty.psychiatry': 'Psychiatry',
     'company.specialty.speech_therapy': 'Speech therapy',
     'company.specialty.urology': 'Urology',
     'company.specialty.ent': 'ENT',
     'company.specialty.nails': 'Nails',
     'company.specialty.hair': 'Hair',
     'company.specialty.brows': 'Brows',
     'company.specialty.lashes': 'Lashes',
     'company.specialty.makeup': 'Makeup',
     'company.specialty.hair_removal': 'Hair removal',
     'company.specialty.facial': 'Facial aesthetics',
     'company.specialty.body': 'Body aesthetics',
     'company.specialty.massage': 'Massage',
     'company.specialty.micropigmentation': 'Micropigmentation',
     'company.specialty.harmonization': 'Facial harmonization',
     'company.specialty.aesthetic_surgery': 'Aesthetic surgery',
     'company.specialty.tanning': 'Tanning',
     'dev.cardapio.save_failed': 'Could not save. Please try again shortly.',
     'dev.cardapio.load_failed': 'Could not load the company. Please try again.',
     'company.toast.no_menu_data_add_category': 'No menu data to add category',
     'company.toast.category_name_exists': 'A category with this name already exists',
     'company.toast.additional_categories_missing': 'Some selected add-on categories do not exist',
     'company.toast.no_menu_data_add_product': 'No menu data to add product',
     'company.toast.product_added_edit_name': 'New product added! Start by editing the name.',
     'company.toast.fix_validation_errors': 'Please fix validation errors before saving.',
     'company.toast.no_changes_detected': 'No changes were detected.',
     'company.toast.payment_methods_updated': 'Payment methods updated successfully',
     'company.toast.payment_methods_update_error': 'Error updating payment methods',
     'company.toast.payment_text_updated': 'Payment text updated successfully',
     'company.toast.payment_text_update_error': 'Error updating payment text',
     'company.toast.public_order_types_updated': 'Public page order types updated',
     'company.toast.public_order_types_update_error': 'Error updating public order types: {error}',
     'company.toast.menu_image_replaced': 'Image replaced successfully.',
     'company.toast.process_image_error': 'Error processing image.',
     'company.toast.cnpj_unavailable_delete_image': 'CNPJ unavailable to delete image.',
     'company.toast.menu_image_deleted': 'Image deleted successfully.',
     'company.toast.delete_image_error': 'Error deleting image.',
   },
  es: {
    ...authTranslations.es,
    ...miscTranslations.es,
    ...devTranslations.es,
    ...contractTranslations.es,
    // Sidebar
    'sidebar.orders': 'PDV',
    'sidebar.conversations': 'Conversaciones',
    'sidebar.company': 'Empresa',
    'sidebar.employees': 'Empleados',
    'sidebar.delivery': 'Delivery',
    'sidebar.delivery_title': 'Delivery — despacho y Fast Order',
    'sidebar.motoboys': 'Motoboys',
    'sidebar.motoboys_title': 'Ranking de repartidores',
    'sidebar.clients': 'Clientes',
    'sidebar.clients_title': 'Clientes (pedidos y citas)',
    'sidebar.schedule': 'Agenda',
    'sidebar.manager': 'Ventas',
    'sidebar.managerTitle': 'Resumen de ventas e informes',
    'sidebar.license': 'Licencia',
    'sidebar.logout': 'Cerrar Sesión',
    'sidebar.language': 'Idioma',
    'sidebar.section_main': 'Principal',
    'sidebar.section_orders': 'Pedidos',
    'sidebar.section_store': 'Tienda',
    'pdv.title': 'PDV',
    'pdv.mode_mesa': 'Mesa',
    'pdv.mode_retirada': 'Retiro',
    'pdv.mode_toggle_aria': 'Alternar entre mesa y retiro',
    'pdv.catalog_all_categories': 'Todas',
    'pdv.catalog_categories': 'Categorías del menú',
    'pdv.catalog_other': 'Otros',
    'pdv.print_error': 'Pedido lanzado, pero la impresión falló. Revisa la impresora.',
    'pdv.sidebar_open_count': '{count} abiertos',
    'pdv.open_table': 'Abrir mesa',
    'pdv.new_pickup': 'Nuevo retiro',
    'pdv.no_open_pickups': 'Ningún retiro abierto.',
    'pdv.open_table_title': 'Abrir mesa',
    'pdv.open_table_hint': 'Escribe el número. Sin cadastro — usa el que quieras.',
    'pdv.table_number': 'Número de mesa',
    'pdv.table_number_placeholder': 'Ej.: 5',
    'pdv.open_table_confirm': 'Abrir',
    'pdv.table_invalid': 'Informa el número de mesa.',
    'pdv.no_open_tables': 'Ninguna mesa abierta.',
    'pdv.past_day_hint': 'Día anterior: cierra las comandas que quedaron abiertas.',
    'pdv.waiting_digital': 'Digital',
    'pdv.waiting_digital_hint': 'Aún no llegó',
    'pdv.waiting_pickup': 'Retiros abiertos',
    'pdv.assign_table_title': 'Abrir mesa de este pedido',
    'pdv.assign_table_hint': 'Cuando se siente, escribe el número de mesa.',
    'pdv.back_to_pickup': 'Retiros',
    'pdv.history_dock_aria': 'Historial de comandas: toca para abrir o cerrar la lista',
    'pdv.open_tables': 'Mesas abiertas',
    'pdv.table_n': 'Mesa {n}',
    'pdv.back_to_tables': 'Mesas',
    'pdv.close_table': 'Cerrar mesa',
    'pdv.close_table_title': 'Cerrar mesa',
    'pdv.close_table_msg': 'Finaliza la comanda de esta mesa.',
    'pdv.close_table_hint': 'Toca la forma para lanzar el resto, o escribe los valores. Puedes dividir.',
    'pdv.charge_selected': 'Cobrar lo seleccionado',
    'pdv.charge_selected_hint': 'Elige cómo entra este valor. La mesa sigue abierta.',
    'pdv.charge_qty': 'Cobrar {n} de {total}',
    'pdv.split_open': 'Dividir',
    'pdv.split_title': 'Dividir este ítem',
    'pdv.split_parts': 'Dividir lo que falta en',
    'pdv.split_amount': 'O cobrar este valor',
    'pdv.split_hint': 'Si escribes un valor, ese entra en este cobro. Si lo dejas vacío, divide lo que aún falta.',
    'pdv.split_apply': 'Usar este valor',
    'pdv.split_now': 'En este cobro {amount}',
    'pdv.charge_half': '1/2',
    'pdv.charge_third': '1/3',
    'pdv.charge_custom': 'Valor de este cobro',
    'pdv.select_item': 'Seleccionar ítem',
    'pdv.paid_open': '{paid} pago · {open} abierto',
    'pdv.paid_amount_left': 'pago {paid} · falta {left}',
    'pdv.total_label': 'Total',
    'pdv.paid_label': 'Pagado',
    'pdv.due_label': 'Falta',
    'pdv.charged': 'Cobro registrado.',
    'pdv.paid_keep': 'Este ítem ya tiene un valor pagado.',
    'pdv.finalize_hint': 'Toca la forma para lanzar el resto, o escribe los valores. Puedes dividir.',
    'pdv.print_customer_title': 'Imprimir cuenta',
    'pdv.print_customer_hint': '¿Cuántas personas van a dividir? La cuenta sale con el valor por persona.',
    'pdv.split_people': 'Personas',
    'pdv.split_people_aria': 'Número de personas para dividir la cuenta',
    'pdv.increase_people': 'Aumentar personas',
    'pdv.decrease_people': 'Disminuir personas',
    'pdv.per_person': '{amount} por persona',
    'pdv.add_payment': 'Otra forma',
    'pdv.pay_click_method': 'Lanzar el resto en {method}',
    'pdv.remove_payment': 'Quitar pago',
    'pdv.pay_amount': 'Valor',
    'pdv.pay_remaining': 'Falta {amount}',
    'pdv.pay_over': 'Sobró {amount}',
    'pdv.pay_ok': 'El pago cierra el total',
    'pdv.pay_need_cover': 'La suma de las formas debe cerrar el total.',
    'pdv.pay_debit': 'Tarjeta débito',
    'pdv.pay_credit': 'Tarjeta crédito',
    'pdv.settle_total': 'Total',
    'pdv.catalog_empty': 'Ningún ítem en el menú.',
    'pdv.search_catalog': 'Buscar en el menú',
    'pdv.no_photo': 'Sin foto',
    'pdv.unnamed_product': 'Ítem',
    'menu.channel_delivery': 'Delivery',
    'menu.channel_mesa': 'Mesa',
    'menu.channel_retirada': 'Retiro',
    'menu.channel_product': 'Menús del producto',
    'menu.channel_category': 'Menús de la categoría',
    'pdv.cart_title': 'Comanda',
    'pdv.cart_empty': 'Toca un ítem para agregar a la comanda.',
    'pdv.client_name': 'Nombre',
    'pdv.client_name_placeholder': 'Nombre (opcional)',
    'pdv.payment': 'Pago',
    'pdv.pay_cash': 'Efectivo',
    'pdv.pay_pix': 'PIX',
    'pdv.pay_card': 'Tarjeta',
    'pdv.launch': 'Lanzar',
    'pdv.launching': 'Lanzando…',
    'pdv.launched': 'Pedido lanzado e impreso.',
    'pdv.launch_error': 'No se pudo lanzar. Inténtalo de nuevo.',
    'pdv.item_added': 'Ítem agregado.',
    'pdv.pick_size': 'Elige el tamaño',
    'pdv.print_format': 'Tipo de cupón',
    'pdv.print_economic': 'Cupón 80mm (Económico)',
    'pdv.print_large': 'Cupón 80mm (Grande)',
    'pdv.print_pro': 'Cupón 80mm (PRO)',
    'pdv.print_dispatch': 'Cupón 80mm (Despacho)',
    'pdv.finalize': 'Finalizar',
    'pdv.finalize_title': 'Finalizar pedido',
    'pdv.finalize_msg': 'Marca este pedido como finalizado.',
    'pdv.finalized': 'Pedido finalizado.',
    'pdv.finalize_error': 'No se pudo finalizar.',
    'pdv.cancel': 'Cancelar pedido',
    'pdv.cancel_title': '¿Cancelar este pedido?',
    'pdv.cancel_msg': 'El pedido sale de producción. Los demás lanzamientos siguen normales.',
    'pdv.cancelled': 'Pedido cancelado.',
    'pdv.cancel_error': 'No se pudo cancelar.',
    'pdv.cancel_order_aria': 'Cancelar pedido de {name}',
    'pdv.other_tickets': 'Otras comandas de esta mesa',
    'pdv.ticket_n': 'Comanda #{n}',
    'pdv.open_ticket_count': '{count} comandas',
    'pdv.walk_in': 'Mostrador',
    'pdv.back_to_categories': 'Categorías',
    'pdv.category_item_count': '{count} ítems',
    'pdv.add_to_comanda': 'Agregar a la comanda',
    'pdv.save_item': 'Guardar ítem',
    'pdv.edit_item': 'Editar',
    'pdv.remove_item': 'Quitar',
    'pdv.increase_qty': 'Aumentar cantidad',
    'pdv.decrease_qty': 'Disminuir cantidad',
    'pdv.pending_badge': 'Nuevo',
    'pdv.pending_count': '{count} ítem(s) para lanzar',
    'pdv.not_launched': 'Aún no lanzado',
    'pdv.launch_print': 'Lanzar e imprimir',
    'pdv.launch_charge': 'Lanzar, cobrar y cerrar',
    'pdv.confirm_launch_title': 'Confirmar lanzamiento',
    'pdv.confirm_launch_msg': 'Imprime en cocina solo lo que aún no se lanzó. Revisa la comanda antes.',
    'pdv.confirm_launch_charge_title': 'Cobrar y cerrar',
    'pdv.confirm_launch_charge_msg': 'Imprime cocina (ítems nuevos), la cuenta y cierra con {method}.',
    'pdv.updated_no_print': 'Comanda actualizada.',
    'pdv.item_updated': 'Ítem actualizado.',
    'pdv.item_removed': 'Ítem quitado.',
    'pdv.print_customer': 'Imprimir cuenta',
    'pdv.print_ask_hint': 'No imprime nada si no lo marcas.',
    'pdv.print_ask_new': 'Imprimir los ítems nuevos',
    'pdv.print_ask_bill': 'Imprimir el resumen',
    'pdv.print_customer_ok': 'Cuenta enviada a la impresora.',
    'pdv.print_customer_need_order': 'Lanza la comanda antes de imprimir la cuenta.',
    'pdv.leave_pending_title': 'Ítems sin lanzar',
    'pdv.leave_pending_msg': 'Hay ítems que aún no se lanzaron. Si sales, se perderán.',
    'pdv.leave_pending_confirm': 'Salir igual',
    'pdv.close_table_pending': 'Hay ítems sin lanzar. Lanza o quítalos antes de cerrar la mesa.',
    'pdv.edit_not_found': 'No encontré este ítem en el menú para editar.',
    'sidebar.section_control': 'Control',
    'sidebar.module_unavailable': 'Módulo no disponible',
    'sidebar.schedule_unavailable': 'Agenda no disponible',
    'orders.print_agent.this_pc': 'Este PC',
    'orders.print_agent.modal_intro':
      'Defina el orden de prioridad de las impresoras del DeliDesk. La primera marcada y online recibe los cupones.',
    'orders.print_agent.list_heading': 'Prioridad de impresoras',
    'orders.print_agent.priority_hint':
      'Arrastre para ordenar. Marque las que deben recibir cupones. Al lado aparece el PC del DeliDesk.',
    'orders.print_agent.drag_priority': 'Arrastrar prioridad',
    'orders.print_agent.pcs_heading': 'PCs vinculados',
    'orders.print_agent.offline_banner':
      'La impresora predeterminada está offline. Los cupones van a la siguiente online de su lista, si hay.',
    'orders.print_agent.offline_title': 'Impresora predeterminada offline',
    'orders.print_agent.offline_body':
      'El PC de la impresora predeterminada está desconectado. ¿Definir {name} (online) como primera prioridad?',
    'orders.print_agent.offline_confirm': 'Usar esta impresora',
    'orders.print_agent.offline_keep': 'Dejar como está',
    'orders.print_agent.offline_use_other': 'Usar {name}',
    'support.report': 'Reportar un problema en esta pantalla',
    'support.hint.company': '¿Algún problema en la configuración de la empresa?',
    'support.hint.delivery': '¿Algún problema en el delivery o en la comanda?',
    'support.hint.pdv': '¿Algún problema en el PDV?',
    'support.hint.sales': '¿Algún problema en las ventas?',
    'support.hint.chats': '¿Algún problema en WhatsApp o en los chats?',
    'support.hint.license': '¿Algún problema en la licencia?',
    'support.hint.staff': '¿Algún problema con el equipo?',
    'support.hint.clients': '¿Algún problema con los clientes?',
    'support.hint.couriers': '¿Algún problema con los repartidores?',
    'support.hint.dev': '¿Alguna mejora del equipo DelivAI?',
    'support.hint.schedule': '¿Algún problema en las citas?',
    'support.hint.fast_order': '¿Qué envió en Fast Order y qué mostró la comanda?',
    'support.hint.menu_read': '¿Qué falló en la lectura? (ej.: ítem o precio). El registro ya va incluido.',
    'support.hint.screen': '¿Algún problema en esta pantalla?',
    'support.report_send': 'Enviar',
    'support.report_close': 'Cerrar',
    'support.report_sent': 'Recibido. Lo vamos a mirar.',
    'support.report_image': 'Imagen {n}',
    'support.report_shot_fail': 'No se pudo leer esa captura.',
    'support.report_failed': 'No se pudo enviar. Intente de nuevo.',
    'support.report_short': 'Escriba un poco más sobre el problema.',
    'license.group_offer': 'Plan del grupo: R$ {price} al mes ({stores} tiendas). Al pagar, la licencia de cada tienda pasa a ser la del grupo.',
    'license.group_pay': 'Contratar plan del grupo',
    'license.dk_title': 'Plan Dark Kitchen',
    'license.dk_subtitle': 'Junta las tiendas en el mismo grupo. La 1ª y la 2ª entran por R$ 300. Desde la 3ª, cada tienda suma R$ 100. Los plazos siguen el Plan Operación Completa, con un valor menor en 180 y 365 días.',
    'license.dk_days': '{days} días',
    'license.dk_save': 'ahorro {amount}',
    'license.dk_extra': 'Cada tienda desde la 3ª suma {extra} en este plazo.',
    'license.dk_store_count': 'Cuántas tiendas',
    'license.dk_same_days': 'Todas las tiendas de esta licencia quedan con los mismos {days} días.',
    'license.dk_after': 'Después del pago, esta tienda puede invitar a otra empresa al grupo. El contrato sigue siendo uno por empresa.',
    'license.dk_paid': 'Plan pagado. Ya puede invitar otra tienda en la pantalla de la empresa.',
    'kitchen.group_accept_title': 'Licencia del grupo',
    'kitchen.group_accept_body': 'Al entrar al grupo, la próxima licencia pasa a ser R$ {price} al mes. Las licencias actuales solo cambian después del pago. El contrato es uno por empresa.',
    'sidebar.open_menu': 'Abrir menú',
    'sidebar.company_logo': 'Logo de la empresa',

    'common.open': 'Abierto',
    'common.closed': 'Cerrado',
    'common.loading': 'Cargando…',
    'common.until': 'hasta',
    'common.cancel': 'Cancelar',
    'common.confirm': 'Confirmar',
    'delivery.loading': 'Cargando datos de delivery…',
    'delivery.error.title': 'Error al cargar',
    'delivery.error.retry': 'Intentar de nuevo',
    'delivery.error.no_cnpj': 'CNPJ de la tienda no disponible. Inicie sesión de nuevo.',
    'delivery.error.customer_unavailable': 'Espere a que carguen los datos de la tienda o inténtelo de nuevo.',
    'delivery.error.load_failed': 'No se pudieron cargar los datos de delivery.',
    'delivery.dispatch.module_name': 'Delivery',
    'delivery.dispatch.title': 'Delivery',
    'delivery.dispatch.subtitle': 'Análisis, producción y despacho rápido — chips, repartidor y ruta.',
    'delivery.dispatch.search_label': 'Buscar comanda',
    'delivery.dispatch.search_placeholder': 'Teléfono o nº de comanda',
    'delivery.dispatch.search_clear': 'Limpiar búsqueda',
    'delivery.dispatch.filter_store': 'Tienda de la comanda',
    'delivery.dispatch.filter_all_stores': 'Todas las tiendas',
    'delivery.dispatch.filter_empty': 'Ninguna comanda con ese filtro.',
    'delivery.dispatch.config_fees': 'Tarifas y barrios',
    'delivery.dispatch.col_analysis': 'En análisis',
    'delivery.dispatch.empty_analysis': 'Ningún delivery en análisis.',
    'delivery.dispatch.accept': 'Aceptar',
    'delivery.dispatch.review': 'Revisar',
    'delivery.dispatch.locator': 'Localizador',
    'delivery.dispatch.demo_title': 'Pedido de demostración iFood',
    'delivery.dispatch.demo_msg': 'No es un pedido real. Revise la tarifa y los ítems y apruebe para simular el flujo.',
    'delivery.dispatch.details': 'Detalles',
    'delivery.dispatch.edit': 'Alterar pedido',
    'delivery.dispatch.cancel_reason_other': 'Describa el motivo',
    'delivery.dispatch.open_details': 'Ver detalles del pedido #{n}',
    'delivery.dispatch.close_details': 'Cerrar detalles',
    'delivery.dispatch.print': 'Imprimir',
    'delivery.dispatch.print_ok': 'Comanda enviada a la impresora.',
    'delivery.dispatch.print_error': 'No se pudo imprimir el pedido.',
    'delivery.dispatch.items': 'Ítems',
    'delivery.dispatch.no_items': 'Ningún ítem listado.',
    'delivery.dispatch.col_production': 'En producción',
    'delivery.dispatch.col_route': 'En ruta',
    'delivery.dispatch.empty_production': 'Ningún delivery en producción.',
    'delivery.dispatch.empty_route': 'Ningún delivery en ruta.',
    'delivery.dispatch.order_n': 'Pedido #{n}',
    'delivery.dispatch.courier': 'Repartidor',
    'delivery.dispatch.pick_courier': 'Seleccione el repartidor',
    'delivery.dispatch.no_couriers': 'Ningún repartidor activo',
    'delivery.dispatch.dispatch': 'Despachar',
    'delivery.dispatch.route': 'Montar ruta',
    'delivery.dispatch.finish': 'Confirmar entrega',
    'delivery.dispatch.reject': 'Rechazar',
    'delivery.dispatch.finish_confirm_title': '¿Finalizar entrega?',
    'delivery.dispatch.finish_confirm_msg': 'La comanda se marcará como finalizada en este panel aunque el motoboy aún no la haya concluido en la app.',
    'delivery.dispatch.select_to_dispatch': 'Marque la casilla para elegir el repartidor y despachar.',
    'delivery.dispatch.select_order': 'Seleccionar pedido #{n}',
    'delivery.dispatch.select_all': 'Seleccionar todos',
    'delivery.dispatch.unselect_all': 'Limpiar selección',
    'delivery.dispatch.selected_count': '{count} seleccionado(s)',
    'delivery.dispatch.drag_handle': 'Arrastrar pedido',
    'delivery.dispatch.select_or_drag_hint': 'Marque para agrupar o arrastre la tarjeta hasta un repartidor.',
    'delivery.dispatch.drag_selected_hint': 'Arrastre la tarjeta — los seleccionados van juntos.',
    'delivery.dispatch.drag_panel_hint': 'Arrastre las tarjetas a un repartidor a la derecha.',
    'delivery.dispatch.couriers_panel': 'Repartidores',
    'delivery.dispatch.couriers_panel_hint': 'Suelte en Despachar o Montar ruta.',
    'delivery.dispatch.drag_group_overlay': '{count} pedido(s)',
    'delivery.dispatch.drag_drop_on_courier': 'Suelte en Despachar o Montar ruta',
    'delivery.dispatch.confirm_dispatch_title': 'Confirmar despacho',
    'delivery.dispatch.confirm_route_title': 'Montar ruta',
    'delivery.dispatch.confirm_dispatch_msg': '¿Despachar {count} pedido(s) con {courier}?',
    'delivery.dispatch.confirm_route_msg': '¿Montar ruta de {count} pedido(s) con {courier}?',
    'delivery.dispatch.load_error': 'No se pudieron cargar las entregas.',
    'delivery.detail.street': 'Calle',
    'delivery.detail.address': 'Dirección',
    'delivery.detail.wait_min': 'Plazo (min)',
    'delivery.detail.unassigned': 'Sin repartidor',
    'delivery.detail.assign_courier': 'Asignar repartidor',
    'delivery.detail.no_address': 'Dirección no informada',
    'delivery.detail.minutes': '{n} min',
    'delivery.detail.discard_edit': 'Descartar cambios',
    'delivery.detail.unit_price': 'Precio unitario',
    'delivery.detail.eta_range': 'Plazo hasta {from}–{to}',
    'delivery.dispatch.prev_day': 'Día anterior',
    'delivery.dispatch.next_day': 'Día siguiente',
    'delivery.dispatch.pick_day': 'Elegir el día',
    'delivery.dispatch.action_error': 'No se pudo actualizar el pedido.',
    'delivery.dispatch.dispatched': 'Pedido despachado.',
    'delivery.dispatch.marked_route': 'Pedido marcado en ruta.',
    'delivery.dispatch.finished': 'Entrega finalizada.',
    'delivery.dispatch.mark_paid': 'Marcar como pagado',
    'delivery.dispatch.mark_paid_ok': 'Pedido marcado como pagado. El repartidor ya ve que no debe cobrar.',
    'delivery.dispatch.pay_title': 'Registrar pago',
    'delivery.dispatch.pay_hint': 'El cliente pagó después del checkout. Indique cómo entró el valor — el repartidor ve en la bolsa si ya está pagado.',
    'delivery.dispatch.pay_confirm': 'Confirmar pago',
    'delivery.dispatch.wait_courier': 'En ruta — la tienda también puede confirmar la entrega.',
    'delivery.dispatch.eta_label': 'Plazo hasta {time}',
    'delivery.dispatch.fee_label': 'Tarifa {fee}',
    'delivery.dispatch.my_fees_title': 'Tus tarifas del día',
    'delivery.dispatch.my_deliveries_count': '{count} entrega(s) en curso',
    'entregas.ifood_locator_label': 'Localizador iFood',
    'entregas.copy_locator': 'Copiar',
    'entregas.ifood_call_with_locator': 'con localizador',
    'entregas.toast.locator_copied': 'Localizador copiado',
    'entregas.toast.locator_copy_error': 'No se pudo copiar el localizador',
    'orders.print_choice_title': '¿Qué desea imprimir?',
    'orders.print_choice_comanda': 'Pedido (comanda)',
    'orders.print_choice_qr': 'Código QR (bolsa)',
    'orders.print_choice_hint': 'Cada opción imprime en un ticket aparte. Puede tocar las dos.',
    'orders.print_choice_close': 'Cerrar',
    'orders.toast.print_qr_ok': 'Código QR enviado a la impresora',
    'orders.qr_offer_title': '¿Imprimir código QR en la bolsa?',
    'orders.qr_offer_body':
      'El cliente escanea, confirma sus datos y empieza a pedir por DelivAI — fuera de iFood.',
    'orders.qr_offer_yes': 'Sí, imprimir QR',
    'orders.qr_offer_no': 'Ahora no',
    'order_claim.headline': '¡Felicidades! Aquí hay un regalo para ti',
    'order_claim.subtitle': 'Confirma tus datos para continuar. Nada sensible aparece antes.',
    'order_claim.loading': 'Cargando…',
    'order_claim.pick_name': '¿Cuál es tu nombre?',
    'order_claim.pick_address': '¿Cuál es tu dirección?',
    'order_claim.continue': 'Continuar',
    'order_claim.phone_label': 'Tu teléfono (WhatsApp)',
    'order_claim.phone_placeholder': '(11) 99999-9999',
    'order_claim.submit_phone': 'Guardar y canjear',
    'order_claim.done_title': '¡Listo!',
    'order_claim.done_body': 'Tu cadastro quedó vinculado. En los próximos pedidos usamos tus datos.',
    'order_claim.prize_title': 'Tu premio',
    'order_claim.prize_min': 'Válido en pedidos desde {min}',
    'order_claim.rule_first_order': 'Válido en el primer pedido en la tienda',
    'order_claim.rule_subsequent': 'Válido a partir del segundo pedido',
    'order_claim.rule_any': 'Válido en el próximo pedido elegible',
    'order_claim.coupon_code': 'Código del cupón',
    'order_claim.coupon_rules': 'Reglas: {rules}',
    'order_claim.error_invalid_link': 'Enlace inválido',
    'order_claim.error_expired': 'Este enlace expiró',
    'order_claim.error_load': 'No se pudo abrir este regalo',
    'order_claim.error_select_both': 'Elige el nombre y la dirección',
    'order_claim.error_challenge': 'Los datos no coinciden — inténtalo de nuevo',
    'order_claim.error_phone': 'No se pudo guardar el teléfono',
    'orders.channel_all': 'Todos',
    'orders.channel_store': 'Tienda',
    'orders.channel_delivery': 'Delivery',
    'orders.channel_hint': 'El delivery operativo está en Entregas.',
    'orders.channel_go_delivery': 'Abrir Entregas',
    'orders.type_mesa': 'Mesa',
    'orders.type_retirada': 'Retiro',
    'orders.type_delivery': 'Delivery',
    'orders.empty_mesa': 'Ningún pedido de mesa en curso.',
    'orders.empty_retirada': 'Ningún pedido para retiro en curso.',
    'orders.empty_delivery_type': 'Ningún delivery en curso.',
    'orders.status.ready_short': 'Listo',
    'orders.status.completed': 'Finalizado',
    'orders.status.cancelled': 'Cancelado',
    'orders.select_to_show_courier': 'Marque la casilla para elegir el repartidor.',
    'orders.select_order_dispatch': 'Seleccionar pedido #{n}',
    'orders.pick_deliverer': 'Seleccione el repartidor',
    'orders.select_all': 'Seleccionar todos',
    'orders.unselect_all': 'Limpiar selección',
    'orders.finish': 'Finalizar',
    'orders.print_ticket': 'Imprimir comanda',
    'orders.print_order_aria': 'Imprimir pedido #{n}',
    'orders.edit': 'Editar',
    'orders.phone_not_informed': 'No informado',
    'common.got_it': 'Entendido',
    'common.delete': 'Eliminar',
    'common.back': 'Volver',

    'google.connect': 'Conectar con Google',
    'google.disconnect': 'Desconectar Google',
    'google.popup_blocked_title': 'Ventana de Google bloqueada',
    'google.popup_blocked_message':
      'No se pudo abrir la ventana de autorización. Toque Continuar para conectar Google en esta misma pantalla.',
    'google.popup_blocked_continue': 'Continuar',
    'google.config_error_title': 'Conexión no disponible',
    'google.config_error_message':
      'No se pudo iniciar la conexión con Google. Verifique la configuración o contacte al soporte DelivAI.',
    'common.characters': '{count} caracteres',
    'common.save': 'Guardar',
    'common.saving': 'Guardando…',
    'common.duration_min': '{count} min',
    'common.duration_days': '{count} día(s)',
    'common.weekday.sunday': 'Domingo',
    'common.weekday.monday': 'Lunes',
    'common.weekday.tuesday': 'Martes',
    'common.weekday.wednesday': 'Miércoles',
    'common.weekday.thursday': 'Jueves',
    'common.weekday.friday': 'Viernes',
    'common.weekday.saturday': 'Sábado',

    'delidesk.authorize.header': 'Autorizar',
    'delidesk.authorize.go_panel': 'Ir al panel',
    'delidesk.authorize.title': '¿Autorizar este PC?',
    'delidesk.authorize.subtitle_before': 'Vincular DeliDesk a la tienda',
    'delidesk.authorize.subtitle_dev_before': 'Vincular DeliDesk a la cuenta',
    'delidesk.authorize.machine': 'máquina',
    'delidesk.authorize.warn_open_from_app':
      'Abra esta página desde la app DeliDesk (Entrar con DelivAI). Sin el parámetro state la autorización no funciona.',
    'delidesk.authorize.approve': 'Autorizar este PC',
    'delidesk.authorize.authorizing': 'Autorizando…',
    'delidesk.authorize.open_app': 'Abrir DeliDesk',
    'delidesk.authorize.success_banner': 'PC autorizado con éxito.',
    'delidesk.authorize.return_to_app':
      'Puede cerrar esta pestaña y volver a DeliDesk — la app termina el login sola, sin popup del navegador.',
    'delidesk.authorize.footer_hint':
      'Solo confirme si inició el login en DeliDesk en este computador. Luego la app recibe el vínculo y puede imprimir cupones de esta tienda.',
    'delidesk.authorize.footer_hint_collaborator':
      'DeliDesk es para el dueño de la tienda o el equipo DelivAI. Pida al responsable de la tienda que autorice este PC.',
    'delidesk.authorize.back_collaborator': 'Volver al panel del colaborador',
    'delidesk.authorize.dialog_title': 'Volver a DeliDesk',
    'delidesk.authorize.dialog_body':
      'Este PC fue autorizado. Vuelva a DeliDesk — el login termina automáticamente.',
    'delidesk.authorize.dialog_os_note':
      'No es necesario abrir la app desde el navegador; DeliDesk detecta la autorización sola.',
    'delidesk.authorize.error_missing_state':
      'Enlace inválido: falta el parámetro state. Abra desde el botón Entrar en DeliDesk.',
    'delidesk.authorize.error_no_redirect': 'El backend no devolvió redirect_uri',
    'delidesk.authorize.error_generic': 'Error al autorizar este PC',
    'delidesk.authorize.error_store_collaborator':
      'Los colaboradores de la tienda no pueden autorizar DeliDesk. Entre con la cuenta del dueño o con un usuario del equipo DelivAI.',
    'delidesk.embed.gate_title': 'Conecte desde DeliDesk',
    'delidesk.embed.gate_body':
      'Esta pantalla del panel solo abre después del login Entrar con DelivAI en la app. El formulario de CNPJ del sitio no se usa aquí.',
    'delidesk.embed.gate_hint':
      'Si la app ya está vinculada, use Recargar en DeliDesk o salga y entre de nuevo con DelivAI.',

    'orders.print_agent.download_resolving': 'Buscando la versión más reciente…',
    'orders.print_agent.download_version_title': 'Versión más reciente: {version}',
    'orders.print_agent.clear_queue': 'Vaciar cola',
    'orders.print_agent.clear_queue_with_count': 'Vaciar cola ({count})',
    'orders.print_agent.clear_queue_hint':
      'Cancela cupones en el servidor — evita imprimir todo al abrir DeliDesk en el PC de la impresora.',
    'orders.print_agent.clear_queue_confirm_title': '¿Vaciar la cola de impresión?',
    'orders.print_agent.clear_queue_confirm_body':
      'Esto cancela {count} cupón(es) pendiente(s) en el servidor. No se puede deshacer.',
    'orders.print_agent.clear_queue_confirm': 'Vaciar cola',
    'orders.print_agent.clear_queue_ok': '{count} cupón(es) eliminado(s) de la cola.',
    'orders.print_agent.clear_queue_error': 'No se pudo vaciar la cola de impresión.',

    'conversations.archived': 'Archivadas',
    'conversations.whatsapp': 'WhatsApp',
    'conversations.search_short': 'Buscar…',
    'conversations.loading_list': 'Cargando conversaciones...',
    'conversations.loading_more_chats': 'Cargando conversaciones anteriores…',
    'conversations.scroll_for_more': 'Desplázate para cargar más conversaciones',
    'conversations.error.load_list': 'Error al cargar conversaciones: {error}',
    'conversations.error.load_list_generic': 'Error al cargar conversaciones',
    'conversations.reload': 'Recargar conversaciones',
    'conversations.select_to_start': 'Seleccione una conversación para comenzar',
    'conversations.loading_messages': 'Cargando mensajes...',

    'motoboys.module_title': 'Módulo Motoboys',
    'motoboys.ranking': 'Ranking de repartidores',
    'motoboys.podium': 'Podio del día',
    'motoboys.place_1': '1.º puesto',
    'motoboys.place_2': '2.º puesto',
    'motoboys.place_3': '3.º puesto',
    'motoboys.deliveries_count': '{count} entrega(s)',
    'motoboys.fees_total_label': 'Total en tarifas',
    'motoboys.daily_label': 'Diaria:',
    'motoboys.daily_edit_hint': 'Doble clic para editar',
    'motoboys.with_deliveries': 'Repartidores con entregas',
    'motoboys.total_deliveries': 'Total de entregas',
    'motoboys.total_fees': 'Total en tarifas',
    'motoboys.dailies': '+ Diarias',
    'motoboys.total': 'Total',
    'motoboys.kitchen_sum': 'Suma de todas las tiendas de la dark kitchen',
    'motoboys.empty': 'Ningún repartidor con entregas en este momento',
    'motoboys.empty_hint': 'Los repartidores aparecerán aquí cuando haya pedidos con repartidor asignado',
    'motoboys.requirements_title': 'Para aparecer aquí, el pedido debe:',
    'motoboys.req_assigned': 'Tener un repartidor asignado',
    'motoboys.req_not_cancelled': 'No estar cancelado',
    'motoboys.req_ready_or_done': 'Estar en ruta o completado',
    'motoboys.console_hint': 'Revise la consola del navegador (F12) para detalles de búsqueda de pedidos',
    'motoboys.my_summary': 'Mis entregas',
    'motoboys.my_name': 'Usted',
    'motoboys.me': 'Yo',
    'motoboys.my_deliveries': 'Tus entregas',
    'motoboys.my_fees': 'Tus tarifas',
    'motoboys.my_day': 'Tu día',
    'motoboys.personal_empty': 'Ninguna entrega tuya en este día',
    'motoboys.personal_empty_hint': 'Cuando un pedido se despache a tu nombre, los números aparecen aquí.',

    'schedule.module_title': 'Módulo de citas',
    'schedule.subtitle': 'Gestione sus citas y agendamientos de forma organizada',
    'schedule.tab_agenda': 'Agenda',
    'schedule.tab_reports': 'Informes',
    'schedule.select_professional': 'Seleccionar agenda del profesional',
    'schedule.store_calendar': 'Agenda del local',
    'schedule.google_missing': 'Google no conectado',
    'schedule.staff_own_agenda': 'Está en su agenda. Conecte Google aquí.',
    'schedule.pick_professional_first': 'Elija un profesional para crear un horario.',
    'schedule.reports.from': 'Desde',
    'schedule.reports.to': 'Hasta',
    'schedule.reports.professional': 'Profesional',
    'schedule.reports.service': 'Servicio',
    'schedule.reports.area': 'Área',
    'schedule.reports.payment': 'Pago',
    'schedule.reports.filter_all_pros': 'Todos los profesionales',
    'schedule.reports.filter_all_services': 'Todos los servicios',
    'schedule.reports.filter_all_areas': 'Todas las áreas',
    'schedule.reports.filter_all_payment': 'Todos',
    'schedule.reports.store_calendar': 'Agenda del local',
    'schedule.reports.me': 'Mi agenda',
    'schedule.reports.paid': 'Pagado',
    'schedule.reports.pending_pay': 'Pendiente',
    'schedule.reports.loading': 'Cargando…',
    'schedule.reports.refresh': 'Actualizar',
    'schedule.reports.load_error': 'No se pudo cargar el informe',
    'schedule.reports.total_count': 'Citas',
    'schedule.reports.total_amount': 'Total',
    'schedule.reports.paid_count': 'Pagados',
    'schedule.reports.pending_pay_count': 'Pago pendiente',
    'schedule.reports.by_professional': 'Por profesional',
    'schedule.reports.by_service': 'Por servicio',
    'schedule.reports.empty': 'Sin datos en el período',
    'schedule.public_link': 'Enlace público',
    'schedule.copy_public_link': 'Copiar enlace público de citas',
    'schedule.public_link_copied': 'Enlace de agenda copiado',
    'schedule.public_link_copy_error': 'No se pudo copiar el enlace',
    'schedule.new_short': 'Nuevo',
    'schedule.define_schedule_hint': 'Toque el día, arrastre en secuencia, Ctrl+clic para días sueltos, o toque el nombre de la semana. Puede cerrar el día entero, el mismo horario en todos, o un horario distinto en cada día.',
    'schedule.day_action_new': 'Nueva cita',
    'schedule.day_action_close': 'Cerrar el día',
    'schedule.day_action_reopen': 'Reabrir el día',
    'schedule.day_action_lock_slot': 'Bloquear horario',
    'schedule.day_action_slots_title': 'Horarios del día',
    'schedule.day_action_no_slots': 'Sin horarios en este día.',
    'schedule.slot_locked': 'Horario bloqueado',
    'schedule.slot_booked': 'Horario ocupado',
    'schedule.slot_lock_hint': 'Haga clic y arrastre para bloquear o liberar varios horarios.',
    'schedule.slot_closed_by_store': 'Este horario fue bloqueado por el negocio.',
    'schedule.closed_slots_save_error': 'No se pudo bloquear el horario.',
    'schedule.confirm_lock_day_title': '¿Bloquear este día?',
    'schedule.confirm_lock_days_title': '¿Bloquear estos días?',
    'schedule.confirm_lock_day_message': 'Nadie podrá agendar el {date}.',
    'schedule.confirm_lock_days_message': 'Nadie podrá agendar en {count} días ({dates}).',
    'schedule.confirm_lock_slot_title': '¿Bloquear este horario?',
    'schedule.confirm_lock_slots_title': '¿Bloquear estos horarios?',
    'schedule.confirm_lock_slot_message': 'Nadie podrá agendar {times} el {date}.',
    'schedule.confirm_lock_slot_days_message': 'Nadie podrá agendar {times} en {count} días ({dates}).',
    'schedule.confirm_lock_slot_map_message': 'Nadie podrá agendar: {details}.',
    'schedule.confirm_lock': 'Bloquear',
    'schedule.confirm_unlock': 'Desbloquear',
    'schedule.confirm_unlock_day_title': '¿Desbloquear este día?',
    'schedule.confirm_unlock_days_title': '¿Desbloquear estos días?',
    'schedule.confirm_unlock_day_message': 'Los horarios de {date} volverán a estar disponibles para agendar.',
    'schedule.confirm_unlock_days_message': 'Los horarios de {count} días ({dates}) volverán a estar disponibles para agendar.',
    'schedule.confirm_unlock_day_reason': 'Motivo del bloqueo: {reason}',
    'schedule.confirm_unlock_slot_title': '¿Desbloquear este horario?',
    'schedule.confirm_unlock_slot_message': '{times} el {date} volverá a estar disponible para agendar.',
    'schedule.filter_locked': 'Bloqueados',
    'schedule.lock_history_day': 'Día cerrado',
    'schedule.lock_history_slots': 'Horarios bloqueados',
    'schedule.lock_range_to': 'a',
    'schedule.lock_reason_mixed': 'Motivos distintos',
    'schedule.confirm_unlock_slots_title': '¿Desbloquear estos horarios?',
    'schedule.lock_history_empty': 'Ningún horario bloqueado',
    'schedule.lock_history_empty_hint': 'Los días y horarios que bloquee en el calendario aparecen aquí.',
    'schedule.lock_history_by_store': 'Bloqueado por la tienda',
    'schedule.lock_reason_unknown': 'Sin motivo informado',
    'schedule.lock_reason_label': 'Motivo del bloqueo',
    'schedule.lock_reason_medical': 'Consulta médica',
    'schedule.lock_reason_personal': 'Compromiso personal',
    'schedule.lock_reason_other': 'Otra ocurrencia',
    'schedule.lock_reason_other_placeholder': 'Describa el motivo (hasta 200 caracteres)',
    'schedule.lock_reason_other_required': 'Describa el motivo con al menos 3 caracteres.',
    'schedule.reason_locked_slots': 'Hay horario(s) bloqueado(s)',
    'schedule.day_click_hint': 'Clic, arrastre, Ctrl+clic o toque el nombre de la semana',
    'schedule.weekday_column_hint': 'Seleccionar todos los {day} de este mes',
    'schedule.multi_selected': '{count} días seleccionados',
    'schedule.multi_clear': 'Limpiar',
    'schedule.multi_close_days': 'Cerrar estos días',
    'schedule.multi_reopen_days': 'Reabrir estos días',
    'schedule.multi_lock_slot': 'Bloquear un horario en estos días',
    'schedule.multi_ctrl_holding': 'Suelte Ctrl para elegir cómo bloquear estos días',
    'schedule.batch_ask_title': '¿Cómo cerrar estos días?',
    'schedule.batch_ask_hint': 'Día entero, el mismo horario en todos, o un horario distinto en cada día.',
    'schedule.batch_close_days': 'Cerrar los días enteros',
    'schedule.batch_reopen_days': 'Reabrir estos días',
    'schedule.batch_same_slot': 'Mismo horario en todos',
    'schedule.batch_per_day_slot': 'Horario distinto por día',
    'schedule.batch_back': 'Volver',
    'schedule.batch_pick_times': 'Toque los horarios de cada día',
    'schedule.batch_no_times': 'Ningún horario',
    'schedule.batch_apply_slots': 'Bloquear horarios elegidos',
    'schedule.batch_apply_open_slots': 'Abrir horarios elegidos',
    'schedule.batch_day_times_confirmed': 'Confirmado: {times}',
    'schedule.day_appointments_count': '{count} cita(s)',
    'schedule.legend_free': 'Libre',
    'schedule.legend_partial': 'Con citas',
    'schedule.legend_full': 'Completo',
    'schedule.legend_outside_hours': 'Fuera del horario registrado',
    'schedule.legend_closed': 'Cerrado manualmente',
    'schedule.cannot_close_outside_hours': 'Estos días ya están fuera del horario registrado.',
    'schedule.skipped_outside_hours': '{count} día(s) ya están fuera del horario y no se cerraron.',
    'schedule.already_outside_hours_hint': 'Ya están fuera del horario registrado. No hace falta cerrar de nuevo.',
    'schedule.cannot_mix_closed_open': 'No se pueden juntar días cerrados con días abiertos.',
    'schedule.batch_ask_reopen_title': '¿Reabrir estos días?',
    'schedule.batch_ask_reopen_hint': 'Todos los seleccionados ya están cerrados.',
    'schedule.batch_open_title': '¿Abrir para atención?',
    'schedule.batch_open_hint': 'El día entero usa el horario registrado. O elija horarios específicos.',
    'schedule.batch_open_full_day': 'Abrir el día entero',
    'schedule.batch_revert_open': 'Volver al horario registrado',
    'schedule.batch_ask_revert_title': '¿Volver al horario registrado?',
    'schedule.batch_ask_revert_hint': 'Estos días vuelven a quedar fuera del expediente.',
    'schedule.day_action_open_full': 'Abrir para atención',
    'schedule.day_action_open_slots': 'Definir horarios',
    'schedule.day_action_revert_open': 'Volver al horario registrado',
    'schedule.legend_open_extra': 'Abierto extra',
    'schedule.open_extra_day': 'Abierto extra',
    'schedule.open_extra_slots': 'Horarios extra',
    'schedule.confirm_open_days_title': '¿Abrir estos días?',
    'schedule.confirm_open_day_title': '¿Abrir este día?',
    'schedule.confirm_open_days_message': 'Quedarán disponibles en el horario registrado ({count} días: {dates}).',
    'schedule.confirm_open_day_message': '{date} queda disponible en el horario registrado.',
    'schedule.confirm_open_slots_title': '¿Abrir estos horarios?',
    'schedule.confirm_open_slots_message': 'Solo estos horarios quedan disponibles para agendar.',
    'schedule.confirm_revert_open_title': '¿Volver al registro?',
    'schedule.confirm_revert_open_message': 'Estos días vuelven a quedar fuera del horario ({dates}).',
    'schedule.confirm_open': 'Abrir',
    'schedule.confirm_revert_open': 'Volver',
    'schedule.open_dates_save_error': 'No se pudo guardar la apertura extra.',
    'schedule.reason_open_extra': 'Abierto extra — fuera del horario registrado',
    'schedule.day_closed_by_store': 'Este día fue cerrado por el negocio.',
    'schedule.closed_dates_load_error': 'No se pudieron cargar los días cerrados.',
    'schedule.closed_dates_save_error': 'No se pudo guardar el cierre del día.',
    'schedule.reason_outside_hours': 'No disponible por el horario registrado',
    'schedule.reason_manual_closed': 'Día cerrado manualmente',
    'schedule.gcal_disconnected_banner': 'Google Agenda desconectado',
    'schedule.gcal_disconnected_banner_staff': 'Su cuenta de Google está desconectada',
    'client_scheduling.email_hint':
      'Opcional. Indique el correo Google del cliente para que la cita aparezca en su Calendar.',
    'schedule.events_hint': 'Confirme para enviar a Google Calendar y WhatsApp.',
    'schedule.no_events_hint': 'Las reservas del enlace público aparecen aquí.',
    'schedule.filter_all': 'Todos',
    'schedule.filter_weekday': 'Día de la semana',
    'schedule.filter_weekday_all': 'Todos los días',
    'schedule.filter_time_from': 'Desde',
    'schedule.filter_time_to': 'Hasta',
    'schedule.filter_time_from_all': 'Cualquiera',
    'schedule.filter_time_to_all': 'Cualquiera',
    'schedule.filter_sort': 'Orden',
    'schedule.filter_sort_asc': 'Ascendente',
    'schedule.filter_sort_desc': 'Descendente',
    'schedule.filter_empty': 'Nada coincide con estos filtros',
    'schedule.filter_empty_hint': 'Cambie el día, el intervalo de horario o el orden.',
    'schedule.google_section': 'Google Calendar',
    'schedule.dismiss_alert': 'Cerrar aviso',
    'schedule.calendar.prev_month': 'Mes anterior',
    'schedule.calendar.next_month': 'Mes siguiente',
    'schedule.tap_to_available': 'Tocar para disponibilizar',
    'schedule.tap_to_unavailable': 'Tocar para cerrar el día',
    'client_scheduling.progress_label': 'Pasos de la reserva',
    'schedule.disconnected': 'Desconectado',
    'schedule.reconnect': 'Reconectar',
    'schedule.new': 'Nueva cita',
    'schedule.total': 'Total de citas',
    'schedule.confirmed': 'Confirmadas',
    'schedule.pending': 'Pendientes',
    'schedule.cancelled': 'Canceladas',
    'schedule.connect_calendar': 'Conéctese a Google Calendar para ver sus citas',
    'schedule.define_schedule': 'Definir agenda',
    'schedule.available': 'Disponible',
    'schedule.unavailable': 'No disponible',
    'schedule.past_date': 'Fecha pasada',
    'schedule.connect_calendar_sync': 'Conéctese a Google Calendar y espere la sincronización de las citas.',
    'schedule.events_title': 'Citas',
    'schedule.last_update': 'Última actualización: {time}',
    'schedule.events_count': '{count} citas',
    'schedule.load_error': 'Error al cargar citas',
    'schedule.try_again': 'Intentar de nuevo',
    'schedule.no_events': 'No se encontraron citas',
    'schedule.notify.load_error': 'Error al cargar citas',
    'schedule.notify.save_error': 'Error al guardar cita',
    'schedule.notify.created': '¡Cita creada con éxito!',
    'schedule.notify.updated': '¡Cita actualizada con éxito!',
    'schedule.notify.connect_to_confirm': 'Conéctese a Google Calendar para confirmar citas',
    'schedule.notify.confirmed': '¡Cita confirmada y evento creado en Google Calendar!',
    'schedule.notify.confirmed_no_google': 'Cita confirmada y WhatsApp enviado. Active la API de Google Calendar en Cloud Console para sincronizar la agenda.',
    'schedule.notify.confirmed_status_only': 'Cita confirmada. Conecte Google Calendar para sincronizar eventos.',
    'schedule.notify.confirm_failed': 'No se pudo confirmar la cita. Intente de nuevo.',
    'schedule.notify.confirm_error': 'Error al crear evento en Google Calendar: {error}',
    'schedule.notify.google_api_disabled': 'Active la API de Google Calendar en Google Cloud de este proyecto e intente de nuevo.',
    'schedule.notify.cancelled': 'Cita cancelada con éxito',
    'schedule.notify.cancel_error': 'Error al cancelar cita',
    'schedule.loading': 'Cargando citas…',
    'schedule.status.confirmed': 'Confirmado',
    'schedule.status.pending': 'Pendiente',
    'schedule.status.cancelled': 'Cancelado',
    'schedule.status.completed': 'Completado',
    'schedule.status.unknown': 'Desconocido',
    'schedule.synced_google': 'Sincronizado con Google Calendar',
    'schedule.google.processing_title': 'Procesando autorización…',
    'schedule.google.processing_body': 'Espere mientras conectamos su cuenta de Google Calendar',
    'schedule.google.success_title': '¡Conectado con éxito!',
    'schedule.google.error_title': 'Error de conexión',
    'schedule.google.redirecting': 'Redirigiendo a la página de citas…',
    'schedule.google.connected': '¡Conectado con éxito a Google Calendar!',
    'schedule.google.connect_failed': 'No se pudo conectar a Google Calendar. Intente de nuevo o contacte a nuestro soporte.',
    'schedule.google.redirect_mismatch': 'URI de redirección no autorizada en Google Cloud. Incluya la URI del panel (.../google-callback) en las URIs autorizadas del cliente OAuth.',
    'schedule.google.server_config': 'Integración de Google mal configurada en el servidor. Revise Client ID y Secret en el backend e intente de nuevo.',
    'schedule.google.invalid_grant': 'Código de Google expirado o ya usado. Cierre y haga clic en Conectar de nuevo.',
    'schedule.google.auth_denied': 'Autorización de Google cancelada o denegada',
    'schedule.google.missing_code': 'Código de autorización no encontrado',
    'schedule.google.need_login': 'Inicie sesión en la tienda antes de conectar Google Calendar',
    'schedule.google.exchange_failed': 'No se pudo obtener el token de Google',
    'schedule.edit_btn': 'Editar',
    'schedule.templates.title': 'Mensajes WhatsApp de citas',
    'schedule.templates.hint': 'Textos enviados en confirmación, recordatorio, cancelación y reprogramación. Usa las variables entre llaves.',
    'schedule.templates.vars': 'Variables: {cliente}, {servico}, {data}, {hora}, {endereco}',
    'schedule.templates.pending': 'Pendiente',
    'schedule.templates.confirmation': 'Confirmación',
    'schedule.templates.reminder': 'Recordatorio',
    'schedule.templates.cancellation': 'Cancelación',
    'schedule.templates.reschedule': 'Reprogramación',
    'schedule.templates.preview': 'Vista previa',
    'schedule.templates.restore_default': 'Restaurar predeterminado',
    'schedule.templates.restore_all_defaults': 'Restaurar todos los predeterminados',
    'schedule.templates.save': 'Guardar plantillas',
    'schedule.templates.saving': 'Guardando…',
    'schedule.templates.toast_saved': 'Plantillas guardadas',
    'schedule.templates.toast_save_error': 'No se pudieron guardar las plantillas',
    'schedule.templates.sample_client': 'María',
    'schedule.templates.sample_service': 'Corte de cabello',
    'schedule.templates.sample_address': 'Calle Ejemplo, 100',
    'schedule.templates.send_reminder': 'Enviar recordatorio',
    'schedule.templates.reminder_sent': 'Recordatorio enviado por WhatsApp',
    'schedule.templates.reschedule_sent': 'Reprogramación enviada por WhatsApp',
    'schedule.templates.send_failed': 'No se pudo enviar. Verifica la conexión de WhatsApp o contacta a nuestro soporte.',
    'client_scheduling.step_professional': 'Profesional',
    'client_scheduling.choose_professional': 'Elige el profesional',
    'client_scheduling.back_to_professional': '← Volver al profesional',
    'client_scheduling.no_services': 'No hay servicios configurados. Pide al negocio que configure los servicios de agenda.',
    'client_scheduling.no_professionals_for_service':
      'Ningún profesional disponible para este servicio. Elige otro servicio.',
    'client_scheduling.professional_day_closed': 'No disponible en este día',
    'client_scheduling.loading_slots': 'Cargando horarios…',
    'client_scheduling.module_off': 'La agenda no está disponible para este negocio.',
    'client_scheduling.module_off_title': 'Agenda no disponible',
    'company.gift.title': 'Regalo / cupón (QR en la bolsa)',
    'company.gift.hint': 'Configura el descuento que el cliente gana al canjear el QR. El cupón se aplica solo en el próximo pedido elegible.',
    'company.gift.enabled': 'Programa activo',
    'company.gift.print_qr': 'Imprimir QR en la bolsa',
    'company.gift.headline': 'Texto del QR / página',
    'company.gift.success_title': 'Título de éxito',
    'company.gift.success_body': 'Mensaje de éxito',
    'company.gift.programs': 'Programas de descuento',
    'company.gift.add_program': 'Agregar programa',
    'company.gift.program_new_name': 'Nuevo programa',
    'company.gift.program_name': 'Nombre del programa',
    'company.gift.program_active': 'Activo',
    'company.gift.remove_program': 'Quitar programa',
    'company.gift.type': 'Tipo',
    'company.gift.type_percent': 'Porcentaje (%)',
    'company.gift.type_fixed': 'Valor fijo',
    'company.gift.value': 'Valor',
    'company.gift.min_order': 'Pedido mínimo',
    'company.gift.eligibility': 'Elegibilidad',
    'company.gift.eligibility_any': 'Cualquier pedido',
    'company.gift.eligibility_first': 'Solo 1.er pedido',
    'company.gift.eligibility_subsequent': 'Desde el 2.º pedido',
    'company.gift.max_uses': 'Máx. cupones por cliente',
    'company.gift.expires_days': 'Validez (días)',
    'company.gift.rule_percent': '{value}% de descuento · mínimo {min}',
    'company.gift.rule_fixed': '{value} de descuento · mínimo {min}',
    'company.gift.save': 'Guardar regalo',
    'company.gift.saving': 'Guardando…',
    'company.gift.toast_saved': 'Reglas del regalo guardadas',
    'company.gift.toast_save_error': 'No se pudieron guardar las reglas del regalo',
    'company.gift.issue_coupons': 'Emitir cupones',
    'company.gift.issue_title': 'Emitir cupones a clientes',
    'company.gift.issue_program': 'Programa',
    'company.gift.issue_search': 'Buscar por nombre o teléfono',
    'company.gift.issue_loading': 'Cargando clientes…',
    'company.gift.issue_empty': 'Ningún cliente encontrado',
    'company.gift.client_unnamed': 'Cliente sin nombre',
    'company.gift.cancel': 'Cancelar',
    'company.gift.issuing': 'Emitiendo…',
    'company.gift.issue_confirm': 'Emitir ({count})',
    'company.gift.toast_issued': '{count} cupón(es) emitido(s)',
    'company.gift.toast_issue_error': 'No se pudieron emitir los cupones',
    'company.gift.toast_clients_error': 'No se pudieron cargar los clientes',
    'schedule.notify.deleted': 'Cita eliminada con éxito',
    'schedule.notify.delete_error': 'Error al eliminar cita',
    'schedule.notify.unknown_error': 'Error desconocido',
    'schedule.available_times_for': 'Horarios disponibles para {date}',
    'schedule.day_closed': 'Este día está cerrado para citas',
    'schedule.time_selected': 'Horario seleccionado: {time}',

    'password_req.title': 'Requisitos de la contraseña:',
    'password_req.min_length': 'Mínimo 6 caracteres',
    'password_req.uppercase': 'Al menos 1 letra mayúscula',
    'password_req.number': 'Al menos 1 número',
    'password_req.special': 'Al menos 1 carácter especial',

    'image_crop.title': 'Ajustar imagen',
    'image_crop.use_image': 'Usar imagen',
    'image_crop.subtitle_zoom': 'Use la rueda del mouse para zoom y arrastre la imagen. Se usará el área del cuadrado.',
    'image_crop.subtitle_crop': 'La imagen completa se muestra. Arrastre el rectángulo; use la esquina inferior derecha para redimensionar. Fuera de la selección la imagen se atenúa.',
    'image_crop.preview_alt': 'Vista previa del logo',
    'image_crop.resize_aria': 'Redimensionar selección',
    'image_crop.zoom': 'Zoom',
    'image_crop.generate_failed': 'Error al generar imagen',

    'gcal_events.invalid_date': 'Fecha inválida',
    'gcal_events.events_count_one': '{count} cita',
    'gcal_events.events_count_many': '{count} citas',
    'gcal_events.current_month': 'Actual',
    'gcal_events.today_badge': 'Hoy',
    'gcal_events.loading': 'Cargando citas...',

    'orders.start_sales_mid_body': 'Entró al panel dentro del horario de atención, pero la tienda está cerrada. ¿Abrir ahora para recibir pedidos?',
    'orders.start_sales_manual_body': 'La tienda solo abre cuando usted confirma. Haga clic en Iniciar ventas para abrir ahora — el estado no cambia solo en el horario.',
    'orders.start_sales_btn': 'Iniciar ventas',
    'orders.opening_btn': 'Abriendo...',
    'orders.store_override_intro': 'Defina cuánto tiempo debe valer este estado manual para toda la tienda.',
    'orders.store_override.auto': 'Automático',
    'orders.store_override.auto_close_hint': 'Cierra ahora y reabre en el próximo horario de la agenda.',
    'orders.store_override.auto_close_at': 'La tienda reabre el {when}.',
    'orders.store_override.auto_open_at': 'La tienda cierra el {when}.',
    'orders.store_override.auto_no_schedule': 'Configure el horario de funcionamiento para usar Automático.',
    'orders.store_override.timed_reopen_at': 'Reabre el {when}.',
    'orders.store_override.timed_outside_hours': 'No disponible: {when} está fuera del horario.',
    'orders.store_override.30min': '30 minutos',
    'orders.store_override.2h': '2 horas',
    'orders.store_override.4h': '4 horas',

    'public_orders.cep_error': 'Error al buscar código postal. Intente de nuevo.',
    'public_orders.no_products_search': 'Ningún producto encontrado para "{term}"',
    'public_orders.edit': 'Editar',

    'client_scheduling.loading': 'Cargando disponibilidad...',
    'client_scheduling.loading_for': 'Buscando horarios disponibles para {name}...',
    'client_scheduling.module_name': 'Módulo de citas',
    'client_scheduling.book_title': 'Agende su cita',
    'client_scheduling.online': 'En línea',
    'client_scheduling.step_datetime': 'Fecha y hora',
    'client_scheduling.step_date': 'Fecha',
    'client_scheduling.step_time': 'Horario',
    'client_scheduling.step_service': 'Servicio',
    'client_scheduling.step_contact': 'Contacto',
    'client_scheduling.step_name': 'Nombre',
    'client_scheduling.step_address': 'Dirección',
    'client_scheduling.step_confirm': 'Confirmación',
    'client_scheduling.choose_time': 'Elija el horario',
    'client_scheduling.contact_title': 'Su teléfono',
    'client_scheduling.contact_hint': 'Usamos WhatsApp para confirmar el agendamiento.',
    'client_scheduling.name_title': 'Su nombre',
    'client_scheduling.address_title': 'Dirección de la visita',
    'client_scheduling.address_required_hint': 'Este servicio es en su dirección.',
    'client_scheduling.continue': 'Continuar',
    'client_scheduling.badge_visit': 'Visita',
    'client_scheduling.badge_store': 'En el local',
    'client_scheduling.place_store': 'Atención en el local',
    'client_scheduling.place_store_at': 'Atención en el local · {name}',
    'client_scheduling.sinal_required': 'Señal de {amount}',
    'client_scheduling.price_service': 'Servicio',
    'client_scheduling.price_sinal': 'Señal',
    'client_scheduling.price_total': 'Total',
    'client_scheduling.sinal_adds_hint':
      'La señal no descuenta del valor del servicio. Se paga señal + servicio.',
    'client_scheduling.sinal_deducts_hint':
      'La señal descuenta del servicio. Restante en la cita: {remaining}',
    'client_scheduling.back_to_time': '← Volver a horarios',
    'client_scheduling.back_to_contact': '← Volver a contacto',
    'client_scheduling.back_to_name': '← Volver al nombre',
    'client_scheduling.error.address_required': 'Informe la dirección de la visita',
    'client_scheduling.label_place': 'Lugar',
    'client_scheduling.choose_date': 'Elija la fecha',
    'client_scheduling.choose_date_hint': 'Seleccione una fecha disponible para su cita',
    'client_scheduling.weekday_short.sun': 'Dom',
    'client_scheduling.weekday_short.mon': 'Lun',
    'client_scheduling.weekday_short.tue': 'Mar',
    'client_scheduling.weekday_short.wed': 'Mié',
    'client_scheduling.weekday_short.thu': 'Jue',
    'client_scheduling.weekday_short.fri': 'Vie',
    'client_scheduling.weekday_short.sat': 'Sáb',
    'client_scheduling.closed_day': 'Cerrado',
    'client_scheduling.legend_available': 'Disponible',
    'client_scheduling.legend_closed': 'Cerrado',
    'client_scheduling.legend_past': 'Fecha pasada',
    'client_scheduling.past_day': 'Fecha pasada',
    'client_scheduling.legend_selected': 'Seleccionado',
    'client_scheduling.today': 'Hoy',
    'client_scheduling.times_available': 'Horarios disponibles:',
    'client_scheduling.more_times': 'más',
    'client_scheduling.times_for': 'Horarios disponibles para',
    'client_scheduling.day_closed_msg': 'Este día está cerrado para citas',
    'client_scheduling.no_times': 'Ningún horario disponible para este día',
    'client_scheduling.choose_other_date': '← Elegir otra fecha',
    'client_scheduling.choose_service': 'Elija el servicio',
    'client_scheduling.date_label': 'Fecha:',
    'client_scheduling.at_time': 'a las',
    'client_scheduling.duration_min': 'Duración: {count} min',
    'client_scheduling.back_to_date': '← Volver a selección de fecha',
    'client_scheduling.confirm_title': 'Confirme su cita',
    'client_scheduling.summary': 'Resumen de la cita',
    'client_scheduling.label_date': 'Fecha',
    'client_scheduling.label_time': 'Horario',
    'client_scheduling.label_service': 'Servicio',
    'client_scheduling.google_hint': 'La cita se agregará automáticamente a Google Calendar',
    'client_scheduling.full_name': 'Nombre completo *',
    'client_scheduling.name_placeholder': 'Su nombre completo',
    'client_scheduling.phone': 'Teléfono *',
    'client_scheduling.phone_placeholder': '(11) 99999-9999',
    'client_scheduling.email': 'Email',
    'client_scheduling.email_placeholder': 'su@email.com',
    'client_scheduling.location': 'Ubicación de la cita',
    'client_scheduling.company_address': 'Dirección de la empresa',
    'client_scheduling.automatic': 'Automático',
    'client_scheduling.your_address': 'Dirección de la visita',
    'client_scheduling.address_placeholder': 'Calle, número, barrio',
    'client_scheduling.notes': 'Observaciones adicionales',
    'client_scheduling.notes_placeholder': 'Información adicional sobre la cita...',
    'client_scheduling.confirm_btn': 'Confirmar cita',
    'client_scheduling.creating_event': 'Creando evento en el calendario...',
    'client_scheduling.confirming': 'Confirmando...',
    'client_scheduling.back_to_service': '← Volver a selección de servicio',
    'client_scheduling.error.name_required': 'El nombre es obligatorio',
    'client_scheduling.error.name_min': 'El nombre debe tener al menos 3 caracteres',
    'client_scheduling.error.phone_required': 'El teléfono es obligatorio',
    'client_scheduling.error.phone_format': 'El teléfono debe estar en formato (11) 99999-9999',
    'client_scheduling.lgpd_consent': 'He leído y acepto los',
    'client_scheduling.lgpd_terms_link': 'Términos de Uso de Agendamientos',
    'client_scheduling.lgpd_privacy_link': 'Política de Privacidad',
    'client_scheduling.error.lgpd_required': 'Para confirmar, acepte los términos de agendamiento.',
    'client_scheduling.error.email_invalid': 'El email debe tener un formato válido',
    'client_scheduling.success_with_gcal': '¡Cita realizada con éxito! El evento fue agregado a su Google Calendar.',
    'client_scheduling.success_no_gcal': '¡Cita realizada con éxito! Conéctese a Google para agregar eventos automáticamente.',
    'client_scheduling.success_booked': '¡Cita reservada con éxito! El negocio confirmará en breve.',
    'client_scheduling.error_confirm': 'Error al confirmar la cita. Intente de nuevo.',
    'client_scheduling.return_included_legend': 'Retorno incluido en este servicio',
    'client_scheduling.return_needs_prior': 'El retorno solo se puede marcar después de una cita previa de este servicio.',
    'client_scheduling.kind_service': 'Atención',
    'client_scheduling.kind_return': 'Retorno (incluido)',

    'gcal.never': 'Nunca',
    'gcal.just_now': 'Ahora mismo',
    'gcal.mins_ago': 'hace {count} min',
    'gcal.hours_ago': 'hace {count}h',
    'gcal.checking': 'Verificando...',
    'gcal.connection_error': 'Error de conexión',
    'gcal.connected': 'Conectado',
    'gcal.auto_refresh': '(Auto-refresh activo)',
    'gcal.last_check': 'Última verificación:',
    'gcal.token': 'Token:',
    'gcal.error_label': 'Error:',
    'gcal.verify_now': 'Verificar ahora',
    'gcal.disable_auto_refresh': 'Desactivar auto-refresh',
    'gcal.enable_auto_refresh': 'Activar auto-refresh',

    'manager.revenue_total': 'Ingresos totales',
    'manager.orders': 'Pedidos',
    'manager.new_clients': 'Clientes nuevos',
    'manager.avg_time': 'Tiempo promedio',
    'manager.vs_previous_month': 'vs {count} mes anterior',
    'manager.tab_overview': 'Visión general',
    'manager.tab_reports': 'Informes',
    'manager.tab_analytics': 'Analytics',
    'manager.payment_summary': 'Resumen por método de pago',
    'manager.top_products': 'Top 5 productos',
    'manager.payment_methods': 'Formas de pago',
    'manager.order_type': 'Tipo de pedido',
    'manager.no_products': 'No se encontraron productos',
    'manager.no_data': 'No hay datos disponibles',
    'manager.reports_title': 'Informes gerenciales',
    'manager.card': 'Tarjeta',
    'manager.cash': 'Efectivo',
    'manager.analytics_title': 'Analytics avanzados',
    'manager.analytics_soon': 'La funcionalidad de analytics se implementará pronto',
    'manager.analytics_hint': 'Visualice gráficos detallados de ventas, tendencias y rendimiento',
    'manager.reports_soon': 'La funcionalidad de informes se implementará pronto',
    'manager.reports_hint': 'Genere informes personalizados de ventas, clientes y rendimiento',
    'manager.type_delivery': 'Entrega',
    'manager.type_dine_in': 'Consumo en el local',
    'manager.orders_count_one': '{count} pedido',
    'manager.orders_count_many': '{count} pedidos',
    'manager.range_too_long': 'Elija un período de como máximo 12 meses.',
    'manager.load_failed': 'No se pudo cargar el resumen. Inténtelo de nuevo.',

    'employees.edit_title': 'Editar colaborador',
    'employees.add_title': 'Agregar colaborador',
    'employees.stores_label': 'Empresas',
    'employees.stores_all': 'Todas',
    'employees.stores_hint': 'Marque en qué empresas del grupo trabaja este colaborador.',
    'employees.stores_required': 'Elija al menos una empresa.',
    'employees.full_name_placeholder': 'Nombre completo',
    'employees.new_role_placeholder': 'Nombre del nuevo cargo',
    'employees.add_role': 'Agregar cargo',
    'employees.daily_rate': 'Tarifa diaria',
    'employees.observations': 'Observaciones',
    'employees.observations_placeholder': 'Observaciones sobre el colaborador...',
    'employees.permissions': 'Permisos',
    'employees.permissions_hint': 'Permisos de este colaborador (el mismo cargo puede tener permisos distintos).',
    'employees.permissions_btn': 'Permisos',
    'employees.permissions_denied': 'No puede cambiar los permisos de otros colaboradores.',
    'employees.cpf_optional': 'CPF (opcional)',
    'employees.cpf_hint': 'Deje en blanco si el colaborador lo informará en el enlace de invitación al crear la contraseña.',
    'employees.cpf_placeholder': '000.000.000-00',
    'employees.status_label': 'Estado',
    'employees.status.active': 'Activo',
    'employees.status.inactive': 'Inactivo',
    'employees.status.vacation': 'Vacaciones',
    'employees.status.leave': 'Licencia',
    'employees.role.waiter': 'Mesero',
    'employees.role.cook': 'Cocinero',
    'employees.role.manager': 'Gerente',
    'employees.role.attendant': 'Atendente',
    'employees.role.delivery': 'Repartidor',
    'employees.role.admin': 'Admin',
    'employees.role.generic': 'Colaborador',
    'employees.delete_role_title': 'Eliminar cargo "{name}"',
    'employees.close_new_role': 'Cerrar nuevo cargo',
    'employees.open_new_role': 'Agregar nuevo cargo',
    'employees.phone_placeholder': '(00) 00000-0000',
    'employees.edit_btn': 'Editar',

    'internal_order.title': 'Pedido interno',
    'internal_order.back_orders': 'Volver a pedidos',
    'internal_order.back_delivery': 'Volver a Delivery',
    'internal_order.view_cart': 'Ver carrito',
    'internal_order.cart': 'Carrito',
    'internal_order.cart_empty': 'Carrito vacío',
    'internal_order.continue_shopping': 'Seguir comprando',
    'internal_order.click_see_products': 'Clic para ver productos',
    'internal_order.click_collapse': 'Clic para recoger',
    'internal_order.item_one': 'ítem',
    'internal_order.item_many': 'ítems',
    'internal_order.checkout_title': 'Finalizar pedido',
    'internal_order.order_type': 'Tipo de pedido',
    'internal_order.dine_in_short': 'Mesa',
    'internal_order.origin_internal': 'Pedido interno',
    'internal_order.table_number': 'Número de mesa',
    'internal_order.table_placeholder': 'Número de mesa (opcional)',
    'internal_order.delivery_address': 'Dirección de entrega *',
    'internal_order.house_number': 'Número *',
    'internal_order.house_placeholder': 'Número de la casa',
    'internal_order.already_paid': 'Pedido ya pagado',
    'internal_order.already_paid_hint': 'El pedido se marcará como pagado',
    'internal_order.observations': 'Observaciones',
    'internal_order.observations_placeholder': 'Observaciones adicionales sobre el pedido',
    'internal_order.finish': 'Finalizar pedido',
    'internal_order.modal.close_esc': 'Cerrar (ESC)',
    'internal_order.modal.items_count': '{count} ítems',
    'internal_order.modal.obs_short': 'Obs',
    'internal_order.modal.discount': 'Descuento',
    'internal_order.modal.type_delivery': 'Entrega',
    'internal_order.modal.type_pickup': 'Retiro',
    'internal_order.modal.phone_optional_placeholder': '(00) 00000-0000 (opcional)',
    'internal_order.modal.customer_name_placeholder': 'Nombre del cliente',
    'internal_order.ifood_orders_count': '{count} pedidos por iFood',
    'internal_order.ifood_orders_count_one': 'primer pedido por iFood',
    'internal_order.store_orders_count': '{count} pedidos en la tienda',
    'internal_order.store_orders_count_one': 'primer pedido en la tienda',
    'internal_order.modal.optional': 'Opcional',
    'internal_order.modal.neighborhood_placeholder': 'Barrio',
    'internal_order.modal.change_placeholder': '$ 0,00',
    'internal_order.modal.order_summary': 'Resumen del pedido',
    'internal_order.modal.ifood_discount': 'Descuento iFood',
    'internal_order.modal.total_order': 'Total del pedido',
    'internal_order.modal.calculated_total': 'Total calculado',
    'internal_order.modal.creating_order': 'Creando pedido...',
    'internal_order.modal.create_internal_order': 'Crear pedido interno',
    'internal_order.modal.select_size_type': 'Seleccione el tamaño/tipo:',
    'internal_order.modal.flavors_count': '{count} sabor(es)',
    'internal_order.modal.whole_pizza_one_flavor': 'Pizza entera (1 sabor)',
    'internal_order.modal.select_up_to_flavors': 'Seleccione hasta {max} sabor(es) ({total}/{max}):',
    'internal_order.modal.add_second_flavor': 'Agregar segundo sabor',
    'internal_order.modal.add_more_flavors': 'Agregar {count} sabor(es) más',
    'internal_order.modal.select_second_flavor': 'Seleccione el segundo sabor',
    'internal_order.modal.select_additional_flavors': 'Seleccione {count} sabor(es) adicional(es)',
    'internal_order.modal.addons_optional': 'Adicionales (opcional):',
    'internal_order.modal.max_short': 'máx. {count}',
    'internal_order.modal.observations_optional': 'Observaciones (opcional):',
    'internal_order.modal.product_observations_placeholder': 'Ej.: sin cebolla, bien cocido...',
    'internal_order.modal.product_fallback': 'Producto',
    'internal_order.modal.addon_fallback': 'Adicional',
    'internal_order.modal.obs_prefix': 'Obs: {obs}',
    'internal_order.modal.table_label_value': 'Mesa {number}',
    'internal_order.modal.obs_table': 'Mesa: {number}',
    'internal_order.modal.obs_ifood_discount': 'Descuento iFood: R$ {amount}',
    'internal_order.modal.obs_order_paid': 'Pedido ya pagado',
    'internal_order.modal.toast.product_added': '¡Producto agregado al carrito!',
    'internal_order.modal.toast.min_additional_required': 'Debe elegir al menos {min} ítem(s) de "{category}" para este producto',
    'internal_order.modal.toast.max_flavors': 'Máximo de {count} sabor(es) para este tipo',
    'internal_order.modal.toast.max_additional_limit': 'Límite máximo de {max} adicional(es) para {category}',
    'internal_order.modal.toast.enter_customer_name': 'Ingrese el nombre del cliente',
    'internal_order.modal.toast.enter_valid_phone': 'Ingrese un teléfono válido (mínimo 10 dígitos)',
    'internal_order.modal.toast.enter_house_number': 'Ingrese el número de la casa',
    'internal_order.modal.toast.enter_neighborhood': 'Ingrese el barrio',
    'internal_order.modal.toast.min_additional_required_item': 'En el ítem "{item}", debe elegir al menos {min} ítem(s) de "{category}"',
    'internal_order.modal.toast.order_created': '¡Pedido interno creado con éxito!',
    'internal_order.modal.toast.create_order_error': 'Error al crear pedido interno',
    'internal_order.modal.toast.process_order_error': 'Error al procesar pedido interno',

    'conversations.back': 'Volver a conversaciones',
    'conversations.archived_hint': 'Las conversaciones archivadas permanecen aquí como en WhatsApp en el celular.',
    'conversations.connected': 'Conectado a WhatsApp',
    'conversations.disconnected': 'Desconectado de WhatsApp',
    'conversations.disconnect': 'Desconectar WhatsApp',
    'conversations.disconnecting': 'Desconectando…',
    'conversations.connect_qr': 'Conectar WhatsApp — generar código QR',
    'conversations.generating_qr': 'Generando código QR…',
    'conversations.generating_qr_ensure': 'Preparando sesión de WhatsApp (creando instancia si es necesario)…',
    'conversations.view_archived': 'Ver conversaciones archivadas',
    'conversations.bot_title': 'DelivAI — bot de atención',
    'conversations.bot_on': 'Bot encendido — clic para apagar',
    'conversations.bot_off': 'Bot apagado — clic para encender',
    'conversations.bot_locked': 'Botón del Zap bloqueado por DelivAI. No se puede encender ni apagar aquí.',
    'conversations.bot_locked_aria': 'Botón del Zap bloqueado por DelivAI',
    'conversations.bot_group_save_failed': 'No pude guardar el bot de esta tienda. Intente de nuevo.',
    'conversations.bot_disable': 'Apagar bot de atención',
    'conversations.bot_enable': 'Encender bot de atención',
    'conversations.bot_badge_lock': 'LOCK',
    'conversations.bot_badge_on': 'ON',
    'conversations.bot_badge_off': 'OFF',
    'conversations.wa_group': 'WhatsApps del grupo',
    'conversations.wa_group_open': 'Abrir conexiones del grupo',
    'conversations.wa_group_close': 'Cerrar conexiones del grupo',
    'conversations.tone_ok': 'Atención automática bien',
    'conversations.tone_paused': 'Bot pausado en esta conversación',
    'conversations.tone_watch': 'La IA tiene dificultad en esta conversación',
    'conversations.tone_urgent': 'Necesita atendente urgente',
    'conversations.load_image': 'Cargar imagen',
    'conversations.load_video': 'Cargar video',
    'conversations.cancel_selection': 'Cancelar selección de mensajes',

    'company.bairro_name_placeholder': 'Nombre del barrio',
    'company.bairro_edit_title': 'Editar nombre del barrio',
    'company.bairro_delete_title': 'Eliminar barrio',
    'company.bairro_add_title': 'Agregar barrio',

    'common.hours_range': '{open} a {close}',

    'clients.title': 'Clientes',
    'clients.filter_all': 'Todas las empresas',
    'clients.filter_aria': 'Empresa',
    'clients.subtitle': 'Clientes guardados para pedidos y citas (autocompletar por teléfono)',
    'clients.search': 'Buscar por nombre o teléfono...',
    'clients.sort_by': 'Ordenar por',
    'clients.name': 'Nombre',
    'clients.ascending': 'Ascendente (A → Z)',
    'clients.descending': 'Descendente (Z → A)',
    'clients.new': 'Nuevo cliente',
    'clients.select_all': 'Seleccionar todos ({count} visibles)',
    'clients.bulk_edit': 'Editar campo en masa',
    'clients.edit': 'Editar',
    'clients.order_one': '{count} pedido',
    'clients.orders_many': '{count} pedidos',
    'clients.badge_qr': 'Vino del QR',
    'clients.qr_stat_issued': 'QR generados (enlaces)',
    'clients.qr_stat_claimed': 'QR canjeados',
    'clients.qr_stat_clients': 'Clientes nuevos por QR',
    'clients.modal_edit': 'Editar cliente',
    'clients.modal_new': 'Nuevo cliente',
    'clients.field_name': 'Nombre',
    'clients.field_phone': 'Teléfono',
    'clients.field_zip': 'Código postal',
    'clients.field_address': 'Dirección',
    'clients.field_number': 'Número',
    'clients.field_neighborhood': 'Barrio',
    'clients.field_complement': 'Complemento',
    'clients.field_reference': 'Referencia',
    'clients.sort_orders': 'Pedidos en la tienda',
    'clients.phone_missing': 'Teléfono no informado',
    'clients.no_name': 'Sin nombre',
    'clients.empty_none': 'No hay clientes registrados. Use el botón de arriba o registre al hacer pedidos/citas.',
    'clients.empty_search': 'Ningún resultado para esta búsqueda.',
    'clients.empty_error': 'Corrija el error de arriba y actualice o cambie la búsqueda para intentar de nuevo.',
    'clients.searching_zip': 'Buscando código postal...',
    'clients.placeholder_name': 'Nombre del cliente',
    'clients.placeholder_phone': '(00) 00000-0000',
    'clients.placeholder_zip': '00000-000',
    'clients.placeholder_address': 'Calle, avenida...',
    'clients.placeholder_number': 'Nº',
    'clients.placeholder_neighborhood': 'Barrio',
    'clients.placeholder_complement': 'Apto, bloque...',
    'clients.placeholder_reference': 'Punto de referencia',
    'clients.bulk_title': 'Actualización masiva',
    'clients.bulk_field': 'Campo',
    'clients.bulk_new_value': 'Nuevo valor',
    'clients.bulk_placeholder': 'Valor para todos los seleccionados',
    'clients.bulk_apply': 'Aplicar',
    'clients.bulk_applying': 'Aplicando...',
    'clients.bulk_hint': 'Aplicar el mismo valor a {count} cliente(s) seleccionado(s) (con ID válido).',

    'company.delivery_data_hint': 'Datos de entrega (doble clic para editar)',
    'company.fee_label': 'Tarifa',
    'company.neighborhoods_count': '{count} barrios',
    'company.order_types_public': 'Tipos de pedido en la página pública',
    'company.pix_data': 'Datos PIX',
    'company.menu_images': 'Imágenes del menú',
    'company.delivery_type_delivery': 'Entrega',
    'company.delivery_type_pickup': 'Retiro',
    'company.delivery_type_dine_in': 'Consumo en el local',
    'company.no_delivery_fees': 'No hay tarifas de entrega configuradas',
    'company.configure_fees_hint': 'Configure tarifas y barrios para comenzar',
    'company.add_first_fee': '+ Agregar primera tarifa',
    'company.pix_disabled_hint': 'PIX desactivado en las formas de pago de arriba. Los datos permanecen guardados; active PIX para editar.',
    'company.pix_key_type': 'Tipo de clave *',
    'company.pix_key': 'Clave PIX *',
    'company.pix_legal_name': 'Razón social *',
    'company.pix_bank': 'Banco *',
    'company.pix_key_placeholder': 'Ingrese la clave PIX',
    'company.pix_key_type_first': 'Seleccione el tipo primero',
    'company.pix_holder_placeholder': 'Nombre del titular / empresa',
    'company.pix_bank_placeholder': 'Ej.: Nubank, Itaú...',
    'company.save_pix': 'Guardar datos PIX',
    'company.no_payment_methods': 'Ninguna forma de pago activada',
    'company.no_payment_methods_hint': 'Active al menos una forma de pago para que los clientes puedan hacer pedidos.',
    'company.order_types_hint': 'Elija qué tipos de pedido puede seleccionar el cliente en la página pública (entrega, retiro, consumo en el local). En el POS interno, todos los tipos siguen disponibles.',
    'company.delivery_loading': 'Cargando datos de entrega...',
    'company.upload_menu_image': 'Subir nueva imagen',
    'company.menu_images_count': 'Imágenes del menú ({count} imágenes)',

    'profile.unnamed': 'Usuario',
    'profile.email': 'Correo',
    'profile.phone': 'Teléfono',
    'profile.save': 'Guardar contacto',
    'profile.saving': 'Guardando…',
    'profile.saved': 'Contacto actualizado.',
    'profile.save_error': 'Error al guardar contacto.',
    'profile.save_unavailable': 'Edición solo para cuentas de colaborador.',
    'profile.identity_locked': 'Nombre y CPF/CNPJ no se editan aquí.',
    'profile.owner_hint': 'Datos de la tienda se editan en Empresa.',
    'profile.open_company': 'Abrir datos de la empresa',
    'profile.panel_label': 'Datos del perfil',
    'profile.change_password': 'Cambiar contraseña',
    'profile.password.title': 'Cambiar contraseña',
    'profile.password.subtitle': 'Ingresa tu contraseña actual y elige una nueva contraseña segura.',
    'profile.password.current': 'Contraseña actual',
    'profile.password.new': 'Nueva contraseña',
    'profile.password.confirm': 'Confirmar nueva contraseña',
    'profile.password.submit': 'Guardar nueva contraseña',
    'profile.password.saving': 'Guardando…',
    'profile.password.changed': 'Contraseña cambiada con éxito.',
    'profile.password.error': 'No se pudo cambiar la contraseña.',
    'profile.password.same_as_old': 'La nueva contraseña debe ser distinta de la actual.',
    'profile.password.mismatch': 'Las contraseñas no coinciden.',
    'profile.password.forgot': 'Olvidé mi contraseña',
    'profile.password.close': 'Cerrar',

    'dev.nav.home': 'Inicio',
    'dev.nav.licenses': 'Licencias',
    'dev.nav.contracts': 'Contratos',
    'dev.nav.contracts_title': 'Contratos y borradores legales',
    'dev.nav.evolution': 'Evolution',
    'dev.nav.companies': 'Empresas',
    'dev.nav.prompts': 'Prompts',
    'dev.nav.consumers': 'Clientes',
    'dev.nav.database': 'Base de datos',
    'dev.nav.logs': 'Logs',
    'dev.nav.observability': 'Observabilidad',
    'dev.nav.ops_alerts': 'Alertas',
    'dev.nav.platform_ai': 'Claves de API',
    'dev.nav.platform_ai_title': 'Claves de API DelivAI',
    'dev.nav.agenda': 'Agenda',
    'dev.nav.agenda_title': 'Agenda — defaults y plantillas',
    'dev.card.platform_ai.title': 'Claves de API',
    'dev.card.platform_ai.desc':
      'Claves por servicio (Atención, Fast Order) para todos los clientes sin clave propia.',
    'dev.card.agenda.title': 'Agenda',
    'dev.card.agenda.desc':
      'Defaults/límites de agenda y plantillas WhatsApp (plataforma y por tienda).',
    'dev.agenda.title': 'Agenda — DelivAI',
    'dev.agenda.hint':
      'Parámetros Dev: defaults, límites (caps) y plantillas WhatsApp. La tienda ajusta el resto en Empresa → Agenda.',
    'dev.agenda.loading': 'Cargando…',
    'dev.agenda.load_error': 'No se pudieron cargar los defaults de agenda.',
    'dev.agenda.save_error': 'No se pudo guardar.',
    'dev.agenda.saved': 'Defaults de agenda guardados.',
    'dev.agenda.saving': 'Guardando…',
    'dev.agenda.save': 'Guardar defaults',
    'dev.agenda.updated': 'Actualizado',
    'dev.agenda.defaults_title': 'Defaults y límites (Dev)',
    'dev.agenda.defaults_hint':
      'Defaults = valor inicial/fallback. Caps = máximo que la tienda puede configurar.',
    'dev.agenda.default_mode': 'Modo predeterminado',
    'dev.agenda.store_can_change_mode': 'La tienda puede cambiar el modo de agenda',
    'dev.agenda.default_slot': 'Slot predeterminado (min)',
    'dev.agenda.default_lead': 'Antelación predeterminada (min)',
    'dev.agenda.default_max_days': 'Días hacia adelante (default)',
    'dev.agenda.default_buffer': 'Buffer predeterminado (min)',
    'dev.agenda.default_stations': 'Estaciones predeterminadas',
    'dev.agenda.default_timezone': 'Fuso horario predeterminado',
    'dev.agenda.cap_max_days': 'Tope: días hacia adelante',
    'dev.agenda.cap_min_slot': 'Tope mínimo: slot (min)',
    'dev.agenda.cap_max_slot': 'Tope máximo: slot (min)',
    'dev.agenda.cap_stations': 'Tope: estaciones',
    'dev.agenda.cap_buffer': 'Tope: buffer (min)',
    'dev.agenda.templates_platform': 'Plantillas WhatsApp (default plataforma)',
    'dev.agenda.templates_platform_hint':
      'Usadas cuando la tienda aún no personalizó. También editables por CNPJ abajo.',
    'dev.agenda.templates_store': 'Plantillas de la tienda (por CNPJ)',
    'dev.agenda.templates_store_hint':
      'Cargue una tienda para editar textos de confirmación/recordatorio/cancelación.',
    'dev.agenda.cnpj_invalid': 'Informe un CNPJ válido (14 dígitos).',
    'dev.agenda.store_load_error': 'No se pudo cargar la tienda.',
    'dev.agenda.load_store': 'Cargar tienda',
    'dev.platform_ai.title': 'Claves de API — DelivAI',
    'dev.platform_ai.models_button': 'Modelos',
    'dev.platform_ai.models_title': 'Modelos disponibles',
    'dev.platform_ai.models_hint': 'Marque los que pueden aparecer en la lista. Al menos uno queda activo.',
    'dev.platform_ai.models_save': 'Guardar modelos',
    'dev.platform_ai.models_saved_local': 'La lista vale en esta pantalla. El servidor aún no la guardó.',
    'dev.platform_ai.model_hint.luna': 'Barato y rápido, para mucho volumen',
    'dev.platform_ai.model_hint.nano5': 'El más barato de la línea 5',
    'dev.platform_ai.model_hint.mini5': 'Rápido, bueno para una tarea definida',
    'dev.platform_ai.model_hint.sol': 'Equilibrio entre capacidad y costo',
    'dev.platform_ai.model_hint.nano41': 'Ligero. Ya estaba en esta pantalla',
    'dev.platform_ai.model_hint.mini41': 'El predeterminado de hoy, bajo costo',
    'dev.platform_ai.model_hint.full41': 'Más capaz que el mini, más caro',
    'dev.platform_ai.model_hint.mini4o': 'Línea anterior, versión ligera',
    'dev.platform_ai.model_hint.full4o': 'Línea anterior, más capaz',
    'dev.platform_ai.model_hint.astra': 'El más capaz. Mucho más caro',
    'dev.platform_ai.hint':
      'Claves DelivAI por servicio. Clientes sin clave en la empresa usan estas. Empresa con clave propia la usa en todos los servicios.',
    'dev.platform_ai.loading': 'Cargando credenciales…',
    'dev.platform_ai.load_error': 'No se pudieron cargar las credenciales de IA.',
    'dev.platform_ai.save_error': 'No se pudo guardar.',
    'dev.platform_ai.saved': 'Credenciales DelivAI guardadas.',
    'dev.platform_ai.saving': 'Guardando…',
    'dev.platform_ai.save': 'Guardar claves de API',
    'dev.platform_ai.privacy': 'Las claves no se vuelven a mostrar tras guardar — solo el estado.',
    'dev.platform_ai.bot_title': 'Atención (bot WhatsApp)',
    'dev.platform_ai.bot_hint': 'Usada por todas las tiendas sin clave propia en el bot.',
    'dev.platform_ai.fast_title': 'Fast Order (iFood + pedido estándar)',
    'dev.platform_ai.fast_hint': 'Usada por todas las tiendas sin clave propia en Fast Order.',
    'dev.platform_ai.general_title': 'Fallback general',
    'dev.platform_ai.general_hint':
      'Si el servicio no tiene clave, se usa esta antes de la variable de entorno del servidor.',
    'dev.platform_ai.new_key': 'Nueva clave (vacío = mantener)',
    'dev.platform_ai.model': 'Modelo',
    'dev.platform_ai.model_env': '(env del servidor)',
    'dev.platform_ai.clear_key': 'Quitar esta clave',
    'dev.platform_ai.status_set': 'Registrada',
    'dev.platform_ai.status_fallback': 'Usa fallback / env',
    'dev.platform_ai.status_env': 'Usando env del servidor',
    'dev.card.ops_alerts.title': 'Alertas operativos',
    'dev.card.ops_alerts.desc':
      'Rutas de alerta (Google Calendar, WhatsApp…) y quién recibe avisos en caída masiva.',
    'dev.ops_alerts.title': 'Alertas operativos',
    'dev.ops_alerts.hint':
      'Global (todas las tiendas / Devs) y por tienda en esta pantalla. Sin módulo de agenda el botón queda bloqueado en apagado.',
    'dev.ops_alerts.loading': 'Cargando alertas…',
    'dev.ops_alerts.load_error': 'No se pudieron cargar las alertas.',
    'dev.ops_alerts.save_error': 'No se pudo guardar.',
    'dev.ops_alerts.routes': 'Rutas / servicios',
    'dev.ops_alerts.recipients': 'Quién es notificado (Dev)',
    'dev.ops_alerts.no_recipients': 'Sin destinatarios — agréguese abajo.',
    'dev.ops_alerts.active': 'Activo',
    'dev.ops_alerts.inactive': 'Inactivo',
    'dev.ops_alerts.channel_stores': 'Tiendas',
    'dev.ops_alerts.channel_devs': 'Devs',
    'dev.ops_alerts.global_stores': 'Todas las tiendas',
    'dev.ops_alerts.gcal_store_rule':
      'Una tienda sin módulo de agenda no recibe este WhatsApp — solo el canal Dev, si está activo.',
    'dev.ops_alerts.stores_title': 'Por tienda',
    'dev.ops_alerts.stores_hint':
      'Elija la ruta y active/desactive cada tienda. Sin módulo de agenda, Google Calendar queda bloqueado en apagado.',
    'dev.ops_alerts.stores_search': 'Buscar tienda o CNPJ',
    'dev.ops_alerts.stores_empty': 'Ninguna tienda en esta búsqueda.',
    'dev.ops_alerts.store_locked_no_module':
      'Sin módulo de agenda — la alerta de la tienda permanece apagada.',
    'dev.ops_alerts.store_locked_global': 'El canal global de tiendas está apagado.',
    'dev.ops_alerts.store_toggled': 'Alerta de la tienda actualizada.',
    'dev.ops_alerts.emitted_title': 'Notificaciones emitidas',
    'dev.ops_alerts.emitted_empty': 'Ninguna alerta emitida en las últimas 24 h.',
    'dev.ops_alerts.emitted_refresh': 'Actualizar',
    'dev.ops_alerts.route_toggled': 'Ruta actualizada.',
    'dev.ops_alerts.mass_threshold': 'Umbral masa',
    'dev.ops_alerts.cooldown': 'Cooldown tienda / Dev',
    'dev.ops_alerts.add_recipient': 'Nuevo destinatario',
    'dev.ops_alerts.name_placeholder': 'Nombre',
    'dev.ops_alerts.phone_placeholder': 'WhatsApp (código + número)',
    'dev.ops_alerts.save_recipient': 'Guardar destinatario',
    'dev.ops_alerts.recipient_saved': 'Destinatario guardado.',
    'dev.ops_alerts.recipient_deleted': 'Destinatario eliminado.',
    'dev.ops_alerts.delete': 'Eliminar',
    'dev.ops_alerts.delete_title': 'Eliminar destinatario',
    'dev.ops_alerts.delete_confirm': '¿Quitar a esta persona de los alertas Dev?',
    'dev.consumers.clear_response_id': 'Zerar response id',
    'dev.consumers.clear_response_id_hint':
      'Quita el openai_response_id de este cliente. El próximo mensaje inicia contexto nuevo en la IA.',
    'dev.consumers.clear_response_id_ok': 'Response id borrado.',
    'dev.consumers.clear_response_id_none': 'No había response id para borrar.',
    'dev.consumers.clear_response_id_error': 'No se pudo borrar el response id.',
    'dev.consumers.close_detail': 'Cerrar',
    'dev.nav.permissions': 'Permisos',
    'dev.nav.bot': 'Bot',
    'dev.nav.bot_title': 'Control del bot WhatsApp',
    'dev.nav.delidesk': 'DeliDesk',
    'dev.nav.delidesk_title': 'Funciones globales del DeliDesk',
    'dev.bot.title': 'Control del bot',
    'dev.bot.subtitle':
      'Global ON libera el Zap de todas las tiendas — cada dueño enciende o apaga su propio robot. Global OFF bloquea todos. Con el global encendido puede bloquear una tienda; con el global apagado, puede liberar solo una.',
    'dev.bot.platform_title': 'Bot de la plataforma',
    'dev.bot.platform_hint':
      'Incidente (spam/ban): apague el global. Los dueños quedan con el Zap bloqueado hasta que lo encienda de nuevo o libere una tienda abajo.',
    'dev.bot.platform_on': 'GLOBAL ON',
    'dev.bot.platform_off': 'GLOBAL OFF',
    'dev.bot.platform_unknown': '…',
    'dev.bot.platform_on_ok': 'Global encendido — Zap liberado en todas las tiendas',
    'dev.bot.platform_off_ok': 'Global apagado — Zap bloqueado en todas las tiendas',
    'dev.bot.platform_locked_hint': 'Reactive el kill-switch global primero',
    'dev.bot.companies_title': 'Botón del Zap por tienda',
    'dev.bot.companies_empty': 'No hay tiendas registradas',
    'dev.bot.company_saved': 'Botón de la tienda actualizado',
    'dev.bot.store_robot_on': 'Robot de la tienda: encendido (lo encendió el dueño)',
    'dev.bot.store_robot_off': 'Robot de la tienda: apagado (lo apagó el dueño)',
    'dev.bot.store_button_locked': 'Zap bloqueado — el robot no atiende',
    'dev.bot.store_inactive':
      'Tienda inactiva en el panel (licencia) — el Zap puede seguir conectado',
    'dev.bot.store_not_in_panel':
      'Instancia en Evolution sin registro en el panel — bloqueo no disponible',
    'dev.bot.row_free': 'LIBRE',
    'dev.bot.row_locked': 'BLOQUEADO',
    'dev.bot.row_unavailable': 'SIN REGISTRO',
    'dev.bot.load_error': 'No se pudo cargar el estado del bot',
    'dev.bot.save_error': 'No se pudo guardar',
    'dev.evolution.title': 'Instancias Evolution',
    'dev.evolution.subtitle':
      'Vista por ambiente (Evolution test o prod) y destino del webhook (backend test o prod). Lectura/escritura vía proxy del backend.',
    'dev.evolution.logs_title': 'Logs Evolution',
    'dev.evolution.logs_empty': 'Ningún log Evolution en las últimas 6 h.',
    'dev.evolution.logs_refresh': 'Actualizar',
    'dev.evolution.logs_error': 'No se pudieron cargar los logs Evolution.',
    'dev.evolution.view_env_label': 'Ver Evolution',
    'dev.evolution.view_env_help':
      'Cambia qué Evolution API estás viendo. Cada ambiente tiene sus propias instancias.',
    'dev.evolution.view_env_test_hint': 'Backend/Evolution de prueba',
    'dev.evolution.view_env_prod_hint': 'Backend/Evolution de producción',
    'dev.evolution.env_test': 'Prueba',
    'dev.evolution.env_prod': 'Producción',
    'dev.evolution.webhook_dest_label': 'Destino del webhook',
    'dev.evolution.webhook_dest_help':
      'A dónde esta Evolution envía eventos. Mismo entorno = URL Docker (red del proyecto). Otro entorno = HTTPS público (stacks EasyPanel aislados — el hostname Docker del otro proyecto no alcanza).',
    'dev.evolution.webhook_dest_same': 'Mismo entorno',
    'dev.evolution.webhook_dest_cross': 'Otro entorno (HTTPS)',
    'dev.evolution.view_prod_proxy_hint':
      'La vista Producción habla con el backend de producción. El proxy /api/evolution/webhook solo está en test hasta el release a main — si aparece “No static resource”, es eso.',
    'dev.evolution.webhook_dest_locked':
      'Instancia desconectada en este ambiente — conecte (QR) para cambiar el destino del webhook.',
    'dev.evolution.webhook_dest_lock_badge': 'LOCK',
    'dev.evolution.webhook_custom': 'URL personalizada',
    'dev.evolution.list_error': 'Error al listar instancias.',
    'dev.evolution.webhook_read_error': 'Error al leer webhook',
    'dev.evolution.webhook_save_error': 'No se pudo guardar el webhook. Inténtelo de nuevo.',
    'dev.evolution.settings_read_error': 'Error al leer settings',
    'dev.evolution.settings_save_error': 'No se pudieron guardar los ajustes. Inténtelo de nuevo.',
    'dev.evolution.reload_partial': 'No se pudo recargar de la API',
    'dev.evolution.detail_error': 'Error al cargar detalles.',
    'dev.evolution.connect_error': 'Error al conectar / QR',
    'dev.evolution.webhook_url_missing': 'URL de webhook del ambiente no disponible.',
    'dev.evolution.webhook_pointed_test': 'Webhook apuntando a prueba (sandbox).',
    'dev.evolution.webhook_pointed_prod': 'Webhook apuntando a producción.',
    'dev.evolution.lease_error': 'Webhook guardado, pero el lease de prueba no inició en el backend.',
    'dev.evolution.lease_hint':
      'Desvío temporal (vista Producción → destino Prueba): sin actividad por 2h el webhook de esta instancia vuelve al backend de producción. En el entorno de prueba, el destino natural es la propia prueba — sin auto-revert.',
    'dev.evolution.connected': 'Conectado',
    'dev.evolution.disconnected': 'Desconectado',
    'dev.card.bot.title': 'Bot WhatsApp',
    'dev.card.bot.desc':
      'Kill-switch global y por tienda. Con global OFF el botón de la tienda queda indisponible.',
    'dev.delidesk.title': 'Impresión — DeliDesk y PrintNode',
    'dev.delidesk.subtitle':
      'Kill-switches globales y por tienda (como el bot): cola DeliDesk, captura de marketplace y PrintNode — independientes.',
    'dev.delidesk.warning':
      'Efectivo = global ON y tienda ON. Apagar PrintNode de una tienda no corta la cola DeliDesk; apagar captura no corta comandas.',
    'dev.delidesk.platform_title': 'Globales (todas las tiendas)',
    'dev.delidesk.companies_title': 'Por tienda',
    'dev.delidesk.companies_hint':
      'Con global OFF el chip de la tienda queda bloqueado. Reactive el global para cambiar la tienda.',
    'dev.delidesk.companies_empty': 'Ninguna tienda activa encontrada',
    'dev.delidesk.company_saved': 'Función de la tienda actualizada',
    'dev.delidesk.company_backend': 'Backend: {backend}',
    'dev.delidesk.platform_locked_hint': 'Reactive primero el kill-switch global de esta función',
    'dev.delidesk.chip.printer': 'Cola',
    'dev.delidesk.chip.virtual_capture': 'Captura',
    'dev.delidesk.chip.printnode': 'PrintNode',
    'dev.delidesk.applies_label': 'Aplica a:',
    'dev.delidesk.empty': 'Ninguna función disponible',
    'dev.delidesk.load_error': 'No se pudieron cargar las funciones del DeliDesk',
    'dev.delidesk.save_error': 'No se pudo guardar',
    'dev.delidesk.feature_on': 'GLOBAL ON',
    'dev.delidesk.feature_off': 'GLOBAL OFF',
    'dev.delidesk.feature_on_ok': 'Función {feature} activada',
    'dev.delidesk.feature_off_ok': 'Función {feature} desactivada',
    'dev.delidesk.feature.generic_hint': 'Control global de esta función.',
    'dev.delidesk.feature.printer.title': 'Cola DeliDesk (comandas)',
    'dev.delidesk.feature.printer.hint':
      'Cupones del panel DelivAI en cola e impresos por DeliDesk (jobs/next). No controla la captura de marketplace.',
    'dev.delidesk.feature.printer.applies':
      'enqueue de comanda · poll jobs/next · impresión térmica vía agente',
    'dev.delidesk.feature.virtual_capture.title': 'Captura de marketplace (virtual)',
    'dev.delidesk.feature.virtual_capture.hint':
      'Cuando un marketplace (iFood, etc.) imprime en la impresora virtual del DeliDesk, el cupón sube para que la IA cree el pedido.',
    'dev.delidesk.feature.virtual_capture.applies':
      'API de captura virtual · listener Windows · creación de pedido desde cupón del marketplace',
    'dev.delidesk.feature.printnode.title': 'PrintNode',
    'dev.delidesk.feature.printnode.hint':
      'Envío de cupón vía PrintNode (tiendas con print_backend=printnode o fallback si la cola DeliDesk está OFF).',
    'dev.delidesk.feature.printnode.applies':
      'OrderPrintService → PrintNode · independiente de la captura de marketplace y de la cola del agente',
    'dev.card.delidesk.title': 'Impresión',
    'dev.card.delidesk.desc':
      'Kill-switch global + por tienda: cola DeliDesk, captura de marketplace y PrintNode.',
    'dev.home.title': 'Panel de control',
    'dev.home.subtitle':
      'Licencias, Evolution, empresas, clientes, prompts, base de datos, logs y permisos — consola interna DelivAI.',
    'dev.home.overview': 'Visión general',
    'dev.home.overview_hint': 'Elija un área. Cada tarjeta abre una herramienta interna.',
    'dev.home.open': 'Abrir',
    'dev.home.continue': 'Continuar',
    'dev.access.denied_title': 'Sin permiso',
    'dev.access.denied_body':
      'Su cargo no tiene acceso. Pida al administrador o inicie sesión de nuevo tras cambios de permisos.',
    'dev.card.licenses.title': 'Licencias y planes',
    'dev.card.licenses.desc': 'Consultar empresa por CNPJ y ajustar días y pay_types.',
    'dev.nav.reports': 'Reportes',
    'dev.nav.reports_title': 'Reportes de las tiendas',
    'dev.card.reports.title': 'Reportes',
    'dev.card.reports.desc': 'Avisos enviados desde la bandera en las pantallas de la tienda, con texto e imágenes.',
    'dev.reports.empty': 'Ningún aviso.',
    'dev.reports.empty_phase': 'Ningún aviso en esta fase.',
    'dev.reports.load_failed': 'No se pudieron cargar los avisos.',
    'dev.reports.save_failed': 'No se pudo guardar la fase.',
    'dev.reports.image': 'Imagen {n}',
    'dev.reports.company_unknown': 'Tienda no identificada',
    'dev.reports.team': 'Equipo DelivAI',
    'dev.reports.filter_label': 'Fase',
    'dev.reports.filter_all': 'Todas',
    'dev.reports.note_label': 'Nota',
    'dev.reports.note_placeholder': 'Qué vamos a hacer con este aviso',
    'dev.reports.phase.open': 'Nuevo',
    'dev.reports.phase.reviewed': 'Revisado',
    'dev.reports.phase.doing': 'En curso',
    'dev.reports.phase.done': 'Hecho',
    'dev.reports.phase.ignored': 'Ignorar',
    'dev.card.contracts.title': 'Contratos legales',
    'dev.card.contracts.desc': 'Revisar cláusulas B2B, términos del consumidor y previews.',
    'dev.contracts.title': 'Contratos por cláusulas',
    'dev.contracts.subtitle':
      'Edite por sector, elija la versión y active la oficial. Los nuevos aceptes usan el hash de la versión oficial de cada pestaña.',
    'dev.contracts.version': 'Versión del documento',
    'dev.contracts.official': 'oficial',
    'dev.contracts.awaiting': 'en revisión',
    'dev.contracts.notices': 'Para el abogado',
    'dev.contracts.subs_paying': 'Suscripciones activas de pago',
    'dev.contracts.subs_free': 'Suscripciones activas en período gratis',
    'dev.contracts.subs_hint': 'La tienda marcada como prueba no entra en estos números.',
    'dev.company.page_title': 'Mantenimiento de la empresa',
    'dev.company.page_hint': 'Registro de la tienda: licencia, operación, menú e IA. Los clientes finales están en la pestaña Clientes.',
    'dev.company.jump': 'Secciones del registro',
    'dev.company.jump_data': 'Datos',
    'dev.company.jump_ops': 'Operación',
    'dev.company.jump_license': 'Licencia',
    'dev.company.jump_menu': 'Menú',
    'dev.company.test_store': 'Tienda de prueba',
    'dev.company.test_store_hint': 'No entra en la cuenta de suscripciones del abogado.',
    'dev.company.fees_title': 'Barrios y tasas',
    'dev.company.fees_hint': 'Una línea por barrio. El JSON queda cerrado.',
    'dev.company.fees_add': 'Agregar barrio',
    'dev.company.fees_advanced': 'JSON avanzado',
    'dev.contracts.notice_define': 'Definir',
    'dev.contracts.notice_comment': 'Comentario',
    'dev.contracts.notice_done': 'Concluida',
    'dev.contracts.notice_useless': 'Inútil',
    'dev.contracts.notice_save': 'Guardar',
    'dev.contracts.decision_pending': 'Sin definir',
    'dev.contracts.decision_yes': 'Hay que cambiar el contrato',
    'dev.contracts.decision_no': 'No hay que cambiar',
    'dev.contracts.add_version': 'Nueva versión (copia de la actual)',
    'dev.contracts.version_added': 'Nueva versión creada. Actívela como oficial cuando esté lista.',
    'dev.contracts.activate_official': 'Activar como oficial',
    'dev.contracts.official_on': 'Versión oficial',
    'dev.contracts.version_activated': 'Versión activada como oficial en este sector.',
    'dev.contracts.view_clauses': 'Cláusula a cláusula',
    'dev.contracts.view_document': 'Documento completo',
    'dev.contracts.doc_title': 'Título del documento',
    'dev.contracts.save': 'Guardar borradores',
    'dev.contracts.save_ok': 'Borradores guardados. Active la versión oficial al publicar un cambio relevante.',
    'dev.contracts.doc_view': 'Ver',
    'dev.contracts.doc_edit': 'Editar texto',
    'dev.contracts.doc_apply': 'Aplicar al documento',
    'dev.contracts.doc_apply_ok': 'Texto aplicado en esta versión. Guarde y active como oficial para publicar.',
    'dev.contracts.doc_edit_hint':
      'Pegue el contrato completo aquí. Use los botones para insertar campos por tienda. Ctrl+Z deshace el último cambio.',
    'dev.contracts.doc_cancel': 'Cancelar edición',
    'dev.contracts.doc_undo': 'Deshacer (Ctrl+Z)',
    'dev.contracts.insert_fields': 'Insertar campo dinámico',
    'dev.contracts.suggest_fields': 'Detectar CNPJ en el texto',
    'dev.contracts.suggest_ok': 'Campos dinámicos sugeridos. Revise y pulse Aplicar.',
    'dev.contracts.suggest_none': 'No se encontró CNPJ de tienda para convertir.',
    'dev.contracts.delete_version': 'Eliminar versión',
    'dev.contracts.delete_version_title': '¿Eliminar esta versión?',
    'dev.contracts.delete_version_msg':
      'La versión va a la papelera (se puede restaurar). No se puede quitar la última versión viva. Solo master.',
    'dev.contracts.delete_version_ok': 'Versión movida a la papelera.',
    'dev.contracts.delete_version_blocked': 'No se puede eliminar la única versión activa.',
    'dev.contracts.master_only_publish': 'Solo un master puede activar la versión oficial.',
    'dev.contracts.master_only_delete': 'Solo un master puede eliminar versiones.',
    'dev.contracts.trash': 'Papelera de versiones',
    'dev.contracts.trash_empty': 'No hay versiones en la papelera.',
    'dev.contracts.restore_version': 'Restaurar',
    'dev.contracts.restore_version_ok': 'Versión restaurada de la papelera.',
    'dev.contracts.history': 'Puntos de recuperación',
    'dev.contracts.history_empty': 'Aún no hay puntos. Al aplicar el texto completo se guarda el contenido anterior.',
    'dev.contracts.restore_revision': 'Restaurar este punto',
    'dev.contracts.restore_revision_ok': 'Contenido anterior restaurado en esta versión.',
    'dev.contracts.field.razao': 'Razón social (tienda)',
    'dev.contracts.field.cnpj': 'CNPJ (tienda)',
    'dev.contracts.field.endereco': 'Dirección (tienda)',
    'dev.contracts.field.representante': 'Representante',
    'dev.contracts.field.versao': 'Versión',
    'dev.contracts.field.data': 'Fecha de actualización',
    'dev.contracts.field.email': 'Email de contacto',
    'dev.contracts.field.dpo': 'DPO',
    'dev.contracts.field.remuneracao': 'Remuneración',
    'dev.card.database.title': 'Base de datos',
    'dev.card.database.desc': 'Studio interno para inspeccionar tablas y soporte.',
    'dev.card.logs.title': 'Executions / logs',
    'dev.card.logs.desc': 'Ejecuciones del backend y flujos en tiempo real.',
    'dev.card.observability.title': 'Observabilidad',
    'dev.card.observability.desc': 'Métricas Prometheus y logs Loki.',
    'dev.home.team_badge': 'Equipo DelivAI',
    'dev.home.stack_badge': 'Backend · Postgres · MinIO',
    'dev.home.internal_badge': 'Interno',
    'dev.permissions.relogin_hint':
      'Permisos actualizados. La persona afectada debe cerrar sesión y volver a entrar.',
    'dev.observability.subtitle':
      'Métricas (Prometheus) y logs (Loki) del backend DelivAI en tiempo casi real.',
    'dev.observability.refresh': 'Actualizar',
    'dev.observability.checking': 'verificando stack…',
    'dev.observability.no_data': 'sin datos',
    'dev.observability.window': 'Ventana:',
    'dev.observability.prometheus_warning':
      'Prometheus deshabilitado o inaccesible. Configure PROMETHEUS_URL y LOKI_URL en el backend.',

    'collaborator.sidebar.title': 'Colaborador',
    'collaborator.sidebar.deliveries': 'Mis entregas',
    'collaborator.sidebar.orders': 'PDV',
    'collaborator.sidebar.delivery_ops': 'Delivery',
    'collaborator.sidebar.agenda': 'Mi agenda',
    'collaborator.no_access.title': 'Sin permisos asignados',
    'collaborator.no_access.body':
      'Pida a la tienda que verifique sus permisos de módulos. Las pantallas aparecerán aquí cuando liberen el acceso.',
    
    // License Screen
    'license.title': 'Gestión de Licencia',
    'license.status': 'Estado de Licencia',
    'license.active': 'Activa',
    'license.inactive': 'Inactiva',
    'license.expired': 'Expirada',
    'license.paused': 'En pausa',
    'license.paused_message': 'El acceso está en pausa. Los días de abajo quedan guardados y no se gastan hasta que termine la pausa.',
    'license.ended_message': 'Los días se acabaron y el acceso al panel está desactivado. Renueve para volver.',
    'license.days_remaining': 'días restantes',
    'license.days_expired': 'días expirados',
    'license.details': 'Detalles de Licencia',
    'license.days_remaining_label': 'Días Restantes',
    'license.last_renewal': 'Última Renovación',
    'license.next_renewal': 'Próxima Renovación',
    'license.available_modules': 'Módulos Disponibles',
    'license.attendance_module': 'Módulo de Atención',
    'license.attendance_module_desc': 'Gestión de conversaciones y atención',
    'license.delivery_module': 'Módulo de Entrega',
    'license.delivery_module_desc': 'Gestión de repartidores',
    'license.manager_module': 'Módulo Resumen de Ventas',
    'license.manager_module_desc': 'Administración e informes',
     'license.module_scheduling_title': 'Módulo de agendamentos',
     'license.module_scheduling_desc': 'Reservas y agenda para clientes',
     'license.snapshot_card_title': 'Plan vigente y renovaciones',
     'license.modules_summary_hint':
       'Módulos habilitados por su contrato. Los nuevos aparecerán aquí cuando los active el equipo DelivAI.',
     'license.checkout_notice_title': 'Pagos en el panel',
     'manager.page_title': 'Resumen de ventas',
    'manager.filter_all': 'Todas',
    'manager.filter_sales': 'Ventas',
    'manager.filter_production': 'En producción',
    'manager.ticket_avg': 'Ticket medio',
    'manager.now': 'Ahora',
    'manager.before': 'Período anterior',
    'manager.by_store': 'Por tienda',
    'manager.cmv_later': 'Costos y CMV entran en un módulo siguiente. Este resumen se queda en las ventas.',
    'manager.page_subtitle': 'Panel financiero con métricas y rendimiento de productos vendidos',
    'license.active_status': 'Activo',
    'license.inactive_status': 'Inactivo',
    'license.available_plans': 'Planes Disponibles para Renovación',
    'license.monthly': 'Mensual',
    'license.quarterly': 'Trimestral',
         'license.annual': 'Anual',
     'license.annual_fire': 'ANUAL',
     'license.days_access': 'días de acceso completo',
     'license.days_30': '30 días de acceso completo',
     'license.days_90': '90 días de acceso completo',
     'license.days_365': '365 días de acceso completo',
    'license.per_month': 'por mes',
    'license.per_quarter': 'por trimestre',
    'license.per_year': 'por año',
    'license.savings': 'Ahorro de',
    'license.most_popular': 'Más Popular',
         'license.all_modules': 'Todos los Módulos',
     'license.suprema_title': 'SUPREMA - Todos los Módulos',
    
    // Company Screen
    'company.title': 'Configuración de Empresa',
    'kitchen.title': 'Dark kitchen',
    'kitchen.hint': 'El registro, las tasas y el menú siguen la tienda elegida. Los pedidos de las dos quedan en la misma lista.',
    'kitchen.store_label': 'Tienda en edición',
    'kitchen.catalog_label': 'Menú',
    'kitchen.mix_hint': 'Se pueden mezclar los menús en el mismo pedido.',
    'kitchen.other_cnpj': 'CNPJ de la otra tienda',
    'kitchen.email': 'Correo de la cocina',
    'kitchen.password': 'Contraseña',
    'kitchen.password_confirm': 'Confirmar contraseña',
    'kitchen.password_rules': 'Mínimo 6 caracteres, con mayúscula, número y un símbolo.',
    'kitchen.email_default': 'Correo de la Dark Kitchen',
    'kitchen.add_store': 'Agregar tienda',
    'kitchen.configure': 'Configurar',
    'kitchen.configure_title': 'Configurar la Dark Kitchen',
    'kitchen.login_email_hint': 'Este correo entra en la cocina, junto con el CNPJ de cualquier tienda.',
    'kitchen.save_email': 'Guardar correo',
    'kitchen.email_saved': 'Correo de la cocina actualizado.',
    'kitchen.close': 'Cerrar',
    'kitchen.extra_cost': 'La 1ª y la 2ª tienda entran en el plan de R$ 300. Desde la 3ª, cada tienda suma R$ 100.',
    'kitchen.need_plan': 'Comprar el plan Dark Kitchen',
    'kitchen.need_plan_hint': 'La invitación solo se libera después del pago del plan.',
    'kitchen.transform': 'Convertir en Dark Kitchen',
    'kitchen.invite': 'Invitar esta tienda',
    'kitchen.cancel': 'Cancelar',
    'kitchen.suggestions': 'Tiendas encontradas',
    'kitchen.other_store': 'Otra tienda',
    'kitchen.invite_sent': 'La otra tienda tiene que aceptar en su propio registro.',
    'kitchen.incoming': '{store} quiere operar junto con esta tienda.',
    'kitchen.accept': 'Aceptar',
    'kitchen.reject': 'Rechazar',
    'kitchen.accepted': 'Las tiendas están juntas.',
    'kitchen.configure_stores': 'Configurar tiendas',
    'kitchen.back': 'Volver',
    'kitchen.unlink_one': 'Separar',
    'kitchen.leave': 'Separar tiendas',
    'kitchen.leave_title': '¿Separar esta tienda?',
    'kitchen.leave_body': 'Volverá a entrar solo con su CNPJ. Los pedidos de cada una siguen en su registro.',
    'kitchen.err.company_not_found': 'No encontramos esa tienda.',
    'kitchen.err.same_store': 'Indique el CNPJ de la otra tienda.',
    'kitchen.err.already_linked': 'Esa tienda ya está en otra cocina.',
    'kitchen.err.invite_pending': 'Ya hay una invitación para esa tienda.',
    'kitchen.err.kitchen_mismatch': 'El correo o la contraseña no coinciden con esta cocina.',
    'kitchen.err.bad_password': 'Contraseña incorrecta.',
    'kitchen.err.weak_password': 'La contraseña necesita 6 caracteres, con mayúscula, número y un símbolo.',
    'kitchen.err.password_mismatch': 'Las contraseñas no coinciden.',
    'kitchen.err.email_taken': 'Ese correo ya es de otra cocina.',
    'kitchen.err.bad_email': 'Indique un correo válido.',
    'kitchen.err.no_invite': 'No hay invitación para aceptar.',
    'kitchen.err.not_linked': 'Esa tienda no está en esta cocina.',
    'kitchen.err.kitchen_not_found': 'Cocina no encontrada.',
    'kitchen.err.kitchen_failed': 'No se pudieron unir las tiendas. Intente de nuevo.',
    'kitchen.err.dark_kitchen_plan_required': 'Compre el plan Dark Kitchen antes de invitar otra tienda.',
    'company.tabs.company': 'Empresa',
    'company.tabs.menu': 'Menú',
    'company.tabs.delivery': 'Entrega',
    'company.tabs.agenda': 'Agenda',
    'company.tabs.aria': 'Secciones de configuración de la empresa',
    'company.header_subtitle_empresa': 'Datos registrales, contacto, dirección y logotipo.',
    'company.header_subtitle_menu': 'Menú y servicios ofrecidos por la tienda.',
    'company.header_subtitle_delivery': 'Tarifas, barrios y opciones de entrega.',
    'company.header_subtitle_agenda': 'Reglas de agenda de la tienda.',
    'company.empresa_toolbar_hint': 'Editar y actualizar valen solo para esta pestaña.',
    'company.agenda.title': 'Reglas de la agenda',
    'company.agenda.hint': 'Defina cómo agenda la tienda (calendario único, multi-profesional o espacio compartido). Active cada profesional en Colaboradores.',
    'company.agenda.open_employees': 'Abrir pantalla de Colaboradores',
    'company.agenda.empty': 'No hay colaboradores activos.',
    'company.agenda.area_placeholder': 'Área / especialidad',
    'company.agenda.toggle_on': 'Agenda activa',
    'company.agenda.toggle_off': 'Agenda inactiva',
    'company.agenda.saved': 'Reglas de agenda guardadas',
    'company.agenda.save_error': 'No se pudieron guardar las reglas de agenda',
    'company.agenda.load_error': 'No se pudieron cargar las reglas de agenda',
    'company.agenda.save': 'Guardar reglas',
    'company.agenda.saving': 'Guardando…',
    'company.agenda.mode': 'Modo de agenda',
    'company.agenda.mode_single': 'Agenda de la tienda (calendario único)',
    'company.agenda.mode_multi': 'Multi-profesional (pueden coincidir)',
    'company.agenda.mode_shared': 'Espacio compartido (sin conflicto)',
    'company.agenda.mode_single_hint': 'Usa el Google de la tienda. Sin selector de profesional.',
    'company.agenda.mode_multi_hint': 'Cada profesional con calendario propio; horarios pueden coincidir (salas separadas).',
    'company.agenda.mode_shared_hint': 'Mismo espacio/sala: horarios no pueden coincidir más allá del número de estaciones.',
    'company.agenda.slot_minutes': 'Intervalo entre horarios (min)',
    'company.agenda.min_lead_minutes': 'Antelación mínima (min)',
    'company.agenda.max_days_ahead': 'Máximo de días hacia adelante',
    'company.agenda.buffer_minutes': 'Descanso entre atenciones (min)',
    'company.agenda.shared_stations': 'Estaciones / salas compartidas',
    'company.agenda.timezone': 'Fuso horario',
    'company.agenda.country': 'País',
    'company.agenda.region': 'Región / ciudad',
    'company.agenda.country_br': 'Brasil',
    'company.agenda.country_pt': 'Portugal',
    'company.agenda.country_ar': 'Argentina',
    'company.agenda.country_py': 'Paraguay',
    'company.agenda.country_uy': 'Uruguay',
    'company.agenda.country_us': 'Estados Unidos',
    'company.agenda.country_ao': 'Angola',
    'company.agenda.country_mz': 'Mozambique',
    'company.agenda.region_sao_paulo': 'São Paulo (Brasilia)',
    'company.agenda.region_fortaleza': 'Fortaleza',
    'company.agenda.region_belem': 'Belém',
    'company.agenda.region_manaus': 'Manaus',
    'company.agenda.region_cuiaba': 'Cuiabá',
    'company.agenda.region_rio_branco': 'Rio Branco',
    'company.agenda.region_lisbon': 'Lisboa',
    'company.agenda.region_buenos_aires': 'Buenos Aires',
    'company.agenda.region_asuncion': 'Asunción',
    'company.agenda.region_montevideo': 'Montevideo',
    'company.agenda.region_new_york': 'Nueva York (Este)',
    'company.agenda.region_chicago': 'Chicago (Central)',
    'company.agenda.region_denver': 'Denver (Montaña)',
    'company.agenda.region_los_angeles': 'Los Ángeles (Pacífico)',
    'company.agenda.region_luanda': 'Luanda',
    'company.agenda.region_maputo': 'Maputo',
    'company.agenda.matrix_title': 'Quién hace qué',
    'company.agenda.matrix_hint':
      'Marque los profesionales que realizan cada servicio y, si hace falta, los minutos. Si nadie está marcado en el servicio, todos pueden atenderlo.',
    'company.agenda.matrix_service': 'Servicio',
    'company.agenda.matrix_empty_services':
      'Registre servicios en el catálogo para definir quién realiza cada uno.',
    'company.agenda.matrix_empty_pros':
      'Active la agenda de al menos un colaborador para armar la matriz.',
    'company.agenda.matrix_default_duration': '{count} min en el catálogo',
    'company.agenda.matrix_unrestricted': 'Todos atienden (sin restricción)',
    'company.agenda.matrix_minutes': 'Minutos de este profesional',
    'company.agenda.matrix_assign': 'Este profesional realiza el servicio',
    'company.agenda.open_catalog': 'Abrir catálogo de servicios',
    'company.services.return_included': 'Retorno incluido en este servicio',
    'company.services.return': 'Retorno',
    'company.services.return_on': 'ON retorno',
    'company.services.return_off': 'OFF retorno',
    'company.services.visit': 'Visita',
    'company.services.visit_on': 'Visita',
    'company.services.visit_off': 'En el local',
    'company.services.sinal': 'Señal',
    'company.services.sinal_amount': 'Valor de la señal',
    'company.services.professionals': 'Profesionales de este servicio',
    'company.services.professionals_empty': 'Ningún colaborador con agenda activa.',
    'company.services.professionals_hint':
      'Ninguno marcado = todos los profesionales pueden atender.',
    'company.agenda.sinal_title': 'Regla de la señal',
    'company.agenda.sinal_hint':
      'Por defecto: la señal se suma al valor del servicio. El cliente lo ve en el resumen.',
    'company.agenda.sinal_adds': 'Señal + servicio',
    'company.agenda.sinal_deducts': 'La señal descuenta del servicio',
    'company.basic_info': 'Información Básica',
    'company.store_switching': 'Cargando la tienda…',
    'company.name': 'Nombre de la Empresa',
    'company.cnpj': 'CNPJ',
    'company.phone': 'Teléfono de la Empresa',
    'company.responsible_phone': 'Teléfono del Responsable',
    'company.email': 'Email',
    'company.address': 'Dirección',
    'company.street': 'Calle',
    'company.number': 'Número',
    'company.neighborhood': 'Barrio',
    'company.city': 'Ciudad',
    'company.state': 'Estado',
    'company.state_uf': 'UF',
    'company.state_placeholder': 'UF',
              'company.zip_code': 'Código Postal',
              'company.cep_looking_up': 'Buscando código postal...',
              'company.cep_fill_hint': 'Digite el CEP para completar calle, barrio, ciudad y UF automáticamente.',
              'company.toast.cep_not_found': 'Código postal no encontrado.',
              'company.toast.cep_fetch_error': 'Error al buscar el código postal. Intente de nuevo.',
              'company.toast.save_success_fields': 'Datos actualizados con éxito! Campos modificados: {fields}',
     'company.description': 'Descripción',
     'company.operating_hours': 'Horarios de Funcionamiento',
     'company.hours.presets_label': 'Presets',
     'company.hours.preset_24h': 'Abierto 24h',
     'company.hours.preset_commercial': 'Comercial 09:00–17:00',
     'company.hours.preset_custom': 'Personalizado',
     'company.hours.open_time': 'Apertura',
     'company.hours.close_time': 'Cierre',
     'company.hours.range_from': 'De',
     'company.hours.range_to': 'A',
     'company.hours.commercial_range_hint': 'Los días del intervalo quedan abiertos; los demás (p. ej. sábado y domingo) quedan cerrados.',
     'company.hours.select_all': 'Seleccionar todos',
     'company.hours.clear_selection': 'Limpiar selección',
     'company.hours.apply_preset': 'Aplicar a los días seleccionados',
     'company.hours.apply_commercial': 'Aplicar horario comercial',
     'company.hours.apply_24h': 'Aplicar 24h en todos los días',
     'company.hours.selected_count': '{count} día(s) seleccionado(s)',
     'company.hours.preset_hint': 'Marque los días y aplique un preset. Luego ajuste horarios en la tabla si hace falta. Guardar graba los 7 días.',
     'company.hours.col_select': 'Seleccionar',
     'company.hours.col_day': 'Día',
     'company.hours.col_open': 'Estado',
     'company.hours.select_day': 'Seleccionar {day}',
     'company.about_company': 'Sobre la Empresa',
     'company.company_description': 'Descripción de la Empresa',
     'company.observations': 'Observaciones',
     'company.observations_placeholder': 'Ej: Promociones activas, sugerencias de combos, lanzamientos de nuevos productos...',
     'company.delivery_settings': 'Configuración de Entrega',
     'company.delivery_time': 'Tiempo Promedio de Entrega (minutos)',
     'company.delivery_fee': 'Tarifa de Entrega ($)',
     
     // Menu Screen
     'menu.title': 'Gestión de Menú',
     'menu.new_category': 'Nueva Categoría',
     'menu.update': 'Actualizar',
     
     // License Screen - Additional
     'license.current_plan': 'Plan Actual',
     'license.annual_plan': 'Plan Anual',
     'license.days_complete_access': '365 días de acceso completo',
     'license.monthly_plan': 'Plan mensual',
     'license.semiannual_plan': 'Plan semestral',
     'license.days_180': '180 días de acceso',
     'license.trial_plan': 'Prueba gratis',
     'license.trial_subtitle': '7 días de acceso, sin contratación',
     'license.trial_price': 'Gratis',
     'license.trial_period': 'sin cobro',
     'license.access_until': 'Acceso hasta',
     'license.working_normally': 'Su licencia está funcionando normalmente',
     'license.expired_message': 'Su licencia ha expirado. Contáctenos para renovar.',
     'license.loading': 'Cargando datos de licencia...',
     'license.load_error': 'No se pudo cargar la licencia. Intente de nuevo.',
     'license.cnpj_unavailable': 'CNPJ de la tienda no disponible.',
     'license.try_again': 'Intentar de Nuevo',
     'license.edit': 'Editar',
     'license.view_license': 'Verificar licencia',
     'license.banner_expiring': 'Quedan solo {{days}} días en su licencia.',
     'license.screen_expiring_lead': 'Quedan solo {{days}} días en su licencia.',
     'license.screen_expiring_body': 'Anticipe la renovación para evitar suspender el panel y generar contratiempos. Hable con nuestro equipo: podemos ofrecer condiciones exclusivas para añadir más días de licencia antes del vencimiento.',
     'license.banner_inactive': 'Su licencia no tiene días activos. Contacte al soporte para restaurar el acceso completo.',
     'license.expired_overlay_title': '¡LICENCIA VENCIDA!',
     'license.expired_overlay_body': 'El periodo de su licencia ha finalizado y el panel permanece suspendido hasta renovar. Pedimos disculpas por las molestias y agradecemos su confianza. Hable con nuestro equipo: con gusto le orientamos en la renovación y restablecemos el acceso con seguridad y rapidez.',
     'license.sidebar_locked_hint': 'Renueve la licencia para acceder a este módulo',
     'license.orders_inactive_banner': 'Para seguir gestionando pedidos con tranquilidad, hable con nuestro equipo para alinear la renovación de la licencia — estamos a su disposición.',
     'license.toast_inactive_session': 'Su licencia no tiene días activos. Renueve con el soporte para mantener el panel completo.',
     'license.pay_with_pix': 'Pagar con PIX',
     'license.pix_checkout_hint_off':
       'El pago por PIX desde aquí estará disponible pronto.',
     'license.catalog_offers_title': 'Planes y precios',
     'license.catalog_offers_lead':
       'Contrate sólo lo que necesita — mensual, trimestral o anual — o cierre todos los módulos con una oferta conjunta.',
     'license.catalog_checkout_disabled_banner':
       'El pago desde el panel aún se está configurando en este entorno. Para renovar o contratar un plan, contacte al soporte DelivAI.',
     'license.catalog_suprema_badge': 'Mejor relación calidad-precio',
     'license.catalog_pix_unavailable_tooltip':
       'PIX no está disponible para este período. Elija otro período o contacte al soporte.',
     'license.catalog_pix_slot_off_hint':
       'El acceso rápido a PIX aparecerá cuando habilitemos cobros en el panel. Por ahora, negocie con el soporte.',
     'license.pack_title': 'Paquete completo',
     'license.pack_subtitle': 'Módulos esenciales de DelivAI en un solo plan.',
     'license.pack_duration_note': 'Este paquete suma {{days}} día(s) a la licencia tras pagar.',
     'license.pack_includes_title': 'Incluye',
     'license.pack_module_orders': 'Gestión de pedidos',
     'license.pack_module_finance': 'Finanzas',
     'license.pack_module_staff': 'Gestión de colaboradores',
     'license.pack_module_motoboy': 'Módulo motoboy / delivery',
     'license.pack_module_company_clients': 'Registro de empresa y clientes',
     'license.pack_pay_pix': 'Pagar con PIX',
     'license.pack_pay_boleto': 'Pagar con boleto',
     'license.pack_pay_mercadopago': 'Pagar con Mercado Pago (PIX o tarjeta)',
     'license.pack_mercadopago_hint':
       'Abre el checkout seguro de Mercado Pago; puede pagar con PIX o tarjeta. Luego vuelva al panel.',
     'license.pack_mercadopago_email_missing':
       'Se necesita un email válido en la cuenta para Mercado Pago. Actualice los datos con soporte.',
     'license.pack_mercadopago_error':
       'No se pudo iniciar el pago. Intente de nuevo o contacte al soporte.',
     'license.pack_mercadopago_loading': 'Abriendo el checkout…',
     'license.pack_mercadopago_click_price_hint':
       'Toca o haz clic en el precio de arriba para pagar con Mercado Pago (PIX o tarjeta).',
     'license.pack_segment_pix': 'Pago vía PIX',
     'license.pack_segment_mp': 'Pago vía Mercado Pago',
     'license.pack_mp_method_card': 'Tarjeta de crédito',
     'license.pack_mp_method_boleto': 'Boleto',
     'license.pack_segment_aria': 'Medio de pago del paquete',
     'license.pack_pix_click_price_hint':
       'Toca o haz clic en el precio de arriba para seguir con el pago PIX.',
     'license.pack_half_label_pix': 'PIX',
     'license.pack_half_label_mp': 'Mercado Pago',
     'license.pack_split_price_group_aria': 'Precios: PIX Santander y checkout Mercado Pago',
     'license.pack_split_payment_hint': '',
     'license.pack_footnote_monthly': '30 días de acceso · renovación mensual',
     'license.pack_footnote_semiannual': '180 días · ahorro vs. mensual',
     'license.pack_footnote_annual': '365 días · mejor costo por día',
    'license.pack_split_pix_nav_note':
      'El botón PIX será clicable cuando se habilite el checkout de licencia en este servidor.',
    'license.pack_split_pix_unavailable_title': 'Solo referencia — PIX sin acción aquí',
    'license.pack_split_pix_unavailable_toast':
      'PIX todavía no está habilitado en este servidor. Use Mercado Pago a la derecha o contacte soporte.',
    'license.mp_feedback_page_title': 'Resultado del pago',
    'license.mp_feedback_page_subtitle':
      'Tu espacio tranquilo después del checkout de Mercado Pago.',
    'license.mp_feedback_badge_success': 'Todo bien',
    'license.mp_feedback_badge_pending': 'Pago en revisión',
    'license.mp_feedback_badge_failure': 'Sin cambios en la cuenta',
    'license.mp_feedback_success_title': '¡Qué gusto tenerte por aquí!',
    'license.mp_feedback_success_body':
      'Gracias por confiar en DelivAI. Todo el equipo se enorgullece de impulsar tu día a día — cuando Mercado Pago confirme el pago, tus días aparecerán en la siguiente pantalla.',
    'license.mp_feedback_pending_title': 'Casi listo — espera un momento',
    'license.mp_feedback_pending_body':
      'Mercado Pago está procesando el pago. En unos segundos abre tu licencia para ver si los días ya se actualizaron.',
    'license.mp_feedback_failure_title': 'No pudimos completar el pago',
    'license.mp_feedback_failure_body':
      'No cambió nada en tu cuenta. Pasa a veces — respira y prueba de nuevo cuando quieras.',
    'license.mp_feedback_cta': 'Ir a la licencia',
    'license.mp_feedback_subnote':
      'El saldo oficial de días siempre está en la siguiente pantalla.',
    'license.mp_feedback_ref_prefix': 'Referencia:',
     'license.pack_pix_hint':
       'Confirmación rápida; los días se suman en cuanto se reconozca el pago.',
     'license.pack_boleto_compensation_notice':
       'Con boleto, los días solo se suman después de la compensación bancaria.',
     'license.pack_pay_disabled_hint':
       'El pago desde el panel se habilitará pronto. Para renovar, contacte al soporte.',
     'license.pack_delivery_title': 'Plan operación completa',
     'license.pack_delivery_subtitle':
       'Pedidos, finanzas, equipo, motoboys y registros — opción económica si no usa el flujo de atención en el panel.',
     'license.pack_billing_heading': 'Periodicidad',
     'license.pack_period_tab_monthly': 'Mensual',
     'license.pack_period_tab_semiannual': 'Semestral',
     'license.pack_period_tab_annual': 'Anual',
     'license.pack_annual_badge_fallback': '⭐ Mejor relación calidad-precio',
     'license.pack_delivery_notice_label': 'Observación:',
     'license.pack_delivery_no_attendance_note':
       'este paquete no incluye el módulo de atención (conversaciones en el panel).',
     'license.pack_pix_badge': 'Recomendado',
     'public_orders.save_principal_title': '¿Guardar dirección principal?',
     'public_orders.save_principal_message': '¿Desea guardar esta nueva dirección como principal en su registro?',
     'public_orders.save_principal_yes': 'Sí, guardar',
     'public_orders.save_principal_no': 'No',
     'public_orders.loading': 'Cargando...',
     'public_orders.cnpj_invalid': 'CNPJ inválido',
     'public_orders.company_not_found': 'Empresa no encontrada',
     'public_orders.company_unavailable': 'La empresa solicitada no fue encontrada o no está disponible.',
     'public_orders.order_online': 'Haga su pedido en línea',
     'public_orders.open': 'Abierto',
     'public_orders.closed': 'Cerrado',
     'public_orders.track_order': 'Rastrear pedido',
     'public_orders.search_products': 'Buscar productos...',
     'public_orders.add_to_cart': 'Agregar al carrito',
     'public_orders.cart': 'Carrito',
     'public_orders.checkout': 'Finalizar pedido',
     'public_orders.your_name': 'Su nombre completo',
     'public_orders.your_phone': 'Su celular',
     'public_orders.delivery_address': 'Dirección de entrega',
     'public_orders.payment_method': 'Forma de pago',
     'public_orders.place_order': 'Hacer pedido',
     'public_orders.cancel': 'Cancelar',
     'public_orders.from_price': 'Desde {price}',
     'public_orders.free_delivery': 'Gratis',
     'public_orders.select_neighborhood': 'Seleccione un barrio de la lista',
     'public_orders.view_cart': 'Ver carrito',
     'public_orders.empty_cart': 'Carrito vacío',
     'public_orders.finish_order': 'Finalizar pedido',
     'public_orders.add_to_cart_btn': 'Agregar al carrito',
     'public_orders.calc_delivery_btn': 'Calcular tarifa y tiempo de entrega',
     'public_orders.calc_delivery_hint': 'Ingrese su código postal para ver si entregamos en su zona',
     'public_orders.delivery_cep_hint': 'Ingrese su código postal para verificar la entrega en su zona y la tarifa.',
     'public_orders.zip_label': 'Código postal',
     'public_orders.zip_placeholder': '00000-000',
     'public_orders.neighborhood_identified': 'Barrio identificado: {name}',
     'public_orders.delivery_fee_label': 'Tarifa de entrega:',
     'public_orders.ok_understood': 'Ok, entendido',
     'public_orders.dont_know_zip': 'No sé mi código postal',
     'public_orders.close': 'Cerrar',
     'public_orders.close_cart': 'Cerrar carrito',
     'public_orders.close_modal': 'Cerrar modal',
     'public_orders.continue_shopping': 'Seguir comprando',
     'public_orders.subtotal': 'Subtotal:',
     'public_orders.total': 'Total:',
     'public_orders.order_type_label': 'Tipo de pedido',
     'public_orders.no_products_category': 'No hay productos disponibles en esta categoría.',
     'public_orders.menu_coming_soon': 'Menú próximamente',
     'public_orders.decrease_qty': 'Disminuir cantidad',
     'public_orders.increase_qty': 'Aumentar cantidad',
     'public_orders.delivery_eta_prefix': 'Entrega en',
     'public_orders.phone_label': 'Teléfono',
     'public_orders.phone_searching': 'Buscando cliente...',
     'public_orders.phone_found': 'Sus datos fueron completados',
     'public_orders.phone_needs_verify': 'Cliente registrado — confirme el código',
     'public_orders.phone_not_found': 'Cliente no encontrado',
     'public_orders.phone_optional_hint': 'Opcional para consumo en el local',
     'public_orders.name_label': 'Nombre',
     'public_orders.saved_address': 'Dirección guardada',
     'public_orders.address_default': 'Dirección',
     'public_orders.new_address': '+ Nueva dirección',
     'public_orders.where_delivery': '¿Dónde es la entrega?',
     'public_orders.where_delivery_hint': 'Arrastre el pin hasta su puerta o toque el mapa. En una esquina, elija la calle correcta.',
     'public_orders.search_address': 'Buscar dirección',
     'public_orders.address_placeholder': 'Ej.: Av Brasil, {city}',
     'public_orders.address_suggestions_hint': 'Las sugerencias priorizan {city}{state}. También aparecen calles cercanas en el área de entrega.',
     'public_orders.number_label': 'Número',
     'public_orders.number_required': 'Número *',
     'public_orders.number_placeholder': 'Número de casa',
     'public_orders.delivery_here': 'Entregar aquí',
     'public_orders.use_my_location': 'Usar mi ubicación',
     'public_orders.getting_location': 'Obteniendo ubicación...',
     'public_orders.map_hint_no_pin': 'Toque el mapa, use el botón de ubicación o ingrese el código postal abajo.',
     'public_orders.searching_address_location': 'Buscando dirección de la ubicación...',
     'public_orders.identifying_street': 'Identificando calle...',
     'public_orders.corner_pick_street': '¿Su casa está en la esquina? ¿Cuál es su calle?',
     'public_orders.address_preview': 'Dirección:',
     'public_orders.confirm_pin_hint': '¿Entregar aquí? Ajuste el pin si es necesario.',
     'public_orders.confirm_delivery_address': 'Confirmar dirección de entrega',
     'public_orders.prefer_manual_zip': 'Prefiero ingresar el código postal manualmente',
     'public_orders.searching_zip': 'Buscando código postal...',
     'public_orders.delivery_address_required': 'Dirección de entrega *',
     'public_orders.street_placeholder': 'Calle, avenida — ej.: Av Brasil, {city}',
     'public_orders.street_type_hint': 'Escriba el nombre de la calle — las sugerencias aparecen mientras escribe (región de {city}{state}, incluyendo barrios cercanos).',
     'public_orders.number_map_hint': 'Con código postal y número, el pin queda más cerca de su casa. El código postal ayuda a encontrar barrio y tarifa.',
     'public_orders.continue_map_confirm': 'Continuar para confirmar en el mapa',
     'public_orders.use_map_location': 'Usar mapa y ubicación',
     'public_orders.adjust_on_map': 'Ajustar en el mapa',
     'public_orders.edit_zip_street': 'Editar código postal y calle',
     'public_orders.street_placeholder_simple': 'Calle, avenida, pasaje...',
     'public_orders.neighborhood_label': 'Barrio *',
     'public_orders.complement_label': 'Complemento',
     'public_orders.complement_placeholder': 'Depto, bloque, etc.',
     'public_orders.reference_label': 'Punto de referencia',
     'public_orders.reference_placeholder': 'Punto de referencia',
     'public_orders.select_payment': 'Seleccione la forma de pago',
     'public_orders.no_payment_configured': 'Ninguna forma de pago configurada. Contacte al establecimiento.',
     'public_orders.need_change': '¿Necesita cambio?',
     'public_orders.change_for': '¿Con cuánto paga?',
     'public_orders.change_hint': 'Ingrese el valor total que pagará (ej.: escriba 50 para $0,50)',
     'public_orders.notes_label': 'Observaciones',
     'public_orders.notes_placeholder': 'Observaciones adicionales sobre el pedido',
     'public_orders.order_summary': 'Resumen del pedido',
     'public_orders.delivery_fee_summary': 'Tarifa de entrega',
     'public_orders.processing': 'Procesando...',
     'public_orders.confirm_order': 'Confirmar pedido',
     'public_orders.obs_prefix': 'Obs:',
     'public_orders.pay.cash': 'Efectivo',
     'public_orders.pay.pix': 'PIX',
     'public_orders.pay.debit': 'Tarjeta de débito',
     'public_orders.pay.credit': 'Tarjeta de crédito',
     'public_orders.pay.meal_voucher': 'Vale de comida',
     'public_orders.pay.other': 'Otro',
     'public_orders.toast.geolocation_unsupported': 'Su navegador no admite ubicación.',
     'public_orders.toast.geolocation_failed': 'No se pudo obtener su ubicación. Ingrese el código postal o use el mapa.',
     'public_orders.toast.corner_street': 'Su casa parece estar en una esquina. Seleccione su calle.',
     'public_orders.toast.address_confirmed': '¡Dirección confirmada! Revise los datos y elija el pago.',
     'public_orders.toast.select_payment': 'Seleccione la forma de pago',
     'public_orders.toast.select_neighborhood_fee': 'Seleccione un barrio válido de la lista para aplicar la tarifa de entrega',
     'public_orders.toast.select_price_type': 'Seleccione un tipo de precio',
     'public_orders.toast.order_type_unavailable': 'Este tipo de pedido no está disponible para esta tienda.',
     'public_orders.toast.order_error': 'Error al crear el pedido. Intente de nuevo.',
     'bairro_field.label': 'Barrio',
     'bairro_field.placeholder': 'Haga clic y seleccione el barrio',
     'bairro_field.no_neighborhoods': 'Ningún barrio registrado. Configure en Configuración → Delivery.',
     'bairro_field.no_match': 'Ningún barrio coincide — elija uno de la lista.',
     'bairro_field.not_found': 'Barrio no encontrado — elija uno de la lista para ver la tarifa',
     'bairro_field.select_hint': 'Seleccione el barrio de la lista para ver la tarifa de entrega',
     'bairro_field.delivery_fee': 'Tarifa de entrega:',
     'bairro_field.ref_hint_whatsapp': ' (valor informado en el mensaje — cambie el barrio para usar el registro)',
     'bairro_field.ref_hint_ifood': ' (valor de iFood — cambie el barrio para usar el registro)',
     'bairro_field.ref_label_whatsapp': 'El mensaje indicaba',
     'bairro_field.ref_label_ifood': 'iFood indicaba',
     'bairro_field.using_registered': '— usando tarifa del barrio registrado.',
     'bairro_field.registered_hint': 'Barrio registrado:',
     'bairro_field.using_ifood_fee': 'usando la menor tarifa. Alterne en el resumen si quiere la otra.',
     'bairro_field.using_higher_ifood': 'usando la tarifa de iFood. Alterne en el resumen para la menor (registro).',
     'public_orders.product_details': 'Detalles del producto',
     'public_orders.quantity': 'Cantidad',
     'public_orders.select_size_type': 'Seleccione tamaño/tipo:',
     'public_orders.flavors_count': '{count} sabor(es)',
     'public_orders.pizza_single_flavor': 'Pizza entera (1 sabor)',
     'public_orders.pizza_select_flavors': 'Seleccione hasta {max} sabor(es) ({current}/{max}):',
     'public_orders.add_second_flavor': 'Agregar segundo sabor',
     'public_orders.add_more_flavors': 'Agregar {count} sabor(es) más',
     'public_orders.select_second_flavor': 'Seleccione el segundo sabor',
     'public_orders.select_additional_flavors': 'Seleccione {count} sabor(es) adicional(es)',
     'public_orders.addons_optional': 'Adicionales (opcional):',
     'public_orders.addons_required': 'Adicionales (obligatorio al menos uno):',
     'public_orders.addons_min_required': 'Debe elegir al menos {min} ítem(s) de "{category}"',
     'public_orders.product_notes': 'Observaciones (opcional):',
     'public_orders.product_notes_placeholder': 'Ej.: sin cebolla, bien cocido...',
     'public_orders.toast.adjust_pin_gps': 'Ajuste el pin en el mapa o use GPS antes de confirmar.',
     'public_orders.toast.address_confirmed_details': '¡Dirección confirmada! Ahora ingrese número y complemento.',
     'public_orders.toast.enter_zip_before_map': 'Ingrese código postal o dirección antes de posicionar en el mapa.',
     'public_orders.toast.pin_near_number': 'Pin colocado cerca del número. Ajuste en el mapa si es necesario.',
     'public_orders.toast.pin_at_address': 'Pin colocado en la dirección. Ajuste en el mapa si es necesario.',
     'public_orders.toast.pin_on_street': 'Pin colocado en la calle. Ingrese el número o ajuste en el mapa.',
     'public_orders.toast.cep_street_found': 'Código postal localizado en la calle. Ingrese el número para ubicar su casa.',
     'public_orders.toast.map_locate_failed': 'No se pudo ubicar en el mapa. Use GPS o toque el mapa para colocar el pin.',
     'public_orders.toast.map_unavailable': 'Mapa no disponible. Use GPS o toque el mapa para colocar el pin.',
     'public_orders.toast.enter_zip_before_continue': 'Ingrese código postal o dirección antes de continuar.',
     'public_orders.toast.item_added': '¡Ítem agregado al carrito!',
     'public_orders.toast.item_updated': '¡Ítem actualizado en el carrito!',
     'public_orders.toast.cart_empty': 'Agregue ítems al carrito antes de finalizar',
     'public_orders.toast.enter_name': 'Ingrese su nombre',
     'public_orders.toast.invalid_phone': 'Ingrese un teléfono válido (mínimo 10 dígitos)',
     'public_orders.toast.enter_delivery_address': 'Ingrese la dirección de entrega',
     'public_orders.toast.enter_house_number': 'Ingrese el número de casa',
     'public_orders.toast.accept_privacy': 'Acepte la Política de Privacidad para continuar',
     'public_orders.toast.enter_change_amount': 'Ingrese el valor para el cambio',
     'public_orders.toast.delivery_fee_error': 'Error al calcular la tarifa de entrega. Intente seleccionar el barrio nuevamente.',
     'public_orders.toast.max_flavors': 'Máximo de {max} sabor(es) para este tipo de pizza',
     'appointment.modal.new_title': 'Nueva cita',
     'appointment.modal.edit_title': 'Editar cita',
     'appointment.modal.create_hint': 'Complete los datos abajo para agendar',
     'appointment.modal.edit_hint': 'Edite la información de la cita',
     'appointment.modal.client_section': 'Información del cliente',
     'appointment.modal.full_name': 'Nombre completo',
     'appointment.modal.client_name_placeholder': 'Nombre del cliente',
     'appointment.modal.phone': 'Teléfono',
     'appointment.modal.email': 'Email',
     'appointment.modal.service_section': 'Servicio',
     'appointment.modal.service': 'Servicio',
     'appointment.modal.loading_services': 'Cargando servicios...',
     'appointment.modal.select_service': 'Seleccione un servicio',
     'appointment.modal.no_services': 'Ningún servicio encontrado. Configure servicios en la sección Catálogo.',
     'appointment.modal.no_services_for_pro': 'Ningún servicio habilitado para este profesional. Márquelos en la ficha del colaborador.',
     'appointment.modal.duration': 'Duración',
     'appointment.modal.duration_placeholder': 'Seleccione un servicio para ver la duración',
     'appointment.modal.duration_auto': 'Duración automática según el servicio seleccionado',
     'appointment.modal.date_time_section': 'Fecha y horario',
     'appointment.modal.notes_section': 'Observaciones',
     'appointment.modal.notes_placeholder': 'Observaciones adicionales sobre la cita...',
     'appointment.modal.address_unknown': 'Dirección no informada',
     'appointment.modal.company_default': 'Empresa',
     'appointment.modal.create_btn': 'Crear cita',
     'appointment.modal.save_btn': 'Guardar cambios',
     'appointment.modal.one_day': '1 día',
     'appointment.modal.days': '{count} días',
     'appointment.modal.minutes': '{count} min',
     'appointment.error.client_required': 'El nombre del cliente es obligatorio',
     'appointment.error.phone_required': 'El teléfono es obligatorio',
     'appointment.error.date_required': 'La fecha es obligatoria',
     'appointment.error.time_required': 'El horario es obligatorio',
     'appointment.error.service_required': 'El servicio es obligatorio',
     'appointment.error.address_required': 'La dirección es obligatoria',
     'appointment.error.email_invalid': 'Email inválido',
     'appointment.error.past_datetime': 'La fecha y hora no pueden ser en el pasado',
     'appointment.error.day_unavailable': 'Este día está marcado como no disponible',
     'appointment.error.time_taken': 'Este horario ya está ocupado por otra cita',
     'appointment.error.time_locked': 'Este horario está bloqueado',
     'appointment.error.save_failed': 'Error al guardar la cita. Intente de nuevo.',
     'public_scheduling.loading': 'Cargando información de la empresa...',
     'public_scheduling.not_found_title': 'Empresa no encontrada',
     'public_scheduling.not_found_message': 'La empresa solicitada no fue encontrada o no está disponible para citas.',
     'public_scheduling.back_home': 'Volver al inicio',
     'public_scheduling.system_title': 'Sistema de citas',
     'public_scheduling.creating': 'Creando...',
     'public_scheduling.new_appointment': 'Nueva cita',
     'public_scheduling.company_info': 'Información de la empresa',
     'public_scheduling.address': 'Dirección',
     'public_scheduling.phone': 'Teléfono',
     'public_scheduling.business_hours': 'Horarios de funcionamiento',
     'public_scheduling.closed': 'Cerrado',
     'public_scheduling.available_services': 'Servicios disponibles',
     'public_scheduling.no_services': 'Ningún servicio disponible por el momento.',
     'public_scheduling.book_now': 'Agendar ahora',
     'public_scheduling.duration_label': 'Duración:',
     'public_scheduling.success': '¡Cita realizada con éxito! Evento creado en Google Calendar.',
     'public_scheduling.error_unknown': 'Error desconocido al guardar la cita.',
     'public_scheduling.address_unknown': 'Dirección no informada',
     'public_scheduling.company_default': 'Empresa',
     'public_scheduling.unavailable_title': 'Sistema de citas no disponible',
     'public_scheduling.unavailable_message': 'El sistema de citas está temporalmente no disponible.',
     'public_scheduling.reconnect_hint': 'La empresa debe reconectar la integración con Google Calendar.',
     'public_scheduling.error_prefix': 'Error: {error}',
     'public_scheduling.reconnecting': 'Intentando reconectar...',
     'public_scheduling.reconnect_btn': 'Intentar reconectar',
     'public_scheduling.how_to_title': 'Cómo agendar',
     'public_scheduling.how_to_step1': 'Conéctese a Google Calendar (si aún no está conectado)',
     'public_scheduling.how_to_step2': 'Haga clic en "Nueva cita" para abrir el formulario',
     'public_scheduling.how_to_step3': 'Complete sus datos personales y seleccione el servicio deseado',
     'public_scheduling.how_to_step4': 'Elija la fecha y horario disponible en el calendario',
     'public_scheduling.how_to_step5': 'Confirme la cita — se creará automáticamente en Google Calendar',
     'fast_order.no_cnpj': 'CNPJ no encontrado',
     'fast_order.title': 'Fast Order',
     'fast_order.back_aria': 'Volver',
     'fast_order.standard_title': 'Pedido estándar',
     'fast_order.ifood_title': 'Fast iFood',
     'fast_order.choose_intro': 'Cree pedidos rápidamente sin armar ítem por ítem del menú. Elija el tipo:',
     'fast_order.standard_card_title': 'Pedido estándar',
     'fast_order.standard_card_desc': 'Pegue mensajes de WhatsApp o envíe captura — la IA arma el pedido en el menú.',
     'fast_order.ifood_card_desc': 'Pegue el pedido completo o envíe captura — la IA completa el pedido en el sistema.',
     'fast_order.standard_banner': 'Pedido estándar — IA interpreta WhatsApp o captura y arma en el menú',
     'fast_order.ifood_banner': 'Fast iFood — IA interpreta el pedido y completa el sistema',
     'fast_order.paste_text': 'Pegar texto',
     'fast_order.upload_print_btn': 'Enviar captura',
     'fast_order.content_ifood_label': 'Contenido del pedido iFood *',
     'fast_order.content_whatsapp_label': 'Mensajes del pedido *',
     'fast_order.hint_ifood': 'Copie todo de la pantalla del pedido en el portal iFood (número, cliente, dirección, ítems y totales).',
     'fast_order.hint_whatsapp': 'Pegue la conversación de WhatsApp con el cliente (nombre, teléfono, dirección, ítems y forma de pago).',
     'fast_order.placeholder_ifood': 'Pegue aquí el pedido completo de iFood...',
     'fast_order.placeholder_whatsapp': 'Pegue aquí los mensajes de WhatsApp...',
     'fast_order.hint_print_ifood': 'Captura de la pantalla del pedido en la app o portal iFood.',
     'fast_order.hint_print_whatsapp': 'Captura de la conversación en WhatsApp o anotación del pedido.',
     'fast_order.choose_image': 'Haga clic para elegir imagen',
     'fast_order.preview_alt': 'Vista previa del pedido',
     'fast_order.extra_text_label': 'Texto extra (opcional)',
     'fast_order.extra_text_placeholder': 'Pegue detalles adicionales si la captura no tiene todo...',
     'fast_order.parsing': 'Interpretando...',
     'fast_order.parse_btn': 'Interpretar con IA',
     'fast_order.apply_btn': 'Aplicar al pedido',
     'fast_order.image_type_error': 'Envíe una imagen (PNG, JPG o WebP)',
     'fast_order.image_size_error': 'Imagen demasiado grande (máx. 8 MB)',
     'fast_order.paste_ifood': 'Pegue el contenido completo del pedido iFood',
     'fast_order.paste_whatsapp': 'Pegue los mensajes de WhatsApp o el texto del pedido',
     'fast_order.upload_print': 'Envíe una captura del pedido',
     'fast_order.read_image_failed': 'Error al leer imagen',
     'fast_order.parsed_success': 'Pedido interpretado — revise y aplique',
     'fast_order.parse_failed': 'No se pudo interpretar el pedido con IA',
     'fast_order.image_too_large': 'La captura es demasiado grande. Envíe una foto menor o pegue el texto del pedido.',
     'fast_order.parse_unavailable': 'El servidor de prueba no respondió. Intente de nuevo en un momento.',
     'fast_order.parse_ai_unavailable': 'La interpretación con IA no está disponible por ahora. Intente de nuevo más tarde.',
     'fast_order.fee_registered': 'barrio registrado',
     'fast_order.fee_message': 'valor en el mensaje',
     'fast_order.fee_ifood': 'valor iFood',
     'fast_order.fee_checkout_neighborhood': 'Tarifa definida por barrio en el checkout',
     'employees.invite_token_missing': 'Invitación generada, pero el token no vino en la respuesta. Use "Reenviar invitación".',
     'employees.invite_whatsapp_failed': 'Invitación no enviada por WhatsApp: {error}. Use "Reenviar invitación" cuando WhatsApp esté en línea.',
     'employees.saved_success': '¡Colaborador "{name}" editado con éxito!',
     'employees.added_success': '¡Colaborador "{name}" agregado con éxito!',
     'employees.updated_whatsapp': 'Colaborador "{name}" actualizado. Invitación enviada por WhatsApp (30 min).',
     'employees.added_whatsapp': 'Colaborador "{name}" agregado. Invitación enviada por WhatsApp (30 min).',
     'employees.removed_success': 'Colaborador eliminado con éxito.',
     'employees.inactive_section': 'Inactivos',
     'employees.inactive_hint': 'Estos accesos no entran en la operación. Eliminar borra el registro.',
     'employees.delete_login': 'Eliminar acceso',
     'employees.deactivate_title': '¿Inactivar colaborador?',
     'employees.deactivate_message': '{name} sale de la lista principal y el acceso deja de valer. Después puede eliminar el registro en la sección de inactivos.',
     'employees.delete_login_title': '¿Eliminar este acceso?',
     'employees.delete_login_message': 'El registro de {name} se borrará. Esa persona ya no entra con este acceso.',
     'employees.delete_login_success': 'Acceso eliminado.',
     'employees.role_added': '¡Cargo "{name}" agregado con éxito!',
     'employees.role_deleted': '¡Cargo "{name}" eliminado con éxito!',
     'employees.role_delete_blocked': 'No es posible eliminar el cargo "{name}" porque {count} colaborador(es) lo están usando.',
     'employees.error.invalid_cnpj': 'CNPJ de la tienda inválido.',
     'employees.error.invalid_cpf': 'CPF inválido. Ingrese 11 dígitos o deje en blanco para que el colaborador complete en la invitación.',
     'employees.error.invalid_cpf_fix': 'CPF inválido. Corrija el número o deje en blanco.',
     'employees.error.name_required': 'Ingrese el nombre del colaborador.',
     'employees.error.no_permission_roles': 'Sin permiso para alterar cargos.',
     'employees.error.deactivate_failed': 'No fue posible inactivar este colaborador. Verifique CPF y CNPJ de la tienda.',
     'employees.error.delete_failed': 'Error al eliminar colaborador. Intente de nuevo.',
     'orders.reject_pending_title': '¿Rechazar pedido pendiente?',
     'orders.reject_pending_message': 'El pedido será eliminado de la cola de pendientes y no se agregará a la lista de pedidos.',
     'orders.reject_reason_label': 'Motivo del rechazo',
     'orders.cancel_reason_label': 'Motivo de cancelación',
     'orders.select_reason': 'Seleccione un motivo',
     'orders.describe_reason': 'Describa el motivo',
     'orders.reason_placeholder': 'Ej.: Hoy no tendremos expediente por mantenimiento.',
     'orders.back': 'Volver',
     'orders.rejecting': 'Rechazando...',
     'orders.confirm_reject': 'Sí, rechazar',
     'orders.reject': 'Rechazar',
     'orders.approve': 'Aprobar',
     'orders.reject_pending': 'Rechazar pedido pendiente',
     'orders.cancel_order': 'Cancelar pedido',
     'orders.confirm_cancel': 'Confirmar cancelación',
     'orders.cancelling': 'Cancelando…',
     'orders.confirm': 'Confirmar',
     'orders.edit_item': 'Editar ítem',
     'orders.remove_item': 'Quitar ítem',
     'orders.update_item': 'Actualizar ítem',
     'orders.add_to_order': 'Agregar al pedido',
     'orders.add_product': 'Agregar producto',
     'orders.toast.product_not_in_menu': 'Producto no encontrado en el menú actual. Quítelo y agréguelo de nuevo.',
     'orders.toast.menu_unavailable': 'Menú no cargado. Intente de nuevo.',
     'orders.confirm_admin': 'Confirmar con administrador',
     'orders.open_store_manual': 'Abrir tienda manualmente',
     'orders.close_store_manual': 'Cerrar tienda manualmente',
     'orders.confirm_dispatch': 'Confirmar despacho',
     'orders.cancel_order_title': 'Cancelar pedido',
     'orders.cancel_confirm_message': '¿Está seguro de que desea cancelar este pedido? Esta acción no se puede deshacer.',
     'orders.ready': 'Listo',
     'orders.error.select_cancel_reason': 'Seleccione un motivo de cancelación. En "Otros", escriba la justificación.',
     'orders.error.select_reject_reason': 'Seleccione un motivo de rechazo. En "Otros", escriba la justificación.',
     'orders.error.no_cnpj_accept': 'CNPJ no disponible para aceptar pedido.',
     'orders.error.admin_cpf': 'Seleccione un administrador con CPF válido.',
     'orders.error.admin_password': 'Ingrese la contraseña del administrador.',
     'orders.error.wrong_password': 'Contraseña incorrecta. Intente de nuevo.',
     'orders.error.validate_password': 'No se pudo validar la contraseña.',
     'orders.error.store_status': 'No se pudo actualizar el estado manual de la tienda.',
     'orders.error.store_schedule_missing': 'No hay próximo horario en la agenda. Configure el horario o elija 30 min / 2h / 4h.',
     'orders.error.store_reopen_outside_hours': 'Ese plazo reabriría fuera del horario. Use Automático u otro tiempo válido.',
     'orders.error.start_sales': 'No se pudieron iniciar las ventas ahora.',
     'orders.detail.payment_method': 'Método de pago',
     'orders.detail.created_at': 'Fecha de creación',
     'orders.detail.order_type': 'Tipo de pedido',
     'orders.detail.order_total': 'Total del pedido:',
     'orders.detail.observations': 'Observaciones',
     'orders.detail.ifood_locator': 'Localizador iFood',
     'orders.delidesk_ifood.badge': 'Capturado de iFood vía DeliDesk',
     'orders.delidesk_ifood.review_hint': 'Revise los ítems y valores y apruebe el pedido.',
     'orders.delidesk_ifood.new_toast': 'Nuevo pedido iFood #{number} de {client} — {total}. Revise y apruebe.',
     'orders.delidesk_ifood.test_badge': 'Pedido de demostración (impresión de prueba iFood)',
     'orders.delidesk_ifood.test_review_hint':
       'No es un pedido real. Apruebe, edite y simule el despacho con el motorizado para conocer el flujo.',
     'orders.delidesk_ifood.test_toast':
       'Impresión de prueba de iFood — pedido demo #{number} ({total}).',
     'orders.delidesk_ifood.test_prompt_title': 'Impresión de prueba de iFood',
     'orders.delidesk_ifood.test_prompt_message':
       'Detectamos una impresión de prueba. Apruebe para seguir el flujo (producción + cupón DelivAI en la térmica) o descarte el pedido demo.',
     'orders.delidesk_ifood.test_prompt_explore': 'Aprobar e imprimir',
     'orders.delidesk_ifood.test_prompt_discard': 'Descartar',
     'orders.delidesk_ifood.test_discarded': 'Pedido de demostración descartado.',
     'orders.delidesk_ifood.test_approved': 'Demostración aprobada y enviada a impresión.',
     'orders.print_slip': 'Imprimir comanda',
     'orders.print_slip_updated_title': '¿Imprimir comanda actualizada?',
     'orders.print_slip_confirm': 'Imprimir comanda',
     'orders.not_now': 'Ahora no',
     'orders.start_sales_now': '¿Iniciar ventas ahora?',
     'orders.start_sales_opening': 'Horario de apertura — ¿iniciar ventas?',
     'orders.store_reopening_soon': 'Tienda a punto de reabrir',
     'orders.store_opening_soon': 'Tienda a punto de abrir',
     'orders.success_store_open': 'Tienda abierta manualmente con éxito.',
     'orders.success_store_open_auto': 'Tienda abierta. Cierra automáticamente en el próximo horario de cierre de la agenda.',
     'orders.success_store_auto': 'Tienda en modo automático — sigue el horario de la agenda.',
     'orders.success_store_close': 'Tienda cerrada manualmente con éxito.',
     'orders.success_store_close_auto': 'Tienda cerrada. Reabre automáticamente en el próximo horario de la agenda.',
     'orders.success_start_sales': 'Ventas iniciadas. Tienda abierta.',
     'orders.success_approve_pending': 'Pedido pendiente aprobado y creado con éxito.',
     'orders.success_reject_pending': 'Pedido pendiente rechazado.',
     'orders.error_approve_pending': 'Error al aprobar pedido pendiente.',
     'orders.error_reject_pending': 'Error al rechazar pedido pendiente.',
     'orders.error_cancel': 'Error al cancelar pedido. Intente de nuevo.',
     'orders.error_accept': 'Fallo al aceptar pedido',
     'orders.success_accept': 'Pedido aceptado.',
     'orders.auto_accept_unavailable_closed': 'La aceptación automática no está disponible mientras la tienda esté cerrada.',
     'orders.badge_store_closed': 'Tienda cerrada',
     'orders.badge_waiting_accept': 'Esperando aceptación',
     'orders.error_dispatch': 'No fue posible despachar el pedido. Intente de nuevo.',
     'orders.error_print_order': 'No fue posible imprimir el pedido #{number}.',
     'orders.success_print_slip': 'Comanda enviada a impresión.',
     'orders.error_print_slip': 'No fue posible imprimir la comanda. Pruebe el botón Imprimir en el pedido.',
     'orders.warn_select_deliverer': 'Por favor, seleccione un repartidor',
     'orders.warn_select_orders_dispatch': 'Seleccione al menos un pedido para despachar',
     'orders.warn_select_deliverer_dispatch': 'Seleccione un repartidor',
     'orders.success_dispatch_count': '¡{count} pedido(s) despachado(s) con éxito!',
     'orders.warn_dispatch_failed_count': '{count} pedido(s) no pudieron ser despachados',
     'orders.warn_dispatch_error': 'Error al despachar pedidos. Intente de nuevo.',
     'orders.success_products_updated': '¡Productos del pedido actualizados con éxito!',
     'orders.error_products_update': 'Error al actualizar productos del pedido',
     'orders.error_save_changes': 'Error al guardar cambios. Intente de nuevo.',
     'orders.error_no_cnpj_print': 'CNPJ no disponible para impresión',
     'orders.add_deadline': 'Agregar plazo',
     'orders.save_deadline_changes': 'Guardar cambios',
     'orders.success_order_accepted_print': '¡Pedido #{number} aceptado e imprimiendo! 🖨️',
     'orders.success_order_accepted_printed': '¡Pedido #{number} aceptado e impreso! 🖨️',
     'orders.success_order_accept_auto_print': 'Pedido #{number} aceptado; impresión automática en curso 🖨️',
     'schedule.confirm_btn': 'Confirmar',
     'schedule.syncing': 'Sincronizando...',
     'schedule.delete_btn': 'Eliminar',
     'schedule.notes_label': 'Observaciones:',
     'route_dispatch.title': 'Despachar ruta',
     'route_dispatch.motoboy_label': 'Repartidor',
     'route_dispatch.select_motoboy': 'Seleccione el repartidor...',
     'route_dispatch.stops_hint': '{count} parada(s) — arrastre para definir el orden de entrega',
     'route_dispatch.drag_aria': 'Arrastrar',
     'route_dispatch.confirming': 'Confirmando...',
     'route_dispatch.confirm_route': 'Confirmar ruta',
     'route_dispatch.error_select_motoboy': 'Seleccione el repartidor',
     'route_dispatch.error_select_orders': 'Seleccione pedidos',
     'route_dispatch.success': '¡Ruta creada y enviada al repartidor!',
     'route_dispatch.error_create': 'Error al crear ruta',
     'route_dispatch.client_fallback': 'Cliente',
     'orders.reject_reason.holiday': 'Feriado / horario de funcionamiento',
     'orders.reject_reason.excess': 'Exceso de pedidos en este momento',
     'orders.reject_reason.out_of_stock': 'Artículos agotados',
     'orders.reject_reason.other': 'Otros (especificar)',
     'employees.readonly_only': 'Solo lectura',
     'employees.copying_link': 'Copiando enlace…',
     'employees.generate_copy_link': 'Generar y copiar enlace',
     'employees.copy_invite_link': 'Copiar enlace de invitación',
     'employees.send_invite_whatsapp': 'Enviar invitación por WhatsApp',
     'employees.sending_invite': 'Enviando invitación…',
     'employees.resend_invite': 'Reenviar invitación',
     'employees.perm_module_denied': 'Sin permiso para acceder: {name}',
     'employees.perm_editor_title': 'Permisos',
     'employees.perm_editor_subtitle': 'Por módulo — acceso y acciones permitidas',
     'employees.perm_actions': 'Acciones',
     'employees.perm_access_module': 'Acceder al módulo: {name}',
     'employees.perm_collapse_actions': 'Contraer acciones',
     'employees.perm_expand_actions': 'Expandir acciones',
     'employees.perm_collapse': 'Contraer',
     'employees.perm_view_actions': 'Ver acciones',
     'employees.perm_done': 'Listo',
     'perm.mod.dashboard': 'Panel / dashboard',
     'perm.mod.pdv': 'PDV (mesa y retiro)',
     'perm.mod.delivery': 'Delivery (despacho)',
     'perm.mod.pedidos': 'Gestión de pedidos (legado)',
     'perm.act.pdv.ver': 'Ver PDV',
     'perm.act.pdv.aceitar': 'Aceptar pedido',
     'perm.act.pdv.cancelar': 'Cancelar pedido',
     'perm.act.pdv.editar': 'Editar pedido',
     'perm.act.pdv.finalizar': 'Finalizar pedido',
     'perm.act.pdv.ver_historico': 'Ver historial',
     'perm.act.pdv.pedido_interno': 'Pegar / pedido interno',
     'perm.act.pdv.imprimir': 'Imprimir / comprobantes',
     'perm.act.delivery.ver': 'Ver Delivery',
     'perm.act.delivery.aceitar': 'Aceptar pedido',
     'perm.act.delivery.cancelar': 'Cancelar pedido',
     'perm.act.delivery.editar': 'Editar pedido',
     'perm.act.delivery.despachar': 'Despachar / asignar repartidor',
     'perm.act.delivery.finalizar': 'Finalizar pedido',
     'perm.act.delivery.ver_historico': 'Ver historial',
     'perm.act.delivery.pedido_interno': 'Fast Order / pedido interno',
     'perm.act.delivery.imprimir': 'Imprimir / comprobantes',
     'perm.mod.conversas': 'Conversaciones (WhatsApp)',
     'perm.mod.company': 'Menú / empresa',
     'perm.mod.agendamentos': 'Citas',
     'perm.mod.motoboys': 'Ranking de repartidores',
     'perm.mod.manager': 'Gerente / resumen',
     'perm.mod.colaboradores': 'Colaboradores',
     'perm.mod.clientes': 'Clientes',
     'perm.mod.financeiro': 'Finanzas',
     'perm.mod.configuracoes': 'Configuración',
     'perm.mod.license': 'Licencia',
     'perm.mod.delivai_controle': 'Control DelivAI (interno)',
     'perm.act.dashboard.ver': 'Acceder al panel',
     'perm.act.pedidos.ver': 'Ver pedidos',
     'perm.act.pedidos.aceitar': 'Aceptar pedido',
     'perm.act.pedidos.cancelar': 'Cancelar pedido',
     'perm.act.pedidos.editar': 'Editar pedido',
     'perm.act.pedidos.despachar': 'Despachar / asignar repartidor',
     'perm.act.pedidos.finalizar': 'Finalizar pedido',
     'perm.act.pedidos.ver_historico': 'Ver historial',
     'perm.act.pedidos.pedido_interno': 'Pedido interno',
     'perm.act.pedidos.imprimir': 'Imprimir / comprobantes',
     'perm.act.conversas.ver': 'Ver conversaciones',
     'perm.act.conversas.enviar_mensagens': 'Enviar mensajes',
     'perm.act.conversas.arquivar': 'Archivar / gestionar',
     'perm.act.company.ver': 'Ver',
     'perm.act.company.editar_cardapio': 'Editar menú',
     'perm.act.company.editar_delivery': 'Editar delivery / barrios',
     'perm.act.company.editar_dados_empresa': 'Editar datos de la empresa',
     'perm.act.agendamentos.ver': 'Ver',
     'perm.act.agendamentos.criar': 'Crear',
     'perm.act.agendamentos.editar': 'Editar',
     'perm.act.agendamentos.cancelar': 'Cancelar',
     'perm.act.motoboys.ver': 'Ver ranking',
     'perm.act.motoboys.editar': 'Editar diarias / ranking',
     'perm.act.manager.ver': 'Ver',
     'perm.act.colaboradores.ver': 'Ver lista',
     'perm.act.colaboradores.criar_editar': 'Crear y editar colaboradores',
     'perm.act.colaboradores.editar_permissoes': 'Editar permisos de otros',
     'perm.act.clientes.ver': 'Ver',
     'perm.act.clientes.editar_clientes': 'Editar',
     'perm.act.financeiro.ver': 'Ver',
     'perm.act.financeiro.editar': 'Editar',
     'perm.act.configuracoes.ver': 'Ver',
     'perm.act.configuracoes.editar': 'Editar',
     'perm.act.license.ver': 'Ver licencia',
     'perm.act.delivai_controle.ver': 'Acceder al panel interno',
     'perm.act.delivai_controle.editar': 'Licencias — editar plazos, módulos y pay_types',
     'perm.act.delivai_controle.criar': 'Evolution — crear y gestionar instancias',
     'perm.act.delivai_controle.editar_cardapio': 'Empresas — editar registro companies',
     'perm.act.delivai_controle.editar_prompts': 'Prompts — editar plantillas del bot',
     'perm.act.delivai_controle.editar_consumers': 'Clientes — listar y fusionar consumers',
     'perm.act.delivai_controle.ver_banco': 'Base de datos — studio interno (tablas y SQL)',
     'perm.act.delivai_controle.ver_logs': 'Logs — ejecuciones n8n en tiempo real',
     'perm.act.delivai_controle.ver_observabilidade': 'Observabilidad — métricas (Prometheus) y Loki',
     'perm.act.delivai_controle.editar_permissoes': 'Permisos — gestionar otros desarrolladores',
     'perm.act.delivai_controle.editar_contratos': 'Contratos — revisar/editar borradores (B2B, consumidor, colaboradores, equipo interno, legal)',
     'perm.act.delivai_controle.publicar_contratos': 'Contratos — activar versión oficial (solo master)',
     'perm.act.delivai_controle.excluir_versoes_contratos': 'Contratos — eliminar versiones (solo master; va a la papelera)',
     'identity_gate.welcome_prefix': 'Bienvenido a',
     'identity_gate.default_store': 'nuestra tienda',
     'identity_gate.verify': 'Verificar',
     'identity_gate.view_menu': 'Ver menú',
     'consumer_verify.title_entry': 'Identifíquese con su celular',
     'consumer_verify.title_checkout': 'Confirme para completar el pedido',
     'consumer_verify.subtitle_whatsapp': 'Ingrese un WhatsApp válido',
     'consumer_verify.subtitle_checkout': 'Por seguridad, confirme que es usted antes de finalizar.',
     'consumer_verify.your_phone': 'WhatsApp',
     'consumer_verify.select_country': 'Seleccionar país',
     'consumer_verify.continue': 'Continuar',
     'consumer_verify.close': 'Cerrar',
     'consumer_verify.invalid_phone': 'Ingrese un WhatsApp válido con código de área',
     'consumer_verify.channel_prompt': '¿Cómo desea recibir el código para {phone}?',
     'consumer_verify.send_whatsapp_code': 'Recibir código por WhatsApp',
     'consumer_verify.retry_whatsapp_code': 'Reintentar WhatsApp',
     'consumer_verify.channel_or_email': 'o por correo',
     'consumer_verify.sending_code': 'Enviando código...',
     'consumer_verify.verifying_code': 'Verificando...',
     'consumer_verify.code_sent_whatsapp': 'Código enviado por WhatsApp',
     'consumer_verify.code_sent_email': 'Código enviado por correo',
     'consumer_verify.code_prompt': 'Ingrese el código de 6 dígitos enviado por {channel}.',
     'consumer_verify.channel_whatsapp': 'WhatsApp',
     'consumer_verify.channel_email': 'correo',
     'consumer_verify.confirm_code': 'Confirmar código',
     'consumer_verify.resend_code': 'Reenviar código',
     'consumer_verify.change_phone': 'Cambiar teléfono',
     'consumer_verify.whatsapp_unavailable': 'WhatsApp de la tienda no disponible. Se avisó al responsable. Use el correo.',
     'consumer_verify.send_email_code': 'Enviar código por correo',
     'consumer_verify.your_email': 'Su correo',
     'consumer_verify.invalid_email': 'Ingrese un correo válido',
     'consumer_verify.invalid_code': 'Ingrese el código de 6 dígitos',
     'consumer_verify.verified': '¡Identificación confirmada!',
     'consumer_verify.verified_as': '{name}',
     'consumer_verify.verified_phone': '{phone}',
     'consumer_verify.chip_details_title': 'Tus datos',
     'consumer_verify.chip_details_aria': 'Ver datos del cliente',
     'consumer_verify.chip_no_address': 'Ninguna dirección completada aún',
     'consumer_verify.send_failed': 'No se pudo enviar el código',
     'consumer_verify.send_failed_manual':
       'Error en la verificación de registro. Por favor, complete sus datos manualmente.',
     'consumer_verify.fill_manually': 'Completar datos manualmente',
     'consumer_verify.verify_failed': 'Verificación fallida — intente de nuevo',
     'consumer_verify.wrong_code': 'Código incorrecto, intente de nuevo',
     'consumer_verify.code_expired': 'Código expirado — solicite uno nuevo',
     'consumer_verify.code_missing': 'No hay código pendiente — solicite uno nuevo',
     'consumer_verify.rate_limited': 'Demasiados intentos — espere un poco e intente de nuevo',
     'consumer_verify.confirm_checkout': 'Confirme su celular para completar el pedido',
     'consumer_verify.auth_methods': 'Formas de identificación',
     'consumer_verify.tab_whatsapp': 'WhatsApp',
     'consumer_verify.tab_password': 'Contraseña',
     'consumer_verify.your_name': 'Su nombre',
     'consumer_verify.name_placeholder': 'Cómo debemos llamarlo',
     'consumer_verify.your_password': 'Contraseña',
     'consumer_verify.password_placeholder': 'Mínimo 6 caracteres',
     'consumer_verify.confirm_password': 'Confirmar contraseña',
     'consumer_verify.confirm_password_placeholder': 'Escriba la contraseña de nuevo',
     'consumer_verify.confirm_password_required': 'Confirme la contraseña',
     'consumer_verify.password_mismatch': 'Las contraseñas no coinciden',
     'consumer_verify.weak_password': 'La contraseña debe tener al menos 6 caracteres',
     'consumer_verify.login_password': 'Entrar con contraseña',
     'consumer_verify.create_password_account': 'Crear cuenta con contraseña',
     'consumer_verify.login_failed': 'Teléfono o contraseña incorrectos',
     'consumer_verify.register_failed': 'No se pudo crear la cuenta — intente de nuevo',
     
     // Orders Screen
    'orders.title': 'PDV',
    'orders.header_subtitle': 'Mesa y retiro — acepte, prepare y siga las comandas del día.',
    'orders.paste_order': 'Pegar',
    'orders.paste_order_title': 'Pegar pedido (texto o captura)',
    'orders.print_format_aria': 'Tamaño del cupón térmico',
    'orders.print_agent.vias': 'Copias',
    'orders.print_agent.vias_less': 'Menos copias',
    'orders.print_agent.vias_more': 'Más copias',
    'orders.print_agent.formats_hint': 'Cada cupón marcado imprime su propia cantidad de copias.',
    'orders.new_order': 'Nuevo Pedido',
    'orders.pending': 'Pendientes',
    'orders.in_progress': 'En Progreso',
    'orders.completed': 'Completados',
    'orders.cancelled': 'Cancelados',
    
    // Conversations Screen
    'conversations.title': 'Conversaciones',
    'conversations.new_conversation': 'Nueva Conversación',
    'conversations.search': 'Buscar conversaciones...',
    
    // Employees Screen
    'employees.title': 'Empleados',
    'employees.add_employee': 'Agregar Empleado',
    'employees.name': 'Nombre',
    'employees.role': 'Cargo',
    'employees.email': 'Email',
    'employees.phone': 'Teléfono',
    'employees.status': 'Estado',
    'employees.search_placeholder': 'Buscar empleado...',
    'employees.no_employees': 'No se encontraron empleados',
    'employees.add_first': 'Agregue su primer empleado para comenzar',
    'employees.agenda_toggle': 'Agenda del profesional',
    'employees.agenda_hint': 'Activa la agenda Google individual de este colaborador.',
    'employees.agenda_area': 'Área / especialidad',
    'employees.agenda_area_placeholder': 'Ej.: manicura, cardiología',
    'employees.agenda_saved': 'Agenda actualizada',
    'employees.agenda_save_error': 'No se pudo actualizar la agenda',
    
    // Orders Screen - Additional
    'orders.update': 'Actualizar',
    'orders.search_placeholder': 'Buscar por número de pedido o cliente...',
    'orders.status.in_analysis': 'En Análisis',
    'orders.status.in_production': 'En Producción',
    'orders.status.ready_delivery': 'Listos para Entrega',
    'orders.status.in_route': 'En Ruta',
    'orders.delivery_deadline': 'Plazo de entrega',
    'orders.select_deadline': 'Seleccione un plazo',
    'orders.manage_deadlines': 'Gestionar plazos',
    'orders.internal_order': 'Pedido interno',
    'orders.public_link': 'Enlace público:',
    'orders.copy_link': 'Copiar enlace',
    'orders.store': 'Tienda',
    'orders.store_open': 'Abierta',
    'orders.store_closed': 'Cerrada',
    'orders.mode_auto': 'Auto',
    'orders.mode_manual': 'Manual',
    'orders.open_store_aria': 'Abrir tienda manualmente',
    'orders.close_store_aria': 'Cerrar tienda manualmente',
    'orders.countdown.open_until': 'Abierta hasta',
    'orders.countdown.next_open': 'Próxima apertura',
    'orders.countdown.next_close': 'Próximo cierre',
    'orders.auto_accept_title': 'Aceptación / impresión automática',
    'orders.auto_accept_delivai_title': 'DelivAI',
    'orders.auto_accept_delidesk_title': 'DeliDesk',
    'orders.auto_accept_delivai_toggle': 'Aceptación automática DelivAI (sitio, WhatsApp, interno)',
    'orders.auto_accept_delidesk_toggle': 'Aceptación automática DeliDesk (captura marketplace)',
    'orders.fast_order_open': 'Fast Order — generar pedido desde texto o iFood',
    'orders.auto_accept_active': 'Activa',
    'orders.auto_accept_disabled': 'Desactivada',
    'orders.auto_accept_when_open': 'Disponible cuando la tienda esté abierta',
    'orders.auto_accept_toggle': 'Activar/desactivar aceptación automática',
    'orders.filters': 'Filtros',
    'orders.clear': 'Limpiar',
    'orders.select_date': 'Seleccionar fecha específica',
    'orders.quick_dispatch': 'Despacho rápido',
    'orders.no_ready_dispatch': 'Ningún pedido listo para despacho en este momento',
    'orders.deliverer': 'Repartidor',
    'orders.dispatch': 'Despachar',
    'orders.dispatch_route': 'Despachar ruta',
    'orders.filter_status': 'Estado',
    'orders.filter_period': 'Período',
    'orders.filter_all': 'Todos',
    'orders.filter_today': 'Hoy',
    'orders.filter_week': 'Esta semana',
    'orders.filter_month': 'Este mes',
    'orders.filter_selected_date': 'Fecha seleccionada: {date}',
    'orders.sort_by': 'Ordenar por',
    'orders.sort_date': 'Fecha',
    'orders.sort_name': 'Nombre',
    'orders.sort_total': 'Valor',
    'orders.sort_order': 'Orden',
    'orders.empty_title': 'No se encontraron pedidos',
    'orders.empty_filters': 'Intente ajustar los filtros para encontrar pedidos.',
    'orders.empty_none': 'Aún no hay pedidos registrados.',
    'orders.orders_count_one': '{count} pedido',
    'orders.orders_count_many': '{count} pedidos',
    'orders.history_title': 'Historial de comandas',
    'orders.history_search': 'Buscar por número o cliente...',
    'orders.history_search_aria': 'Buscar en el historial por número o cliente',
    'orders.cash_summary': 'Resumen de caja',
    'orders.cash_entry_button': 'Lanzamiento',
    'orders.cash_entry_title': 'Lanzamiento de caja',
    'orders.cash_entry_subtitle': 'Sangría, refuerzo de caja, cambio externo, gasto o acierto de motoboy.',
    'orders.cash_entry_type': 'Tipo',
    'orders.cash_entry_amount': 'Valor',
    'orders.cash_entry_method': 'Forma de pago',
    'orders.cash_entry_note': 'Observación',
    'orders.cash_entry_save': 'Registrar',
    'orders.cash_entry_success': 'Lanzamiento registrado',
    'orders.cash_entry_error': 'No se pudo registrar el lanzamiento',
    'orders.cash_entry_invalid_amount': 'Ingrese un valor válido mayor que cero',
    'orders.cash_entry_no_session': 'Ninguna caja abierta',
    'orders.cash_entry_insufficient_cash':
      'Saldo en efectivo insuficiente. Haga un refuerzo de caja antes de la sangría o retiro.',
    'orders.cash_entry_opening_session': 'Abriendo caja…',
    'orders.cash_entry_open_failed': 'No se pudo abrir la caja para el lanzamiento.',
    'orders.cash_entry_type_sangria': 'Sangría',
    'orders.cash_entry_type_retirada': 'Retiro',
    'orders.cash_entry_type_aporte': 'Refuerzo de caja',
    'orders.cash_summary_reforco': 'Refuerzo',
    'orders.cash_summary_reforco_short': 'refuerzo',
    'orders.cash_summary_troco_ext_short': 'cambio ext.',
    'orders.cash_summary_sangria_short': 'sangría',
    'orders.cash_summary_retirada_short': 'retiro',
    'orders.cash_summary_despesa_short': 'gasto',
    'orders.cash_summary_sales_short': 'ventas',
    'orders.cash_summary_hint': 'PIX, tarjeta, efectivo y total son ventas. El refuerzo es entrada de caja (no es venta).',
    'orders.cash_entry_type_troco_externo': 'Cambio externo',
    'orders.cash_entry_type_acerto_motoboy': 'Acierto de motoboy',
    'orders.cash_entry_type_despesa': 'Gasto',
    'orders.cash_entry_method_pix': 'PIX',
    'orders.cash_entry_method_cartao': 'Tarjeta',
    'orders.cash_entry_method_dinheiro': 'Efectivo',
    'courier.settlement_title': 'Acierto con la tienda',
    'courier.settlement_subtitle': 'Valores a acertar por forma de pago (pedidos del día).',
    'orders.payment_card': 'Tarjeta',
    'orders.payment_cash': 'Efectivo',
    'orders.payment_cancelled': 'Cancelados',
    'orders.payment_pix': 'PIX',
    'orders.col_number': 'Nº',
    'orders.col_datetime': 'Fecha/Hora',
    'orders.col_client': 'Cliente',
    'orders.col_phone': 'Teléfono',
    'orders.col_address': 'Dirección',
    'orders.col_type': 'Tipo',
    'orders.col_status': 'Estado',
    'orders.col_deliverer': 'Repartidor',
    'orders.col_actions': 'Acciones',
    'orders.history_empty': 'No se encontraron pedidos en el historial',
         'company.edit': 'Editar',
     'company.save': 'Guardar',
     'company.cancel': 'Cancelar',
     'company.update': 'Actualizar',
     
     // Orders Screen - Additional Details
     'orders.order_number': 'Pedido #',
     'orders.client_not_informed_short': 'Cliente no informado',
     'orders.client': 'Cliente:',
     'orders.phone': 'Teléfono:',
     'orders.address': 'Dirección:',
     'orders.payment_method': 'Método de Pago:',
     'orders.items': 'Artículos:',
     'orders.no_items': 'No hay artículos disponibles',
     'orders.total': 'Total:',
     'orders.payment': 'Pago:',
     'orders.details': 'Detalles',
     
     // Employees Screen - Additional
     'employees.department': 'Departamento:',
     'employees.salary': 'Salario:',
     'employees.admission': 'Admisión:',
     'employees.edit': 'Editar',
     'employees.details': 'Detalles',
     'menu_display.edit_category.title': 'Editar Categoría',
     'menu_display.edit_category.subtitle': 'Cambie el nombre y los tipos de precio de la categoría',
     'menu_display.edit_category.service_title': 'Editar categoría de servicio',
     'menu_display.edit_category.service_subtitle':
       'Nombre, duración (minutos o días) y precio de esta categoría',
     'menu_display.edit_category.service_mode': 'Cómo se mide la duración',
     'menu_display.edit_category.mode_time': 'Por minutos',
     'menu_display.edit_category.mode_day': 'Por días',
     'menu_display.edit_category.service_price': 'Precio del servicio',
     'menu_display.edit_category.close': 'Cerrar',
     'menu_display.edit_category.type': 'Tipo de Categoría',
     'menu_display.edit_category.type_product': 'Producto',
     'menu_display.edit_category.type_pizza': 'Pizza',
     'menu_display.edit_category.type_additional': 'Adicional',
     'menu_display.edit_category.name': 'Nombre de la Categoría',
     'menu_display.edit_category.name_placeholder': 'Nombre de la categoría',
     'menu_display.edit_category.price_types': 'Tipos de Precio',
     'menu_display.edit_category.add': 'Agregar',
     'menu_display.edit_category.price_type_placeholder': 'Tipo de precio {index}',
     'menu_display.edit_category.remove_price_type': 'Eliminar tipo de precio',
     'menu_display.edit_category.flavors': 'Sabores',
     'menu_display.edit_category.suggested_price': 'Precio Sugerido (R$)',
     'menu_display.edit_category.suggested_price_placeholder': 'R$ 0,00',
     'menu_display.edit_category.additional_scope_title': 'En qué categorías de productos se pueden aplicar estos adicionales',
     'menu_display.edit_category.additional_scope_hint': 'Marque toda la categoría o expanda y elija solo los productos deseados.',
     'menu_display.edit_category.no_product_categories': 'Ninguna categoría de producto.',
     'menu_display.edit_category.collapse': 'Contraer',
     'menu_display.edit_category.expand': 'Expandir',
     'menu_display.edit_category.additional_categories': 'Categorías de Adicionales',
     'menu_display.edit_category.no_additional_categories': 'Ninguna categoría adicional registrada.',
     'menu_display.edit_category.additional_limit': 'Límite de Adicionales por Producto',
     'menu_display.edit_category.additional_limit_hint': 'Cantidad máxima de adicionales de esta categoría por producto. Déjelo vacío para sin límite.',
     'menu_display.edit_category.type_value_placeholder': 'O escriba un valor',
     'menu_display.edit_category.additional_minimum': 'Mínimo de Adicionales por Producto',
     'menu_display.edit_category.additional_minimum_hint': 'Cantidad mínima de adicionales de esta categoría por producto. Déjelo vacío o 0 para sin mínimo.',
     'menu_display.edit_category.minimum_value_placeholder': 'O escriba un valor (0 = sin mínimo)',
     'menu_display.edit_category.save_changes': 'Guardar Cambios',
     'menu_display.delete_image.title': 'Eliminar imagen',
     'menu_display.delete_image.subtitle': 'Esta acción no se puede deshacer',
     'menu_display.delete_image.confirm_text': '¿Seguro que desea eliminar la imagen del producto "{product}"?',
     'menu_display.delete_image.no_name': 'sin nombre',
     'conversations.group': 'Grupo',
     'conversations.hidden_number': 'Número oculto (privacidad de WhatsApp)',
     'conversations.fallback_dash': '—',
     'conversations.tap_qr_to_connect': 'Toque el ícono QR para conectar y escanear la sesión.',
     'conversations.instance_unavailable_title': 'Instancia no disponible',
     'conversations.instance_unavailable_body': 'Las conversaciones usan la instancia de WhatsApp vinculada al CNPJ de su empresa. Inicie sesión con un usuario con CNPJ o cree una instancia abajo.',
     'conversations.create_instance_title': 'Crear instancia WhatsApp (Evolution API)',
     'conversations.create_instance_body': 'Se creará una instancia con el CNPJ de su empresa. Ingrese el número de WhatsApp (con DDD) para vincular.',
     'conversations.phone_placeholder': '(11) 99999-9999',
     'conversations.creating_instance': 'Creando...',
     'conversations.create_instance': 'Crear instancia',
     'conversations.instance_created_refreshing': 'Instancia creada. Actualizando la página...',
     'conversations.empty_archived': 'Ninguna conversación archivada',
     'conversations.empty_found': 'Ninguna conversación encontrada',
     'conversations.reopen_fast_order': 'Reabrir Fast Order',
     'conversations.fast_order_tooltip': 'Fast Order — seleccionar mensajes para generar pedido',
     'conversations.fast_order': 'Fast Order',
     'conversations.client_state_loading': 'Cargando estado del cliente…',
     'conversations.client_missing_hint': 'Registre al cliente en Clientes de delivery (mismo teléfono de WhatsApp). Si ya lo registró, revise el 9.º dígito del celular.',
     'conversations.client_new_badge': 'Cliente nuevo',
     'conversations.client_orders_badge': '{count} pedidos',
     'conversations.client_orders_badge_one': '1 pedido',
     'conversations.auto_support_active_hint': 'Atención automática activa para este contacto. Haga clic para pausar.',
     'conversations.auto_support_paused_hint': 'Atención automática pausada para este contacto. Haga clic para reactivar.',
     'conversations.auto_support_individual': 'Atención Auto Individual',
     'conversations.protected_photo_hint': 'Foto protegida por WhatsApp — cargue por la API Evolution.',
     'conversations.open_fullscreen_zoom': 'Abrir en pantalla completa con zoom (en el panel)',
     'conversations.encrypted_video_hint': 'Video cifrado — cargar para reproducir.',
     'conversations.audio_unrecognized': 'Audio — formato no reconocido en este mensaje',
     'conversations.use_as_field': 'Usar como {field}',
     'conversations.message_placeholder': 'Escriba un mensaje',
     'conversations.send': 'Enviar',
     'conversations.confirm_pick_title': 'Usar mensaje del cliente',
     'conversations.message_label': 'Mensaje',
     'conversations.set_value_as': 'Definir',
     'conversations.set_value_as_suffix': 'como',
     'conversations.connect_whatsapp': 'Conectar WhatsApp',
     'conversations.scan_qr_hint': 'Escanee con WhatsApp → Dispositivos vinculados',
     'conversations.qr_alt': 'Código QR de WhatsApp',
     'conversations.waiting_scan': 'Esperando lectura',
     'conversations.qr_modal_auto_close': 'El modal se cierra automáticamente cuando se establezca la conexión.',
     'conversations.unarchive_chat': 'Desarchivar conversación',
     'conversations.archive_chat': 'Archivar conversación',
     'conversations.menu.mute_notifications': 'Silenciar notificaciones',
     'conversations.menu.pin_chat': 'Fijar conversación',
     'conversations.menu.mark_unread': 'Marcar como no leída',
     'conversations.menu.add_favorites': 'Agregar a Favoritos',
     'conversations.menu.add_list': 'Agregar a la lista',
     'conversations.menu.block_contact': 'Bloquear contacto',
     'conversations.menu.block': 'Bloquear',
     'conversations.menu.clear_chat': 'Limpiar conversación',
     'conversations.menu.delete_chat': 'Eliminar conversación',
     'conversations.disconnect_confirm_title': 'Desconectar WhatsApp',
     'conversations.disconnect_confirm_message': '¿Desconectar WhatsApp de esta instancia? Será necesario escanear el QR nuevamente para reconectar.',
     'conversations.toast.instance_unavailable': 'Instancia WhatsApp no disponible.',
     'conversations.toast.last_message_not_found': 'No fue posible identificar el último mensaje. Abra el chat e inténtelo de nuevo.',
     'conversations.toast.invalid_contact_action': 'Contacto inválido para esta acción.',
     'conversations.toast.archive_failed': 'Error al actualizar archivado.',
     'conversations.toast.chat_archived': 'Conversación archivada.',
     'conversations.toast.chat_unarchived': 'Conversación desarchivada.',
     'conversations.toast.mark_unread_failed': 'Error al marcar como no leída.',
     'conversations.toast.marked_unread': 'Marcada como no leída.',
     'conversations.toast.individual_support_update_failed': 'No fue posible actualizar la atención individual.',
     'conversations.toast.number_unavailable_send': 'Número no disponible — no es posible enviar mensaje.',
     'conversations.toast.fast_order_individual_only': 'Fast Order disponible solo en conversaciones individuales',
     'conversations.toast.contact_phone_unavailable_order': 'Teléfono del contacto no disponible para generar pedido',
     'conversations.toast.select_messages_generate_order': 'Seleccione los mensajes del pedido y toque Generar pedido',
     'conversations.toast.contact_phone_unavailable': 'Teléfono del contacto no disponible',
     'conversations.toast.select_at_least_one_message': 'Seleccione al menos un mensaje',
     'conversations.toast.messages_without_text': 'Los mensajes seleccionados no tienen texto utilizable',
     'conversations.toast.order_interpreted_review': 'Pedido interpretado — revise y finalice',
     'conversations.toast.payment_not_identified': 'No identifiqué la forma de pago en ese mensaje',
     'conversations.toast.field_updated': '{field} actualizado',
     'conversations.error.media_unavailable': 'No es posible cargar este medio (instancia de WhatsApp o ID del mensaje ausente).',
     'conversations.error.evolution_media_not_returned': 'Evolution no devolvió el medio. Revise el endpoint getBase64FromMediaMessage en el servidor.',
     'conversations.error.load_messages': 'Error al cargar mensajes',
     'conversations.error.cnpj_unavailable_send': 'CNPJ no disponible para enviar mensaje.',
     'conversations.error.privacy_number_unavailable': 'Número del contacto no disponible (privacidad de WhatsApp). No se puede enviar hasta que aparezca el teléfono en la lista.',
     'conversations.error.send_message': 'Error al enviar mensaje',
     'conversations.error.send_failed_support':
       'Verifique la conexión de WhatsApp o contacte a nuestro soporte.',
     'conversations.error.generate_qr': 'No fue posible generar el código QR',
     'conversations.error.get_qr': 'Error al obtener el código QR',
     'conversations.error.disconnect_failed':
       'No fue posible desconectar. Verifique la conexión de WhatsApp o contacte a nuestro soporte.',
     'conversations.error.create_instance': 'Error al crear instancia',
     'internal_order.toast.cep_not_found': 'Código postal no encontrado',
     'internal_order.toast.paste_ifood_text': 'Pegue el texto del pedido de iFood antes de interpretar.',
     'internal_order.toast.cnpj_not_found': 'CNPJ no encontrado para contextualizar el pedido.',
     'internal_order.toast.ifood_parse_error': 'Error al interpretar pedido de iFood con IA.',
     'internal_order.toast.ai_unexpected_format': 'Respuesta de la IA en formato inesperado.',
     'internal_order.toast.ifood_parsed_success': 'Pedido de iFood interpretado con éxito. Revise antes de aplicar al carrito.',
     'internal_order.toast.ai_unexpected_error': 'Error inesperado al llamar a la IA.',
     'internal_order.toast.address_updated': 'Dirección actualizada en el registro del cliente',
     'internal_order.toast.address_update_failed': 'Pedido creado, pero no se pudo actualizar el registro',
     'internal_order.phone_lookup.error': 'Error al buscar el registro — intente de nuevo',
     'internal_order.toast.confirm_customer_name': 'Confirme el nombre del cliente (parece inválido o incompleto)',
     'internal_order.toast.missing_required_group': 'En el ítem "{item}", elija al menos {min} en "{group}" antes de finalizar',
     'internal_order.toast.open_required_addon': 'Elija al menos {min} en "{group}" del ítem "{item}" — luego continúe la finalización',
     'internal_order.toast.capture_loaded_review': 'Pedido capturado del iFood — revise tasa, ítems y adicionales en el checkout',
     'internal_order.toast.capture_order_not_found': 'No encontramos el pedido capturado. Vuelva a Pedidos y ábralo de nuevo.',
     'internal_order.toast.capture_order_updated': 'Pedido iFood actualizado — apruébelo en Pedidos cuando esté listo',
     'internal_order.toast.double_click_message_name': 'Doble clic en un mensaje del cliente para usarlo como nombre',
     'internal_order.confirm_name_hint': 'Confirme el nombre — parece inválido o incompleto',
     'internal_order.online_ifood_payment': 'Pago Online/iFood',
     'internal_order.toggle_delivery_fee_source': 'Clic para alternar: Sistema ↔ iFood',
     'internal_order.fee_source_lowest': 'Menor',
     'internal_order.fee_source_system': 'Sistema',
     'internal_order.fee_source_ifood': 'iFood',
     'internal_order.charge_customer': 'Cobrar al Cliente',
     'internal_order.confirm_address_update_title': '¿Actualizar dirección del cliente?',
     'internal_order.confirm_address_update_message': 'La dirección de este pedido es distinta al registro del cliente. ¿Desea guardar la nueva dirección para próximos pedidos?',
     'internal_order.current_registry_address': 'Registro actual',
     'internal_order.current_order_address': 'Dirección de este pedido',
     'internal_order.confirm_address_update_hint': 'Si es solo una entrega puntual en otro lugar, elija "No guardar".',
     'internal_order.save_to_registry': 'Guardar en el registro',
     'internal_order.do_not_save': 'No guardar',
     'company.toast.fill_pix_fields': 'Complete todos los campos PIX antes de guardar.',
     'company.toast.pix_saved': 'Datos PIX guardados con éxito',
     'company.toast.pix_save_error': 'Error al guardar datos PIX',
     'company.toast.refreshing_server_data': 'Actualizando datos del servidor...',
     'company.toast.refreshing_services_data': 'Actualizando datos de servicios del servidor...',
     'company.toast.service_category_created': '¡Categoría "{name}" creada con éxito!',
     'company.toast.cnpj_not_found': 'CNPJ no encontrado',
     'company.toast.save_data_error': 'Error al guardar datos: {error}',
     'company.toast.saving_services_changes': 'Guardando cambios de servicios...',
     'company.toast.services_changes_saved': '¡Cambios de servicios guardados con éxito!',
     'company.toast.save_services_error': 'Error al guardar servicios: {error}',
     'company.toast.unknown_error': 'Error desconocido',
     'company.toast.service_updated': '¡Servicio actualizado con éxito!',
     'company.toast.save_changes_error': 'Error al guardar cambios',
     'company.toast.service_not_found': 'Servicio no encontrado',
     'company.toast.invalid_categories_structure': 'Estructura de categorías inválida',
     'company.toast.category_not_found_named': 'Categoría "{name}" no encontrada',
     'company.toast.invalid_service_identifier': 'Identificador de servicio inválido',
     'company.toast.service_deleted': '¡Servicio "{name}" eliminado con éxito!',
     'company.toast.category_updated': '¡Categoría actualizada con éxito!',
     'company.toast.save_category_changes_error': 'Error al guardar cambios de la categoría',
     'company.toast.no_category_found': 'Ninguna categoría encontrada',
     'company.toast.category_not_found': 'Categoría no encontrada',
     'company.toast.category_access_error': 'Error al acceder a la categoría',
     'company.toast.category_deleted': '¡Categoría "{name}" eliminada con éxito!',
     'company.toast.delete_category_error': 'Error al eliminar categoría: {error}',
     'company.toast.operation_cancelled': 'Operación cancelada',
     'company.toast.save_categories_order_error': 'Error al guardar el orden de categorías',
     'company.toast.save_product_position_error': 'Error al guardar la posición del producto',
     'company.toast.no_menu_data_delete_product': 'No hay datos de menú para eliminar producto',
     'company.toast.save_deletion_error': 'Error al guardar eliminación en la base',
     'company.toast.product_not_found': 'Producto no encontrado',
     'company.toast.no_menu_data_delete_category': 'No hay datos de menú para eliminar categoría',
     'company.toast.invalid_delivery_fee': 'Por favor, ingrese una tarifa válida mayor que cero',
     'company.toast.delivery_fees_saved': 'Tarifas de entrega guardadas.',
     'company.toast.delivery_update_failed': 'No se pudieron guardar las tarifas. Intente de nuevo.',
     'company.toast.menu_images_saved': 'Imágenes del menú actualizadas.',
     'company.confirm.delete_fee_title': '¿Eliminar esta tarifa?',
     'company.confirm.delete_fee_message': 'Esto elimina la tarifa de R$ {amount} y sus {count} barrios.',
     'company.toast.add_at_least_one_neighborhood': 'Por favor, agregue al menos un barrio',
     'company.toast.delivery_data_not_found': 'Error: datos de delivery no encontrados',
     'company.toast.delivery_fee_already_exists': 'Esta tarifa ya existe. Por favor, elija otro valor.',
     'company.toast.category_name_required': 'El nombre de la categoría es obligatorio',
     'company.toast.at_least_one_price_type': 'Es obligatorio al menos un tipo de precio',
     'company.toast.pizza_flavors_range': 'Para categorías de Pizza, cada tipo de precio debe tener entre 1 y 4 sabores',
     'company.niche.label': 'Nicho de la empresa',
     'company.niche.delivery': 'Delivery',
     'company.niche.clinic': 'Clínica',
     'company.niche.tattoo': 'Tatuaje',
     'company.niche.workshop': 'Taller',
     'company.niche.digital': 'Digital',
     'company.niche.aesthetics': 'Estética',
     'company.specialty.label': 'Especialidades',
     'company.specialty.hint': 'Puede marcar varias.',
     'company.specialty.pizza': 'Pizza',
     'company.specialty.sushi': 'Sushi',
     'company.specialty.hamburger': 'Hamburguesa',
     'company.specialty.pastel': 'Pastel',
     'company.specialty.acai': 'Açaí',
     'company.specialty.mexican': 'Mexicano',
     'company.specialty.snackbar': 'Cafetería',
     'company.specialty.infoproducts': 'Infoproductos',
     'company.specialty.marketplace': 'Marketplace',
     'company.specialty.marketplace_sales': 'Venta en marketplaces',
     'company.specialty.ecommerce': 'Tienda virtual',
     'company.specialty.subscription': 'Suscripción',
     'company.specialty.affiliates': 'Afiliados',
     'company.specialty.digital_services': 'Servicios digitales',
     'company.specialty.digital_service_label': '¿Qué servicio digital?',
     'company.specialty.digital_service_placeholder': 'Ej.: sitios web, consultoría, diseño',
     'company.specialty.general_practice': 'Clínica general',
     'company.specialty.cardiology': 'Cardiología',
     'company.specialty.veterinary': 'Veterinaria',
     'company.specialty.nutrition': 'Nutricionista',
     'company.specialty.dentistry': 'Odontología',
     'company.specialty.dermatology': 'Dermatología',
     'company.specialty.psychology': 'Psicología',
     'company.specialty.physiotherapy': 'Fisioterapia',
     'company.specialty.ophthalmology': 'Oftalmología',
     'company.specialty.gynecology': 'Ginecología',
     'company.specialty.pediatrics': 'Pediatría',
     'company.specialty.orthopedics': 'Ortopedia',
     'company.specialty.aesthetics': 'Estética',
     'company.specialty.endocrinology': 'Endocrinología',
     'company.specialty.neurology': 'Neurología',
     'company.specialty.psychiatry': 'Psiquiatría',
     'company.specialty.speech_therapy': 'Fonoaudiología',
     'company.specialty.urology': 'Urología',
     'company.specialty.ent': 'Otorrino',
     'company.specialty.nails': 'Uñas',
     'company.specialty.hair': 'Cabello',
     'company.specialty.brows': 'Cejas',
     'company.specialty.lashes': 'Pestañas',
     'company.specialty.makeup': 'Maquillaje',
     'company.specialty.hair_removal': 'Depilación',
     'company.specialty.facial': 'Estética facial',
     'company.specialty.body': 'Estética corporal',
     'company.specialty.massage': 'Masaje',
     'company.specialty.micropigmentation': 'Micropigmentación',
     'company.specialty.harmonization': 'Armonización',
     'company.specialty.aesthetic_surgery': 'Cirugía estética',
     'company.specialty.tanning': 'Bronceado',
     'dev.cardapio.save_failed': 'No se pudo guardar. Intente de nuevo en unos momentos.',
     'dev.cardapio.load_failed': 'No se pudo cargar la empresa. Intente de nuevo.',
     'company.toast.no_menu_data_add_category': 'No hay datos de menú para agregar categoría',
     'company.toast.category_name_exists': 'Ya existe una categoría con este nombre',
     'company.toast.additional_categories_missing': 'Algunas categorías de adicionales seleccionadas no existen',
     'company.toast.no_menu_data_add_product': 'No hay datos de menú para agregar producto',
     'company.toast.product_added_edit_name': '¡Nuevo producto agregado! Comience editando el nombre.',
     'company.toast.fix_validation_errors': 'Por favor, corrija los errores de validación antes de guardar.',
     'company.toast.no_changes_detected': 'No se detectaron cambios.',
     'company.toast.payment_methods_updated': 'Formas de pago actualizadas con éxito',
     'company.toast.payment_methods_update_error': 'Error al actualizar formas de pago',
     'company.toast.payment_text_updated': 'Texto de pago actualizado con éxito',
     'company.toast.payment_text_update_error': 'Error al actualizar texto de pago',
     'company.toast.public_order_types_updated': 'Tipos de pedido en la página pública actualizados',
     'company.toast.public_order_types_update_error': 'Error al actualizar tipos de pedido en la página pública: {error}',
     'company.toast.menu_image_replaced': 'Imagen reemplazada con éxito.',
     'company.toast.process_image_error': 'Error al procesar imagen.',
     'company.toast.cnpj_unavailable_delete_image': 'CNPJ no disponible para eliminar imagen.',
     'company.toast.menu_image_deleted': 'Imagen eliminada con éxito.',
     'company.toast.delete_image_error': 'Error al eliminar imagen.',
   }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => detectInitialLanguage());

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    }
  }, []);

  useEffect(() => {
    const onDesk = (ev: Event) => {
      const detail = (ev as CustomEvent<string>).detail;
      if (detail === 'pt' || detail === 'en' || detail === 'es') setLanguage(detail);
    };
    window.addEventListener('delidesk-set-language', onDesk);
    return () => window.removeEventListener('delidesk-set-language', onDesk);
  }, [setLanguage]);

  const t = useCallback((key: string, vars?: Record<string, string | number>): string => {
    let text =
      translations[language][key as keyof typeof translations[typeof language]] || key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return text;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
